import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { buildAbsoluteUrl, useSeo } from './seo';

/*
  NOTE FOR SETUP: To use the custom fonts, add the following line to the <head> of your public/index.html file:
  <link href="https://fonts.googleapis.com/css2?family=Lora:wght@700&family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
*/

const isLocalHostName = (host) => host === 'localhost' || host === '127.0.0.1' || host === '[::1]';

const getApiBaseUrl = () => {
    const configuredBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    const isBrowser = typeof window !== 'undefined';

    if (!isBrowser) {
        return configuredBaseUrl || 'https://localhost:7287';
    }

    const currentHost = window.location.hostname;
    if (configuredBaseUrl) {
        try {
            const parsed = new URL(configuredBaseUrl, window.location.origin);
            if (!isLocalHostName(currentHost) && isLocalHostName(parsed.hostname)) {
                return '/caravanapi';
            }
        } catch {
            // Keep configured value if URL parsing fails.
        }

        return configuredBaseUrl;
    }

    return isLocalHostName(currentHost) ? 'https://localhost:7287' : '/caravanapi';
};
const API_BASE_URL = getApiBaseUrl();
const PLACEHOLDER_IMG = 'https://placehold.co/800x600/cccccc/FFFFFF?text=Image+Not+Found';
const PUBLIC_URL_BASE = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
const APP_BASE_PATH = PUBLIC_URL_BASE;
const LOGO_FILE_NAME = 'udlejningsvogn-logo.jpeg';
const SITE_NAME = 'Udlejningsvogn.dk';
const SITE_DEFAULT_DESCRIPTION = 'Rent caravans, campers, and motorhomes across Denmark with transparent pricing, verified listings, and direct online booking.';
const SITE_DANISH_DESCRIPTION = 'Lej campingvogne, autocampere og campere i hele Danmark med gennemsigtige priser, verificerede annoncer og direkte online booking.';
const buildLogoPath = (basePath) => {
    const normalizedBasePath = (basePath || '').replace(/\/+$/, '');
    return normalizedBasePath ? `${normalizedBasePath}/${LOGO_FILE_NAME}` : LOGO_FILE_NAME;
};
const DEPLOYMENT_BASE_PATH = typeof window !== 'undefined'
    ? (() => {
        const firstPathSegment = window.location.pathname.split('/').filter(Boolean)[0];
        return firstPathSegment ? `/${firstPathSegment}` : '';
    })()
    : '';
const PORTAL_LOGO_SRC_CANDIDATES = [...new Set([
    buildLogoPath(PUBLIC_URL_BASE),
    buildLogoPath(DEPLOYMENT_BASE_PATH),
    `/${LOGO_FILE_NAME}`,
    LOGO_FILE_NAME
])];
const PORTAL_LOGO_SRC = PORTAL_LOGO_SRC_CANDIDATES[0];
const trimSlashes = (value) => String(value || '').replace(/^\/+|\/+$/g, '');
const slugifyRouteSegment = (value) => trimSlashes(
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
);
const buildDetailSlug = (caravan) => {
    const caravanId = Number(caravan?.caravan_id ?? caravan?.caravanId ?? 0);
    const nameSlug = slugifyRouteSegment(caravan?.name || caravan?.location || 'caravan');
    if (caravanId > 0 && nameSlug) return `${caravanId}-${nameSlug}`;
    if (caravanId > 0) return String(caravanId);
    return nameSlug || 'caravan';
};
const readCaravanIdFromSlug = (value) => {
    const normalized = String(value || '').trim();
    const match = normalized.match(/^(\d+)/);
    if (match) {
        return Number(match[1]);
    }
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : 0;
};
const buildRouteDescriptor = (route = {}, caravan = null) => {
    const page = route?.page || 'home';
    const startDate = route?.startDate || '';
    const endDate = route?.endDate || '';
    const appendDateFilters = (query) => {
        if (startDate) query.set('startDate', startDate);
        if (endDate) query.set('endDate', endDate);
    };

    switch (page) {
        case 'list': {
            return { pathname: '/caravans', query: new URLSearchParams() };
        }
        case 'detail': {
            const query = new URLSearchParams();
            appendDateFilters(query);
            const fallbackId = Number(route?.caravan_id || 0);
            const slug = caravan
                ? buildDetailSlug(caravan)
                : (route?.slug || (fallbackId > 0 ? String(fallbackId) : 'caravan'));
            return { pathname: `/caravans/${encodeURIComponent(slug)}`, query };
        }
        case 'about':
            return { pathname: '/about', query: new URLSearchParams() };
        case 'terms':
            return { pathname: '/terms', query: new URLSearchParams() };
        case 'login': {
            const query = new URLSearchParams();
            if (route?.email) query.set('email', route.email);
            if (route?.message) query.set('message', route.message);
            return { pathname: '/login', query };
        }
        case 'forgot-password': {
            const query = new URLSearchParams();
            if (route?.email) query.set('email', route.email);
            return { pathname: '/forgot-password', query };
        }
        case 'signup':
            return { pathname: '/signup', query: new URLSearchParams() };
        case 'booking': {
            const query = new URLSearchParams();
            appendDateFilters(query);
            return { pathname: `/booking/${encodeURIComponent(route?.caravanId || '')}`, query };
        }
        case 'booking-success': {
            const query = new URLSearchParams();
            if (route?.caravanName) query.set('caravanName', route.caravanName);
            if (route?.ownerContactNumber) query.set('ownerContactNumber', route.ownerContactNumber);
            return { pathname: '/booking-success', query };
        }
        case 'become-owner':
            return { pathname: '/become-owner', query: new URLSearchParams() };
        case 'owner-availability':
            return { pathname: '/owner/availability', query: new URLSearchParams() };
        case 'owner-business':
            return { pathname: '/owner/business', query: new URLSearchParams() };
        case 'admin':
            return { pathname: '/admin', query: new URLSearchParams() };
        case 'my-bookings':
            return { pathname: '/my-bookings', query: new URLSearchParams() };
        case 'profile-details':
            return { pathname: '/profile', query: new URLSearchParams() };
        case 'delivery-portal':
            return { pathname: '/delivery-portal', query: new URLSearchParams() };
        case 'become-delivery-partner':
            return { pathname: '/become-delivery-partner', query: new URLSearchParams() };
        case 'change-password':
            return { pathname: '/change-password', query: new URLSearchParams() };
        case 'home':
        default:
            return { pathname: '/', query: new URLSearchParams() };
    }
};
const buildHrefFromRoute = (route = {}, caravan = null) => {
    const { pathname, query } = buildRouteDescriptor(route, caravan);
    const basePath = APP_BASE_PATH || '';
    const basePrefix = basePath ? `${basePath}` : '';
    const normalizedPath = pathname === '/' ? '' : pathname;
    const search = query.toString();
    const href = `${basePrefix}${normalizedPath || '/'}`;
    return search ? `${href}?${search}` : href;
};
const getRouteFromLocation = () => {
    if (typeof window === 'undefined') {
        return { page: 'home' };
    }

    const currentPath = window.location.pathname;
    const basePath = APP_BASE_PATH || '';
    const relativePath = basePath && currentPath.startsWith(basePath)
        ? currentPath.slice(basePath.length)
        : currentPath;
    const normalizedRelativePath = relativePath || '/';
    const segments = normalizedRelativePath.split('/').filter(Boolean);
    const searchParams = new URLSearchParams(window.location.search);

    if (segments.length === 0) return { page: 'home' };

    const [first, second] = segments;
    if (first === 'caravans' && second) {
        return {
            page: 'detail',
            caravan_id: readCaravanIdFromSlug(decodeURIComponent(second)),
            slug: decodeURIComponent(second),
            startDate: searchParams.get('startDate') || '',
            endDate: searchParams.get('endDate') || ''
        };
    }
    if (first === 'caravans') return { page: 'list' };
    if (first === 'about') return { page: 'about' };
    if (first === 'terms') return { page: 'terms' };
    if (first === 'login') return { page: 'login', email: searchParams.get('email') || '', message: searchParams.get('message') || '' };
    if (first === 'forgot-password') return { page: 'forgot-password', email: searchParams.get('email') || '' };
    if (first === 'signup') return { page: 'signup' };
    if (first === 'booking' && second) {
        return {
            page: 'booking',
            caravanId: Number(second) || 0,
            startDate: searchParams.get('startDate') || '',
            endDate: searchParams.get('endDate') || ''
        };
    }
    if (first === 'booking-success') {
        return {
            page: 'booking-success',
            caravanName: searchParams.get('caravanName') || '',
            ownerContactNumber: searchParams.get('ownerContactNumber') || ''
        };
    }
    if (first === 'become-owner') return { page: 'become-owner' };
    if (first === 'owner' && second === 'availability') return { page: 'owner-availability' };
    if (first === 'owner' && second === 'business') return { page: 'owner-business' };
    if (first === 'admin') return { page: 'admin' };
    if (first === 'my-bookings') return { page: 'my-bookings' };
    if (first === 'profile') return { page: 'profile-details' };
    if (first === 'delivery-portal') return { page: 'delivery-portal' };
    if (first === 'become-delivery-partner') return { page: 'become-delivery-partner' };
    if (first === 'change-password') return { page: 'change-password' };
    return { page: 'home' };
};
const truncateSeoText = (value, maxLength = 160) => {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, Math.max(maxLength - 1, 0)).trimEnd()}…`;
};
const getSeoPathname = (route = {}, caravan = null) => buildRouteDescriptor(route, caravan).pathname;
const buildSeoMetadata = ({ route, caravans, language }) => {
    const routePage = route?.page || 'home';
    const locale = language === 'da' ? 'da_DK' : 'en_US';
    const websiteUrl = buildAbsoluteUrl({ path: '/', basePath: APP_BASE_PATH });
    const logoUrl = buildAbsoluteUrl({ path: `/${LOGO_FILE_NAME}`, basePath: APP_BASE_PATH });
    const matchedCaravan = routePage === 'detail'
        ? (Array.isArray(caravans) ? caravans.find((item) => Number(item?.caravan_id) === Number(route?.caravan_id)) : null)
        : null;
    const detailPath = getSeoPathname(route, matchedCaravan);

    const buildWebsiteSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: websiteUrl,
        inLanguage: language === 'da' ? 'da-DK' : 'en-US'
    });

    if (routePage === 'detail' && matchedCaravan) {
        const locationText = String(matchedCaravan?.location || 'Denmark').trim();
        const vehicleType = String(matchedCaravan?.typeName || matchedCaravan?.type_name || 'Caravan').trim();
        const description = truncateSeoText(
            matchedCaravan?.description
            || `Rent ${matchedCaravan?.name || 'this caravan'} in ${locationText}. ${vehicleType} rental with pricing, amenities, and booking availability on ${SITE_NAME}.`
        );
        const imageUrl = getCaravanMainImage(matchedCaravan);
        const detailRating = getCaravanDisplayRating(matchedCaravan);
        const reviewCount = Math.max(Number(matchedCaravan?.reviewCount || 0), 0);
        const detailTitle = `${matchedCaravan?.name || vehicleType} in ${locationText} | ${SITE_NAME}`;
        const schema = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: matchedCaravan?.name || vehicleType,
            description,
            image: imageUrl ? [imageUrl] : undefined,
            brand: {
                '@type': 'Brand',
                name: SITE_NAME
            },
            category: vehicleType,
            url: buildAbsoluteUrl({ path: detailPath, basePath: APP_BASE_PATH }),
            offers: {
                '@type': 'Offer',
                priceCurrency: 'DKK',
                price: Number(matchedCaravan?.price_per_day || 0),
                availability: 'https://schema.org/InStock',
                url: buildAbsoluteUrl({ path: detailPath, basePath: APP_BASE_PATH })
            }
        };

        if (reviewCount > 0) {
            schema.aggregateRating = {
                '@type': 'AggregateRating',
                ratingValue: detailRating.toFixed(1),
                reviewCount
            };
        }

        return {
            title: detailTitle,
            description,
            path: detailPath,
            imagePath: imageUrl || `/${LOGO_FILE_NAME}`,
            locale,
            schema: [buildWebsiteSchema(), schema]
        };
    }

    if (routePage === 'list') {
        const isDanish = language === 'da';
        const description = isDanish
            ? 'Find campingvogn udlejning i Danmark. Sammenlign ledige campingvogne, autocampere og campere efter pris, kapacitet, dato og faciliteter.'
            : 'Browse available caravans, campers, and motorhomes in Denmark by price, capacity, trip dates, and features.';
        const listItems = (Array.isArray(caravans) ? caravans : [])
            .slice(0, 24)
            .map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item?.name || `Caravan ${index + 1}`,
                url: buildAbsoluteUrl({
                    path: getSeoPathname({ page: 'detail', caravan_id: item?.caravan_id }, item),
                    basePath: APP_BASE_PATH
                })
            }));

        return {
            title: isDanish
                ? `Campingvogn udlejning i Danmark | ${SITE_NAME}`
                : `Caravan Rentals in Denmark | ${SITE_NAME}`,
            description,
            path: '/caravans',
            imagePath: `/${LOGO_FILE_NAME}`,
            locale,
            schema: [
                buildWebsiteSchema(),
                {
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    name: isDanish ? 'Ledige campingvogne til leje i Danmark' : 'Available caravan rentals',
                    itemListElement: listItems
                }
            ]
        };
    }

    if (routePage === 'about') {
        return {
            title: `About ${SITE_NAME} | Caravan Marketplace in Denmark`,
            description: 'Learn how Udlejningsvogn.dk helps renters and caravan owners connect across Denmark with trusted listings and direct bookings.',
            path: '/about',
            imagePath: `/${LOGO_FILE_NAME}`,
            locale,
            schema: [
                buildWebsiteSchema(),
                {
                    '@context': 'https://schema.org',
                    '@type': 'Organization',
                    name: SITE_NAME,
                    url: websiteUrl,
                    logo: logoUrl
                }
            ]
        };
    }

    if (routePage === 'terms') {
        return {
            title: `Terms and Conditions | ${SITE_NAME}`,
            description: 'Read the latest customer and owner rental terms, booking conditions, and PDF policy documents for Udlejningsvogn.dk.',
            path: '/terms',
            imagePath: `/${LOGO_FILE_NAME}`,
            locale,
            schema: [buildWebsiteSchema()]
        };
    }

    const noIndexPages = new Set([
        'login',
        'forgot-password',
        'signup',
        'booking',
        'booking-success',
        'become-owner',
        'owner-availability',
        'owner-business',
        'admin',
        'my-bookings',
        'profile-details',
        'delivery-portal',
        'become-delivery-partner',
        'change-password'
    ]);

    return {
        title: language === 'da'
            ? `Campingvogn udlejning i Danmark | ${SITE_NAME}`
            : `${SITE_NAME} | Caravan Rentals in Denmark`,
        description: language === 'da' ? SITE_DANISH_DESCRIPTION : SITE_DEFAULT_DESCRIPTION,
        path: getSeoPathname(route),
        imagePath: `/${LOGO_FILE_NAME}`,
        locale,
        robots: noIndexPages.has(routePage) ? 'noindex,nofollow' : 'index,follow',
        schema: [
            buildWebsiteSchema(),
            {
                '@context': 'https://schema.org',
                '@type': 'Organization',
                name: SITE_NAME,
                url: websiteUrl,
                logo: logoUrl
            }
        ]
    };
};
const handlePortalLogoError = (event) => {
    const image = event.currentTarget;
    const currentIndex = Number(image.dataset.logoIndex || '0');
    const nextIndex = currentIndex + 1;
    if (nextIndex < PORTAL_LOGO_SRC_CANDIDATES.length) {
        image.dataset.logoIndex = String(nextIndex);
        image.src = PORTAL_LOGO_SRC_CANDIDATES[nextIndex];
        return;
    }

    image.style.display = 'none';
};
const METHOD_OVERRIDE_HEADER = 'X-HTTP-Method-Override';
const METHOD_OVERRIDE_QUERY_PARAM = '_method';
const RETRYABLE_HTTP_METHODS = new Set(['PUT', 'PATCH', 'DELETE']);
const FORCE_METHOD_OVERRIDE = (process.env.REACT_APP_FORCE_METHOD_OVERRIDE || '').toLowerCase() === 'true';
const MAX_CARAVAN_IMAGES = 10;
const MAX_CARAVAN_IMAGE_SIZE_MB = 10;
const MAX_CARAVAN_IMAGE_SIZE_BYTES = MAX_CARAVAN_IMAGE_SIZE_MB * 1024 * 1024;
const MAX_COMPLAINT_IMAGES = 6;
const MAX_COMPLAINT_IMAGE_SIZE_MB = 10;
const MAX_COMPLAINT_IMAGE_SIZE_BYTES = MAX_COMPLAINT_IMAGE_SIZE_MB * 1024 * 1024;
const MAX_TERMS_PDF_SIZE_MB = 25;
const MAX_TERMS_PDF_SIZE_BYTES = MAX_TERMS_PDF_SIZE_MB * 1024 * 1024;
const TERMS_AUDIENCES = ['customer', 'owner'];
const STANDARD_PICKUP_TIME = '15:00';
const STANDARD_DROP_TIME = '11:00';
const MOTORHOME_SPEC_TYPE_NAMES = new Set(['motorhome', 'campervan', 'rooftop', 'rooftopcamper']);
const normalizeVehicleTypeName = (typeName) => String(typeName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
const isMotorhomeSpecType = (typeName) => {
    const normalizedTypeName = normalizeVehicleTypeName(typeName);
    return MOTORHOME_SPEC_TYPE_NAMES.has(normalizedTypeName)
        || normalizedTypeName.startsWith('rooftopcamper');
};
const STATIONARY_SPEC_TYPE_NAMES = new Set(['stationary', 'stationarycaravan', 'staticcaravan', 'static']);
const isStationarySpecType = (typeName) => {
    const normalizedTypeName = normalizeVehicleTypeName(typeName);
    if (!normalizedTypeName) return false;
    if (STATIONARY_SPEC_TYPE_NAMES.has(normalizedTypeName)) return true;
    return normalizedTypeName.includes('stationary') || normalizedTypeName.includes('static');
};
const isStationaryCaravan = (caravan) => {
    if (!caravan) return false;
    const category = normalizeVehicleTypeName(caravan?.vehicle_category || caravan?.vehicleCategory);
    if (category === 'stationary') return true;
    const typeName = caravan?.typeName || caravan?.type_name;
    return isStationarySpecType(typeName);
};
const isLongTermCaravan = (caravan) => Boolean(caravan?.long_term_enabled ?? caravan?.longTermEnabled ?? false);
const getMinimumBookingMonths = (caravan) => {
    const value = Number(caravan?.minimum_booking_months ?? caravan?.minimumBookingMonths ?? 0);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.floor(value);
};
const hasValidOwnerBankDetails = (profile) => {
    const bankRegistrationNo = String(profile?.bankRegistrationNo ?? profile?.BankRegistrationNo ?? '').replace(/\D/g, '');
    const bankAccountNo = String(profile?.bankAccountNo ?? profile?.BankAccountNo ?? '').replace(/\D/g, '');
    return bankRegistrationNo.length === 4 && bankAccountNo.length >= 4 && bankAccountNo.length <= 20;
};
const isOwnerProfileRoute = (page) => (
    page === 'become-owner'
    || page === 'owner-availability'
    || page === 'owner-business'
);
const getOversizedImageFiles = (files) => (
    (Array.isArray(files) ? files : [])
        .filter((file) => Number(file?.size || 0) > MAX_CARAVAN_IMAGE_SIZE_BYTES)
);
const buildImageSizeError = (files) => {
    const oversized = getOversizedImageFiles(files);
    if (oversized.length === 0) return '';

    const shownNames = oversized
        .slice(0, 3)
        .map((file) => file?.name || 'unnamed-file')
        .join(', ');
    const moreText = oversized.length > 3 ? ` and ${oversized.length - 3} more` : '';
    return `Each image must be ${MAX_CARAVAN_IMAGE_SIZE_MB} MB or smaller. Oversized file(s): ${shownNames}${moreText}.`;
};
const rawFetch = window.fetch.bind(window);
const shouldForceMethodOverride = () => (
    typeof window !== 'undefined'
    && FORCE_METHOD_OVERRIDE
    && !isLocalHostName(window.location.hostname)
);
const addMethodOverrideQueryToUrl = (urlValue, methodValue) => {
    try {
        const baseOrigin = window.location?.origin || 'http://localhost';
        const url = new URL(urlValue, baseOrigin);
        url.searchParams.set(METHOD_OVERRIDE_QUERY_PARAM, methodValue);
        return url.toString();
    } catch {
        const separator = String(urlValue).includes('?') ? '&' : '?';
        return `${urlValue}${separator}${METHOD_OVERRIDE_QUERY_PARAM}=${encodeURIComponent(methodValue)}`;
    }
};
const buildMethodOverrideRequest = (input, init, requestMethod) => {
    const retryHeaders = new Headers(init?.headers || {});
    retryHeaders.set(METHOD_OVERRIDE_HEADER, requestMethod);

    const hasFormDataBody = typeof FormData !== 'undefined' && init?.body instanceof FormData;
    if (init?.body && !hasFormDataBody && !retryHeaders.has('Content-Type')) {
        retryHeaders.set('Content-Type', 'application/json');
    }

    const requestUrl = typeof input === 'string'
        ? input
        : (input && typeof input.url === 'string' ? input.url : null);
    const retryInput = requestUrl
        ? addMethodOverrideQueryToUrl(requestUrl, requestMethod)
        : input;

    return {
        retryInput,
        retryInit: {
            ...init,
            method: 'POST',
            headers: retryHeaders
        }
    };
};
const fetch = async (input, init = {}) => {
    const requestMethod = String(
        init?.method
        || ((typeof Request !== 'undefined' && input instanceof Request) ? input.method : 'GET')
    ).toUpperCase();

    if (RETRYABLE_HTTP_METHODS.has(requestMethod) && shouldForceMethodOverride()) {
        const { retryInput, retryInit } = buildMethodOverrideRequest(input, init, requestMethod);
        return rawFetch(retryInput, retryInit);
    }

    let response = await rawFetch(input, init);
    if (!RETRYABLE_HTTP_METHODS.has(requestMethod) || response.status !== 405) {
        return response;
    }

    const { retryInput, retryInit } = buildMethodOverrideRequest(input, init, requestMethod);
    return rawFetch(retryInput, retryInit);
};

const getInitialLanguage = () => 'da';

const resolveApiAssetUrl = (url) => {
    const raw = (url || '').toString().trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;

    const normalizedPath = `/${raw.replace(/\\/g, '/').replace(/^\/+/, '')}`;
    const normalizedBase = (API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (!normalizedBase) {
        return normalizedPath;
    }

    if (/^https?:\/\//i.test(normalizedBase)) {
        try {
            const absolute = new URL(normalizedPath, `${normalizedBase}/`);
            return absolute.toString();
        } catch {
            return `${normalizedBase}${normalizedPath}`;
        }
    }

    if (normalizedBase.startsWith('/')) {
        if (normalizedPath === normalizedBase || normalizedPath.startsWith(`${normalizedBase}/`)) {
            return normalizedPath;
        }
        return `${normalizedBase}${normalizedPath}`;
    }

    return normalizedPath;
};

const resolveImageUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    return resolveApiAssetUrl(url);
};

const getCaravanImages = (caravan) => {
    const raw = [caravan?.mainImageUrl, caravan?.imageUrl, ...(caravan?.images || [])];
    const normalized = raw
        .map(resolveImageUrl)
        .filter(Boolean);
    return [...new Set(normalized)];
};

// Prefer a usable image even if the API shape changes
const getCaravanMainImage = (caravan) => getCaravanImages(caravan)[0] || PLACEHOLDER_IMG;
const formatBookingNumber = (bookingId) => `BK-${String(Number(bookingId) || 0).padStart(6, '0')}`;
const toAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
};
const BASE_CARAVAN_RATING = 3.9;
const getCaravanSittingCapacity = (caravan) => Math.max(Number(caravan?.capacity || 0), 0);
const getCaravanBedCount = (caravan) => {
    const beds = Number(caravan?.beds ?? caravan?.Beds ?? 0);
    if (Number.isFinite(beds) && beds > 0) {
        return Math.max(Math.floor(beds), 1);
    }

    const sittingCapacity = getCaravanSittingCapacity(caravan);
    return Math.max(sittingCapacity || 1, 1);
};
const getCaravanDisplayRating = (caravan) => {
    const rating = Number(caravan?.rating || 0);
    return Number.isFinite(rating) && rating > 0 ? rating : BASE_CARAVAN_RATING;
};
const getCaravanOwnWeightKg = (caravan) => {
    const value = Number(caravan?.own_weight_kg ?? caravan?.ownWeightKg ?? caravan?.motorhome_curb_weight_kg ?? caravan?.motorhomeCurbWeightKg ?? 0);
    return Number.isFinite(value) && value > 0 ? value : null;
};
const getCaravanTotalWeightKg = (caravan) => {
    const value = Number(caravan?.total_weight_kg ?? caravan?.totalWeightKg ?? caravan?.motorhome_allowed_total_weight_kg ?? caravan?.motorhomeAllowedTotalWeightKg ?? 0);
    return Number.isFinite(value) && value > 0 ? value : null;
};
const formatCaravanWeightKg = (value) => value === null || value === undefined
    ? 'N/A'
    : `${Number(value).toLocaleString('da-DK')} kg`;
const formatInr = (value) => `DKK ${toAmount(value).toLocaleString('da-DK')}`;
const getDeliveryJobPayoutAmount = (job) => toAmount(
    job?.committedAmount ?? job?.suggestedPayoutAmount ?? job?.doorstepFee
);
const readApiErrorMessage = async (response, fallbackMessage) => {
    const text = await response.text();
    const trimmed = (text || '').trim();
    if (!trimmed) return fallbackMessage;

    const startsWithHtml = trimmed.startsWith('<!DOCTYPE') || /^<html/i.test(trimmed);
    if (startsWithHtml) {
        if (response.status === 405) {
            return 'Request method is blocked by server configuration (IIS/WebDAV).';
        }
        return `Server returned HTTP ${response.status}.`;
    }

    return trimmed;
};
const normalizeTermsAudience = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'customer') return 'customer';
    if (normalized === 'owner' || normalized === 'caravan owner' || normalized === 'caravan_owner' || normalized === 'caravan-owner') {
        return 'owner';
    }
    return '';
};
const getTermsAudienceLabel = (audience, language = 'en') => {
    const normalizedAudience = normalizeTermsAudience(audience);
    if (normalizedAudience === 'owner') {
        return language === 'da' ? 'Campingejer' : 'Caravan Owner';
    }
    return language === 'da' ? 'Kunde' : 'Customer';
};
const groupTermsDocumentsByAudience = (documents) => TERMS_AUDIENCES.reduce((accumulator, audience) => {
    accumulator[audience] = (Array.isArray(documents) ? documents : [])
        .filter((item) => normalizeTermsAudience(item?.audience) === audience)
        .sort((left, right) => (
            Number(right?.versionNumber || 0) - Number(left?.versionNumber || 0)
            || Number(right?.termsDocumentId || 0) - Number(left?.termsDocumentId || 0)
        ));
    return accumulator;
}, { customer: [], owner: [] });
const formatFileSizeLabel = (value) => {
    const size = Number(value);
    if (!Number.isFinite(size) || size <= 0) return 'N/A';
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${size} bytes`;
};
const formatTermsDateTime = (value, language = 'en') => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString(language === 'da' ? 'da-DK' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};
const normalizeDeliveryJobStatus = (status) => String(status || '').trim().toLowerCase();
const formatDeliveryDateTime = (value, language = 'en') => {
    if (!value) return language === 'da' ? 'Ikke planlagt' : 'Not scheduled';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return language === 'da' ? 'Ikke planlagt' : 'Not scheduled';
    }

    return parsed.toLocaleString(language === 'da' ? 'da-DK' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};
const getDeliveryJobTypeLabel = (type, language = 'en') => {
    const normalized = String(type || '').trim().toLowerCase();
    if (normalized === 'return') {
        return language === 'da' ? 'Returlevering' : 'Return trip';
    }

    return language === 'da' ? 'Udlevering' : 'Outbound trip';
};
const getDeliveryJobStatusLabel = (status, language = 'en') => {
    const normalized = normalizeDeliveryJobStatus(status);
    if (normalized === 'accepted') {
        return language === 'da' ? 'Accepteret' : 'Accepted';
    }
    if (normalized === 'in_progress') {
        return language === 'da' ? 'I gang' : 'In progress';
    }
    if (normalized === 'completed') {
        return language === 'da' ? 'Fuldført' : 'Completed';
    }
    if (normalized === 'cancelled') {
        return language === 'da' ? 'Annulleret' : 'Cancelled';
    }

    return language === 'da' ? 'Åben' : 'Open';
};
const getDeliveryJobStatusBadgeClass = (status) => {
    const normalized = normalizeDeliveryJobStatus(status);
    if (normalized === 'accepted') return 'bg-blue-100 text-blue-700';
    if (normalized === 'in_progress') return 'bg-amber-100 text-amber-800';
    if (normalized === 'completed') return 'bg-emerald-100 text-emerald-800';
    if (normalized === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
};
const canAdminReopenDeliveryJob = (status) => ['accepted', 'in_progress'].includes(normalizeDeliveryJobStatus(status));
const canAdminCancelDeliveryJob = (status) => ['open', 'accepted', 'in_progress'].includes(normalizeDeliveryJobStatus(status));
const parsePendingUpdateSnapshot = (jsonValue) => {
    if (!jsonValue || typeof jsonValue !== 'string') return null;
    try {
        const parsed = JSON.parse(jsonValue);
        return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch {
        return null;
    }
};
const formatBoolLabel = (value) => (value ? 'Yes' : 'No');
const getPendingPricingChanges = (previousSnapshot, nextSnapshot) => {
    const previousPricing = previousSnapshot?.pricing || {};
    const nextPricing = nextSnapshot?.pricing || {};
    const changes = [];

    const pushNumericChange = (label, previousValue, nextValue) => {
        const prevAmount = Number(previousValue ?? 0);
        const nextAmount = Number(nextValue ?? 0);
        if (prevAmount !== nextAmount) {
            changes.push({
                label,
                previous: formatInr(prevAmount),
                next: formatInr(nextAmount)
            });
        }
    };

    pushNumericChange('Price/Day', previousPricing.pricePerDay, nextPricing.pricePerDay);
    pushNumericChange('Price/Week', previousPricing.pricePerWeek, nextPricing.pricePerWeek);
    pushNumericChange('Deposit', previousPricing.depositAmount, nextPricing.depositAmount);

    const previousDoorstep = Boolean(previousPricing.doorstepAvailable);
    const nextDoorstep = Boolean(nextPricing.doorstepAvailable);
    if (previousDoorstep !== nextDoorstep) {
        changes.push({
            label: 'Doorstep Available',
            previous: formatBoolLabel(previousDoorstep),
            next: formatBoolLabel(nextDoorstep)
        });
    }

    const previousDoorstepRate = previousPricing.doorstepRatePerKm === null || previousPricing.doorstepRatePerKm === undefined
        ? null
        : Number(previousPricing.doorstepRatePerKm);
    const nextDoorstepRate = nextPricing.doorstepRatePerKm === null || nextPricing.doorstepRatePerKm === undefined
        ? null
        : Number(nextPricing.doorstepRatePerKm);
    if ((previousDoorstepRate ?? null) !== (nextDoorstepRate ?? null)) {
        changes.push({
            label: 'Doorstep Rate/KM',
            previous: previousDoorstepRate === null ? '-' : formatInr(previousDoorstepRate),
            next: nextDoorstepRate === null ? '-' : formatInr(nextDoorstepRate)
        });
    }

    return changes;
};
const getPendingSeasonalChanges = (previousSnapshot, nextSnapshot) => {
    const previousList = Array.isArray(previousSnapshot?.seasonalPrices) ? previousSnapshot.seasonalPrices : [];
    const nextList = Array.isArray(nextSnapshot?.seasonalPrices) ? nextSnapshot.seasonalPrices : [];
    const previousBySeasonId = new Map(previousList.map((item) => [Number(item.seasonId), item]));
    const nextBySeasonId = new Map(nextList.map((item) => [Number(item.seasonId), item]));
    const seasonIds = Array.from(new Set([...previousBySeasonId.keys(), ...nextBySeasonId.keys()]));

    return seasonIds
        .map((seasonId) => {
            const previous = previousBySeasonId.get(seasonId);
            const next = nextBySeasonId.get(seasonId);
            const previousDay = Number(previous?.pricePerDay ?? 0);
            const nextDay = Number(next?.pricePerDay ?? 0);
            const previousWeek = Number(previous?.pricePerWeek ?? 0);
            const nextWeek = Number(next?.pricePerWeek ?? 0);
            const changed = previousDay !== nextDay || previousWeek !== nextWeek;
            if (!changed) return null;

            return {
                seasonName: next?.seasonName || previous?.seasonName || `Season ${seasonId}`,
                previousDay,
                nextDay,
                previousWeek,
                nextWeek
            };
        })
        .filter(Boolean);
};
const getPricingModeLabel = (pricingMode, language = 'en') => {
    const normalized = String(pricingMode || 'day').trim().toLowerCase();
    const isDanish = String(language || 'en').trim().toLowerCase() === 'da';
    if (normalized === 'month') {
        return isDanish ? 'Pr. mÃ¥ned' : 'Per Month';
    }
    return normalized === 'week'
        ? (isDanish ? 'Pr. uge' : 'Per Week')
        : (isDanish ? 'Pr. dag' : 'Per Day');
};
const getTotalCustomerDue = (booking) => {
    if (booking && booking.totalCustomerDue !== null && booking.totalCustomerDue !== undefined && booking.totalCustomerDue !== '') {
        return toAmount(booking.totalCustomerDue);
    }

    return toAmount(booking?.depositAmount) + toAmount(booking?.totalPrice);
};
const isTruthyValue = (value) => (
    value === true
    || value === 1
    || value === '1'
    || String(value || '').toLowerCase() === 'true'
);
const isFestivalAvailable = (caravan) => {
    const value = caravan?.available_for_festival ?? caravan?.availableForFestival;
    if (value === undefined || value === null) {
        return true;
    }

    return isTruthyValue(value);
};
const ADDON_DEFINITIONS = [
    {
        key: 'gas',
        label: 'Gas',
        caravanPriceField: 'addon_gas_price',
        quoteSelectedField: 'addon_gas_selected',
        quotePriceField: 'addon_gas_price',
        bookingSelectedField: 'addonGasSelected',
        bookingPriceField: 'addonGasPrice'
    },
    {
        key: 'sideMirrors',
        label: 'Sidespejle',
        caravanPriceField: 'addon_side_mirrors_price',
        quoteSelectedField: 'addon_side_mirrors_selected',
        quotePriceField: 'addon_side_mirrors_price',
        bookingSelectedField: 'addonSideMirrorsSelected',
        bookingPriceField: 'addonSideMirrorsPrice'
    },
    {
        key: 'tentSetup',
        label: 'Opsætning af telt',
        caravanPriceField: 'addon_tent_setup_price',
        quoteSelectedField: 'addon_tent_setup_selected',
        quotePriceField: 'addon_tent_setup_price',
        bookingSelectedField: 'addonTentSetupSelected',
        bookingPriceField: 'addonTentSetupPrice'
    },
    {
        key: 'adapter613',
        label: 'Adapter 6/13 pol',
        caravanPriceField: 'addon_adapter_6_13_price',
        quoteSelectedField: 'addon_adapter_6_13_selected',
        quotePriceField: 'addon_adapter_6_13_price',
        bookingSelectedField: 'addonAdapter613Selected',
        bookingPriceField: 'addonAdapter613Price'
    },
    {
        key: 'cleaning',
        label: 'Cleaning',
        caravanPriceField: 'addon_cleaning_price',
        quoteSelectedField: 'addon_cleaning_selected',
        quotePriceField: 'addon_cleaning_price',
        bookingSelectedField: 'addonCleaningSelected',
        bookingPriceField: 'addonCleaningPrice'
    },
    {
        key: 'toiletEmptying',
        label: 'Tømning af toilet',
        caravanPriceField: 'addon_toilet_emptying_price',
        quoteSelectedField: 'addon_toilet_emptying_selected',
        quotePriceField: 'addon_toilet_emptying_price',
        bookingSelectedField: 'addonToiletEmptyingSelected',
        bookingPriceField: 'addonToiletEmptyingPrice'
    },
    {
        key: 'tentRental',
        label: 'Leje af telt',
        caravanPriceField: 'addon_tent_rental_price',
        quoteSelectedField: 'addon_tent_rental_selected',
        quotePriceField: 'addon_tent_rental_price',
        bookingSelectedField: 'addonTentRentalSelected',
        bookingPriceField: 'addonTentRentalPrice'
    },
    {
        key: 'tentDrying',
        label: 'Tørring af telt',
        caravanPriceField: 'addon_tent_drying_price',
        quoteSelectedField: 'addon_tent_drying_selected',
        quotePriceField: 'addon_tent_drying_price',
        bookingSelectedField: 'addonTentDryingSelected',
        bookingPriceField: 'addonTentDryingPrice'
    }
];
const getAvailableCaravanAddons = (caravan) => ADDON_DEFINITIONS
    .map((addon) => {
        const rawPrice = caravan?.[addon.caravanPriceField];
        if (rawPrice === null || rawPrice === undefined || rawPrice === '') {
            return null;
        }

        return {
            key: addon.key,
            label: addon.label,
            price: toAmount(rawPrice)
        };
    })
    .filter(Boolean);
const getSelectedQuoteAddons = (quote) => ADDON_DEFINITIONS
    .map((addon) => (
        isTruthyValue(quote?.[addon.quoteSelectedField])
            ? {
                key: addon.key,
                label: addon.label,
                price: toAmount(quote?.[addon.quotePriceField])
            }
            : null
    ))
    .filter(Boolean);
const getSelectedBookingAddons = (booking) => ADDON_DEFINITIONS
    .map((addon) => {
        const selectedValue = booking?.[addon.bookingSelectedField] ?? booking?.[addon.quoteSelectedField];
        if (!isTruthyValue(selectedValue)) {
            return null;
        }

        return {
            key: addon.key,
            label: addon.label,
            price: toAmount(booking?.[addon.bookingPriceField] ?? booking?.[addon.quotePriceField])
        };
    })
    .filter(Boolean);
const formatAddonSummary = (addons) => addons.map((addon) => `${addon.label} (${formatInr(addon.price)})`).join(', ');
const isBookingDepositPaid = (booking) => {
    const paidAt = booking?.depositPaidAt ?? booking?.deposit_paid_at;
    return Boolean(paidAt);
};
const isBookingRentPaid = (booking) => {
    const paidAt = booking?.rentPaidAt ?? booking?.rent_paid_at;
    return Boolean(paidAt);
};
const getBookingCurrentDue = (booking) => {
    if (!booking) return 0;
    if (isBookingRentPaid(booking)) return 0;
    if (isBookingDepositPaid(booking)) return toAmount(booking?.totalPrice);
    return getTotalCustomerDue(booking);
};
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const parseDateOnlyLocal = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const stringValue = String(value || '').trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(stringValue);
    if (match) {
        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        if (year && month && day) {
            return new Date(year, month - 1, day);
        }
    }
    const parsed = new Date(stringValue);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};
const formatDateOnlyInput = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const ExploreDatePicker = ({ id, label, value, onChange, min, disabled = false, language = 'da' }) => {
    const selectedDate = parseDateOnlyLocal(value);
    const minimumDate = parseDateOnlyLocal(min);
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(() => selectedDate || minimumDate || new Date());
    const wrapperRef = useRef(null);

    useEffect(() => {
        const nextSelectedDate = parseDateOnlyLocal(value);
        if (nextSelectedDate) setMonth(nextSelectedDate);
    }, [value]);

    useEffect(() => {
        const closeOnOutsideClick = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, []);

    const monthAnchor = new Date(month.getFullYear(), month.getMonth(), 1);
    const weeks = getCalendarWeeksForMonth(monthAnchor);
    const weekdayLabels = language === 'da'
        ? ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
        <div ref={wrapperRef} className="relative">
            <label htmlFor={id} className={CONTROL_LABEL_CLASS}>{label}</label>
            <button
                type="button"
                id={id}
                disabled={disabled}
                onClick={() => !disabled && setOpen((current) => !current)}
                className={`${CONTROL_INPUT_CLASS} flex items-center justify-between text-left disabled:cursor-not-allowed disabled:bg-gray-100`}
            >
                <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || (language === 'da' ? 'Vælg dato' : 'Select date')}</span>
                <span aria-hidden="true">▾</span>
            </button>
            {open && !disabled && (
                <div className="absolute left-0 z-40 mt-2 w-[330px] rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                    <div className="mb-3 flex items-center justify-between">
                        <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded px-2 py-1 hover:bg-gray-100" aria-label="Previous month">‹</button>
                        <p className="text-sm font-semibold capitalize">
                            {monthAnchor.toLocaleString(language === 'da' ? 'da-DK' : 'en-US', { month: 'long', year: 'numeric' })}
                        </p>
                        <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded px-2 py-1 hover:bg-gray-100" aria-label="Next month">›</button>
                    </div>
                    <div className="mb-1 grid grid-cols-[2.2rem_repeat(7,1fr)] gap-1 text-center text-[10px] font-semibold text-gray-500">
                        <span>{language === 'da' ? 'Uge' : 'Wk'}</span>
                        {weekdayLabels.map((day) => <span key={day}>{day}</span>)}
                    </div>
                    <div className="space-y-1">
                        {weeks.map((week) => (
                            <div key={`${id}-${week.weekNumber}-${formatDateOnlyInput(week.days[0].date)}`} className="grid grid-cols-[2.2rem_repeat(7,1fr)] gap-1">
                                <span className="flex h-8 items-center justify-center rounded bg-green-50 text-[10px] font-bold text-green-800">{week.weekNumber}</span>
                                {week.days.map(({ date, inMonth }) => {
                                    const ymd = formatDateOnlyInput(date);
                                    const isSelected = ymd === value;
                                    const isDisabled = !inMonth || (minimumDate && date < minimumDate);
                                    return (
                                        <button
                                            type="button"
                                            key={ymd}
                                            disabled={isDisabled}
                                            onClick={() => {
                                                onChange(ymd);
                                                setOpen(false);
                                            }}
                                            className={`h-8 rounded text-xs ${isSelected ? 'bg-green-600 font-bold text-white' : isDisabled ? 'text-gray-300' : 'text-gray-700 hover:bg-green-100'}`}
                                        >
                                            {date.getDate()}
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
const getIsoWeekNumber = (dateValue) => {
    const date = parseDateOnlyLocal(dateValue);
    if (!date) return '';
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = utcDate.getUTCDay() || 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
    return Math.ceil((((utcDate - yearStart) / ONE_DAY_MS) + 1) / 7);
};
const getCalendarWeeksForMonth = (monthDate) => {
    const firstDayOfMonth = parseDateOnlyLocal(monthDate);
    if (!firstDayOfMonth) return [];

    const year = firstDayOfMonth.getFullYear();
    const month = firstDayOfMonth.getMonth();
    const firstCalendarDay = new Date(year, month, 1);
    const dayOffset = (firstCalendarDay.getDay() + 6) % 7;
    firstCalendarDay.setDate(firstCalendarDay.getDate() - dayOffset);

    const weeks = [];
    let cursor = new Date(firstCalendarDay.getFullYear(), firstCalendarDay.getMonth(), firstCalendarDay.getDate());
    while (weeks.length < 6) {
        const days = [];
        let includesMonthDay = false;
        for (let index = 0; index < 7; index += 1) {
            const dayDate = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
            const inMonth = dayDate.getMonth() === month;
            if (inMonth) includesMonthDay = true;
            days.push({
                date: dayDate,
                inMonth
            });
            cursor.setDate(cursor.getDate() + 1);
        }

        if (!includesMonthDay && weeks.length > 3) {
            break;
        }

        weeks.push({
            weekNumber: getIsoWeekNumber(days[0]?.date),
            days
        });

        if (cursor.getMonth() !== month && cursor.getDate() >= 7 && weeks.length >= 4) {
            break;
        }
    }

    return weeks;
};
const addCalendarMonthsLocal = (dateValue, monthsToAdd) => {
    const date = parseDateOnlyLocal(dateValue);
    const months = Number(monthsToAdd);
    if (!date || !Number.isFinite(months)) return null;
    const monthIndex = (date.getMonth()) + Math.trunc(months);
    const targetYear = date.getFullYear() + Math.floor(monthIndex / 12);
    const targetMonth = ((monthIndex % 12) + 12) % 12;
    const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    const targetDay = Math.min(date.getDate(), lastDayOfTargetMonth);
    return new Date(targetYear, targetMonth, targetDay);
};
const calculateDateOnlyDiffDays = (startValue, endValue) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    if (!startDate || !endDate || endDate <= startDate) return 0;
    return Math.round((endDate.getTime() - startDate.getTime()) / ONE_DAY_MS);
};
const addCalendarDaysLocal = (dateValue, daysToAdd) => {
    const date = parseDateOnlyLocal(dateValue);
    const days = Number(daysToAdd);
    if (!date || !Number.isFinite(days)) return null;
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    next.setDate(next.getDate() + Math.trunc(days));
    return next;
};
const calculateInclusiveDateCount = (startValue, endValue) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    if (!startDate || !endDate || endDate < startDate) return 0;
    return Math.round((endDate.getTime() - startDate.getTime()) / ONE_DAY_MS) + 1;
};
const getCalendarMonthsInRange = (startValue, endValue) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    if (!startDate || !endDate || endDate < startDate) return [];

    const months = [];
    const lastMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    let cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    while (cursor <= lastMonth) {
        months.push(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    return months;
};
const getAdminBookingOverviewDefaultStartDate = () => {
    const now = new Date();
    return formatDateOnlyInput(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
};
const getAdminBookingOverviewDefaultEndDate = () => {
    const now = new Date();
    return formatDateOnlyInput(addCalendarMonthsLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate()), 12));
};
const getStartOfIsoWeekLocal = (dateValue) => {
    const date = parseDateOnlyLocal(dateValue);
    if (!date) return null;
    const dayIndex = (date.getDay() + 6) % 7;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - dayIndex);
};
const getBookingOverviewBlockStatus = (row) => {
    const source = String(row?.source || row?.Source || '').trim().toLowerCase();
    if (source === 'booking') {
        if (isTruthyValue(row?.doorstepEnabled ?? row?.DoorstepEnabled)) {
            return isTruthyValue(row?.deliveryAssigned ?? row?.DeliveryAssigned) ? 'doorstep_assigned' : 'doorstep';
        }
        return 'booked';
    }
    if (source === 'unavailability') {
        return 'unavailable';
    }
    return 'free';
};
const getBookingOverviewStatusPriority = (status) => {
    switch (String(status || '').trim().toLowerCase()) {
        case 'doorstep_assigned':
            return 4;
        case 'doorstep':
            return 3;
        case 'booked':
            return 2;
        case 'unavailable':
            return 1;
        default:
            return 0;
    }
};
const pickBookingOverviewStatus = (currentStatus, nextStatus) => (
    getBookingOverviewStatusPriority(nextStatus) > getBookingOverviewStatusPriority(currentStatus)
        ? nextStatus
        : currentStatus
);
const buildBookingOverviewDayStatusMap = (ranges, startValue, endValue) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    const endDateExclusive = addCalendarDaysLocal(endDate, 1);
    if (!startDate || !endDate || !endDateExclusive || endDate < startDate) {
        return {};
    }

    const statusByCaravanId = {};
    (Array.isArray(ranges) ? ranges : []).forEach((row) => {
        const caravanId = Number(row?.caravanId ?? row?.CaravanId ?? 0);
        const rangeStart = parseDateOnlyLocal(row?.startAt || row?.StartAt);
        const rangeEnd = parseDateOnlyLocal(row?.endAt || row?.EndAt);
        if (!caravanId || !rangeStart || !rangeEnd || rangeEnd <= startDate || rangeStart >= endDateExclusive) {
            return;
        }

        const status = getBookingOverviewBlockStatus(row);
        const cursorStart = rangeStart > startDate ? rangeStart : startDate;
        const cursor = new Date(cursorStart.getFullYear(), cursorStart.getMonth(), cursorStart.getDate());
        const stopDate = rangeEnd < endDateExclusive ? rangeEnd : endDateExclusive;
        statusByCaravanId[caravanId] = statusByCaravanId[caravanId] || {};

        while (cursor < stopDate) {
            const ymd = formatDateOnlyInput(cursor);
            statusByCaravanId[caravanId][ymd] = pickBookingOverviewStatus(
                statusByCaravanId[caravanId][ymd] || 'free',
                status
            );
            cursor.setDate(cursor.getDate() + 1);
        }
    });

    return statusByCaravanId;
};
const getBookingOverviewDayRows = (startValue, endValue, locale) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    if (!startDate || !endDate || endDate < startDate) return [];

    const rows = [];
    const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    while (cursor <= endDate) {
        const ymd = formatDateOnlyInput(cursor);
        rows.push({
            key: ymd,
            ymd,
            weekNumber: getIsoWeekNumber(cursor),
            primaryLabel: cursor.toLocaleDateString(locale, { day: '2-digit', month: 'short' }),
            secondaryLabel: cursor.toLocaleDateString(locale, { weekday: 'short' })
        });
        cursor.setDate(cursor.getDate() + 1);
    }

    return rows;
};
const getBookingOverviewWeekRows = (startValue, endValue, locale) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    if (!startDate || !endDate || endDate < startDate) return [];

    const rows = [];
    let weekStart = getStartOfIsoWeekLocal(startDate);
    while (weekStart && weekStart <= endDate) {
        const weekEnd = addCalendarDaysLocal(weekStart, 6);
        const visibleStart = weekStart < startDate ? startDate : weekStart;
        const visibleEnd = weekEnd > endDate ? endDate : weekEnd;
        const dates = [];
        const cursor = new Date(visibleStart.getFullYear(), visibleStart.getMonth(), visibleStart.getDate());
        while (cursor <= visibleEnd) {
            dates.push(formatDateOnlyInput(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }

        rows.push({
            key: `${formatDateOnlyInput(weekStart)}-${formatDateOnlyInput(visibleEnd)}`,
            weekNumber: getIsoWeekNumber(weekStart),
            primaryLabel: `W${String(getIsoWeekNumber(weekStart)).padStart(2, '0')}`,
            secondaryLabel: `${visibleStart.toLocaleDateString(locale, { day: '2-digit', month: 'short' })} - ${visibleEnd.toLocaleDateString(locale, { day: '2-digit', month: 'short' })}`,
            dates
        });

        weekStart = addCalendarDaysLocal(weekStart, 7);
    }

    return rows;
};
const getFullMonthBlocks = (startValue, endValue) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    if (!startDate || !endDate || endDate <= startDate) return 0;

    let months = 0;
    let cursor = startDate;
    while (true) {
        const next = addCalendarMonthsLocal(cursor, 1);
        if (!next || next > endDate) {
            break;
        }
        cursor = next;
        months += 1;
    }

    return months;
};
const getMonthRemainderDays = (startValue, endValue, monthBlocks) => {
    const startDate = parseDateOnlyLocal(startValue);
    const endDate = parseDateOnlyLocal(endValue);
    if (!startDate || !endDate || endDate <= startDate) return 0;
    const blocks = Number(monthBlocks) || 0;
    if (blocks <= 0) return 0;
    const remainderStart = addCalendarMonthsLocal(startDate, blocks);
    if (!remainderStart || endDate <= remainderStart) return 0;
    return Math.round((endDate.getTime() - remainderStart.getTime()) / ONE_DAY_MS);
};
const calculateMonthBookingEndDate = (startValue, monthsToAdd, extraDaysToAdd = 0) => {
    const normalizedMonths = Number(monthsToAdd);
    if (!startValue || !Number.isFinite(normalizedMonths) || normalizedMonths <= 0) {
        return '';
    }
    const endDate = addCalendarMonthsLocal(startValue, normalizedMonths);
    if (!endDate) return '';
    const extraDays = Math.max(0, Number(extraDaysToAdd) || 0);
    if (extraDays > 0) {
        endDate.setDate(endDate.getDate() + extraDays);
    }
    return formatDateOnlyInput(endDate);
};
const getPricingDurationParts = (startDate, endDate, pricingMode) => {
    const rawPricingMode = String(pricingMode || 'day').trim().toLowerCase();
    const normalizedPricingMode = rawPricingMode === 'month'
        ? 'month'
        : (rawPricingMode === 'week' ? 'week' : 'day');
    const nights = calculateDateOnlyDiffDays(startDate, endDate);
    const months = normalizedPricingMode === 'month' ? getFullMonthBlocks(startDate, endDate) : 0;
    const monthRemainderDays = normalizedPricingMode === 'month'
        ? getMonthRemainderDays(startDate, endDate, months)
        : 0;
    const weeks = normalizedPricingMode === 'week'
        ? Math.floor(nights / 7)
        : (normalizedPricingMode === 'month' ? Math.floor(monthRemainderDays / 7) : 0);
    const days = normalizedPricingMode === 'week'
        ? (nights % 7)
        : (normalizedPricingMode === 'month' ? (monthRemainderDays % 7) : nights);

    return {
        normalizedPricingMode,
        months,
        nights,
        weeks,
        days
    };
};
const formatPricingDurationDisplay = (startDate, endDate, pricingMode, language = 'en') => {
    const parts = getPricingDurationParts(startDate, endDate, pricingMode);
    const isDanish = String(language || 'en').trim().toLowerCase() === 'da';
    if (parts.normalizedPricingMode === 'month') {
        const labels = [];
        if (parts.months > 0) {
            labels.push(`${parts.months} ${isDanish ? (parts.months === 1 ? 'måned' : 'måneder') : `month${parts.months === 1 ? '' : 's'}`}`);
        }
        if (parts.weeks > 0) {
            labels.push(`${parts.weeks} ${isDanish ? (parts.weeks === 1 ? 'uge' : 'uger') : `week${parts.weeks === 1 ? '' : 's'}`}`);
        }
        if (parts.days > 0) {
            labels.push(`${parts.days} ${isDanish ? (parts.days === 1 ? 'dag' : 'dage') : `day${parts.days === 1 ? '' : 's'}`}`);
        }
        return labels.length > 0 ? labels.join(' + ') : (isDanish ? '0 dage' : '0 days');
    }
    if (parts.normalizedPricingMode === 'week') {
        const labels = [];
        if (parts.weeks > 0) {
            labels.push(`${parts.weeks} ${isDanish ? (parts.weeks === 1 ? 'uge' : 'uger') : `week${parts.weeks === 1 ? '' : 's'}`}`);
        }
        if (parts.days > 0) {
            labels.push(`${parts.days} ${isDanish ? (parts.days === 1 ? 'dag' : 'dage') : `day${parts.days === 1 ? '' : 's'}`}`);
        }
        return labels.length > 0 ? labels.join(' + ') : (isDanish ? '0 dage' : '0 days');
    }

    return `${parts.nights} ${isDanish ? (parts.nights === 1 ? 'nat' : 'nætter') : `night${parts.nights === 1 ? '' : 's'}`}`;
};
const formatPricingCalculationRule = (startDate, endDate, pricingMode, language = 'en') => {
    const parts = getPricingDurationParts(startDate, endDate, pricingMode);
    const isDanish = String(language || 'en').trim().toLowerCase() === 'da';
    if (parts.normalizedPricingMode === 'month') {
        const monthPart = isDanish
            ? `${parts.months} ${parts.months === 1 ? 'fuld månedsblok' : 'fulde månedsblokke'} til månedspris`
            : `${parts.months} full month block${parts.months === 1 ? '' : 's'} at monthly rate`;
        const weekPart = parts.weeks > 0
            ? (isDanish
                ? `${parts.weeks} ${parts.weeks === 1 ? 'fuld ugeblok' : 'fulde ugeblokke'} til ugepris`
                : `${parts.weeks} full week block${parts.weeks === 1 ? '' : 's'} at weekly rate`)
            : '';
        const dayPart = parts.days > 0
            ? (isDanish
                ? `${parts.days} ${parts.days === 1 ? 'dag' : 'dage'} til dagspris`
                : `${parts.days} day${parts.days === 1 ? '' : 's'} at per-day rate`)
            : '';
        return [monthPart, weekPart, dayPart].filter(Boolean).join(' + ');
    }
    if (parts.normalizedPricingMode === 'week') {
        if (parts.weeks > 0 && parts.days > 0) {
            return isDanish
                ? `${parts.weeks} ${parts.weeks === 1 ? 'fuld ugeblok' : 'fulde ugeblokke'} til ugepris + ${parts.days} ${parts.days === 1 ? 'resterende dag' : 'resterende dage'} til dagspris`
                : `${parts.weeks} full week block${parts.weeks === 1 ? '' : 's'} at weekly rate + ${parts.days} remaining day${parts.days === 1 ? '' : 's'} at per-day rate`;
        }
        if (parts.weeks > 0) {
            return isDanish
                ? `${parts.weeks} ${parts.weeks === 1 ? 'fuld ugeblok' : 'fulde ugeblokke'} til ugepris`
                : `${parts.weeks} full week block${parts.weeks === 1 ? '' : 's'} at weekly rate`;
        }
        return isDanish
            ? `${parts.days} ${parts.days === 1 ? 'dag' : 'dage'} til dagspris`
            : `${parts.days} day${parts.days === 1 ? '' : 's'} at per-day rate`;
    }

    return isDanish
        ? `${parts.nights} ${parts.nights === 1 ? 'nat' : 'nætter'} til dagspris`
        : `${parts.nights} night${parts.nights === 1 ? '' : 's'} at per-day rate`;
};
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ActionLoaderOverlay = ({
    show,
    title = 'Processing request...',
    subtitle = 'Please wait while email notifications are being sent.'
}) => {
    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/45 backdrop-blur-[1px] flex items-center justify-center px-4">
            <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-5 text-center">
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-gray-200 border-t-green-600 animate-spin" />
                <p className="text-base font-semibold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
            </div>
        </div>
    );
};

const LanguageContext = React.createContext({
    language: 'da',
    setLanguage: () => { },
    t: (key) => key
});

const translations = {
    da: {
        navExplore: 'Udforsk',
        navAbout: 'Om os',
        navBecomeOwner: 'Registrer dit køretøj',
        navManageCalendar: 'Manage',
        navTerms: 'Vilk\u00e5r',
        navAdmin: 'Admin panel',
        navMyBookings: 'Mine bookinger',
        navWelcome: 'Velkommen, {name}!',
        navLogout: 'Log ud',
        navLogin: 'Log ind',
        navSignup: 'Opret konto',
        navProfile: 'Profil',
        signupTitle: 'Opret en ny konto',
        signupNameLabel: 'Fuldt navn',
        signupNamePlaceholder: 'Fuldt navn',
        signupEmailLabel: 'E-mailadresse',
        signupEmailPlaceholder: 'E-mailadresse',
        signupPasswordLabel: 'Adgangskode',
        signupPasswordPlaceholder: 'Adgangskode',
        signupAlreadyHaveAccount: 'Har du allerede en konto?',
        signupSuccess: 'Registrering gennemf\u00f8rt. Du kan logge ind nu.',
        signupFailed: 'Oprettelse mislykkedes',
        changePasswordTitle: 'Skift adgangskode',
        changePasswordTemporaryNotice: 'Du skal skifte din midlertidige adgangskode, f\u00f8r du kan forts\u00e6tte.',
        changePasswordSubtitle: 'Opdater din adgangskode nedenfor.',
        changePasswordCurrentLabel: 'Nuv\u00e6rende adgangskode',
        changePasswordCurrentPlaceholder: 'Nuv\u00e6rende adgangskode',
        changePasswordNewLabel: 'Ny adgangskode',
        changePasswordNewPlaceholder: 'Ny adgangskode',
        changePasswordConfirmLabel: 'Bekr\u00e6ft ny adgangskode',
        changePasswordConfirmPlaceholder: 'Bekr\u00e6ft ny adgangskode',
        changePasswordSuccess: 'Adgangskoden blev opdateret.',
        changePasswordFailed: 'Kunne ikke opdatere adgangskoden.',
        changePasswordSaving: 'Gemmer...',
        changePasswordSubmit: 'Opdater adgangskode',
        navProfileDone: 'Profiloplysninger udfyldt',
        navProfilePending: 'Profiloplysninger mangler',
        navLanguage: 'Sprog',
        langDanish: 'Dansk',
        langEnglish: 'Engelsk',
        fileChoose: 'V\u00e6lg en fil',
        fileNoSelection: 'Ingen fil valgt',
        fileCountSelected: '{count} fil(er) valgt',
        footerTagline: 'Dit eventyr starter her. Lej den perfekte campingvogn til din n\u00e6ste tur.',
        footerQuickLinks: 'Hurtige links',
        footerAbout: 'Om os',
        footerContact: 'Kontakt',
        footerAddressLabel: 'Adresse',
        footerAddressValue: 'Nymarks Alle 352, 8320 Maarslet Denmark',
        footerCvrLabel: 'CVR',
        footerCvrValue: '46317165',
        footerOfficeTimingLabel: 'Kontortid',
        footerOfficeTimingValue: '08:00 - 16:00 mandag til fredag',
        footerEmailLabel: 'Email',
        footerEmailValue: 'info@udlejningsvogn.dk',
        footerPhoneLabel: 'Telefon',
        footerPhoneValue: '+45 53715367',
        footerFaq: 'FAQ',
        footerTerms: 'Vilk\u00e5r og betingelser',
        footerFollow: 'F\u00f8lg os',
        footerRights: 'Alle rettigheder forbeholdes.',
        homeHeroTitle: 'Din rejse starter her',
        homeHeroSubtitle: 'Oplev friheden p\u00e5 vejen. Uforglemmelige eventyr er kun en leje v\u00e6k.',
        homeStartExploring: 'Kom i gang',
        homeFeatured: 'Udvalgte campingvogne',
        homeLoadingFeatured: 'Indl\u00e6ser udvalgte campingvogne...',
        listFilters: 'Filtre',
        listSearchLabel: 'S\u00f8g navn/placering',
        listSearchPlaceholder: 'f.eks. Goa eller Adventurer',
        listMaxPrice: 'Maks pris pr. uge',
        listSleeps: 'Siddepladser mindst',
        listAny: 'Alle',
        listPeople2: '2 siddepladser',
        listPeople3: '3 siddepladser',
        listPeople4: '4 siddepladser',
        listPeople5: '5+ siddepladser',
        listFestivalAvailable: 'Tilgaengelig til festival',
        listCaravanType: 'Campingvogntype',
        listSortBy: 'Sorter efter',
        listSortPriceAsc: 'Pris: lav til høj',
        listSortPriceDesc: 'Pris: høj til lav',
        listSortRatingDesc: 'Rating: høj til lav',
        listSortRatingAsc: 'Rating: lav til høj',
        listAvailable: 'Campingvogn udlejning i Danmark',
        listNoFound: 'Ingen campingvogne fundet',
        listLoading: 'Indlæser campingvogne...',
        listAdjust: 'Pr\u00f8v at justere filtrene for at finde den perfekte vogn.',
        detailType: 'Type',
        detailSleeps: 'Siddepladser',
        detailBeds: 'Senge',
        detailAbout: 'Om denne campingvogn',
        detailAmenities: 'Faciliteter',
        detailReviews: 'Anmeldelser',
        detailWriteReview: 'Skriv en anmeldelse',
        detailYourRating: 'Din vurdering',
        detailYourComment: 'Din kommentar',
        detailSubmitReview: 'Send anmeldelse',
        detailReviewSuccess: 'Tak! Din anmeldelse er gemt.',
        detailReviewLogin: 'Log ind for at skrive en anmeldelse.',
        detailCheckIn: 'Check-in',
        detailCheckOut: 'Check-out',
        detailRequest: 'Anmod om booking',
        detailNoCharge: 'Du bliver ikke opkr\u00e6vet endnu',
        bookingTitle: 'Bekr\u00e6ft din booking',
        bookingTrip: 'Din tur',
        bookingRate: 'Bookingpris',
        bookingPerDay: 'Pr. dag',
        bookingPerWeek: 'Pr. uge',
        bookingWeekNote: 'Ugebookinger bruger ugepris for hele uger plus dagspris for resterende dage.',
        bookingWeeks: 'Uger',
        bookingExtraDays: 'Ekstra dage',
        bookingCheckInTime: 'Check-in tid',
        bookingCheckOutTime: 'Check-out tid',
        bookingCalendarTitle: 'V\u00e6lg datoer',
        bookingCalendarHint: 'Klik for at v\u00e6lge start- og slutdato. Bookede datoer er deaktiveret.',
        bookingCalendarLoading: 'Indl\u00e6ser...',
        bookingCalendarAvailable: 'Tilg\u00e6ngelig',
        bookingCalendarUnavailable: 'Booket',
        bookingCalendarPrevious: 'Forrige m\u00e5ned',
        bookingCalendarNext: 'N\u00e6ste m\u00e5ned',
        bookingCalendarWeekNumber: 'Uge',
        bookingDateBlocked: 'Denne dato er allerede booket. V\u00e6lg venligst en anden dato.',
        bookingRangeBlocked: 'Den valgte periode indeholder allerede bookede datoer. V\u00e6lg venligst en anden periode.',
        bookingChecking: 'Tjekker tilg\u00e6ngelighed...',
        bookingAvailable: 'Datoer er tilg\u00e6ngelige!',
        bookingServiceFee: 'Servicegebyr',
        bookingDoorstepTitle: 'Levering og afhentning mulig',
        bookingDoorstepSubtitle: 'Vi leverer campingvognen til din adresse.',
        bookingDoorstepLine: 'Leverings service',
        bookingDoorstepPendingLine: 'Pris fastsaettes af admin efter godkendelse.',
        bookingDeliveryAddress: 'Leveringsadresse (doorstep)',
        bookingDeliveryAddressPlaceholder: 'Indtast gade, by og postnummer til levering',
        bookingDeliveryAddressRequired: 'Angiv leveringsadresse for doorstep levering.',
        bookingDoorstepUnavailable: 'Levering og afhentning er ikke tilg\u00e6ngelig for dette k\u00f8ret\u00f8j.',
        bookingTotal: 'Total',
        bookingTotalDueWithDeposit: 'Samlet bel\u00f8b (booking + depositum)',
        bookingPaymentDueDeposit: 'Depositum forfalder inden for 24 timer',
        bookingPaymentDueFull: 'Depositum og leje forfalder inden for 24 timer',
        bookingConfirming: 'Bekr\u00e6fter...',
        bookingConfirm: 'Bekr\u00e6ft booking',
        bookingCheckingShort: 'Tjekker...',
        bookingNight: 'nat',
        bookingNights: 'n\u00e6tter',
        bookingDay: 'dag',
        bookingDays: 'dage',
        bookingWeek: 'uge',
        bookingWeeksPlural: 'uger',
        bookingCalculation: 'Beregning',
        bookingBifurcation: 'Prisopdeling',
        bookingWeeklyRate: 'ugepris',
        bookingDailyRate: 'dagspris',
        bookingWeeklyCharge: 'Ugebetaling',
        bookingDailyCharge: 'Dagsbetaling',
        bookingRequested: '\u00d8nsket',
        bookingOptional: 'Valgfri',
        bookingOptionalAddonsTitle: 'Valgfrie tilvalg',
        bookingOptionalAddonsSubtitle: 'Valgte tilvalg l\u00e6gges til bookingbel\u00f8bet.',
        bookingAddonsTotal: 'Samlet tilvalg',
        bookingRemarksLabel: 'Bookingbem\u00e6rkninger',
        bookingRemarksRequired: 'Bookingbem\u00e6rkninger er p\u00e5kr\u00e6vet.',
        bookingDoorstepChargeNote: 'Bem\u00e6rk: Gebyr for leverings service beregnes for 4 ruteled (afhentning/levering), samlet beregningsafstand = {km}.',
        bookingPendingUntilDeposit: 'Bookingen forbliver afventende, indtil admin bekr\u00e6fter den kr\u00e6vede betaling.',
        bookingBlockedCount: '{count} blokeret',
        bookingCouldNotLoadBlockedRanges: 'Kunne ikke indl\u00e6se blokerede perioder.',
        bookingCouldNotCheckAvailability: 'Kunne ikke tjekke tilg\u00e6ngelighed.',
        bookingCouldNotCalculateQuote: 'Kunne ikke beregne pris.',
        bookingDatesUnavailable: 'Disse datoer er ikke tilg\u00e6ngelige. V\u00e6lg venligst en anden periode.',
        bookingFestivalPrompt: 'Denne campingvogn er ikke tilgaengelig til festival. Vil du booke til en festival? Klik OK for at se festival-venlige alternativer for de valgte datoer, eller Annuller for at fortsaette med denne booking.',
        bookingFestivalSuggestionsTitle: 'Festival-venlige alternativer',
        bookingFestivalSuggestionsSubtitle: 'Kun campingvogne, der er tilgaengelige til festival og ledige i den valgte periode, vises her.',
        bookingFestivalSuggestionsLoading: 'Finder festival-venlige alternativer...',
        bookingFestivalSuggestionsEmpty: 'Ingen festival-venlige campingvogne er ledige i den valgte periode.',
        bookingFestivalSuggestionsError: 'Kunne ikke hente festival-venlige alternativer.',
        bookingFestivalSuggestionView: 'Se detaljer',
        bookingFestivalSuggestionCta: 'Book denne campingvogn',
        bookingFestivalDetailLoading: 'Indlaeser campingvognsdetaljer...',
        bookingFestivalDetailError: 'Kunne ikke hente campingvognsdetaljer.',
        bookingFestivalNoReviews: 'Ingen anmeldelser endnu.',
        commonClose: 'Luk',
        listDateValidation: 'Slutdato skal v\u00e6re efter startdato.',
        cardTopOwner: 'Topudlejer',
        cardPetFriendly: 'K\u00e6ledyrsvenlig',
        cardNoSmoking: 'Ingen rygning',
        cardFestivalAvailable: 'Tilgaengelig til festival',
        cardFestivalUnavailable: 'Ikke tilgaengelig til festival',
        cardType: 'Type',
        cardLocationUnavailable: 'Placering ikke tilg\u00e6ngelig',
        cardDoorstepAvailable: 'Levering og afhentning mulig',
        cardFrom: 'Fra',
        cardPerNight: 'pr. nat',
        cardPerDay: 'Pr. dag',
        cardPerWeek: 'Pr. uge',
        cardSitting: 'Siddepladser',
        cardBeds: 'Senge',
        myBookingsTitle: 'Mine bookinger',
        myBookingsLoading: 'Indl\u00e6ser dine bookinger...',
        myBookingsEmptyTitle: 'Ingen bookinger endnu',
        myBookingsEmptyText: 'Du har ikke booket noget endnu. Tid til at udforske!',
        myBookingsExplore: 'Udforsk campingvogne',
        myBookingsDates: 'Datoer',
        myBookingsTotal: 'Total pris',
        profileTitle: 'Udfyld profiloplysninger',
        profileSubtitle: 'Navn, gade, by, postnummer, mobil og bankoplysninger skal udfyldes f\u00f8r booking eller registrering af k\u00f8ret\u00f8j.',
        profileName: 'Navn',
        profileStreet: 'Gade',
        profileCity: 'By',
        profileZipcode: 'Postnummer',
        profileMobile: 'Mobil',
        profileBank: 'Bankoplysninger',
        profileBankRegNo: 'Bankregistreringsnr.',
        profileBankAccountNo: 'Bankkontonr.',
        profileSave: 'Gem profil',
        profileRequired: 'Alle felter er p\u00e5kr\u00e6vede.',
        profileSaved: 'Profil gemt.',
        ownerCalendarTitle: 'Administrer utilg\u00e6ngelige datoer',
        ownerCalendarSubtitle: 'Blok\u00e9r datoer, hvor campingvognen ikke kan bookes.',
        ownerSelectCaravan: 'V\u00e6lg campingvogn',
        ownerNoCaravans: 'Du har ingen campingvogne endnu.',
        ownerStartDate: 'Startdato',
        ownerEndDate: 'Slutdato',
        ownerReason: '\u00C5rsag (valgfri)',
        ownerSaveBlock: 'Blok\u00e9r datoer',
        ownerExistingBlocks: 'Eksisterende blokeringer',
        ownerDeleteBlock: 'Fjern',
        ownerDateValidation: 'Slutdato skal v\u00e6re efter startdato.',
        ownerBlockConflict: 'Datointervallet overlapper en eksisterende blokering.',
        listCheckIn: 'Check-in dato',
        listCheckOut: 'Check-out dato',
        listDateFilterNote: 'Med datoer vises kun ledige campingvogne.',
        addressRequired: 'Gade, by og postnummer er p\u00e5kr\u00e6vet.',
        weeklyRateValidation: 'Prisen pr. uge skal v\u00e6re mindre end eller lig med 7 dages pris.',
        termsTitle: 'Vilk\u00e5r og betingelser',
        termsEffective: 'Ikrafttr\u00e6delsesdato: [Tilf\u00f8j dato]',
        termsBack: 'Tilbage til forsiden',
        aboutTitle: 'Om Udlejningsvogn.dk',
        aboutSubtitle: 'Vi g\u00f8r campingvognsrejser simple, p\u00e5lidelige og mindev\u00e6rdige.',
        aboutBack: 'Tilbage til forsiden',
        aboutStory: 'Vores historie',
        aboutStoryP1: 'Udlejningsvogn.dk startede i 2021 med \u00e9t m\u00e5l: g\u00f8re roadtrips tilg\u00e6ngelige uden b\u00f8vlet ved ejerskab. Det begyndte med tre campingvogne i en lille kystby og er vokset til en betroet markedsplads for verificerede udlejere og trygge lejere.',
        aboutStoryP2: 'I dag fokuserer vi p\u00e5 sikre, fleksible udlejninger med klare priser, lokal support og kvalitetstjek, s\u00e5 du kan bruge mindre tid p\u00e5 planl\u00e6gning og mere tid p\u00e5 at udforske.',
        aboutAtGlance: 'Kort fortalt',
        aboutTrips: 'Ture gennemf\u00f8rt',
        aboutCaravans: 'Registrerede k\u00f8ret\u00f8jer',
        aboutCities: '25 byer d\u00e6kket',
        aboutRating: 'Gennemsnitlig rating',
        aboutBelief: 'Hvad vi tror p\u00e5',
        aboutTrust: 'Tillid f\u00f8rst',
        aboutTrustText: 'Alle annoncer er verificerede, og hver booking er beskyttet, s\u00e5 du kan leje trygt.',
        aboutPricing: 'Klar priss\u00e6tning',
        aboutPricingText: 'Ingen skjulte gebyrer. Gennemsigtige priser pr. dag og pr. uge fra start.',
        aboutSupport: 'Lokal support',
        aboutSupportText: 'Vi samarbejder med udlejere i hele landet for at give hj\u00e6lp og vejledning i realtid.',
        aboutReady: 'Klar til din n\u00e6ste tur?',
        aboutReadyText: 'Udforsk campingvogne og book p\u00e5 f\u00e5 minutter.',
        aboutBrowse: 'Se campingvogne',
        adminAllBookingsTitle: 'Alle bookinger',
        adminCheckInPhotos: 'Check-in-billeder',
        adminCheckInPhotoGalleryTitle: 'Check-in-billeder - {bookingNo}',
        adminCheckInPhotoAlt: 'Check-in-billede {count}',
        adminBookingNumber: 'Bookingnr.',
        adminBookingId: 'Booking-ID',
        adminCustomer: 'Kunde',
        adminOwner: 'Ejer',
        adminDates: 'Datoer',
        adminPricingMode: 'Prismodel',
        adminRentalCharge: 'Lejepris',
        adminPricingDuration: 'Prisvarighed',
        adminPricingCalculation: 'Prisberegning',
        adminDoorstepFee: 'Doorstep-gebyr',
        adminDeliveryAddress: 'Leveringsadresse',
        adminAddons: 'Tilvalg',
        adminAddonsTotal: 'Samlet tilvalg',
        adminBookingAmount: 'Bookingbel\u00f8b',
        adminDeposit: 'Depositum',
        adminNetDueAmount: 'Netto forfaldent bel\u00f8b',
        adminBookingRemarks: 'Bookingbem\u00e6rkninger',
        adminCheckIn: 'Check-in',
        adminCheckOut: 'Check-out',
        adminCheckoutReview: 'Checkout-gennemgang',
        adminSubmittedAt: 'Indsendt',
        adminReviewedAt: 'Gennemg\u00e5et',
        adminAdminComment: 'Admin-kommentar',
        adminDeduction: 'Fradrag',
        adminDepositRefund: 'Depositumsrefusion',
        adminCancelledAt: 'Annulleret',
        adminCancellationRemarks: 'Annulleringsbem\u00e6rkninger',
        adminCancellationRentRefund: 'Refusion af leje',
        adminCancellationDepositRefund: 'Refusion af depositum',
        adminCreated: 'Oprettet',
        adminNotAvailable: 'Ikke angivet',
        adminPaid: 'Betalt',
        adminPending: 'Afventer',
        adminCancelBookingTitle: 'Annuller booking',
        adminCancelBookingHelp: 'Brug dette til afventende eller bekr\u00e6ftede bookinger. Bekr\u00e6ftede bookinger flyttes til siden for annulleringsrefusion efter annullering.',
        adminCancelBookingAction: 'Annuller booking',
        adminCancelling: 'Annullerer...',
        adminNoBookingsFound: 'Ingen bookinger fundet endnu.',
        adminBookingStatusPending: 'Afventer',
        adminBookingStatusConfirmed: 'Bekr\u00e6ftet',
        adminBookingStatusCheckedIn: 'Checket ind',
        adminBookingStatusCheckoutPendingCustomer: 'Checkout afventer kunde',
        adminBookingStatusCheckoutPendingApproval: 'Checkout afventer godkendelse',
        adminBookingStatusCheckoutRejected: 'Checkout afvist',
        adminBookingStatusCheckedOut: 'Checket ud',
        adminBookingStatusCompleted: 'Fuldf\u00f8rt',
        adminBookingStatusExpired: 'Udl\u00f8bet',
        adminBookingStatusCancelled: 'Annulleret',
        adminCheckoutReviewStatusPending: 'Afventer',
        adminCheckoutReviewStatusApproved: 'Godkendt',
        adminCheckoutReviewStatusRejected: 'Afvist'
    },
    en: {
        navExplore: 'Explore',
        navAbout: 'About',
        navBecomeOwner: 'Register Your Vehicle',
        navManageCalendar: 'Manage',
        navTerms: 'Terms',
        navAdmin: 'Admin Panel',
        navMyBookings: 'My Bookings',
        navWelcome: 'Welcome, {name}!',
        navLogout: 'Log Out',
        navLogin: 'Log In',
        navSignup: 'Sign Up',
        navProfile: 'Profile',
        signupTitle: 'Create a new account',
        signupNameLabel: 'Full Name',
        signupNamePlaceholder: 'Full name',
        signupEmailLabel: 'Email address',
        signupEmailPlaceholder: 'Email address',
        signupPasswordLabel: 'Password',
        signupPasswordPlaceholder: 'Password',
        signupAlreadyHaveAccount: 'Already have an account?',
        signupSuccess: 'Registration successful. You can log in now.',
        signupFailed: 'Sign up failed',
        changePasswordTitle: 'Change Password',
        changePasswordTemporaryNotice: 'You must change your temporary password before continuing.',
        changePasswordSubtitle: 'Update your password below.',
        changePasswordCurrentLabel: 'Current password',
        changePasswordCurrentPlaceholder: 'Current password',
        changePasswordNewLabel: 'New password',
        changePasswordNewPlaceholder: 'New password',
        changePasswordConfirmLabel: 'Confirm new password',
        changePasswordConfirmPlaceholder: 'Confirm new password',
        changePasswordSuccess: 'Password changed successfully.',
        changePasswordFailed: 'Failed to change password.',
        changePasswordSaving: 'Saving...',
        changePasswordSubmit: 'Update Password',
        navProfileDone: 'Profile details completed',
        navProfilePending: 'Profile details required',
        navLanguage: 'Language',
        langDanish: 'Danish',
        langEnglish: 'English',
        fileChoose: 'Choose file',
        fileNoSelection: 'No file chosen',
        fileCountSelected: '{count} file(s) selected',
        footerTagline: 'Your adventure starts here. Rent the perfect caravan for your next road trip.',
        footerQuickLinks: 'Quick Links',
        footerAbout: 'About Us',
        footerContact: 'Contact',
        footerAddressLabel: 'Address',
        footerAddressValue: 'Nymarks Alle 352, 8320 Maarslet Denmark',
        footerCvrLabel: 'CVR',
        footerCvrValue: '46317165',
        footerOfficeTimingLabel: 'Office timing',
        footerOfficeTimingValue: '08:00 - 16:00 Monday to Friday',
        footerEmailLabel: 'Email',
        footerEmailValue: 'info@udlejningsvogn.dk',
        footerPhoneLabel: 'Phone',
        footerPhoneValue: '+45 53715367',
        footerFaq: 'FAQ',
        footerTerms: 'Terms and Conditions',
        footerFollow: 'Follow Us',
        footerRights: 'All rights reserved.',
        homeHeroTitle: 'Your Journey Begins Here',
        homeHeroSubtitle: 'Discover the freedom of the open road. Unforgettable adventures are just a rental away.',
        homeStartExploring: 'Start Exploring',
        homeFeatured: 'Featured Caravans',
        homeLoadingFeatured: 'Loading featured caravans...',
        listFilters: 'Filters',
        listSearchLabel: 'Search Name/Location',
        listSearchPlaceholder: 'e.g., Goa or Adventurer',
        listMaxPrice: 'Max Price / Week',
        listSleeps: 'Sitting at least',
        listAny: 'Any',
        listPeople2: '2 seats',
        listPeople3: '3 seats',
        listPeople4: '4 seats',
        listPeople5: '5+ seats',
        listFestivalAvailable: 'Available for Festival',
        listCaravanType: 'Caravan Type',
        listSortBy: 'Sort by',
        listSortPriceAsc: 'Price: Low to High',
        listSortPriceDesc: 'Price: High to Low',
        listSortRatingDesc: 'Rating: High to Low',
        listSortRatingAsc: 'Rating: Low to High',
        listAvailable: 'Caravan Rentals in Denmark',
        listNoFound: 'No Caravans Found',
        listAdjust: 'Try adjusting your filters to find your perfect ride.',
        listLoading: 'Loading caravans...',
        detailType: 'Type',
        detailSleeps: 'Sitting',
        detailBeds: 'Beds',
        detailAbout: 'About this caravan',
        detailAmenities: 'Amenities',
        detailReviews: 'Reviews',
        detailWriteReview: 'Write a Review',
        detailYourRating: 'Your Rating',
        detailYourComment: 'Your Comment',
        detailSubmitReview: 'Submit Review',
        detailReviewSuccess: 'Thank you! Your review has been saved.',
        detailReviewLogin: 'Log in to write a review.',
        detailCheckIn: 'Check-in',
        detailCheckOut: 'Check-out',
        detailRequest: 'Request to Book',
        detailNoCharge: "You won't be charged yet",
        bookingTitle: 'Confirm Your Booking',
        bookingTrip: 'Your Trip',
        bookingRate: 'Booking Rate',
        bookingPerDay: 'Per Day',
        bookingPerWeek: 'Per Week',
        bookingWeekNote: 'Per-week bookings use weekly rate for full weeks plus per-day rate for remaining days.',
        bookingWeeks: 'Weeks',
        bookingExtraDays: 'Extra days',
        bookingCheckInTime: 'Check-in time',
        bookingCheckOutTime: 'Check-out time',
        bookingCalendarTitle: 'Select dates',
        bookingCalendarHint: 'Click to pick a start and end date. Booked dates are disabled.',
        bookingCalendarLoading: 'Loading...',
        bookingCalendarAvailable: 'Available',
        bookingCalendarUnavailable: 'Booked',
        bookingCalendarPrevious: 'Previous month',
        bookingCalendarNext: 'Next month',
        bookingCalendarWeekNumber: 'Week',
        bookingDateBlocked: 'This date is already booked. Please choose another date.',
        bookingRangeBlocked: 'Your selected range includes already booked dates. Please choose another range.',
        bookingChecking: 'Checking availability...',
        bookingAvailable: 'Dates are available!',
        bookingServiceFee: 'Service fee',
        bookingDoorstepTitle: 'Doorstep delivery & pickup',
        bookingDoorstepSubtitle: 'We deliver the caravan to your doorstep.',
        bookingDoorstepLine: 'Doorstep service',
        bookingDoorstepPendingLine: 'Price is decided by admin after approval.',
        bookingDeliveryAddress: 'Delivery address (doorstep)',
        bookingDeliveryAddressPlaceholder: 'Enter street, city, and zipcode for delivery',
        bookingDeliveryAddressRequired: 'Delivery address is required for doorstep delivery.',
        bookingDoorstepUnavailable: 'Doorstep delivery is not available for this caravan.',
        bookingTotal: 'Total',
        bookingTotalDueWithDeposit: 'Total amount (Booking + Deposit)',
        bookingPaymentDueDeposit: 'Deposit due within 24 hours',
        bookingPaymentDueFull: 'Deposit and rent due within 24 hours',
        bookingConfirming: 'Confirming...',
        bookingConfirm: 'Confirm Booking',
        bookingCheckingShort: 'Checking...',
        bookingNight: 'night',
        bookingNights: 'nights',
        bookingDay: 'day',
        bookingDays: 'days',
        bookingWeek: 'week',
        bookingWeeksPlural: 'weeks',
        bookingCalculation: 'Calculation',
        bookingBifurcation: 'Price bifurcation',
        bookingWeeklyRate: 'weekly rate',
        bookingDailyRate: 'per-day rate',
        bookingWeeklyCharge: 'Weekly charge',
        bookingDailyCharge: 'Daily charge',
        bookingRequested: 'Requested',
        bookingOptional: 'Optional',
        bookingOptionalAddonsTitle: 'Optional add-ons',
        bookingOptionalAddonsSubtitle: 'Selected add-ons are added to the booking amount.',
        bookingAddonsTotal: 'Add-ons Total',
        bookingRemarksLabel: 'Booking Remarks',
        bookingRemarksRequired: 'Booking remarks are required.',
        bookingDoorstepChargeNote: 'Note: Doorstep charge is calculated for 4 route legs (pickup/drop movements), total chargeable km = {km}.',
        bookingPendingUntilDeposit: 'Booking stays pending until admin confirms the required payment.',
        bookingBlockedCount: '{count} blocked',
        bookingCouldNotLoadBlockedRanges: 'Could not load blocked ranges.',
        bookingCouldNotCheckAvailability: 'Could not check availability.',
        bookingCouldNotCalculateQuote: 'Could not calculate quote.',
        bookingDatesUnavailable: 'These dates are not available. Please select a different range.',
        bookingFestivalPrompt: 'This caravan is not available for festivals. Are you booking for a festival? Click OK to see festival-friendly alternatives for the selected dates, or Cancel to continue with this booking.',
        bookingFestivalSuggestionsTitle: 'Festival-friendly alternatives',
        bookingFestivalSuggestionsSubtitle: 'Only caravans that allow festivals and are available for your selected dates are shown here.',
        bookingFestivalSuggestionsLoading: 'Finding festival-friendly alternatives...',
        bookingFestivalSuggestionsEmpty: 'No festival-friendly caravans are available for the selected dates.',
        bookingFestivalSuggestionsError: 'Could not load festival-friendly alternatives.',
        bookingFestivalSuggestionView: 'View details',
        bookingFestivalSuggestionCta: 'Book this caravan',
        bookingFestivalDetailLoading: 'Loading caravan details...',
        bookingFestivalDetailError: 'Could not load caravan details.',
        bookingFestivalNoReviews: 'No reviews yet.',
        commonClose: 'Close',
        listDateValidation: 'End date must be after start date.',
        cardTopOwner: 'Top owner',
        cardPetFriendly: 'Pet friendly',
        cardNoSmoking: 'No smoking',
        cardFestivalAvailable: 'Available for Festival',
        cardFestivalUnavailable: 'Not Available for Festival',
        cardType: 'Type',
        cardLocationUnavailable: 'Location unavailable',
        cardDoorstepAvailable: 'Doorstep delivery available',
        cardFrom: 'from',
        cardPerNight: 'per night',
        cardPerDay: 'Per day',
        cardPerWeek: 'Per week',
        cardSitting: 'Sitting',
        cardBeds: 'Beds',
        myBookingsTitle: 'My Bookings',
        myBookingsLoading: 'Loading your bookings...',
        myBookingsEmptyTitle: 'No Bookings Yet',
        myBookingsEmptyText: "You haven't booked any adventures. Time to start exploring!",
        myBookingsExplore: 'Explore Caravans',
        myBookingsDates: 'Dates',
        myBookingsTotal: 'Total Price',
        profileTitle: 'Complete Profile Details',
        profileSubtitle: 'Name, Street, City, Zipcode, Mobile, and Bank Reg No + Account No are required before booking or listing a vehicle.',
        profileName: 'Name',
        profileStreet: 'Street',
        profileCity: 'City',
        profileZipcode: 'Zipcode',
        profileMobile: 'Mobile',
        profileBank: 'Bank Account details',
        profileBankRegNo: 'Bank Registration No',
        profileBankAccountNo: 'Bank Account No',
        profileSave: 'Save Profile',
        profileRequired: 'All fields are required.',
        profileSaved: 'Profile saved.',
        ownerCalendarTitle: 'Manage Unavailable Dates',
        ownerCalendarSubtitle: 'Block dates when your caravan should not be bookable.',
        ownerSelectCaravan: 'Select Caravan',
        ownerNoCaravans: 'You do not have any caravans yet.',
        ownerStartDate: 'Start Date',
        ownerEndDate: 'End Date',
        ownerReason: 'Reason (optional)',
        ownerSaveBlock: 'Block Dates',
        ownerExistingBlocks: 'Existing Blocks',
        ownerDeleteBlock: 'Remove',
        ownerDateValidation: 'End date must be after start date.',
        ownerBlockConflict: 'Date range overlaps an existing block.',
        listCheckIn: 'Check-in Date',
        listCheckOut: 'Check-out Date',
        listDateFilterNote: 'When dates are set, only available caravans are shown.',
        addressRequired: 'Street, City, and Zipcode are required.',
        weeklyRateValidation: 'Per-week rate must be less than or equal to 7 days of the per-day rate.',
        termsTitle: 'Terms and Conditions',
        termsEffective: 'Effective date: [Add Date]',
        termsBack: 'Back to home',
        aboutTitle: 'About Udlejningsvogn.dk',
        aboutSubtitle: 'We make caravan travel simple, reliable, and memorable.',
        aboutBack: 'Back to home',
        aboutStory: 'Our story',
        aboutStoryP1: 'Udlejningsvogn.dk started in 2021 with a single goal: make road trips accessible without the hassles of ownership. What began with three caravans in a small coastal town has grown into a trusted marketplace for verified owners and confident renters.',
        aboutStoryP2: 'Today, we focus on safe, flexible rentals with clear pricing, local support, and quality checks so you can spend less time planning and more time exploring.',
        aboutAtGlance: 'At a glance',
        aboutTrips: 'Trips completed',
        aboutCaravans: 'Registered vehicles',
        aboutCities: '25 cities covered',
        aboutRating: 'Average rating',
        aboutBelief: 'What we believe',
        aboutTrust: 'Trust first',
        aboutTrustText: 'Every listing is verified and every booking is protected so you can rent with confidence.',
        aboutPricing: 'Clear pricing',
        aboutPricingText: 'No hidden fees. Transparent per-day and per-week rates shown up front.',
        aboutSupport: 'Local support',
        aboutSupportText: 'We partner with owners across regions to provide real-time help and guidance.',
        aboutReady: 'Ready for your next trip?',
        aboutReadyText: 'Explore caravans and book in minutes.',
        aboutBrowse: 'Browse caravans',
        adminAllBookingsTitle: 'All Bookings',
        adminCheckInPhotos: 'Check-in Photos',
        adminCheckInPhotoGalleryTitle: 'Check-in Photos - {bookingNo}',
        adminCheckInPhotoAlt: 'Check-in photo {count}',
        adminBookingNumber: 'Booking No',
        adminBookingId: 'Booking ID',
        adminCustomer: 'Customer',
        adminOwner: 'Owner',
        adminDates: 'Dates',
        adminPricingMode: 'Pricing Mode',
        adminRentalCharge: 'Rental Charge',
        adminPricingDuration: 'Pricing Duration',
        adminPricingCalculation: 'Pricing Calculation',
        adminDoorstepFee: 'Doorstep Fee',
        adminDeliveryAddress: 'Delivery Address',
        adminAddons: 'Add-ons',
        adminAddonsTotal: 'Add-ons Total',
        adminBookingAmount: 'Booking Amount',
        adminDeposit: 'Deposit',
        adminNetDueAmount: 'Net Due Amount',
        adminBookingRemarks: 'Booking Remarks',
        adminCheckIn: 'Check-in',
        adminCheckOut: 'Check-out',
        adminCheckoutReview: 'Checkout Review',
        adminSubmittedAt: 'Submitted At',
        adminReviewedAt: 'Reviewed At',
        adminAdminComment: 'Admin Comment',
        adminDeduction: 'Deduction',
        adminDepositRefund: 'Deposit Refund',
        adminCancelledAt: 'Cancelled At',
        adminCancellationRemarks: 'Cancellation Remarks',
        adminCancellationRentRefund: 'Cancellation Rent Refund',
        adminCancellationDepositRefund: 'Cancellation Deposit Refund',
        adminCreated: 'Created',
        adminNotAvailable: 'N/A',
        adminPaid: 'Paid',
        adminPending: 'Pending',
        adminCancelBookingTitle: 'Cancel Booking',
        adminCancelBookingHelp: 'Use this for pending or confirmed bookings. Confirmed bookings move to the cancellation refunds screen after cancellation.',
        adminCancelBookingAction: 'Cancel Booking',
        adminCancelling: 'Cancelling...',
        adminNoBookingsFound: 'No bookings found yet.',
        adminBookingStatusPending: 'Pending',
        adminBookingStatusConfirmed: 'Confirmed',
        adminBookingStatusCheckedIn: 'Checked in',
        adminBookingStatusCheckoutPendingCustomer: 'Checkout pending customer',
        adminBookingStatusCheckoutPendingApproval: 'Checkout pending approval',
        adminBookingStatusCheckoutRejected: 'Checkout rejected',
        adminBookingStatusCheckedOut: 'Checked out',
        adminBookingStatusCompleted: 'Completed',
        adminBookingStatusExpired: 'Expired',
        adminBookingStatusCancelled: 'Cancelled',
        adminCheckoutReviewStatusPending: 'Pending',
        adminCheckoutReviewStatusApproved: 'Approved',
        adminCheckoutReviewStatusRejected: 'Rejected'
    }
};

// --- UI COMPONENTS ---

const NAV_LINK_CLASS = 'text-[1.02rem] font-medium text-gray-700 hover:text-green-700 transition-colors';
const NAV_LINK_ACTIVE_CLASS = 'relative text-green-700 after:absolute after:left-0 after:-bottom-3 after:h-0.5 after:w-full after:rounded-full after:bg-green-600';
const MOBILE_NAV_LINK_CLASS = 'py-1 text-left text-[1rem] font-medium text-gray-700 hover:text-green-700 transition-colors';
const MOBILE_NAV_LINK_ACTIVE_CLASS = 'rounded-xl bg-green-50 px-3 py-2 text-green-700';
const CONTROL_LABEL_CLASS = 'block text-[0.95rem] font-semibold text-gray-700 mb-2';
const CONTROL_INPUT_CLASS = 'w-full min-h-[3.25rem] rounded-2xl border border-gray-300 bg-white px-4 py-3 text-[1rem] text-gray-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500';
const CONTROL_SELECT_CLASS = `${CONTROL_INPUT_CLASS} pr-10`;
const CONTROL_TEXTAREA_CLASS = 'w-full min-h-[8rem] rounded-2xl border border-gray-300 bg-white px-4 py-3 text-[1rem] text-gray-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500';
const DELIVERY_PARTNER_INPUT_CLASS = `${CONTROL_INPUT_CLASS} delivery-partner-field`;
const DELIVERY_PARTNER_TEXTAREA_CLASS = `${CONTROL_TEXTAREA_CLASS} delivery-partner-field`;
const PRIMARY_BUTTON_CLASS = 'inline-flex min-h-[3.25rem] items-center justify-center rounded-full bg-green-600 px-6 py-3 text-[1rem] font-semibold text-white transition-colors hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed';
const SECONDARY_BUTTON_CLASS = 'inline-flex min-h-[3.25rem] items-center justify-center rounded-2xl border border-gray-300 bg-white px-5 py-3 text-[0.98rem] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60';

const LocalizedFileInput = ({ id, selectedFiles = [], className = '', buttonClassName = '', statusClassName = '', ...inputProps }) => {
    const { t } = useContext(LanguageContext);
    const inputRef = useRef(null);
    const normalizedSelectedFiles = Array.isArray(selectedFiles)
        ? selectedFiles.filter(Boolean)
        : [];
    const selectionLabel = normalizedSelectedFiles.length === 0
        ? t('fileNoSelection')
        : normalizedSelectedFiles.length === 1
            ? (normalizedSelectedFiles[0]?.name || t('fileCountSelected').replace('{count}', '1'))
            : t('fileCountSelected').replace('{count}', normalizedSelectedFiles.length);
    const openFilePicker = useCallback(() => {
        if (!inputProps.disabled) {
            inputRef.current?.click();
        }
    }, [inputProps.disabled]);

    return (
        <div className={className}>
            <input
                ref={inputRef}
                id={id}
                type="file"
                className="sr-only"
                {...inputProps}
            />
            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={openFilePicker}
                    disabled={inputProps.disabled}
                    className={`${SECONDARY_BUTTON_CLASS} ${buttonClassName}`.trim()}
                >
                    {t('fileChoose')}
                </button>
                <span className={`text-[0.98rem] text-gray-600 break-all ${statusClassName}`.trim()}>{selectionLabel}</span>
            </div>
        </div>
    );
};

const isModifiedClickEvent = (event) => (
    event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0
);

const AppLink = ({ route, caravan = null, onNavigate, className = '', children, ...restProps }) => {
    const href = buildHrefFromRoute(route, caravan);

    const handleClick = (event) => {
        if (!onNavigate || isModifiedClickEvent(event) || restProps.target === '_blank') {
            return;
        }

        event.preventDefault();
        onNavigate(route);
    };

    return (
        <a href={href} onClick={handleClick} className={className} {...restProps}>
            {children}
        </a>
    );
};

const CaravanWeightSummary = ({
    caravan,
    className = 'grid grid-cols-1 gap-2 sm:grid-cols-2',
    itemClassName = 'rounded-xl border border-gray-200 bg-gray-50 px-3 py-2',
    labelClassName = 'text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500',
    valueClassName = 'mt-1 text-sm font-semibold text-gray-900'
}) => {
    const { language } = useContext(LanguageContext);
    const ownWeightKg = getCaravanOwnWeightKg(caravan);
    const totalWeightKg = getCaravanTotalWeightKg(caravan);

    if (!caravan || (ownWeightKg === null && totalWeightKg === null)) {
        return null;
    }

    const ownWeightLabel = language === 'da' ? 'Egenvaegt' : 'Own Weight';
    const totalWeightLabel = language === 'da' ? 'Totalvaegt' : 'Total Weight';

    return (
        <div className={className}>
            <div className={itemClassName}>
                <p className={labelClassName}>{ownWeightLabel}</p>
                <p className={valueClassName}>{formatCaravanWeightKg(ownWeightKg)}</p>
            </div>
            <div className={itemClassName}>
                <p className={labelClassName}>{totalWeightLabel}</p>
                <p className={valueClassName}>{formatCaravanWeightKg(totalWeightKg)}</p>
            </div>
        </div>
    );
};

const Header = ({ user, onNavigate, onLogout, isProfileComplete, showManageMenu, currentPage, hasDeliveryPartnerProfile }) => {
    const { language, setLanguage, t } = useContext(LanguageContext);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const welcomeText = t('navWelcome').replace('{name}', user?.name || '');
    const canOpenManageMenu = Boolean(user && showManageMenu);
    const showDeliverySignupLink = !hasDeliveryPartnerProfile;
    const showDeliveryPortalLink = hasDeliveryPartnerProfile;
    const deliverySignupText = language === 'da' ? 'Bliv leveringspartner' : 'Become Delivery Partner';
    const deliveryPortalText = language === 'da' ? 'Leveringscenter' : 'Delivery Center';
    const isRouteActive = useCallback((pages) => pages.includes(currentPage), [currentPage]);
    const getDesktopNavClass = useCallback((pages, baseClass = NAV_LINK_CLASS) => (
        `${baseClass} ${isRouteActive(pages) ? NAV_LINK_ACTIVE_CLASS : ''}`.trim()
    ), [isRouteActive]);
    const getMobileNavClass = useCallback((pages, baseClass = MOBILE_NAV_LINK_CLASS) => (
        `${baseClass} ${isRouteActive(pages) ? MOBILE_NAV_LINK_ACTIVE_CLASS : ''}`.trim()
    ), [isRouteActive]);
    const navigateAndClose = useCallback((destination) => {
        setIsMobileMenuOpen(false);
        onNavigate(destination);
    }, [onNavigate]);
    const logoutAndClose = useCallback(() => {
        setIsMobileMenuOpen(false);
        onLogout();
    }, [onLogout]);

    return (
        <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur">
            <nav className="mx-auto flex max-w-[1880px] items-center justify-between px-6 py-4">
                <AppLink route={{ page: 'home' }} onNavigate={navigateAndClose} className="flex items-center gap-3 text-gray-800 hover:text-green-700 transition-colors">
                    <img
                        src={PORTAL_LOGO_SRC}
                        alt="Udlejningsvogn.dk logo"
                        data-logo-index="0"
                        className="h-16 sm:h-[4.5rem] w-auto max-w-[340px] object-contain p-1 bg-white border border-gray-200 rounded-xl shadow-sm"
                        onError={handlePortalLogoError}
                    />
                </AppLink>
                <div className="hidden md:flex items-center gap-7">
                    <AppLink route={{ page: 'list' }} onNavigate={navigateAndClose} className={getDesktopNavClass(['list', 'detail', 'booking', 'booking-success'])}>{t('navExplore')}</AppLink>
                    <AppLink route={{ page: 'about' }} onNavigate={navigateAndClose} className={getDesktopNavClass(['about'])}>{t('navAbout')}</AppLink>
                    <button onClick={() => navigateAndClose({ page: 'become-owner' })} className={getDesktopNavClass(['become-owner'])}>{t('navBecomeOwner')}</button>
                    {showDeliverySignupLink && (
                        <button onClick={() => navigateAndClose({ page: 'become-delivery-partner' })} className={getDesktopNavClass(['become-delivery-partner'])}>{deliverySignupText}</button>
                    )}
                    {showDeliveryPortalLink && (
                        <button onClick={() => navigateAndClose({ page: 'delivery-portal' })} className={getDesktopNavClass(['delivery-portal'], 'text-[1.02rem] font-semibold text-red-600 hover:text-red-800 transition-colors')}>{deliveryPortalText}</button>
                    )}
                    <AppLink route={{ page: 'terms' }} onNavigate={navigateAndClose} className={getDesktopNavClass(['terms'])}>{t('navTerms')}</AppLink>
                    {user?.role === 'admin' && (
                        <button onClick={() => navigateAndClose({ page: 'admin' })} className={getDesktopNavClass(['admin'], 'text-[1.02rem] font-semibold text-red-600 hover:text-red-800 transition-colors')}>{t('navAdmin')}</button>
                    )}
                    {user?.role === 'user' && (
                        <>
                            <button onClick={() => navigateAndClose({ page: 'my-bookings' })} className={getDesktopNavClass(['my-bookings'], 'text-[1.02rem] font-semibold text-red-600 hover:text-red-800 transition-colors')}>{t('navMyBookings')}</button>
                        </>
                    )}
                    {canOpenManageMenu && (
                        <button onClick={() => navigateAndClose({ page: 'owner-business' })} className={getDesktopNavClass(['owner-business', 'owner-availability'])}>{t('navManageCalendar')}</button>
                    )}
                    {user ? (
                        <>
                            <button
                                onClick={() => navigateAndClose({ page: 'profile-details' })}
                                className={`relative transition-colors ${isRouteActive(['profile-details']) ? 'text-green-700' : 'text-gray-600 hover:text-green-700'}`}
                                title={isProfileComplete ? t('navProfileDone') : t('navProfilePending')}
                                aria-label={t('navProfile')}
                            >
                                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0a3 3 0 11-6 0m6 0H9" />
                                </svg>
                                <span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full border border-white ${isProfileComplete ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            </button>
                            <span className="text-[1rem] font-semibold text-gray-800">{welcomeText}</span>
                            <button onClick={logoutAndClose} className={NAV_LINK_CLASS}>{t('navLogout')}</button>
                        </>
                    ) : (
                        <>
                            <AppLink route={{ page: 'login' }} onNavigate={navigateAndClose} className={getDesktopNavClass(['login', 'forgot-password', 'change-password'])}>{t('navLogin')}</AppLink>
                            <AppLink route={{ page: 'signup' }} onNavigate={navigateAndClose} className={PRIMARY_BUTTON_CLASS}>{t('navSignup')}</AppLink>
                        </>
                    )}
                    <div className="flex items-center gap-3 text-gray-600">
                        <span className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gray-500">{t('navLanguage')}</span>
                        <select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            className="min-h-[2.9rem] rounded-full border border-gray-300 bg-white px-4 py-2 text-[0.95rem] font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            <option value="da">{t('langDanish')}</option>
                            <option value="en">{t('langEnglish')}</option>
                        </select>
                    </div>
                </div>
                <div className="md:hidden">
                    <button
                        onClick={() => setIsMobileMenuOpen((previous) => !previous)}
                        className="text-gray-600 hover:text-gray-800 focus:outline-none"
                        aria-label="Toggle menu"
                        aria-expanded={isMobileMenuOpen}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            {isMobileMenuOpen
                                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>}
                        </svg>
                    </button>
                </div>
            </nav>
            {isMobileMenuOpen && (
                <div className="md:hidden border-t border-gray-200 bg-white shadow-sm">
                    <div className="mx-auto flex max-w-[1880px] flex-col gap-3 px-6 py-5">
                        <AppLink route={{ page: 'list' }} onNavigate={navigateAndClose} className={getMobileNavClass(['list', 'detail', 'booking', 'booking-success'])}>{t('navExplore')}</AppLink>
                        <AppLink route={{ page: 'about' }} onNavigate={navigateAndClose} className={getMobileNavClass(['about'])}>{t('navAbout')}</AppLink>
                        <button onClick={() => navigateAndClose({ page: 'become-owner' })} className={getMobileNavClass(['become-owner'])}>{t('navBecomeOwner')}</button>
                        {showDeliverySignupLink && (
                            <button onClick={() => navigateAndClose({ page: 'become-delivery-partner' })} className={getMobileNavClass(['become-delivery-partner'])}>{deliverySignupText}</button>
                        )}
                        {showDeliveryPortalLink && (
                            <button onClick={() => navigateAndClose({ page: 'delivery-portal' })} className={getMobileNavClass(['delivery-portal'], 'py-1 text-left text-[1rem] font-semibold text-red-600 hover:text-red-800 transition-colors')}>{deliveryPortalText}</button>
                        )}
                        <AppLink route={{ page: 'terms' }} onNavigate={navigateAndClose} className={getMobileNavClass(['terms'])}>{t('navTerms')}</AppLink>
                        {user?.role === 'admin' && (
                            <>
                                <button onClick={() => navigateAndClose({ page: 'admin' })} className={getMobileNavClass(['admin'], 'py-1 text-left text-[1rem] font-semibold text-red-600 hover:text-red-800 transition-colors')}>{t('navAdmin')}</button>
                                {canOpenManageMenu && (
                                    <button onClick={() => navigateAndClose({ page: 'owner-business' })} className={getMobileNavClass(['owner-business', 'owner-availability'])}>{t('navManageCalendar')}</button>
                                )}
                                <span className="text-[0.98rem] text-gray-700">{welcomeText}</span>
                                <button onClick={logoutAndClose} className="py-1 text-left text-[1rem] font-medium text-gray-700 hover:text-green-700 transition-colors">{t('navLogout')}</button>
                            </>
                        )}
                        {user?.role === 'user' && (
                            <>
                                <button onClick={() => navigateAndClose({ page: 'my-bookings' })} className={getMobileNavClass(['my-bookings'], 'py-1 text-left text-[1rem] font-semibold text-red-600 hover:text-red-800 transition-colors')}>{t('navMyBookings')}</button>
                                {canOpenManageMenu && (
                                    <button onClick={() => navigateAndClose({ page: 'owner-business' })} className={getMobileNavClass(['owner-business', 'owner-availability'])}>{t('navManageCalendar')}</button>
                                )}
                                <button onClick={() => navigateAndClose({ page: 'profile-details' })} className={getMobileNavClass(['profile-details'])}>
                                    {t('navProfile')}
                                </button>
                                <span className={`inline-flex w-fit items-center rounded-full px-2 py-1 text-xs font-semibold ${isProfileComplete ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {isProfileComplete ? t('navProfileDone') : t('navProfilePending')}
                                </span>
                                <span className="text-[0.98rem] text-gray-700">{welcomeText}</span>
                                <button onClick={logoutAndClose} className="py-1 text-left text-[1rem] font-medium text-gray-700 hover:text-green-700 transition-colors">{t('navLogout')}</button>
                            </>
                        )}
                        {user?.role === 'delivery_partner' && (
                            <>
                                <button onClick={() => navigateAndClose({ page: 'profile-details' })} className={getMobileNavClass(['profile-details'])}>
                                    {t('navProfile')}
                                </button>
                                <span className={`inline-flex w-fit items-center rounded-full px-2 py-1 text-xs font-semibold ${isProfileComplete ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {isProfileComplete ? t('navProfileDone') : t('navProfilePending')}
                                </span>
                                <span className="text-[0.98rem] text-gray-700">{welcomeText}</span>
                                <button onClick={logoutAndClose} className="py-1 text-left text-[1rem] font-medium text-gray-700 hover:text-green-700 transition-colors">{t('navLogout')}</button>
                            </>
                        )}
                        {!user && (
                            <>
                                <AppLink route={{ page: 'login' }} onNavigate={navigateAndClose} className={getMobileNavClass(['login', 'forgot-password', 'change-password'])}>{t('navLogin')}</AppLink>
                                <AppLink route={{ page: 'signup' }} onNavigate={navigateAndClose} className={`${PRIMARY_BUTTON_CLASS} w-fit`}>{t('navSignup')}</AppLink>
                            </>
                        )}
                        <div className="flex items-center gap-3 border-t border-gray-100 pt-3 text-gray-600">
                            <span className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gray-500">{t('navLanguage')}</span>
                            <select
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                className="min-h-[3rem] rounded-full border border-gray-300 bg-white px-4 py-2 text-[0.95rem] font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                <option value="da">{t('langDanish')}</option>
                                <option value="en">{t('langEnglish')}</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

const Footer = ({ onNavigate }) => {
    const { t } = useContext(LanguageContext);

    return (
        <footer className="bg-green-900 text-white mt-12">
            <div className="container mx-auto px-6 py-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
                    <div>
                        <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                            <img
                                src={PORTAL_LOGO_SRC}
                                alt="Udlejningsvogn.dk logo"
                                data-logo-index="0"
                                className="h-14 w-auto max-w-[220px] object-contain"
                                onError={handlePortalLogoError}
                            />
                        </div>
                        <p className="text-green-200">{t('footerTagline')}</p>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold mb-2">{t('footerQuickLinks')}</h3>
                        <ul>
                            <li><AppLink route={{ page: 'about' }} onNavigate={onNavigate} className="hover:text-green-300 transition-colors">{t('footerAbout')}</AppLink></li>
                            <li className="text-green-200">{t('footerAddressLabel')}: {t('footerAddressValue')}</li>
                            <li className="text-green-200">{t('footerCvrLabel')}: {t('footerCvrValue')}</li>
                            <li className="text-green-200">{t('footerOfficeTimingLabel')}: {t('footerOfficeTimingValue')}</li>
                            <li>
                                <a href={`mailto:${t('footerEmailValue')}`} className="hover:text-green-300 transition-colors">
                                    {t('footerEmailLabel')}: {t('footerEmailValue')}
                                </a>
                            </li>
                            <li>
                                <a href={`tel:${String(t('footerPhoneValue')).replace(/\s+/g, '')}`} className="hover:text-green-300 transition-colors">
                                    {t('footerPhoneLabel')}: {t('footerPhoneValue')}
                                </a>
                            </li>
                            <li><AppLink route={{ page: 'terms' }} onNavigate={onNavigate} className="hover:text-green-300 transition-colors">{t('footerFaq')}</AppLink></li>
                            <li><AppLink route={{ page: 'terms' }} onNavigate={onNavigate} className="hover:text-green-300 transition-colors">{t('footerTerms')}</AppLink></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold mb-2">{t('footerFollow')}</h3>
                        <div className="flex justify-center md:justify-start space-x-4">
                            <a
                                href="https://www.facebook.com/profile.php?id=61580474821792&amp;name=xhp_nt__fb__action__open_user"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Udlejningsvogn.dk on Facebook"
                                className="text-green-200 transition-colors hover:text-white hover:underline"
                            >
                                Facebook
                            </a>
                            <a
                                href="https://www.instagram.com/udlejningsvogn.dk"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Udlejningsvogn.dk on Instagram"
                                className="text-green-200 transition-colors hover:text-white hover:underline"
                            >
                                Instagram
                            </a>
                        </div>
                    </div>
                </div>
                <div className="border-t border-green-800 mt-8 pt-4 text-center text-green-300">
                    &copy; {new Date().getFullYear()} Udlejningsvogn.dk. {t('footerRights')}
                </div>
            </div>
        </footer>
    );
};

const SeatIcon = ({ className = 'h-5 w-5' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className} aria-hidden="true">
        <path d="M7 13V8a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 13h10v3H5z" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 16v3M13 16v3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const BedIcon = ({ className = 'h-5 w-5' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className} aria-hidden="true">
        <path d="M3 8h18v8H3z" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 8V5h6a3 3 0 0 1 3 3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 16v3M21 16v3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CaravanItem = ({ caravan, onNavigate, selectedStartDate = '', selectedEndDate = '' }) => {
    const { t } = useContext(LanguageContext);
    const isPetAllowed = isTruthyValue(caravan?.pets_allowed ?? caravan?.petsAllowed);
    const isNoSmoking = isTruthyValue(caravan?.no_smoking ?? caravan?.noSmoking);
    const isDoorstepAvailable = isTruthyValue(caravan?.doorstep_available ?? caravan?.doorstepAvailable);
    const festivalAvailable = isFestivalAvailable(caravan);
    const rating = getCaravanDisplayRating(caravan);
    const reviewCount = Number(caravan?.reviewCount || 0);
    const dailyPrice = Number(caravan?.price_per_day || 0);
    const weeklyPrice = Number(caravan?.price_per_week || (dailyPrice * 7));
    const sittingCapacity = getCaravanSittingCapacity(caravan);
    const bedCount = getCaravanBedCount(caravan);
    const isTopOwner = rating >= 4.7 && reviewCount >= 5;
    const vehicleTypeLabel = String(caravan?.typeName || caravan?.type_name || caravan?.vehicle_category || '').trim();
    const badges = [
        ...(isTopOwner ? [t('cardTopOwner')] : []),
        ...(!isTopOwner && isPetAllowed ? [t('cardPetFriendly')] : []),
        ...(isNoSmoking ? [t('cardNoSmoking')] : []),
        ...(festivalAvailable ? [t('cardFestivalAvailable')] : [t('cardFestivalUnavailable')]),
        ...(vehicleTypeLabel ? [`${t('cardType')}: ${vehicleTypeLabel}`] : [])
    ];
    const locationLabel = (caravan?.location || t('cardLocationUnavailable')).trim();
    const detailRoute = {
        page: 'detail',
        caravan_id: caravan?.caravan_id,
        startDate: selectedStartDate,
        endDate: selectedEndDate
    };

    return (
        <article className="group h-full overflow-hidden rounded-[22px] border border-gray-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
            <AppLink route={detailRoute} caravan={caravan} onNavigate={onNavigate} className="flex h-full w-full flex-col text-left">
                <div className="relative aspect-[16/11] overflow-hidden bg-gray-100">
                    <img
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        src={getCaravanMainImage(caravan)}
                        alt={caravan.name}
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                    />
                    {badges.length > 0 && (
                        <div className="absolute left-3 top-3 flex max-w-[70%] flex-col gap-2">
                            {badges.map((badgeText) => (
                                <div key={badgeText} className="w-fit rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-gray-900 shadow">
                                    {badgeText}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
                            <path d="M12 21s-6.7-4.35-9.14-7.9A5.6 5.6 0 0 1 12 5.18a5.6 5.6 0 0 1 9.14 7.92C18.7 16.65 12 21 12 21Z" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
                <div className="flex flex-1 flex-col p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                        <p className="min-w-0 flex-1 truncate text-[1.05rem] font-semibold text-gray-900">{locationLabel}</p>
                        <div className="flex shrink-0 items-center whitespace-nowrap text-base font-semibold text-gray-900">
                            <svg viewBox="0 0 24 24" className="mr-1 h-4 w-4 fill-current text-gray-900" aria-hidden="true">
                                <path d="M12 1.8l2.95 6 6.62.96-4.79 4.66 1.13 6.6L12 16.9 6.09 20l1.13-6.58L2.4 8.76l6.62-.96L12 1.8Z" />
                            </svg>
                            {rating.toFixed(1)}
                            <span className="ml-1 text-sm font-medium text-gray-500">({reviewCount})</span>
                        </div>
                    </div>
                    <h3 className="mt-2 truncate text-[1.45rem] font-medium leading-tight text-gray-900">{caravan.name}</h3>
                    {isDoorstepAvailable && (
                        <p className="mt-1 text-xs font-medium text-green-700">{t('cardDoorstepAvailable')}</p>
                    )}
                    <CaravanWeightSummary
                        caravan={caravan}
                        className="mt-4 grid grid-cols-2 gap-2"
                        itemClassName="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2"
                        labelClassName="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500"
                        valueClassName="mt-1 text-sm font-semibold text-gray-900"
                    />
                    <div className="mt-5 flex items-end justify-between gap-4">
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                            <div className="flex items-center gap-1.5">
                                <SeatIcon className="h-4 w-4" />
                                <span className="text-lg font-medium">{sittingCapacity || 1}</span>
                            </div>
                            <span className="h-5 w-px bg-gray-300" />
                            <div className="flex items-center gap-1.5">
                                <BedIcon className="h-4 w-4" />
                                <span className="text-lg font-medium">{bedCount}</span>
                            </div>
                        </div>
                        <div className="text-right leading-tight">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">{t('cardFrom')}</p>
                            <p className="text-[1.9rem] font-bold text-gray-900">DKK {dailyPrice.toLocaleString('da-DK')}</p>
                            <p className="text-sm text-gray-600">{t('cardPerDay')}</p>
                            <p className="mt-2 text-xs text-gray-500">{t('cardPerWeek')}: DKK {weeklyPrice.toLocaleString('da-DK')}</p>
                        </div>
                    </div>
                </div>
            </AppLink>
        </article>
    );
};


// --- PAGE COMPONENTS ---

const HomePage = ({ onNavigate, caravans, isLoading, error }) => {
    const { t } = useContext(LanguageContext);
    const [visibleCount, setVisibleCount] = useState(9);
    const loadMoreRef = useRef(null);
    const visibleCaravans = caravans.slice(0, visibleCount);

    useEffect(() => {
        setVisibleCount(9);
    }, [caravans.length]);

    useEffect(() => {
        const target = loadMoreRef.current;
        if (!target || visibleCount >= caravans.length) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                setVisibleCount((prev) => Math.min(prev + 6, caravans.length));
            }
        }, { rootMargin: '300px 0px' });

        observer.observe(target);
        return () => observer.disconnect();
    }, [visibleCount, caravans.length]);

    return (
        <div>
            <div className="relative w-full min-h-[25vh] bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1470246973918-29a93221c455?q=80&w=1600&auto=format&fit=crop')" }}>
                <div className="flex items-center justify-center min-h-[25vh] w-full bg-black/50 py-5 md:py-7">
                    <div className="text-center px-4 max-w-3xl mx-auto">
                        <div className="inline-flex items-center gap-3 bg-white/95 rounded-full px-3 py-1.5 mb-3 shadow-lg">
                            <img
                                src={PORTAL_LOGO_SRC}
                                alt="Udlejningsvogn.dk logo"
                                data-logo-index="0"
                                className="h-10 md:h-12 w-auto object-contain"
                                onError={handlePortalLogoError}
                            />
                            <span className="text-sm md:text-base font-semibold text-gray-900 tracking-wide">Udlejningsvogn.dk</span>
                        </div>
                        <h1 className="text-white text-3xl font-bold sm:text-4xl md:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: "'Lora', serif" }}>{t('homeHeroTitle')}</h1>
                        <p className="text-white text-lg mt-3 max-w-2xl mx-auto">{t('homeHeroSubtitle')}</p>
                        <AppLink route={{ page: 'list' }} onNavigate={onNavigate} className="mt-5 inline-block bg-green-600 text-white py-3 px-8 rounded-full text-lg font-semibold hover:bg-green-700 transition-transform hover:scale-105">
                            {t('homeStartExploring')}
                        </AppLink>
                    </div>
                </div>
            </div>
            <section className="py-16 bg-stone-50">
                <div className="mx-auto max-w-[1880px] px-6">
                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-10">{t('homeFeatured')}</h2>
                    {isLoading && <p className="text-center">{t('homeLoadingFeatured')}</p>}
                    {error && <p className="text-center text-red-500">{error}</p>}
                    {!isLoading && !error && (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
                            {visibleCaravans.map(caravan => (
                                <CaravanItem key={caravan.caravan_id} caravan={caravan} onNavigate={onNavigate} />
                            ))}
                        </div>
                    )}
                    {!isLoading && !error && visibleCount < caravans.length && (
                        <div ref={loadMoreRef} className="h-10 mt-6 flex items-center justify-center text-sm text-gray-500">
                            Scroll to load more caravans...
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

const CaravanListPage = ({ onNavigate, caravans, isLoading, error, caravanTypes }) => {
    const { t, language } = useContext(LanguageContext);
    const [filteredCaravans, setFilteredCaravans] = useState([]);
    const [dateFilteredCaravans, setDateFilteredCaravans] = useState(null);
    const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
    const [availabilityError, setAvailabilityError] = useState('');
    const [sortBy, setSortBy] = useState('price_asc');
    const [visibleCount, setVisibleCount] = useState(12);
    const loadMoreRef = useRef(null);
    const [filters, setFilters] = useState({
        searchTerm: '',
        maxPrice: 20000,
        capacity: 0,
        festivalOnly: false,
        type: 'All',
        startDate: '',
        endDate: '',
        longTermOnly: false,
        months: 0
    });
    const sourceCaravans = Array.isArray(dateFilteredCaravans) ? dateFilteredCaravans : caravans;
    const [hasInitializedMaxPrice, setHasInitializedMaxPrice] = useState(false);
    const maxWeeklyPrice = useMemo(() => {
        const prices = (Array.isArray(sourceCaravans) ? sourceCaravans : [])
            .map((item) => Number(item?.price_per_week || (Number(item?.price_per_day || 0) * 7)))
            .filter((value) => Number.isFinite(value) && value > 0);
        return prices.length > 0 ? Math.max(...prices) : 20000;
    }, [sourceCaravans]);
    const priceSliderMax = Math.max(20000, Math.ceil(maxWeeklyPrice / 100) * 100);

    useEffect(() => {
        if (hasInitializedMaxPrice) {
            return;
        }
        if (!Number.isFinite(priceSliderMax) || priceSliderMax <= 0) {
            return;
        }
        setFilters((prev) => ({ ...prev, maxPrice: Math.max(prev.maxPrice, priceSliderMax) }));
        setHasInitializedMaxPrice(true);
    }, [hasInitializedMaxPrice, priceSliderMax]);

    useEffect(() => {
        let ignore = false;
        const startDate = String(filters.startDate || '').trim();
        const endDate = String(filters.endDate || '').trim();

        if (!startDate && !endDate) {
            setDateFilteredCaravans(null);
            setIsAvailabilityLoading(false);
            setAvailabilityError('');
            return () => {
                ignore = true;
            };
        }

        if (!startDate || !endDate) {
            setDateFilteredCaravans(null);
            setIsAvailabilityLoading(false);
            setAvailabilityError('');
            return () => {
                ignore = true;
            };
        }

        const startLocal = parseDateOnlyLocal(startDate);
        const endLocal = parseDateOnlyLocal(endDate);
        if (!startLocal || !endLocal || endLocal <= startLocal) {
            setDateFilteredCaravans(null);
            setIsAvailabilityLoading(false);
            setAvailabilityError(t('listDateValidation'));
            return () => {
                ignore = true;
            };
        }

        const loadAvailableCaravans = async () => {
            setIsAvailabilityLoading(true);
            setAvailabilityError('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/caravans?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
                if (!response.ok) {
                    throw new Error(t('bookingCouldNotCheckAvailability'));
                }

                const data = await response.json();
                if (ignore) return;
                setDateFilteredCaravans(Array.isArray(data) ? data : []);
            } catch (err) {
                if (!ignore) {
                    setDateFilteredCaravans(null);
                    setAvailabilityError(err.message || t('bookingCouldNotCheckAvailability'));
                }
            } finally {
                if (!ignore) {
                    setIsAvailabilityLoading(false);
                }
            }
        };

        loadAvailableCaravans();
        return () => {
            ignore = true;
        };
    }, [filters.startDate, filters.endDate, t]);

    useEffect(() => {
        if (!filters.longTermOnly || Number(filters.months) <= 0) {
            return;
        }
        const computedEndDate = calculateMonthBookingEndDate(filters.startDate, Number(filters.months));
        if (computedEndDate && computedEndDate !== filters.endDate) {
            setFilters(prev => ({ ...prev, endDate: computedEndDate }));
        }
    }, [filters.longTermOnly, filters.months, filters.startDate, filters.endDate]);

    const typeFilterOptions = useMemo(() => {
        const options = new Set();
        (Array.isArray(caravanTypes) ? caravanTypes : []).forEach((item) => {
            const typeLabel = String(item?.type_name || item?.typeName || '').trim();
            if (typeLabel) {
                options.add(typeLabel);
            }
        });
        (Array.isArray(sourceCaravans) ? sourceCaravans : []).forEach((item) => {
            const typeLabel = String(item?.typeName || item?.type_name || item?.vehicle_category || item?.vehicleCategory || '').trim();
            if (typeLabel) {
                options.add(typeLabel);
            }
        });
        return ['All', ...Array.from(options)];
    }, [caravanTypes, sourceCaravans]);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    useEffect(() => {
        if (filters.type === 'All') {
            return;
        }
        if (!typeFilterOptions.includes(filters.type)) {
            setFilters(prev => ({ ...prev, type: 'All' }));
        }
    }, [filters.type, typeFilterOptions]);

    useEffect(() => {
        const results = sourceCaravans.filter(caravan => {
            const caravanName = String(caravan?.name || '').toLowerCase();
            const caravanLocation = String(caravan?.location || '').toLowerCase();
            const normalizedSearchTerm = String(filters.searchTerm || '').trim().toLowerCase();
            const searchTermMatch = normalizedSearchTerm
                ? caravanName.includes(normalizedSearchTerm) || caravanLocation.includes(normalizedSearchTerm)
                : true;
            const weeklyPrice = Number(caravan.price_per_week || (Number(caravan.price_per_day || 0) * 7));
            const priceMatch = weeklyPrice <= filters.maxPrice;
            const capacityMatch = filters.capacity > 0 ? getCaravanSittingCapacity(caravan) >= filters.capacity : true;
            const festivalMatch = filters.festivalOnly ? isFestivalAvailable(caravan) : true;
            const selectedType = normalizeVehicleTypeName(filters.type);
            const caravanTypeCandidates = [
                normalizeVehicleTypeName(caravan.typeName),
                normalizeVehicleTypeName(caravan.type_name),
                normalizeVehicleTypeName(caravan.vehicle_category),
                normalizeVehicleTypeName(caravan.vehicleCategory)
            ].filter(Boolean);
            const typeMatch = selectedType && selectedType !== 'all'
                ? caravanTypeCandidates.includes(selectedType)
                : true;
            const longTermMatch = filters.longTermOnly
                ? (isLongTermCaravan(caravan)
                    && (Number(filters.months) > 0 ? getMinimumBookingMonths(caravan) <= Number(filters.months) : true))
                : true;
            return searchTermMatch && priceMatch && capacityMatch && festivalMatch && typeMatch && longTermMatch;
        });
        const sorted = [...results].sort((a, b) => {
            if (sortBy === 'price_desc') return Number(b.price_per_day || 0) - Number(a.price_per_day || 0);
            if (sortBy === 'rating_desc') return getCaravanDisplayRating(b) - getCaravanDisplayRating(a);
            if (sortBy === 'rating_asc') return getCaravanDisplayRating(a) - getCaravanDisplayRating(b);
            return Number(a.price_per_day || 0) - Number(b.price_per_day || 0);
        });
        setFilteredCaravans(sorted);
        setVisibleCount(12);
    }, [filters, sourceCaravans, sortBy]);

    useEffect(() => {
        const target = loadMoreRef.current;
        if (!target || visibleCount >= filteredCaravans.length) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                setVisibleCount((prev) => Math.min(prev + 8, filteredCaravans.length));
            }
        }, { rootMargin: '300px 0px' });

        observer.observe(target);
        return () => observer.disconnect();
    }, [visibleCount, filteredCaravans.length]);

    return (
        <div className="mx-auto max-w-[1880px] px-6 py-8">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_minmax(0,1fr)] xl:gap-8">
                <aside className="relative z-30 h-fit rounded-[1.5rem] bg-white p-6 shadow-md lg:sticky top-24">
                    <h3 className="mb-5 border-b pb-3 text-[1.35rem] font-bold text-gray-900">{t('listFilters')}</h3>
                    <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <ExploreDatePicker
                            id="startDate"
                            label={t('listCheckIn')}
                            value={filters.startDate}
                            onChange={(startDate) => handleFilterChange({
                                startDate,
                                endDate: filters.endDate && filters.endDate < startDate ? '' : filters.endDate
                            })}
                            language={language}
                        />
                        <ExploreDatePicker
                            id="endDate"
                            label={t('listCheckOut')}
                            min={filters.startDate || undefined}
                            value={filters.endDate}
                            onChange={(endDate) => handleFilterChange({ endDate })}
                            disabled={filters.longTermOnly && Number(filters.months) > 0}
                            language={language}
                        />
                    </div>
                    <div className="mb-4 flex items-center gap-2 text-sm text-gray-700">
                        <input
                            id="list-long-term-only"
                            type="checkbox"
                            checked={filters.longTermOnly}
                            onChange={(e) => handleFilterChange({
                                longTermOnly: e.target.checked,
                                months: e.target.checked ? (Number(filters.months) || 1) : 0
                            })}
                            className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <label htmlFor="list-long-term-only">
                            {language === 'da' ? 'Kun langtidsleje' : 'Long-term only'}
                        </label>
                    </div>
                    {filters.longTermOnly && (
                        <div className="mb-4">
                            <label htmlFor="list-min-months" className={CONTROL_LABEL_CLASS}>{language === 'da' ? 'Minimum mÃ¥neder' : 'Minimum months'}</label>
                            <select
                                id="list-min-months"
                                value={filters.months}
                                onChange={(e) => handleFilterChange({ months: Number(e.target.value) })}
                                className={CONTROL_INPUT_CLASS}
                            >
                                {Array.from({ length: 24 }, (_, index) => index + 1).map((value) => (
                                    <option key={value} value={value}>
                                        {value} {language === 'da' ? (value === 1 ? 'mÃ¥ned' : 'mÃ¥neder') : `month${value === 1 ? '' : 's'}`}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                {language === 'da'
                                    ? 'NÃ¥r mÃ¥neder vÃ¦lges, udfyldes slutdatoen automatisk fra startdatoen.'
                                    : 'When months are selected, the end date is calculated automatically from the start date.'}
                            </p>
                        </div>
                    )}
                    <p className="mb-4 text-[0.88rem] leading-6 text-gray-500">{t('listDateFilterNote')}</p>
                    {availabilityError && <p className="mb-4 text-[0.95rem] text-red-600">{availabilityError}</p>}
                    <div className="mb-5">
                        <label htmlFor="searchTerm" className={CONTROL_LABEL_CLASS}>{t('listSearchLabel')}</label>
                        <input type="text" id="searchTerm" value={filters.searchTerm} onChange={e => handleFilterChange({ searchTerm: e.target.value })} className={CONTROL_INPUT_CLASS} placeholder="" />
                    </div>
                    <div className="mb-5">
                        <label htmlFor="maxPrice" className={CONTROL_LABEL_CLASS}>{t('listMaxPrice')}: DKK {filters.maxPrice.toLocaleString('da-DK')}</label>
                        <input type="range" id="maxPrice" value={filters.maxPrice} onChange={e => handleFilterChange({ maxPrice: Number(e.target.value) })} min="0" max={priceSliderMax} step="100" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600" />
                    </div>
                    <div className="mb-5">
                        <label htmlFor="capacity" className={CONTROL_LABEL_CLASS}>{t('listSleeps')}</label>
                        <select id="capacity" value={filters.capacity} onChange={e => handleFilterChange({ capacity: Number(e.target.value) })} className={CONTROL_SELECT_CLASS}>
                            <option value="0">{t('listAny')}</option><option value="2">{t('listPeople2')}</option><option value="3">{t('listPeople3')}</option><option value="4">{t('listPeople4')}</option><option value="5">{t('listPeople5')}</option>
                        </select>
                    </div>
                    <div className="mb-5 rounded-xl border border-gray-200 bg-white px-4 py-3">
                        <label htmlFor="festivalOnly" className="flex items-center gap-3 text-sm font-medium text-gray-700">
                            <input
                                id="festivalOnly"
                                type="checkbox"
                                checked={filters.festivalOnly}
                                onChange={e => handleFilterChange({ festivalOnly: e.target.checked })}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            {t('listFestivalAvailable')}
                        </label>
                    </div>
                    <div className="mb-5">
                        <label className={CONTROL_LABEL_CLASS}>{t('listCaravanType')}</label>
                        <div className="space-y-3">
                            {typeFilterOptions.map(type => (
                                <div key={type} className="flex items-center gap-3 text-[0.98rem] text-gray-700">
                                    <input type="radio" id={`type${type}`} name="type" checked={filters.type === type} onChange={() => handleFilterChange({ type })} value={type} className="h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500" />
                                    <label htmlFor={`type${type}`}>{type}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mb-4">
                        <label htmlFor="sortBy" className={CONTROL_LABEL_CLASS}>{t('listSortBy')}</label>
                        <select
                            id="sortBy"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className={CONTROL_SELECT_CLASS}
                        >
                            <option value="price_asc">{t('listSortPriceAsc')}</option>
                            <option value="price_desc">{t('listSortPriceDesc')}</option>
                            <option value="rating_desc">{t('listSortRatingDesc')}</option>
                            <option value="rating_asc">{t('listSortRatingAsc')}</option>
                        </select>
                    </div>
                </aside>
                <main className="min-w-0">
                    {(isLoading || isAvailabilityLoading) && <p>{t('listLoading')}</p>}
                    {error && <p className="text-red-500">{error}</p>}
                    {!isLoading && !error && (
                        <>
                            <h1 className="mb-5 text-[2.15rem] font-bold tracking-tight text-gray-800">{t('listAvailable')} ({filteredCaravans.length})</h1>
                            {filteredCaravans.length > 0 ? (
                                <>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                                        {filteredCaravans.slice(0, visibleCount).map(caravan => <CaravanItem key={caravan.caravan_id} caravan={caravan} onNavigate={onNavigate} selectedStartDate={filters.startDate} selectedEndDate={filters.endDate} />)}
                                    </div>
                                    {visibleCount < filteredCaravans.length && (
                                        <div ref={loadMoreRef} className="h-12 mt-5 flex items-center justify-center text-sm text-gray-500">
                                            Scroll to load more caravans...
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-10 bg-gray-50 rounded-lg">
                                    <h3 className="text-xl font-semibold">{t('listNoFound')}</h3>
                                    <p className="text-gray-600 mt-2">{t('listAdjust')}</p>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

const CaravanDetailPage = ({ caravan_id, onNavigate, user, initialStartDate = '', initialEndDate = '' }) => {
    const { t } = useContext(LanguageContext);
    const [caravan, setCaravan] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mainImage, setMainImage] = useState(null);
    const [detailSliderOpen, setDetailSliderOpen] = useState(false);
    const [detailSliderIndex, setDetailSliderIndex] = useState(0);
    const [reviews, setReviews] = useState([]);
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewComment, setReviewComment] = useState('');
    const [reviewError, setReviewError] = useState('');
    const [reviewSuccess, setReviewSuccess] = useState('');
    const [reviewEligibility, setReviewEligibility] = useState({
        canReview: false,
        hasReviewed: false,
        hasBooking: false,
        message: ''
    });
    const [reviewEligibilityLoading, setReviewEligibilityLoading] = useState(false);

    useEffect(() => {
        const fetchCaravanDetails = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`${API_BASE_URL}/api/caravans/${caravan_id}`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch caravan details. Status: ${response.status}`);
                }
                const data = await response.json();
                setCaravan(data);
                setMainImage(getCaravanMainImage(data));
            } catch (err) {
                setError(err.message);
                console.error("Fetch error:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCaravanDetails();
        window.scrollTo(0, 0);
    }, [caravan_id]);

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/reviews/caravan/${caravan_id}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch reviews.');
                }
                const data = await response.json();
                setReviews(data);
            } catch (err) {
                console.error("Review fetch error:", err);
            }
        };
        fetchReviews();
    }, [caravan_id]);

    useEffect(() => {
        let ignore = false;
        const fetchEligibility = async () => {
            if (!user?.userId) {
                setReviewEligibility({
                    canReview: false,
                    hasReviewed: false,
                    hasBooking: false,
                    message: t('detailReviewLogin')
                });
                return;
            }

            setReviewEligibilityLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/reviews/eligibility?caravanId=${caravan_id}&renterId=${user.userId}`);
                if (!response.ok) {
                    throw new Error('Could not verify review eligibility.');
                }
                const data = await response.json();
                if (!ignore) {
                    setReviewEligibility({
                        canReview: Boolean(data.canReview),
                        hasReviewed: Boolean(data.hasReviewed),
                        hasBooking: Boolean(data.hasBooking),
                        message: data.message || ''
                    });
                }
            } catch (err) {
                if (!ignore) {
                    setReviewEligibility({
                        canReview: false,
                        hasReviewed: false,
                        hasBooking: false,
                        message: err.message || 'Could not verify review eligibility.'
                    });
                }
            } finally {
                if (!ignore) {
                    setReviewEligibilityLoading(false);
                }
            }
        };

        fetchEligibility();
        return () => { ignore = true; };
    }, [caravan_id, user?.userId, t]);

    useEffect(() => {
        if (!detailSliderOpen) return;

        const imageCount = Math.max(getCaravanImages(caravan || {}).length, 1);
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setDetailSliderOpen(false);
            } else if (event.key === 'ArrowLeft') {
                setDetailSliderIndex((prev) => (prev - 1 + imageCount) % imageCount);
            } else if (event.key === 'ArrowRight') {
                setDetailSliderIndex((prev) => (prev + 1) % imageCount);
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [detailSliderOpen, caravan]);

    const handleRequestToBook = () => {
        onNavigate({ page: 'booking', caravanId: caravan.caravan_id, startDate: initialStartDate, endDate: initialEndDate });
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        setReviewError('');
        setReviewSuccess('');
        if (!user) {
            setReviewError(t('detailReviewLogin'));
            return;
        }
        if (!reviewEligibility.canReview) {
            setReviewError(reviewEligibility.message || 'You are not eligible to review this caravan yet.');
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/reviews`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    caravanId: caravan.caravan_id,
                    renterId: user.userId,
                    rating: Number(reviewRating),
                    comment: reviewComment
                }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to submit review.');
            }
            setReviewSuccess(t('detailReviewSuccess'));
            setReviewComment('');
            setReviewEligibility(prev => ({
                ...prev,
                canReview: false,
                hasReviewed: true,
                message: 'You have already reviewed this caravan.'
            }));
            const refreshed = await fetch(`${API_BASE_URL}/api/reviews/caravan/${caravan_id}`);
            if (refreshed.ok) {
                setReviews(await refreshed.json());
            }
            const caravanRes = await fetch(`${API_BASE_URL}/api/caravans/${caravan_id}`);
            if (caravanRes.ok) {
                const updatedCaravan = await caravanRes.json();
                setCaravan(updatedCaravan);
                if (!mainImage) {
                    setMainImage(getCaravanMainImage(updatedCaravan));
                }
            }
        } catch (err) {
            setReviewError(err.message);
        }
    };

    if (isLoading) return <div className="text-center py-20">Loading caravan details...</div>;
    if (error) return <div className="text-center py-20 text-red-500">Error: {error}</div>;
    if (!caravan) return <div className="text-center py-20">Caravan not found.</div>;
    const caravanImages = getCaravanImages(caravan);
    const detailSliderImages = caravanImages.length > 0 ? caravanImages : [PLACEHOLDER_IMG];
    const isDetailPetAllowed = isTruthyValue(caravan?.pets_allowed ?? caravan?.petsAllowed);
    const isDetailNoSmoking = isTruthyValue(caravan?.no_smoking ?? caravan?.noSmoking);
    const detailRating = getCaravanDisplayRating(caravan);
    const detailSittingCapacity = getCaravanSittingCapacity(caravan);
    const detailBedCount = getCaravanBedCount(caravan);

    const openDetailSlider = (startIndex = 0) => {
        const safeIndex = Math.max(0, Math.min(startIndex, detailSliderImages.length - 1));
        setDetailSliderIndex(safeIndex);
        setDetailSliderOpen(true);
    };

    const closeDetailSlider = () => {
        setDetailSliderOpen(false);
    };

    const showPrevDetailImage = () => {
        setDetailSliderIndex((prev) => {
            if (!detailSliderImages.length) return 0;
            return (prev - 1 + detailSliderImages.length) % detailSliderImages.length;
        });
    };

    const showNextDetailImage = () => {
        setDetailSliderIndex((prev) => {
            if (!detailSliderImages.length) return 0;
            return (prev + 1) % detailSliderImages.length;
        });
    };

    return (
        <div className="container mx-auto px-4 py-8">
            {detailSliderOpen && (
                <div className="fixed inset-0 z-[10000] bg-black/90 px-4 py-6">
                    <div className="mx-auto max-w-6xl h-full flex flex-col">
                        <div className="flex items-center justify-between text-white mb-3">
                            <h3 className="text-lg font-semibold truncate pr-4">{caravan?.name || 'Caravan Images'}</h3>
                            <button
                                type="button"
                                onClick={closeDetailSlider}
                                className="bg-white/20 hover:bg-white/30 rounded-md px-3 py-1 text-sm"
                            >
                                Close
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 flex items-center justify-center gap-3">
                            <button
                                type="button"
                                onClick={showPrevDetailImage}
                                className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                                aria-label="Previous image"
                            >
                                &lt;
                            </button>
                            <img
                                src={detailSliderImages[detailSliderIndex] || PLACEHOLDER_IMG}
                                alt={`${caravan?.name || 'Caravan'} ${detailSliderIndex + 1}`}
                                className="max-h-full max-w-full object-contain rounded-lg border border-white/20"
                                onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                            />
                            <button
                                type="button"
                                onClick={showNextDetailImage}
                                className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                                aria-label="Next image"
                            >
                                &gt;
                            </button>
                        </div>

                        <div className="mt-3">
                            <p className="text-xs text-white/80 mb-2">
                                {detailSliderIndex + 1} / {detailSliderImages.length}
                            </p>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {detailSliderImages.map((img, idx) => (
                                    <button
                                        key={`${img}-${idx}`}
                                        type="button"
                                        onClick={() => setDetailSliderIndex(idx)}
                                        className={`shrink-0 border rounded-md ${idx === detailSliderIndex ? 'border-green-400' : 'border-white/30'}`}
                                    >
                                        <img
                                            src={img}
                                            alt={`Thumb ${idx + 1}`}
                                            className="w-20 h-14 object-cover rounded-md"
                                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="mb-8">
                <button
                    type="button"
                    onClick={() => {
                        const currentImage = mainImage || caravanImages[0] || PLACEHOLDER_IMG;
                        const idx = detailSliderImages.findIndex((img) => img === currentImage);
                        openDetailSlider(idx >= 0 ? idx : 0);
                    }}
                    className="w-full"
                >
                    <img
                        src={mainImage || caravanImages[0] || PLACEHOLDER_IMG}
                        alt={`Main view ${caravan.name}`}
                        className="w-full h-[300px] md:h-[500px] object-cover rounded-lg shadow-lg mb-4"
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                    />
                </button>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {caravanImages.map((img, idx) => (
                        img && <button key={idx} type="button" onClick={() => { setMainImage(img); openDetailSlider(idx); }}>
                            <img src={img} alt={`Thumbnail ${idx + 1}`} className={`w-full h-32 object-cover rounded-md shadow-sm cursor-pointer transition-transform duration-200 ${mainImage === img ? 'ring-4 ring-green-500 scale-95' : 'hover:scale-105'}`} />
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <h1 className="text-4xl font-bold text-gray-900">{caravan.name}</h1>
                    <p className="text-lg text-gray-600 mt-1">{caravan.location}</p>
                    {(isDetailPetAllowed || isDetailNoSmoking) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {isDetailPetAllowed && (
                                <span className="inline-flex items-center rounded-full border border-green-200 bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">
                                    {t('cardPetFriendly')}
                                </span>
                            )}
                            {isDetailNoSmoking && (
                                <span className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700">
                                    {t('cardNoSmoking')}
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center text-lg mt-4 sm:mt-0 flex-shrink-0">
                        <span className="text-yellow-500 text-2xl">{'\u2605'}</span>
                        <span className="ml-1 font-bold">{detailRating.toFixed(1)}</span>
                        <span className="ml-2 text-gray-500">({caravan.reviewCount} reviews)</span>
                    </div>
                    <CaravanWeightSummary caravan={caravan} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" />
                    <div className="border-t border-b my-6 py-4 flex items-center space-x-8 text-center">
                        <div><p className="font-bold text-xl">{caravan.typeName}</p><p className="text-sm text-gray-500">{t('detailType')}</p></div>
                        <div className="flex flex-col items-center">
                            <SeatIcon className="h-6 w-6 text-gray-600" />
                            <p className="font-bold text-xl">{detailSittingCapacity || 1}</p>
                            <p className="text-sm text-gray-500">{t('detailSleeps')}</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <BedIcon className="h-6 w-6 text-gray-600" />
                            <p className="font-bold text-xl">{detailBedCount}</p>
                            <p className="text-sm text-gray-500">{t('detailBeds')}</p>
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold mb-2">{t('detailAbout')}</h2>
                    <p className="text-gray-700 leading-relaxed">{caravan.description}</p>
                    <h2 className="text-2xl font-bold mt-8 mb-4">{t('detailAmenities')}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {caravan.amenities.map(amenity => (
                            <div key={amenity} className="bg-gray-100 p-3 rounded-md flex items-center">
                                <span className="text-green-600 font-bold">{'\u2713'}</span><span className="ml-2 text-gray-800">{amenity}</span>
                            </div>
                        ))}
                    </div>

                    <h2 className="text-2xl font-bold mt-8 mb-4">{t('detailReviews')}</h2>
                    <div className="space-y-4">
                        {reviews.length === 0 && <p className="text-gray-500">No reviews yet.</p>}
                        {reviews.map((review) => (
                            <div key={review.reviewId} className="bg-white border rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <p className="font-semibold text-gray-800">{review.renterName}</p>
                                    <p className="text-sm text-gray-500">{new Date(review.createdAt).toLocaleDateString()}</p>
                                </div>
                                <p className="text-yellow-600 text-sm mt-1">{'\u2605'.repeat(review.rating)}{'\u2606'.repeat(5 - review.rating)}</p>
                                {review.comment && <p className="mt-2 text-gray-700">{review.comment}</p>}
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 bg-gray-50 border rounded-lg p-4">
                        <h3 className="text-xl font-bold mb-3">{t('detailWriteReview')}</h3>
                        {reviewError && <p className="text-sm text-red-600 mb-2">{reviewError}</p>}
                        {reviewSuccess && <p className="text-sm text-green-700 mb-2">{reviewSuccess}</p>}
                        {!user && (
                            <p className="text-sm text-gray-700">{t('detailReviewLogin')}</p>
                        )}
                        {!!user && reviewEligibilityLoading && (
                            <p className="text-sm text-gray-700">Checking review eligibility...</p>
                        )}
                        {!!user && !reviewEligibilityLoading && !reviewEligibility.canReview && (
                            <p className="text-sm text-amber-700">{reviewEligibility.message || 'Review is available only after your booking is completed.'}</p>
                        )}
                        {!!user && !reviewEligibilityLoading && reviewEligibility.canReview && (
                            <form className="space-y-3" onSubmit={handleSubmitReview}>
                                <div>
                                    <label className={CONTROL_LABEL_CLASS}>{t('detailYourRating')}</label>
                                    <select value={reviewRating} onChange={(e) => setReviewRating(Number(e.target.value))} className={CONTROL_SELECT_CLASS}>
                                        {[5, 4, 3, 2, 1].map((value) => (
                                            <option key={value} value={value}>{value} Star{value > 1 ? 's' : ''}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={CONTROL_LABEL_CLASS}>{t('detailYourComment')}</label>
                                    <textarea
                                        value={reviewComment}
                                        onChange={(e) => setReviewComment(e.target.value)}
                                        rows="3"
                                        maxLength={1000}
                                        className={CONTROL_TEXTAREA_CLASS}
                                        placeholder=""
                                    />
                                </div>
                                <button type="submit" className={PRIMARY_BUTTON_CLASS}>
                                    {t('detailSubmitReview')}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
                <aside className="lg:col-span-1">
                    <div className="sticky top-24 bg-white p-6 rounded-lg shadow-xl border">
                        <p className="text-2xl font-bold mb-4">DKK {Number(caravan.price_per_day).toLocaleString('da-DK')} <span className="text-base font-normal text-gray-600">/ day</span></p>
                        <div className="space-y-4">
                            <div><label className="block text-sm font-medium text-gray-700">{t('detailCheckIn')}</label><input type="date" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2" /></div>
                            <div><label className="block text-sm font-medium text-gray-700">{t('detailCheckOut')}</label><input type="date" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2" /></div>
                        </div>
                        <button onClick={handleRequestToBook} className="w-full mt-6 bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors duration-300">
                            {t('detailRequest')}
                        </button>
                        <p className="text-xs text-center text-gray-500 mt-3">{t('detailNoCharge')}</p>
                    </div>
                </aside>
            </div>
        </div>
    );
};

const AuthPageWrapper = ({ title, children, maxWidthClass = 'max-w-md', showLogo = true }) => (
    <div className="flex items-center justify-center bg-stone-50 px-4 py-16 sm:px-6 lg:px-8">
        <div className={`${maxWidthClass} w-full space-y-10`}>
            <div>
                {showLogo && (
                    <div className="flex justify-center">
                        <img
                            src={PORTAL_LOGO_SRC}
                            alt="Udlejningsvogn.dk"
                            data-logo-index="0"
                            className="h-24 sm:h-28 w-auto max-w-[340px] object-contain"
                            onError={handlePortalLogoError}
                        />
                    </div>
                )}
                <h2 className="mt-6 text-center text-[2.35rem] font-extrabold tracking-tight text-gray-900">{title}</h2>
            </div>
            <div className="rounded-[1.5rem] bg-white p-10 shadow-lg">
                {children}
            </div>
        </div>
    </div>
);

const TermsPage = ({ onNavigate }) => {
    const { t, language } = useContext(LanguageContext);
    const [termsDocuments, setTermsDocuments] = useState([]);
    const [termsLoading, setTermsLoading] = useState(true);
    const [termsError, setTermsError] = useState('');
    const fallbackSections = language === 'da'
        ? [
            {
                title: '1. Booking og betaling',
                content: 'Bookinger er f\u00f8rst bekr\u00e6ftet efter betaling af det kr\u00e6vede depositum eller det fulde bel\u00f8b.'
            },
            {
                title: '2. Lejeperiode',
                content: 'Lejeperioden starter ved afhentningstid p\u00e5 startdatoen og slutter ved afleveringstid p\u00e5 slutdatoen.'
            },
            {
                title: '3. Priser',
                content: 'Pris pr. dag og pris pr. uge g\u00e6lder som vist ved checkout.'
            },
            {
                title: '4. Depositum',
                content: 'Et refunderbart sikkerhedsdepositum kan v\u00e6re p\u00e5kr\u00e6vet.'
            },
            {
                title: '5. Ansvar og brug',
                content: 'Kunden er ansvarlig for korrekt brug i lejeperioden.'
            },
            {
                title: '6. Aflysninger',
                content: 'Aflysninger og \u00e6ndringer afh\u00e6nger af den g\u00e6ldende refusionspolitik.'
            }
        ]
        : [
            {
                title: '1. Booking and payment',
                content: 'Bookings are confirmed only after required payment.'
            },
            {
                title: '2. Rental period',
                content: 'The rental starts at pickup time and ends at return time.'
            },
            {
                title: '3. Pricing',
                content: 'Per-day and per-week prices are applied as shown at checkout.'
            },
            {
                title: '4. Security deposit',
                content: 'A refundable security deposit may be required.'
            },
            {
                title: '5. Liability and use',
                content: 'Customers are responsible for proper use during the rental period.'
            },
            {
                title: '6. Cancellations',
                content: 'Cancellations and changes are subject to the active refund policy.'
            }
        ];
    const termsCopy = language === 'da'
        ? {
            intro: 'Her kan du se de senest uploadede PDF-vilk\u00e5r for kunder og campingejere.',
            currentVersion: 'Aktuel version',
            uploadedOn: 'Uploadet',
            fileSize: 'Filst\u00f8rrelse',
            openPdf: '\u00c5bn PDF',
            downloadPdf: 'Download PDF',
            previewHeading: 'PDF-forh\u00e5ndsvisning',
            noDocument: 'Der er endnu ikke uploadet nogen PDF for denne m\u00e5lgruppe.',
            noDocuments: 'Der er endnu ikke uploadet nogen vilk\u00e5rs-PDFer.',
            loading: 'Indl\u00e6ser vilk\u00e5r...',
            failed: 'Kunne ikke indl\u00e6se vilk\u00e5r og betingelser.',
            browserFallback: 'Hvis PDF-forh\u00e5ndsvisningen ikke virker, kan du \u00e5bne eller downloade filen via knapperne ovenfor.',
            customerDescription: 'G\u00e6ldende lejevilk\u00e5r for kunder.',
            ownerDescription: 'G\u00e6ldende vilk\u00e5r for campingejere, der udlejer.'
        }
        : {
            intro: 'View the latest uploaded terms PDF for customers and caravan owners.',
            currentVersion: 'Current version',
            uploadedOn: 'Uploaded on',
            fileSize: 'File size',
            openPdf: 'Open PDF',
            downloadPdf: 'Download PDF',
            previewHeading: 'PDF preview',
            noDocument: 'No PDF has been uploaded for this audience yet.',
            noDocuments: 'No terms PDFs have been uploaded yet.',
            loading: 'Loading terms and conditions...',
            failed: 'Could not load terms and conditions.',
            browserFallback: 'If the PDF preview does not load, open or download the file using the buttons above.',
            customerDescription: 'Current rental terms for customers.',
            ownerDescription: 'Current terms for caravan owners who list rentals.'
        };
    const documentsByAudience = useMemo(() => groupTermsDocumentsByAudience(termsDocuments), [termsDocuments]);

    useEffect(() => {
        let isCancelled = false;

        const loadTermsDocuments = async () => {
            setTermsLoading(true);
            setTermsError('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/terms-documents?currentOnly=true`);
                if (!response.ok) {
                    throw new Error(await readApiErrorMessage(response, 'Failed to load terms PDFs.'));
                }

                const data = await response.json();
                if (!isCancelled) {
                    setTermsDocuments(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                if (!isCancelled) {
                    setTermsError(err.message || 'Failed to load terms PDFs.');
                }
            } finally {
                if (!isCancelled) {
                    setTermsLoading(false);
                }
            }
        };

        loadTermsDocuments();
        return () => {
            isCancelled = true;
        };
    }, []);

    return (
        <div className="bg-stone-50 py-12">
            <div className="container mx-auto px-6 max-w-5xl">
                <div className="bg-white shadow-lg rounded-xl p-8 md:p-10">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>{t('termsTitle')}</h1>
                            <p className="text-gray-600 mt-2">{t('termsEffective')}</p>
                        </div>
                        <button onClick={() => onNavigate({ page: 'home' })} className="text-green-700 font-semibold hover:text-green-900 transition-colors">
                            {t('termsBack')}
                        </button>
                    </div>

                    <div className="mt-8 space-y-6">
                        <p className="text-gray-600">{termsCopy.intro}</p>

                        {termsLoading && <p className="text-gray-600">{termsCopy.loading}</p>}
                        {!termsLoading && termsError && <p className="text-red-600">{termsCopy.failed} {termsError}</p>}

                        {TERMS_AUDIENCES.map((audience) => {
                            const document = documentsByAudience[audience][0] || null;
                            const documentUrl = document ? resolveApiAssetUrl(document.fileUrl) : null;
                            const description = audience === 'owner'
                                ? termsCopy.ownerDescription
                                : termsCopy.customerDescription;

                            return (
                                <div key={audience} className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div>
                                            <h2 className="text-xl font-semibold text-gray-900">{getTermsAudienceLabel(audience, language)}</h2>
                                            <p className="text-sm text-gray-600 mt-1">{description}</p>
                                        </div>
                                        {documentUrl && (
                                            <div className="flex gap-3">
                                                <a
                                                    href={documentUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                                >
                                                    {termsCopy.openPdf}
                                                </a>
                                                <a
                                                    href={documentUrl}
                                                    download={document.originalFileName || undefined}
                                                    className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-200"
                                                >
                                                    {termsCopy.downloadPdf}
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {document ? (
                                        <div className="mt-4 space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                                <div className="bg-stone-50 rounded-lg p-3">
                                                    <p className="text-gray-500">{termsCopy.currentVersion}</p>
                                                    <p className="font-semibold text-gray-900">v{document.versionNumber}</p>
                                                </div>
                                                <div className="bg-stone-50 rounded-lg p-3">
                                                    <p className="text-gray-500">{termsCopy.uploadedOn}</p>
                                                    <p className="font-semibold text-gray-900">{formatTermsDateTime(document.createdAt, language)}</p>
                                                </div>
                                                <div className="bg-stone-50 rounded-lg p-3">
                                                    <p className="text-gray-500">{termsCopy.fileSize}</p>
                                                    <p className="font-semibold text-gray-900">{formatFileSizeLabel(document.fileSizeBytes)}</p>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-sm font-medium text-gray-700 mb-2">{termsCopy.previewHeading}</p>
                                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                                                    <iframe
                                                        title={`${getTermsAudienceLabel(audience, language)} Terms PDF`}
                                                        src={documentUrl}
                                                        className="w-full h-[720px] bg-white"
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2">{termsCopy.browserFallback}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 mt-4">{termsCopy.noDocument}</p>
                                    )}
                                </div>
                            );
                        })}

                        {!termsLoading && termsDocuments.length === 0 && (
                            <div className="border border-dashed border-gray-300 rounded-xl p-5 bg-stone-50">
                                <p className="text-gray-700 font-medium">{termsCopy.noDocuments}</p>
                                <div className="mt-4 space-y-6">
                                    {fallbackSections.map((section) => (
                                        <div key={section.title}>
                                            <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                                            <p className="mt-1 text-gray-700 leading-relaxed">{section.content}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AboutPage = ({ onNavigate, fallbackCaravanCount = 0 }) => {
    const { t, language } = useContext(LanguageContext);
    const [stats, setStats] = useState({
        tripsCompleted: 43,
        registeredVehicles: fallbackCaravanCount,
        averageRating: BASE_CARAVAN_RATING
    });

    useEffect(() => {
        let ignore = false;

        const loadStats = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/caravans/stats/summary`);
                if (!response.ok) {
                    throw new Error('Could not load marketplace stats.');
                }

                const data = await response.json();
                if (ignore) return;

                setStats({
                    tripsCompleted: Number(data?.tripsCompleted || 43),
                    registeredVehicles: Number(data?.registeredVehicles || fallbackCaravanCount || 0),
                    averageRating: Number(data?.averageRating || BASE_CARAVAN_RATING)
                });
            } catch (err) {
                if (!ignore) {
                    setStats((prev) => ({
                        ...prev,
                        registeredVehicles: fallbackCaravanCount || prev.registeredVehicles || 0
                    }));
                }
            }
        };

        loadStats();
        return () => {
            ignore = true;
        };
    }, [fallbackCaravanCount]);

    const numberLocale = language === 'da' ? 'da-DK' : 'en-US';
    const highlights = [
        { value: stats.tripsCompleted.toLocaleString(numberLocale), label: t('aboutTrips') },
        { value: stats.registeredVehicles.toLocaleString(numberLocale), label: t('aboutCaravans') },
        { value: '25', label: t('aboutCities') },
        { value: stats.averageRating.toLocaleString(numberLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), label: t('aboutRating') }
    ];
    const values = [
        { title: t('aboutTrust'), text: t('aboutTrustText') },
        { title: t('aboutPricing'), text: t('aboutPricingText') },
        { title: t('aboutSupport'), text: t('aboutSupportText') }
    ];

    return (
        <div className="bg-stone-50 py-12">
            <div className="container mx-auto px-6 max-w-5xl">
                <div className="bg-white shadow-lg rounded-xl p-8 md:p-10">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>{t('aboutTitle')}</h1>
                            <p className="text-gray-600 mt-2">{t('aboutSubtitle')}</p>
                        </div>
                        <button onClick={() => onNavigate({ page: 'home' })} className="text-green-700 font-semibold hover:text-green-900 transition-colors">
                            {t('aboutBack')}
                        </button>
                    </div>

                    <section className="mt-8">
                        <h2 className="text-2xl font-bold text-gray-900">{t('aboutStory')}</h2>
                        <p className="mt-3 text-gray-700 leading-relaxed">{t('aboutStoryP1')}</p>
                        <p className="mt-3 text-gray-700 leading-relaxed">{t('aboutStoryP2')}</p>
                    </section>

                    <section className="mt-10">
                        <h2 className="text-2xl font-bold text-gray-900">{t('aboutAtGlance')}</h2>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {highlights.map((item) => (
                                <div key={item.label} className="rounded-lg bg-stone-100 p-4 text-gray-800">
                                    <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                                    <p className="mt-1 text-sm font-medium">{item.label}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="mt-10">
                        <h2 className="text-2xl font-bold text-gray-900">{t('aboutBelief')}</h2>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            {values.map((value) => (
                                <div key={value.title} className="rounded-lg border border-stone-200 p-5">
                                    <h3 className="text-lg font-semibold text-gray-900">{value.title}</h3>
                                    <p className="mt-2 text-gray-700 text-sm leading-relaxed">{value.text}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="mt-10 rounded-lg bg-green-50 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t('aboutReady')}</h2>
                            <p className="mt-1 text-gray-700">{t('aboutReadyText')}</p>
                        </div>
                        <button
                            onClick={() => onNavigate({ page: 'list' })}
                            className="bg-green-700 text-white px-5 py-2 rounded-md hover:bg-green-800 transition-colors font-semibold"
                        >
                            {t('aboutBrowse')}
                        </button>
                    </section>
                </div>
            </div>
        </div>
    );
};
const SuggestedCaravanDetailModal = ({ caravanId, startDate, endDate, isOpen, onClose, onBook }) => {
    const { t } = useContext(LanguageContext);
    const [caravan, setCaravan] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeImage, setActiveImage] = useState('');

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen || !caravanId) {
            setCaravan(null);
            setReviews([]);
            setError('');
            setActiveImage('');
            return;
        }

        let ignore = false;
        const loadDetails = async () => {
            setIsLoading(true);
            setError('');
            try {
                const [caravanResponse, reviewsResponse] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/caravans/${caravanId}`),
                    fetch(`${API_BASE_URL}/api/reviews/caravan/${caravanId}`)
                ]);

                if (!caravanResponse.ok) {
                    throw new Error(t('bookingFestivalDetailError'));
                }

                const caravanData = await caravanResponse.json();
                const reviewsData = reviewsResponse.ok ? await reviewsResponse.json() : [];
                if (ignore) return;

                setCaravan(caravanData);
                setReviews(Array.isArray(reviewsData) ? reviewsData : []);
                setActiveImage(getCaravanMainImage(caravanData));
            } catch (err) {
                if (ignore) return;
                setCaravan(null);
                setReviews([]);
                setError(err?.message || t('bookingFestivalDetailError'));
            } finally {
                if (!ignore) {
                    setIsLoading(false);
                }
            }
        };

        loadDetails();
        return () => {
            ignore = true;
        };
    }, [caravanId, isOpen, t]);

    if (!isOpen) {
        return null;
    }

    const caravanImages = getCaravanImages(caravan || {});
    const displayImages = caravanImages.length > 0 ? caravanImages : [PLACEHOLDER_IMG];
    const currentImage = activeImage || displayImages[0] || PLACEHOLDER_IMG;
    const isPetAllowed = isTruthyValue(caravan?.pets_allowed ?? caravan?.petsAllowed);
    const isNoSmoking = isTruthyValue(caravan?.no_smoking ?? caravan?.noSmoking);
    const isDoorstepAvailable = isTruthyValue(caravan?.doorstep_available ?? caravan?.doorstepAvailable);
    const festivalAvailable = isFestivalAvailable(caravan);
    const rating = getCaravanDisplayRating(caravan);
    const reviewCount = Number(caravan?.reviewCount || reviews.length || 0);
    const dailyPrice = Number(caravan?.price_per_day || 0);
    const weeklyPrice = Number(caravan?.price_per_week || (dailyPrice * 7));
    const depositAmount = Number(caravan?.deposit_amount || caravan?.depositAmount || 0);
    const sittingCapacity = getCaravanSittingCapacity(caravan);
    const bedCount = getCaravanBedCount(caravan);
    const amenities = Array.isArray(caravan?.amenities) ? caravan.amenities : [];

    return (
        <div className="fixed inset-0 z-[10020] bg-black/60 px-4 py-6" onClick={onClose}>
            <div className="mx-auto flex h-full max-w-6xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                    <div className="min-w-0">
                        <h3 className="truncate text-xl font-semibold text-gray-900">{caravan?.name || t('bookingFestivalSuggestionsTitle')}</h3>
                        {caravan?.location && <p className="truncate text-sm text-gray-600">{caravan.location}</p>}
                    </div>
                    <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
                        {t('commonClose')}
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
                    {isLoading && <p className="py-10 text-center text-sm text-gray-600">{t('bookingFestivalDetailLoading')}</p>}
                    {!isLoading && error && <p className="py-10 text-center text-sm text-red-600">{error}</p>}
                    {!isLoading && !error && caravan && (
                        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.7fr)]">
                            <div>
                                <div className="overflow-hidden rounded-[24px] border border-gray-200 bg-gray-100">
                                    <img
                                        src={currentImage}
                                        alt={caravan.name}
                                        className="h-[260px] w-full object-cover md:h-[420px]"
                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                    />
                                </div>
                                <div className="mt-3 grid grid-cols-4 gap-3 md:grid-cols-6">
                                    {displayImages.slice(0, 12).map((img, idx) => (
                                        <button
                                            key={`${img}-${idx}`}
                                            type="button"
                                            onClick={() => setActiveImage(img)}
                                            className={`overflow-hidden rounded-xl border ${currentImage === img ? 'border-green-600 ring-2 ring-green-200' : 'border-gray-200'}`}
                                        >
                                            <img
                                                src={img}
                                                alt={`${caravan.name} ${idx + 1}`}
                                                className="h-16 w-full object-cover"
                                                onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                            />
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-5 flex flex-wrap gap-2">
                                    {festivalAvailable && <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">{t('cardFestivalAvailable')}</span>}
                                    {!festivalAvailable && <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-700">{t('cardFestivalUnavailable')}</span>}
                                    {isPetAllowed && <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">{t('cardPetFriendly')}</span>}
                                    {isNoSmoking && <span className="rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700">{t('cardNoSmoking')}</span>}
                                    {isDoorstepAvailable && <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">{t('cardDoorstepAvailable')}</span>}
                                </div>

                                <div className="mt-5 flex flex-wrap items-center gap-5 border-y border-gray-200 py-4 text-center">
                                    <div>
                                        <p className="text-xl font-bold text-gray-900">{String(caravan?.typeName || caravan?.type_name || caravan?.vehicle_category || '-')}</p>
                                        <p className="text-sm text-gray-500">{t('detailType')}</p>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <SeatIcon className="h-6 w-6 text-gray-600" />
                                        <p className="text-xl font-bold text-gray-900">{sittingCapacity || 1}</p>
                                        <p className="text-sm text-gray-500">{t('detailSleeps')}</p>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <BedIcon className="h-6 w-6 text-gray-600" />
                                        <p className="text-xl font-bold text-gray-900">{bedCount}</p>
                                        <p className="text-sm text-gray-500">{t('detailBeds')}</p>
                                    </div>
                                    <div>
                                        <p className="text-xl font-bold text-gray-900">{rating.toFixed(1)}</p>
                                        <p className="text-sm text-gray-500">{reviewCount} {t('detailReviews')}</p>
                                    </div>
                                </div>
                                <CaravanWeightSummary caravan={caravan} className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2" />

                                <section className="mt-5">
                                    <h4 className="text-xl font-bold text-gray-900">{t('detailAbout')}</h4>
                                    <p className="mt-2 whitespace-pre-line text-gray-700">{caravan.description || '-'}</p>
                                </section>

                                <section className="mt-6">
                                    <h4 className="text-xl font-bold text-gray-900">{t('detailAmenities')}</h4>
                                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                                        {amenities.length > 0 ? amenities.map((amenity) => (
                                            <div key={String(amenity)} className="rounded-xl bg-gray-100 px-3 py-2 text-sm text-gray-800">
                                                {amenity}
                                            </div>
                                        )) : (
                                            <p className="text-sm text-gray-500">-</p>
                                        )}
                                    </div>
                                </section>

                                <section className="mt-6">
                                    <h4 className="text-xl font-bold text-gray-900">{t('detailReviews')}</h4>
                                    <div className="mt-3 space-y-3">
                                        {reviews.length === 0 && <p className="text-sm text-gray-500">{t('bookingFestivalNoReviews')}</p>}
                                        {reviews.map((review) => (
                                            <div key={review.reviewId} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                                <div className="flex items-center justify-between gap-3">
                                                    <p className="font-semibold text-gray-900">{review.renterName}</p>
                                                    <p className="text-xs text-gray-500">{new Date(review.createdAt).toLocaleDateString()}</p>
                                                </div>
                                                <p className="mt-1 text-sm text-yellow-600">{'\u2605'.repeat(review.rating)}{'\u2606'.repeat(5 - review.rating)}</p>
                                                {review.comment && <p className="mt-2 text-sm text-gray-700">{review.comment}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            </div>

                            <aside>
                                <div className="xl:sticky xl:top-0 rounded-[24px] border border-gray-200 bg-gray-50 p-5">
                                    <p className="text-3xl font-bold text-gray-900">
                                        {formatInr(dailyPrice)}
                                        <span className="ml-2 text-sm font-normal text-gray-600">/ {t('bookingPerDay')}</span>
                                    </p>
                                    <p className="mt-2 text-sm text-gray-600">{formatInr(weeklyPrice)} / {t('bookingPerWeek')}</p>
                                    {depositAmount > 0 && <p className="mt-2 text-sm text-gray-600">Deposit: {formatInr(depositAmount)}</p>}
                                    <div className="mt-5 space-y-3 rounded-2xl bg-white p-4 shadow-sm">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('detailCheckIn')}</p>
                                            <p className="mt-1 text-sm font-medium text-gray-900">{startDate || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('detailCheckOut')}</p>
                                            <p className="mt-1 text-sm font-medium text-gray-900">{endDate || '-'}</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => onBook(caravan)}
                                        className={`${PRIMARY_BUTTON_CLASS} mt-5 w-full`}
                                    >
                                        {t('bookingFestivalSuggestionCta')}
                                    </button>
                                    <p className="mt-3 text-center text-xs text-gray-500">{t('detailNoCharge')}</p>
                                </div>
                            </aside>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const LoginPage = ({ onLogin, onNavigate, initialEmail = '', initialMessage = '' }) => {
    const { language } = useContext(LanguageContext);
    const [email, setEmail] = useState(initialEmail || 'user@example.com');
    const [password, setPassword] = useState('password');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(initialMessage || '');
    const deliverySignupPrompt = language === 'da'
        ? 'Vil du levere campingvogne?'
        : 'Want to deliver caravans?';
    const deliverySignupAction = language === 'da'
        ? 'Bliv leveringspartner'
        : 'Become a delivery partner';

    useEffect(() => {
        setEmail(initialEmail || 'user@example.com');
    }, [initialEmail]);

    useEffect(() => {
        setSuccess(initialMessage || '');
    }, [initialMessage]);

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                let errorMessage = 'Login failed';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData?.message || errorMessage;
                } catch {
                    // Ignore JSON parse issues and use fallback message.
                }
                throw new Error(errorMessage);
            }

            const userData = await response.json();
            onLogin(userData); // Pass user data to the main App component

        } catch (err) {
            setError(err?.message || 'Login failed');
            console.error("Login error:", err);
        }
    };

    return (
        <AuthPageWrapper title="Log in to your account">
            <form className="space-y-6" onSubmit={handleLoginSubmit}>
                {error && <p className="text-center text-red-500">{error}</p>}
                {success && <p className="text-center text-green-600">{success}</p>}
                <div className="rounded-md shadow-sm -space-y-px">
                    <div>
                        <label htmlFor="email-address" className="sr-only">Email address</label>
                        <input id="email-address" name="email" type="email" required
                            className="login-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                            placeholder={language === 'da' ? 'E-mailadresse' : 'Email address'}
                            value={email}
                            onChange={e => setEmail(e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="password-login" className="sr-only">Password</label>
                        <input id="password-login" name="password" type="password" required
                            className="login-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                            placeholder={language === 'da' ? 'Adgangskode' : 'Password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)} />
                    </div>
                </div>
                <div>
                    <button type="submit" className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                        Log In
                    </button>
                </div>
            </form>
            <p className="mt-4 text-center text-sm text-gray-600">
                <button onClick={() => onNavigate({ page: 'forgot-password', email: email.trim() })} className="font-medium text-green-600 hover:text-green-500">
                    Forgot password?
                </button>
            </p>
            <p className="mt-2 text-center text-sm text-gray-600">
                No account?{' '}
                <button onClick={() => onNavigate({ page: 'signup' })} className="font-medium text-green-600 hover:text-green-500">
                    Create one
                </button>
            </p>
            <p className="mt-2 text-center text-sm text-gray-600">
                {deliverySignupPrompt}{' '}
                <button onClick={() => onNavigate({ page: 'become-delivery-partner' })} className="font-medium text-green-600 hover:text-green-500">
                    {deliverySignupAction}
                </button>
            </p>
        </AuthPageWrapper>
    );
};

const ForgotPasswordPage = ({ onForgotPassword, onNavigate, initialEmail }) => {
    const [email, setEmail] = useState(initialEmail || '');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setEmail(initialEmail || '');
    }, [initialEmail]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);
        try {
            const message = await onForgotPassword({ email });
            setSuccess(message || 'If this email is registered, a temporary password has been sent.');
        } catch (err) {
            setError(err?.message || 'Failed to process forgot password request.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AuthPageWrapper title="Forgot Password">
            <form className="space-y-6" onSubmit={handleSubmit}>
                {error && <p className="text-center text-red-500">{error}</p>}
                {success && <p className="text-center text-green-600">{success}</p>}
                <div>
                    <label htmlFor="forgot-password-email" className="sr-only">Email address</label>
                    <input
                        id="forgot-password-email"
                        name="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        placeholder=""
                    />
                </div>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
                >
                    {isSubmitting ? 'Sending...' : 'Send Temporary Password'}
                </button>
            </form>
            <p className="mt-4 text-center text-sm text-gray-600">
                <button onClick={() => onNavigate({ page: 'login', email: email.trim() })} className="font-medium text-green-600 hover:text-green-500">
                    Back to login
                </button>
            </p>
        </AuthPageWrapper>
    );
};

const ChangePasswordPage = ({ user, onChangePassword, onNavigate }) => {
    const { t } = useContext(LanguageContext);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);
        try {
            const message = await onChangePassword({
                currentPassword,
                newPassword,
                confirmPassword
            });
            setSuccess(message || t('changePasswordSuccess'));
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            onNavigate({ page: 'home' });
        } catch (err) {
            setError(err?.message || t('changePasswordFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AuthPageWrapper title={t('changePasswordTitle')}>
            <form className="space-y-6" onSubmit={handleSubmit}>
                {error && <p className="text-center text-red-500">{error}</p>}
                {success && <p className="text-center text-green-600">{success}</p>}
                <p className="text-sm text-gray-600 text-center">
                    {user?.requiresPasswordChange
                        ? t('changePasswordTemporaryNotice')
                        : t('changePasswordSubtitle')}
                </p>
                <div className="rounded-md shadow-sm -space-y-px">
                    <div>
                        <label htmlFor="current-password" className="sr-only">{t('changePasswordCurrentLabel')}</label>
                        <input
                            id="current-password"
                            type="password"
                            required
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="change-password-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                            placeholder={t('changePasswordCurrentPlaceholder')}
                        />
                    </div>
                    <div>
                        <label htmlFor="new-password" className="sr-only">{t('changePasswordNewLabel')}</label>
                        <input
                            id="new-password"
                            type="password"
                            required
                            minLength={6}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="change-password-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                            placeholder={t('changePasswordNewPlaceholder')}
                        />
                    </div>
                    <div>
                        <label htmlFor="confirm-password" className="sr-only">{t('changePasswordConfirmLabel')}</label>
                        <input
                            id="confirm-password"
                            type="password"
                            required
                            minLength={6}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="change-password-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                            placeholder={t('changePasswordConfirmPlaceholder')}
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
                >
                    {isSubmitting ? t('changePasswordSaving') : t('changePasswordSubmit')}
                </button>
            </form>
        </AuthPageWrapper>
    );
};


const SignUpPage = ({ onSignUp, onNavigate }) => {
    const { t, language } = useContext(LanguageContext);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const deliverySignupPrompt = language === 'da'
        ? 'Vil du registrere dig som chauffør?'
        : 'Want to register as a delivery driver?';
    const deliverySignupAction = language === 'da'
        ? 'Bliv leveringspartner'
        : 'Become a delivery partner';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            const result = await onSignUp({ name, email, password });
            const message = result?.message || t('signupSuccess');
            const registeredEmail = result?.email || email;
            setSuccess(message);
            setName('');
            setEmail('');
            setPassword('');
            onNavigate({ page: 'login', email: registeredEmail, message });
        } catch (err) {
            setError(err?.message || t('signupFailed'));
        }
    };

    return (
        <AuthPageWrapper title={t('signupTitle')}>
            <form className="space-y-6" onSubmit={handleSubmit}>
                {error && <p className="text-center text-red-500">{error}</p>}
                {success && <p className="text-center text-green-600">{success}</p>}
                <div className="rounded-md shadow-sm -space-y-px">
                    <div>
                        <label htmlFor="name" className="sr-only">{t('signupNameLabel')}</label>
                        <input id="name" name="name" type="text" required
                            value={name} onChange={e => setName(e.target.value)}
                            className="signup-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm" placeholder={t('signupNamePlaceholder')} />
                    </div>
                    <div>
                        <label htmlFor="email-signup" className="sr-only">{t('signupEmailLabel')}</label>
                        <input id="email-signup" name="email" type="email" required
                            value={email} onChange={e => setEmail(e.target.value)}
                            className="signup-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm" placeholder={t('signupEmailPlaceholder')} />
                    </div>
                    <div>
                        <label htmlFor="password-signup" className="sr-only">{t('signupPasswordLabel')}</label>
                        <input id="password-signup" name="password" type="password" required
                            value={password} onChange={e => setPassword(e.target.value)}
                            className="signup-field appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm" placeholder={t('signupPasswordPlaceholder')} />
                    </div>
                </div>
                <div>
                    <button type="submit" className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                        {t('navSignup')}
                    </button>
                </div>
            </form>
            <p className="mt-2 text-center text-sm text-gray-600">
                {t('signupAlreadyHaveAccount')}{' '}
                <button onClick={() => onNavigate({ page: 'login' })} className="font-medium text-green-600 hover:text-green-500">
                    {t('navLogin')}
                </button>
            </p>
            <p className="mt-2 text-center text-sm text-gray-600">
                {deliverySignupPrompt}{' '}
                <button onClick={() => onNavigate({ page: 'become-delivery-partner' })} className="font-medium text-green-600 hover:text-green-500">
                    {deliverySignupAction}
                </button>
            </p>
        </AuthPageWrapper>
    );
};

const DeliveryPartnerRegistrationPage = ({ onRegister, onNavigate, user, profile }) => {
    const { language } = useContext(LanguageContext);
    const isExistingUserRegistration = Boolean(user?.userId);
    const [formData, setFormData] = useState({
        name: profile?.name || user?.name || '',
        email: user?.email || '',
        password: '',
        street: profile?.street || profile?.address || '',
        city: profile?.city || '',
        zipcode: profile?.zipcode || '',
        mobile: profile?.mobile || '',
        vehicleRegistrationNumber: '',
        drivingLicenseNumber: '',
        drivingLicenseCopyFile: null,
        serviceArea: '',
        maximumTowWeightKg: '',
        pickupLocationRangeKm: '',
        availabilityNotes: '',
        isAvailable: true
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const copy = language === 'da'
        ? {
            title: 'Bliv leveringspartner',
            subtitle: 'Registrer dig for at modtage leveringsopgaver for campingvogne via den samme portal. Din registrering aktiveres, nar admin har godkendt dit korekort.',
            availabilityLabel: 'Jeg er klar til at modtage leveringsopgaver med det samme',
            submit: 'Registrer som partner',
            submitting: 'Registrerer...',
            loginPrompt: 'Har du allerede en konto?',
            loginAction: 'Log ind',
            licenseCopyLabel: 'Upload kopi af korekort',
            towWeightLabel: 'Tilladt traekvaegt for varevogn (kg)',
            pickupRangeLabel: 'Afhentningsradius fra baseadresse (km)',
            placeholders: {
                name: 'Indtast dit fulde navn',
                email: 'Indtast din e-mailadresse',
                password: 'Vaelg en adgangskode pa mindst 6 tegn',
                street: 'Indtast gade og husnummer',
                city: 'Indtast by',
                zipcode: 'Indtast postnummer',
                mobile: 'Indtast mobilnummer',
                vehicleRegistrationNumber: 'Indtast registreringsnummer',
                drivingLicenseNumber: 'Indtast korekortnummer',
                serviceArea: 'F.eks. Kobenhavn, Roskilde og omegn',
                maximumTowWeightKg: 'F.eks. 1800',
                pickupLocationRangeKm: 'F.eks. 40',
                availabilityNotes: 'Tilfoj eventuelle bemaerkninger om arbejdstider eller ruter'
            }
        }
        : {
            title: 'Become a Delivery Partner',
            subtitle: 'Register to receive doorstep caravan delivery jobs through the same portal. Your account starts receiving jobs only after admin approves the uploaded license copy.',
            availabilityLabel: 'I am ready to receive delivery jobs immediately',
            submit: 'Register as partner',
            submitting: 'Registering...',
            loginPrompt: 'Already have an account?',
            loginAction: 'Log in',
            licenseCopyLabel: 'Upload license copy',
            towWeightLabel: 'Allowed towing weight for van (kg)',
            pickupRangeLabel: 'Pickup radius from base address (km)',
            placeholders: {
                name: 'Enter your full name',
                email: 'Enter your email address',
                password: 'Choose a password with at least 6 characters',
                street: 'Enter street and house number',
                city: 'Enter city',
                zipcode: 'Enter zipcode',
                mobile: 'Enter mobile number',
                vehicleRegistrationNumber: 'Enter vehicle registration number',
                drivingLicenseNumber: 'Enter driving license number',
                serviceArea: 'For example: Copenhagen, Roskilde and nearby areas',
                maximumTowWeightKg: 'For example: 1800',
                pickupLocationRangeKm: 'For example: 40',
                availabilityNotes: 'Add notes about working hours, routes, or limits'
            }
        };

    useEffect(() => {
        if (!isExistingUserRegistration) {
            return;
        }

        setFormData((previous) => ({
            ...previous,
            name: profile?.name || user?.name || '',
            email: user?.email || '',
            street: profile?.street || profile?.address || '',
            city: profile?.city || '',
            zipcode: profile?.zipcode || '',
            mobile: profile?.mobile || ''
        }));
    }, [isExistingUserRegistration, profile, user]);

    const handleChange = (event) => {
        const { name, value, type, checked, files } = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: type === 'checkbox' ? checked : type === 'file' ? (files?.[0] || null) : value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);
        try {
            const result = await onRegister(formData, user?.userId || null);
            const message = result?.message || (language === 'da'
                ? 'Leveringspartner blev registreret. Du kan logge ind nu.'
                : 'Delivery partner registration successful. You can log in now.');
            const registeredEmail = result?.email || formData.email;
            setSuccess(message);
            setFormData({
                name: isExistingUserRegistration ? (profile?.name || user?.name || '') : '',
                email: isExistingUserRegistration ? (user?.email || '') : '',
                password: '',
                street: isExistingUserRegistration ? (profile?.street || profile?.address || '') : '',
                city: isExistingUserRegistration ? (profile?.city || '') : '',
                zipcode: isExistingUserRegistration ? (profile?.zipcode || '') : '',
                mobile: isExistingUserRegistration ? (profile?.mobile || '') : '',
                vehicleRegistrationNumber: '',
                drivingLicenseNumber: '',
                drivingLicenseCopyFile: null,
                serviceArea: '',
                maximumTowWeightKg: '',
                pickupLocationRangeKm: '',
                availabilityNotes: '',
                isAvailable: true
            });
            if (isExistingUserRegistration) {
                onNavigate({ page: 'delivery-portal' });
            } else {
                onNavigate({ page: 'login', email: registeredEmail, message });
            }
        } catch (err) {
            setError(err?.message || (language === 'da' ? 'Registrering mislykkedes.' : 'Registration failed.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AuthPageWrapper title={copy.title} maxWidthClass="max-w-3xl">
            <p className="mb-6 text-center text-sm text-gray-600">{copy.subtitle}</p>
            <form className="space-y-5" onSubmit={handleSubmit}>
                {error && <p className="text-center text-red-500">{error}</p>}
                {success && <p className="text-center text-green-600">{success}</p>}
                <div className="grid grid-cols-1 gap-4">
                    <input
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={DELIVERY_PARTNER_INPUT_CLASS}
                        placeholder={copy.placeholders.name}
                    />
                    <input
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        readOnly={isExistingUserRegistration}
                        className={DELIVERY_PARTNER_INPUT_CLASS}
                        placeholder={copy.placeholders.email}
                    />
                    {!isExistingUserRegistration && (
                        <input
                            name="password"
                            type="password"
                            required
                            minLength={6}
                            value={formData.password}
                            onChange={handleChange}
                            className={DELIVERY_PARTNER_INPUT_CLASS}
                            placeholder={copy.placeholders.password}
                        />
                    )}
                    <input
                        name="street"
                        type="text"
                        required
                        value={formData.street}
                        onChange={handleChange}
                        className={DELIVERY_PARTNER_INPUT_CLASS}
                        placeholder={copy.placeholders.street}
                    />
                    <input
                        name="mobile"
                        type="text"
                        required
                        value={formData.mobile}
                        onChange={handleChange}
                        className={DELIVERY_PARTNER_INPUT_CLASS}
                        placeholder={copy.placeholders.mobile}
                    />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <input
                            name="city"
                            type="text"
                            required
                            value={formData.city}
                            onChange={handleChange}
                            className={DELIVERY_PARTNER_INPUT_CLASS}
                            placeholder={copy.placeholders.city}
                        />
                        <input
                            name="zipcode"
                            type="text"
                            required
                            value={formData.zipcode}
                            onChange={handleChange}
                            className={DELIVERY_PARTNER_INPUT_CLASS}
                            placeholder={copy.placeholders.zipcode}
                        />
                    </div>
                    <input
                        name="vehicleRegistrationNumber"
                        type="text"
                        required
                        value={formData.vehicleRegistrationNumber}
                        onChange={handleChange}
                        className={DELIVERY_PARTNER_INPUT_CLASS}
                        placeholder={copy.placeholders.vehicleRegistrationNumber}
                    />
                    <input
                        name="drivingLicenseNumber"
                        type="text"
                        required
                        value={formData.drivingLicenseNumber}
                        onChange={handleChange}
                        className={DELIVERY_PARTNER_INPUT_CLASS}
                        placeholder={copy.placeholders.drivingLicenseNumber}
                    />
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">{copy.licenseCopyLabel}</label>
                        <input
                            name="drivingLicenseCopyFile"
                            type="file"
                            required
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            onChange={handleChange}
                            className={DELIVERY_PARTNER_INPUT_CLASS}
                        />
                    </div>
                    <input
                        name="serviceArea"
                        type="text"
                        required
                        value={formData.serviceArea}
                        onChange={handleChange}
                        className={DELIVERY_PARTNER_INPUT_CLASS}
                        placeholder={copy.placeholders.serviceArea}
                    />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">{copy.towWeightLabel}</label>
                            <input
                                name="maximumTowWeightKg"
                                type="number"
                                min="1"
                                step="1"
                                required
                                value={formData.maximumTowWeightKg}
                                onChange={handleChange}
                                className={DELIVERY_PARTNER_INPUT_CLASS}
                                placeholder={copy.placeholders.maximumTowWeightKg}
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">{copy.pickupRangeLabel}</label>
                            <input
                                name="pickupLocationRangeKm"
                                type="number"
                                min="1"
                                step="0.1"
                                required
                                value={formData.pickupLocationRangeKm}
                                onChange={handleChange}
                                className={DELIVERY_PARTNER_INPUT_CLASS}
                                placeholder={copy.placeholders.pickupLocationRangeKm}
                            />
                        </div>
                    </div>
                    <textarea
                        name="availabilityNotes"
                        rows="4"
                        value={formData.availabilityNotes}
                        onChange={handleChange}
                        className={DELIVERY_PARTNER_TEXTAREA_CLASS}
                        placeholder={copy.placeholders.availabilityNotes}
                    />
                </div>
                <label className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    <input
                        name="isAvailable"
                        type="checkbox"
                        checked={formData.isAvailable}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span>{copy.availabilityLabel}</span>
                </label>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
                >
                    {isSubmitting ? copy.submitting : copy.submit}
                </button>
            </form>
            {!isExistingUserRegistration && (
                <p className="mt-4 text-center text-sm text-gray-600">
                    {copy.loginPrompt}{' '}
                    <button onClick={() => onNavigate({ page: 'login' })} className="font-medium text-green-600 hover:text-green-500">
                        {copy.loginAction}
                    </button>
                </p>
            )}
        </AuthPageWrapper>
    );
};

const createDeliveryPartnerProfileDraft = (profile, user) => ({
    name: profile?.name ?? profile?.Name ?? user?.name ?? '',
    street: profile?.street ?? profile?.Street ?? '',
    city: profile?.city ?? profile?.City ?? '',
    zipcode: profile?.zipcode ?? profile?.Zipcode ?? '',
    mobile: profile?.mobile ?? profile?.Mobile ?? '',
    bankRegistrationNo: profile?.bankRegistrationNo ?? profile?.BankRegistrationNo ?? '',
    bankAccountNo: profile?.bankAccountNo ?? profile?.BankAccountNo ?? '',
    vehicleType: profile?.vehicleType ?? profile?.VehicleType ?? '',
    vehicleRegistrationNumber: profile?.vehicleRegistrationNumber ?? profile?.VehicleRegistrationNumber ?? '',
    drivingLicenseNumber: profile?.drivingLicenseNumber ?? profile?.DrivingLicenseNumber ?? '',
    serviceArea: profile?.serviceArea ?? profile?.ServiceArea ?? '',
    maximumTowWeightKg: (profile?.maximumTowWeightKg ?? profile?.MaximumTowWeightKg) !== null
        && (profile?.maximumTowWeightKg ?? profile?.MaximumTowWeightKg) !== undefined
        ? String(profile?.maximumTowWeightKg ?? profile?.MaximumTowWeightKg)
        : '',
    pickupLocationRangeKm: (profile?.pickupLocationRangeKm ?? profile?.PickupLocationRangeKm) !== null
        && (profile?.pickupLocationRangeKm ?? profile?.PickupLocationRangeKm) !== undefined
        ? String(profile?.pickupLocationRangeKm ?? profile?.PickupLocationRangeKm)
        : '',
    availabilityNotes: profile?.availabilityNotes ?? profile?.AvailabilityNotes ?? ''
});

const DeliveryPortalPage = ({ user, onNavigate, onSyncProfile, hasDeliveryPartnerProfile }) => {
    const { language, t } = useContext(LanguageContext);
    const [profile, setProfile] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [availabilityBusy, setAvailabilityBusy] = useState(false);
    const [actionJobId, setActionJobId] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [profileForm, setProfileForm] = useState(() => createDeliveryPartnerProfileDraft(null, user));
    const [licenseFile, setLicenseFile] = useState(null);
    const copy = useMemo(() => (language === 'da'
        ? {
            title: 'Leveringscenter',
            subtitle: 'Hver doorstep-booking opretter to separate opgaver. Du kan acceptere en enkelt tur eller begge, afhængigt af din rute og kapacitet.',
            refresh: 'Opdater',
            profileCard: 'Partnerprofil',
            serviceArea: 'Serviceområde',
            vehicle: 'Køretøj',
            vehicleRegistration: 'Registrering',
            license: 'Kørekort',
            licenseCopy: 'Kørekortkopi',
            towingCapacity: 'Trækvægt',
            pickupRangeKm: 'KM-radius',
            approvalStatus: 'Godkendelse',
            address: 'Adresse',
            mobile: 'Mobil',
            availabilityNotes: 'Noter',
            openLicense: 'Åben kopi',
            paymentStatus: 'Betaling',
            committedAmount: 'Aftalt beløb',
            payoutAmount: 'Udbetaling (efter 15% gebyr)',
            paid: 'Betalt',
            unpaid: 'Ikke betalt endnu',
            approvalPending: 'Afventer admin-godkendelse. Du modtager først jobs, når licensen er godkendt.',
            approvalRejected: 'Licensen er afvist af admin. Kontakt admin eller opdater registreringen.',
            approvalApproved: 'Licensen er godkendt. Du kan modtage jobs, hvis vægt og afstand matcher.',
            bankDetailsMissing: 'Tilføj bankoplysninger i din profil, så admin kan udbetale afsluttede leveringer.',
            editProfile: 'Opdater profil',
            cancelEdit: 'Annuller',
            saveProfile: 'Gem profil',
            savingProfile: 'Gemmer...',
            profileSaved: 'Partnerprofilen er opdateret.',
            replaceLicense: 'Udskift korekortkopi',
            replaceLicenseHint: 'Upload kun en ny fil, hvis korekortkopien skal erstattes eller gensendes til admin.',
            currentLicense: 'Nuvaerende korekortkopi',
            unavailableNow: 'Midlertidigt utilgaengelig',
            toggleLabel: 'Tilgaengelig for nye opgaver',
            loading: 'Indlaeser leveringscenter...',
            openJobs: 'Åbne opgaver',
            myActiveJobs: 'Mine aktive opgaver',
            myHistory: 'Jobhistorik',
            emptyOpenJobs: 'Ingen åbne leveringsopgaver lige nu.',
            emptyActiveJobs: 'Du har ingen aktive leveringsopgaver.',
            emptyHistory: 'Ingen afsluttede leveringsopgaver endnu.',
            bookingId: 'Booking',
            caravan: 'Campingvogn',
            schedule: 'Planlagt',
            pickup: 'Afhentning',
            dropoff: 'Levering',
            customer: 'Kunde',
            acceptedBy: 'Tildelt til',
            notProvided: 'Ikke oplyst',
            accept: 'Accepter job',
            start: 'Start levering',
            complete: 'Marker som fuldfort',
            availabilitySaved: 'Tilgaengelighed er opdateret.',
            jobAccepted: 'Leveringsjob blev accepteret.',
            jobStarted: 'Leveringsjob er sat i gang.',
            jobCompleted: 'Leveringsjob er markeret som fuldført.',
            blockedTitle: 'Denne side er kun for leveringspartnere.',
            backHome: 'Tilbage til forsiden'
        }
        : {
            title: 'Delivery Center',
            subtitle: 'Each doorstep booking creates two separate jobs. You can accept one leg or both, depending on your route and capacity.',
            refresh: 'Refresh',
            profileCard: 'Partner profile',
            serviceArea: 'Service area',
            vehicle: 'Vehicle',
            vehicleRegistration: 'Registration',
            license: 'License',
            licenseCopy: 'License copy',
            towingCapacity: 'Towing capacity',
            pickupRangeKm: 'KM radius',
            approvalStatus: 'Approval',
            address: 'Address',
            mobile: 'Mobile',
            availabilityNotes: 'Notes',
            openLicense: 'Open copy',
            paymentStatus: 'Payment',
            committedAmount: 'Committed amount',
            payoutAmount: 'Payout (after 15% fee)',
            paid: 'Paid',
            unpaid: 'Not paid yet',
            approvalPending: 'Pending admin approval. You will start receiving jobs only after the uploaded license copy is approved.',
            approvalRejected: 'License was rejected by admin. Contact admin or update the registration details.',
            approvalApproved: 'License approved. You can receive jobs when your range and towing rules match the caravan.',
            bankDetailsMissing: 'Add bank details in your profile so admin can release payouts for completed deliveries.',
            editProfile: 'Update profile',
            cancelEdit: 'Cancel',
            saveProfile: 'Save profile',
            savingProfile: 'Saving...',
            profileSaved: 'Partner profile updated.',
            replaceLicense: 'Replace license copy',
            replaceLicenseHint: 'Upload a new file only if you want to replace or resubmit the existing license copy.',
            currentLicense: 'Current license copy',
            unavailableNow: 'Temporarily unavailable',
            toggleLabel: 'Available for new jobs',
            loading: 'Loading delivery center...',
            openJobs: 'Open jobs',
            myActiveJobs: 'My active jobs',
            myHistory: 'Job history',
            emptyOpenJobs: 'No open delivery jobs right now.',
            emptyActiveJobs: 'You do not have any active delivery jobs.',
            emptyHistory: 'No completed delivery jobs yet.',
            bookingId: 'Booking',
            caravan: 'Caravan',
            schedule: 'Scheduled',
            pickup: 'Pickup',
            dropoff: 'Drop-off',
            customer: 'Customer',
            acceptedBy: 'Assigned to',
            notProvided: 'Not provided',
            accept: 'Accept job',
            start: 'Start delivery',
            complete: 'Mark completed',
            availabilitySaved: 'Availability updated.',
            jobAccepted: 'Delivery job accepted.',
            jobStarted: 'Delivery job marked as in progress.',
            jobCompleted: 'Delivery job marked as completed.',
            blockedTitle: 'This page is only for delivery partners.',
            backHome: 'Back to home'
        }), [language]);

    const loadPortalData = useCallback(async () => {
        if (!user?.userId) {
            setProfile(null);
            setJobs([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const [profileResponse, jobsResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/api/delivery-partners/${user.userId}/profile`),
                fetch(`${API_BASE_URL}/api/delivery-partners/${user.userId}/jobs`)
            ]);

            if (!profileResponse.ok) {
                throw new Error(await readApiErrorMessage(profileResponse, 'Failed to load delivery partner profile.'));
            }
            if (!jobsResponse.ok) {
                throw new Error(await readApiErrorMessage(jobsResponse, 'Failed to load delivery jobs.'));
            }

            const [profileData, jobsData] = await Promise.all([
                profileResponse.json(),
                jobsResponse.json()
            ]);

            setProfile(profileData || null);
            setJobs(Array.isArray(jobsData) ? jobsData : []);
        } catch (err) {
            setError(err?.message || 'Failed to load delivery center.');
        } finally {
            setIsLoading(false);
        }
    }, [user?.userId]);

    useEffect(() => {
        loadPortalData();
    }, [loadPortalData]);

    useEffect(() => {
        if (!isEditingProfile) {
            setProfileForm(createDeliveryPartnerProfileDraft(profile, user));
            setLicenseFile(null);
        }
    }, [profile, user, isEditingProfile]);

    const openJobs = useMemo(() => (
        jobs.filter((job) => normalizeDeliveryJobStatus(job?.jobStatus) === 'open')
    ), [jobs]);
    const myJobs = useMemo(() => (
        jobs.filter((job) => Number(job?.acceptedByUserId) === Number(user?.userId))
    ), [jobs, user?.userId]);
    const myActiveJobs = useMemo(() => (
        myJobs.filter((job) => ['accepted', 'in_progress'].includes(normalizeDeliveryJobStatus(job?.jobStatus)))
    ), [myJobs]);
    const myHistoryJobs = useMemo(() => (
        myJobs.filter((job) => ['completed', 'cancelled'].includes(normalizeDeliveryJobStatus(job?.jobStatus)))
    ), [myJobs]);

    const handleProfileFieldChange = (event) => {
        const { name, value } = event.target;
        setProfileForm((previous) => ({ ...previous, [name]: value }));
    };

    const openProfileEditor = () => {
        setError('');
        setSuccess('');
        setProfileForm(createDeliveryPartnerProfileDraft(profile, user));
        setLicenseFile(null);
        setIsEditingProfile(true);
    };

    const cancelProfileEditor = () => {
        setError('');
        setSuccess('');
        setProfileForm(createDeliveryPartnerProfileDraft(profile, user));
        setLicenseFile(null);
        setIsEditingProfile(false);
    };

    const handleProfileSubmit = async (event) => {
        event.preventDefault();
        if (!user?.userId) return;

        setIsSavingProfile(true);
        setError('');
        setSuccess('');
        try {
            const formData = new FormData();
            formData.append('name', profileForm.name.trim());
            formData.append('street', profileForm.street.trim());
            formData.append('city', profileForm.city.trim());
            formData.append('zipcode', profileForm.zipcode.trim());
            formData.append('mobile', profileForm.mobile.trim());
            formData.append('bankRegistrationNo', profileForm.bankRegistrationNo.trim());
            formData.append('bankAccountNo', profileForm.bankAccountNo.trim());
            formData.append('vehicleType', profileForm.vehicleType.trim());
            formData.append('vehicleRegistrationNumber', profileForm.vehicleRegistrationNumber.trim());
            formData.append('drivingLicenseNumber', profileForm.drivingLicenseNumber.trim());
            formData.append('serviceArea', profileForm.serviceArea.trim());
            formData.append('maximumTowWeightKg', profileForm.maximumTowWeightKg.trim());
            formData.append('pickupLocationRangeKm', profileForm.pickupLocationRangeKm.trim());
            formData.append('availabilityNotes', profileForm.availabilityNotes.trim());
            if (licenseFile) {
                formData.append('drivingLicenseCopyFile', licenseFile);
            }

            const response = await fetch(`${API_BASE_URL}/api/delivery-partners/${user.userId}/profile`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to update delivery partner profile.'));
            }

            const updatedProfile = await response.json();
            setProfile(updatedProfile || null);
            onSyncProfile?.(updatedProfile || null);
            setLicenseFile(null);
            setIsEditingProfile(false);
            setSuccess(copy.profileSaved);
        } catch (err) {
            setError(err?.message || 'Failed to update delivery partner profile.');
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleAvailabilityToggle = async (isAvailable) => {
        if (!user?.userId) return;

        setAvailabilityBusy(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/delivery-partners/${user.userId}/availability`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.userId,
                    isAvailable
                })
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to update availability.'));
            }

            setProfile((previous) => previous ? { ...previous, isAvailable } : previous);
            setSuccess(copy.availabilitySaved);
        } catch (err) {
            setError(err?.message || 'Failed to update availability.');
        } finally {
            setAvailabilityBusy(false);
        }
    };

    const runJobAction = async (deliveryJobId, endpoint, payload, successMessage, fallbackMessage) => {
        setActionJobId(deliveryJobId);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, fallbackMessage));
            }

            setSuccess(successMessage);
            await loadPortalData();
        } catch (err) {
            setError(err?.message || fallbackMessage);
        } finally {
            setActionJobId(null);
        }
    };

    const handleAcceptJob = (deliveryJobId) => runJobAction(
        deliveryJobId,
        `/api/delivery-partners/jobs/${deliveryJobId}/accept`,
        { userId: user.userId },
        copy.jobAccepted,
        'Failed to accept delivery job.'
    );

    const handleUpdateJobStatus = (deliveryJobId, status, successMessage) => runJobAction(
        deliveryJobId,
        `/api/delivery-partners/jobs/${deliveryJobId}/status`,
        { userId: user.userId, status },
        successMessage,
        'Failed to update delivery job status.'
    );

    if (!hasDeliveryPartnerProfile) {
        return (
            <div className="bg-stone-50 py-16">
                <div className="container mx-auto max-w-3xl px-6">
                    <div className="rounded-3xl bg-white p-8 text-center shadow-lg">
                        <h1 className="text-3xl font-bold text-gray-900">{copy.blockedTitle}</h1>
                        <button
                            type="button"
                            onClick={() => onNavigate({ page: 'home' })}
                            className="mt-6 rounded-full bg-green-600 px-5 py-3 font-semibold text-white hover:bg-green-700"
                        >
                            {copy.backHome}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const addressLine = [
        profile?.street || '',
        profile?.city || '',
        profile?.zipcode || ''
    ].filter(Boolean).join(', ');
    const approvalStatus = String(profile?.approvalStatus || 'pending').trim().toLowerCase();
    const hasBankDetails = Boolean((profile?.bankRegistrationNo || '').trim() && (profile?.bankAccountNo || '').trim());
    const approvalMessage = approvalStatus === 'approved'
        ? copy.approvalApproved
        : approvalStatus === 'rejected'
            ? copy.approvalRejected
            : copy.approvalPending;

    const renderJobCard = (job, showAcceptedBy = false) => {
        const status = normalizeDeliveryJobStatus(job?.jobStatus);
        const isMine = Number(job?.acceptedByUserId) === Number(user?.userId);
        const isPaid = Boolean(job?.isPaid || job?.paidAt);
        const payoutAmount = getDeliveryJobPayoutAmount(job);

        return (
            <div key={job.deliveryJobId} className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">{getDeliveryJobTypeLabel(job.jobType, language)}</p>
                        <h3 className="mt-1 text-xl font-semibold text-gray-900">#{job.deliveryJobId} · {copy.bookingId} {job.bookingId}</h3>
                    </div>
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getDeliveryJobStatusBadgeClass(job.jobStatus)}`}>
                        {getDeliveryJobStatusLabel(job.jobStatus, language)}
                    </span>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-gray-700 md:grid-cols-2">
                    <div>
                        <p className="text-gray-500">{copy.caravan}</p>
                        <p className="font-medium text-gray-900">{job.caravanName || copy.notProvided}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">{copy.schedule}</p>
                        <p className="font-medium text-gray-900">{formatDeliveryDateTime(job.scheduledAt, language)}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">{copy.pickup}</p>
                        <p className="font-medium text-gray-900">{job.pickupLocation || copy.notProvided}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">{copy.dropoff}</p>
                        <p className="font-medium text-gray-900">{job.dropoffLocation || copy.notProvided}</p>
                    </div>
                    <div>
                        <p className="text-gray-500">{copy.customer}</p>
                        <p className="font-medium text-gray-900">{job.customerName || copy.notProvided}</p>
                        <p className="text-xs text-gray-500">{job.customerEmail || copy.notProvided}</p>
                    </div>
                    {showAcceptedBy && (
                        <div>
                            <p className="text-gray-500">{copy.acceptedBy}</p>
                            <p className="font-medium text-gray-900">{job.acceptedByName || copy.notProvided}</p>
                        </div>
                    )}
                    <div>
                        <p className="text-gray-500">{copy.payoutAmount}</p>
                        <p className="font-medium text-gray-900">{payoutAmount > 0 ? formatInr(payoutAmount) : copy.notProvided}</p>
                    </div>
                    {status === 'completed' && (
                        <div>
                            <p className="text-gray-500">{copy.paymentStatus}</p>
                            <p className="font-medium text-gray-900">{isPaid ? copy.paid : copy.unpaid}</p>
                        </div>
                    )}
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                    {status === 'open' && !job?.acceptedByUserId && (
                        <button
                            type="button"
                            onClick={() => handleAcceptJob(job.deliveryJobId)}
                            disabled={actionJobId === job.deliveryJobId || availabilityBusy || !profile?.isAvailable}
                            className="rounded-full bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                        >
                            {copy.accept}
                        </button>
                    )}
                    {isMine && status === 'accepted' && (
                        <button
                            type="button"
                            onClick={() => handleUpdateJobStatus(job.deliveryJobId, 'in_progress', copy.jobStarted)}
                            disabled={actionJobId === job.deliveryJobId}
                            className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-gray-400"
                        >
                            {copy.start}
                        </button>
                    )}
                    {isMine && status === 'in_progress' && (
                        <button
                            type="button"
                            onClick={() => handleUpdateJobStatus(job.deliveryJobId, 'completed', copy.jobCompleted)}
                            disabled={actionJobId === job.deliveryJobId}
                            className="rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                        >
                            {copy.complete}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="bg-stone-50 py-12">
            <div className="container mx-auto max-w-6xl px-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-green-700">{copy.title}</p>
                        <h1 className="mt-2 text-4xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>{copy.title}</h1>
                        <p className="mt-3 max-w-3xl text-gray-600">{copy.subtitle}</p>
                    </div>
                    <button
                        type="button"
                        onClick={loadPortalData}
                        disabled={isLoading}
                        className="rounded-full border border-gray-300 bg-white px-5 py-3 font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {copy.refresh}
                    </button>
                </div>

                {error && <p className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                {success && <p className="mt-6 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</p>}

                {isLoading ? (
                    <div className="mt-8 rounded-3xl bg-white p-8 text-center shadow-sm">
                        <p className="text-gray-600">{copy.loading}</p>
                    </div>
                ) : (
                    <>
                        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                            <div className="rounded-3xl bg-white p-6 shadow-sm">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <h2 className="text-2xl font-semibold text-gray-900">{copy.profileCard}</h2>
                                        <p className="mt-1 text-sm text-gray-500">{profile?.name || user?.name || copy.notProvided}</p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={openProfileEditor}
                                            className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                        >
                                            {copy.editProfile}
                                        </button>
                                        <label className="inline-flex items-center gap-3 rounded-full bg-stone-100 px-4 py-2 text-sm font-medium text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(profile?.isAvailable)}
                                                onChange={(event) => handleAvailabilityToggle(event.target.checked)}
                                                disabled={availabilityBusy}
                                                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                            />
                                            <span>{copy.toggleLabel}</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="mt-6 grid grid-cols-1 gap-4 text-sm text-gray-700 md:grid-cols-2">
                                    <div>
                                        <p className="text-gray-500">{copy.serviceArea}</p>
                                        <p className="font-medium text-gray-900">{profile?.serviceArea || copy.notProvided}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.vehicle}</p>
                                        <p className="font-medium text-gray-900">{profile?.vehicleType || copy.notProvided}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.vehicleRegistration}</p>
                                        <p className="font-medium text-gray-900">{profile?.vehicleRegistrationNumber || copy.notProvided}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.license}</p>
                                        <p className="font-medium text-gray-900">{profile?.drivingLicenseNumber || copy.notProvided}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.licenseCopy}</p>
                                        {profile?.drivingLicenseCopyUrl ? (
                                            <a
                                                href={resolveApiAssetUrl(profile.drivingLicenseCopyUrl)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="font-medium text-blue-600 hover:text-blue-800"
                                            >
                                                {copy.openLicense}
                                            </a>
                                        ) : (
                                            <p className="font-medium text-gray-900">{copy.notProvided}</p>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.towingCapacity}</p>
                                        <p className="font-medium text-gray-900">{profile?.maximumTowWeightKg ? `${profile.maximumTowWeightKg} kg` : copy.notProvided}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.pickupRangeKm}</p>
                                        <p className="font-medium text-gray-900">{profile?.pickupLocationRangeKm ? `${profile.pickupLocationRangeKm} km` : copy.notProvided}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.approvalStatus}</p>
                                        <p className="font-medium text-gray-900 capitalize">{approvalStatus}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.mobile}</p>
                                        <p className="font-medium text-gray-900">{profile?.mobile || copy.notProvided}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">{copy.address}</p>
                                        <p className="font-medium text-gray-900">{addressLine || copy.notProvided}</p>
                                    </div>
                                </div>
                                <div className="mt-5 rounded-2xl bg-stone-50 p-4 text-sm text-gray-700">
                                    <p className="text-gray-500">{copy.availabilityNotes}</p>
                                    <p className="mt-1 font-medium text-gray-900">{profile?.availabilityNotes || copy.notProvided}</p>
                                </div>
                                <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${
                                    approvalStatus === 'approved'
                                        ? 'border-green-200 bg-green-50 text-green-800'
                                        : approvalStatus === 'rejected'
                                            ? 'border-red-200 bg-red-50 text-red-800'
                                            : 'border-amber-200 bg-amber-50 text-amber-800'
                                }`}>
                                    {approvalMessage}
                                </div>
                                {!hasBankDetails && (
                                    <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                                        <p>{copy.bankDetailsMissing}</p>
                                        <button
                                            type="button"
                                            onClick={openProfileEditor}
                                            className="mt-3 rounded-full bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
                                        >
                                            {copy.editProfile}
                                        </button>
                                    </div>
                                )}
                                {isEditingProfile && (
                                    <form onSubmit={handleProfileSubmit} className="mt-6 rounded-2xl border border-gray-200 bg-stone-50 p-5">
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            <InputField
                                                label={t('profileName')}
                                                name="name"
                                                value={profileForm.name}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <div>
                                                <label htmlFor="delivery-profile-email" className={CONTROL_LABEL_CLASS}>Email</label>
                                                <input
                                                    id="delivery-profile-email"
                                                    type="email"
                                                    value={profile?.email || user?.email || ''}
                                                    readOnly
                                                    className={`${CONTROL_INPUT_CLASS} bg-gray-100 text-gray-500`}
                                                />
                                            </div>
                                            <InputField
                                                label={t('profileStreet')}
                                                name="street"
                                                value={profileForm.street}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={t('profileCity')}
                                                name="city"
                                                value={profileForm.city}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={t('profileZipcode')}
                                                name="zipcode"
                                                value={profileForm.zipcode}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={t('profileMobile')}
                                                name="mobile"
                                                value={profileForm.mobile}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={t('profileBankRegNo')}
                                                name="bankRegistrationNo"
                                                value={profileForm.bankRegistrationNo}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={t('profileBankAccountNo')}
                                                name="bankAccountNo"
                                                value={profileForm.bankAccountNo}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={copy.vehicle}
                                                name="vehicleType"
                                                value={profileForm.vehicleType}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={copy.vehicleRegistration}
                                                name="vehicleRegistrationNumber"
                                                value={profileForm.vehicleRegistrationNumber}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={copy.license}
                                                name="drivingLicenseNumber"
                                                value={profileForm.drivingLicenseNumber}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={copy.serviceArea}
                                                name="serviceArea"
                                                value={profileForm.serviceArea}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={copy.towingCapacity}
                                                name="maximumTowWeightKg"
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={profileForm.maximumTowWeightKg}
                                                onChange={handleProfileFieldChange}
                                            />
                                            <InputField
                                                label={copy.pickupRangeKm}
                                                name="pickupLocationRangeKm"
                                                type="number"
                                                min="0.1"
                                                step="0.1"
                                                value={profileForm.pickupLocationRangeKm}
                                                onChange={handleProfileFieldChange}
                                            />
                                        </div>
                                        <div className="mt-4">
                                            <label htmlFor="delivery-profile-notes" className={CONTROL_LABEL_CLASS}>{copy.availabilityNotes}</label>
                                            <textarea
                                                id="delivery-profile-notes"
                                                name="availabilityNotes"
                                                value={profileForm.availabilityNotes}
                                                onChange={handleProfileFieldChange}
                                                rows={3}
                                                className={CONTROL_TEXTAREA_CLASS}
                                            />
                                        </div>
                                        <div className="mt-4 rounded-2xl border border-dashed border-gray-300 bg-white p-4">
                                            <label htmlFor="delivery-profile-license-file" className={CONTROL_LABEL_CLASS}>{copy.replaceLicense}</label>
                                            {profile?.drivingLicenseCopyUrl && (
                                                <p className="mb-2 text-sm text-gray-600">
                                                    {copy.currentLicense}:{' '}
                                                    <a
                                                        href={resolveApiAssetUrl(profile.drivingLicenseCopyUrl)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="font-medium text-blue-600 hover:text-blue-800"
                                                    >
                                                        {copy.openLicense}
                                                    </a>
                                                </p>
                                            )}
                                            <input
                                                id="delivery-profile-license-file"
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                                onChange={(event) => setLicenseFile(event.target.files?.[0] || null)}
                                                className="block w-full text-sm text-gray-700"
                                            />
                                            <p className="mt-2 text-xs text-gray-500">{copy.replaceLicenseHint}</p>
                                        </div>
                                        <div className="mt-5 flex flex-wrap gap-3">
                                            <button
                                                type="submit"
                                                disabled={isSavingProfile}
                                                className="rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                                            >
                                                {isSavingProfile ? copy.savingProfile : copy.saveProfile}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={cancelProfileEditor}
                                                disabled={isSavingProfile}
                                                className="rounded-full border border-gray-300 bg-white px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                {copy.cancelEdit}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-1">
                                <div className="rounded-3xl bg-white p-5 shadow-sm">
                                    <p className="text-sm text-gray-500">{copy.openJobs}</p>
                                    <p className="mt-2 text-3xl font-bold text-gray-900">{openJobs.length}</p>
                                </div>
                                <div className="rounded-3xl bg-white p-5 shadow-sm">
                                    <p className="text-sm text-gray-500">{copy.myActiveJobs}</p>
                                    <p className="mt-2 text-3xl font-bold text-gray-900">{myActiveJobs.length}</p>
                                </div>
                                <div className="rounded-3xl bg-white p-5 shadow-sm">
                                    <p className="text-sm text-gray-500">{copy.myHistory}</p>
                                    <p className="mt-2 text-3xl font-bold text-gray-900">{myHistoryJobs.length}</p>
                                </div>
                            </div>
                        </div>
                        <section className="mt-8">
                            <div className="mb-4 flex items-center justify-between">
                                <h2 className="text-2xl font-semibold text-gray-900">{copy.openJobs}</h2>
                            </div>
                            {openJobs.length === 0 ? (
                                <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
                                    {copy.emptyOpenJobs}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                    {openJobs.map((job) => renderJobCard(job))}
                                </div>
                            )}
                        </section>

                        <section className="mt-10">
                            <div className="mb-4 flex items-center justify-between">
                                <h2 className="text-2xl font-semibold text-gray-900">{copy.myActiveJobs}</h2>
                            </div>
                            {myActiveJobs.length === 0 ? (
                                <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
                                    {copy.emptyActiveJobs}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                    {myActiveJobs.map((job) => renderJobCard(job, true))}
                                </div>
                            )}
                        </section>

                        <section className="mt-10">
                            <div className="mb-4 flex items-center justify-between">
                                <h2 className="text-2xl font-semibold text-gray-900">{copy.myHistory}</h2>
                            </div>
                            {myHistoryJobs.length === 0 ? (
                                <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
                                    {copy.emptyHistory}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                    {myHistoryJobs.map((job) => renderJobCard(job, true))}
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

const ProfileDetailsPage = ({ user, profile, onSaveProfile, onNavigate, redirectRoute, hasOwnerCaravans, hasDeliveryPartnerProfile }) => {
    const { t, language } = useContext(LanguageContext);
    const requiresBankDetails = Boolean(hasOwnerCaravans) || isOwnerProfileRoute(redirectRoute?.page) || hasDeliveryPartnerProfile;
    const profileSubtitle = requiresBankDetails
        ? (language === 'da'
            ? 'Navn, gade, by, postnummer, mobil, bankregistreringsnr. og bankkontonr. skal udfyldes for at registrere eller administrere et køretøj eller modtage udbetalinger.'
            : 'Name, Street, City, Zipcode, Mobile, Bank Registration No, and Bank Account No are required before you can register or manage a vehicle or receive payouts.')
        : (language === 'da'
            ? 'Navn, gade, by, postnummer og mobil skal udfyldes for at fortsætte.'
            : 'Name, Street, City, Zipcode, and Mobile are required before you continue.');
    const profileRequiredMessage = requiresBankDetails
        ? (language === 'da'
            ? 'Alle synlige felter er påkrævet.'
            : 'All visible fields are required.')
        : (language === 'da'
            ? 'Navn, gade, by, postnummer og mobil er påkrævet.'
            : 'Name, Street, City, Zipcode, and Mobile are required.');
    const [formData, setFormData] = useState({
        name: profile?.name || user?.name || '',
        street: profile?.street || profile?.address || '',
        city: profile?.city || '',
        zipcode: profile?.zipcode || '',
        mobile: profile?.mobile || '',
        bankRegistrationNo: profile?.bankRegistrationNo || '',
        bankAccountNo: profile?.bankAccountNo || ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setFormData({
            name: profile?.name || user?.name || '',
            street: profile?.street || profile?.address || '',
            city: profile?.city || '',
            zipcode: profile?.zipcode || '',
            mobile: profile?.mobile || '',
            bankRegistrationNo: profile?.bankRegistrationNo || '',
            bankAccountNo: profile?.bankAccountNo || ''
        });
    }, [profile, user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (
            !formData.name.trim() ||
            !formData.street.trim() ||
            !formData.city.trim() ||
            !formData.zipcode.trim() ||
            !formData.mobile.trim()
        ) {
            setError(profileRequiredMessage);
            return;
        }
        if (
            requiresBankDetails
            && (
                !formData.bankRegistrationNo.trim()
                || !formData.bankAccountNo.trim()
            )
        ) {
            setError(profileRequiredMessage);
            return;
        }
        setIsSaving(true);
        try {
            await onSaveProfile(user.userId, formData);
            setSuccess(t('profileSaved'));
            const nextRoute = redirectRoute || { page: 'home' };
            setTimeout(() => onNavigate(nextRoute), 0);
        } catch (err) {
            setError(err.message || 'Failed to save profile details.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <AuthPageWrapper title={t('profileTitle')}>
            <p className="text-sm text-gray-600 mb-4">{profileSubtitle}</p>
            <form className="space-y-4" onSubmit={handleSubmit}>
                {error && <p className="text-center text-red-500">{error}</p>}
                {success && <p className="text-center text-green-600">{success}</p>}
                <InputField label={t('profileName')} name="name" value={formData.name} onChange={handleChange} />
                <InputField label={t('profileStreet')} name="street" value={formData.street} onChange={handleChange} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField label={t('profileCity')} name="city" value={formData.city} onChange={handleChange} />
                    <InputField label={t('profileZipcode')} name="zipcode" value={formData.zipcode} onChange={handleChange} />
                </div>
                <InputField label={t('profileMobile')} name="mobile" value={formData.mobile} onChange={handleChange} />
                {requiresBankDetails && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField label={t('profileBankRegNo')} name="bankRegistrationNo" value={formData.bankRegistrationNo} onChange={handleChange} placeholder="" />
                        <InputField label={t('profileBankAccountNo')} name="bankAccountNo" value={formData.bankAccountNo} onChange={handleChange} placeholder="" />
                    </div>
                )}
                <button type="submit" className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400" disabled={isSaving}>
                    {isSaving ? 'Saving...' : t('profileSave')}
                </button>
            </form>
        </AuthPageWrapper>
    );
};


// --- Booking Page Component ---
// This component displays the form for creating a new booking.
const BookingPage = ({ caravanId, onNavigate, getCaravanById, user, onCreateBooking, initialStartDate = '', initialEndDate = '' }) => {
    const { t, language } = useContext(LanguageContext);
    const caravan = getCaravanById(caravanId);
    const festivalAvailable = isFestivalAvailable(caravan);
    const [startDate, setStartDate] = useState(() => String(initialStartDate || '').trim());
    const [endDate, setEndDate] = useState(() => String(initialEndDate || '').trim());
    const startTime = STANDARD_PICKUP_TIME;
    const endTime = STANDARD_DROP_TIME;
    const [bookingRate, setBookingRate] = useState('day'); // day | week | month
    const [weeks, setWeeks] = useState(1);
    const [extraDays, setExtraDays] = useState(0);
    const [months, setMonths] = useState(1);
    const [doorstepEnabled, setDoorstepEnabled] = useState(false);
    const [doorstepDeliveryAddress, setDoorstepDeliveryAddress] = useState('');
    const [selectedAddons, setSelectedAddons] = useState({
        gas: false,
        sideMirrors: false,
        tentSetup: false,
        adapter613: false,
        cleaning: false
    });
    const [remarks, setRemarks] = useState('');
    const [error, setError] = useState('');
    const [quote, setQuote] = useState(null);
    const [isAvailable, setIsAvailable] = useState(null); // null, true, or false
    const [isChecking, setIsChecking] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingBlockedRanges, setIsLoadingBlockedRanges] = useState(false);
    const [blockedRanges, setBlockedRanges] = useState([]);
    const [blockedDates, setBlockedDates] = useState(() => new Set());
    const [festivalSuggestions, setFestivalSuggestions] = useState([]);
    const [festivalSuggestionsError, setFestivalSuggestionsError] = useState('');
    const [isLoadingFestivalSuggestions, setIsLoadingFestivalSuggestions] = useState(false);
    const [showFestivalSuggestions, setShowFestivalSuggestions] = useState(false);
    const [selectedFestivalSuggestionId, setSelectedFestivalSuggestionId] = useState(null);
    const [calendarAnchorMonth, setCalendarAnchorMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const isSelfPickEnabled = Boolean(caravan?.self_pick_enabled ?? caravan?.selfPickEnabled ?? true);
    const isDoorstepAvailable = Boolean(caravan?.doorstep_available ?? caravan?.doorstepAvailable ?? false);
    const isDoorstepRequired = !isSelfPickEnabled && isDoorstepAvailable;
    const effectiveDoorstepEnabled = isDoorstepRequired || (isDoorstepAvailable && doorstepEnabled);
    const longTermEnabled = isLongTermCaravan(caravan);
    const minimumBookingMonths = Math.max(1, getMinimumBookingMonths(caravan) || 1);

    const normalizedPricingMode = bookingRate === 'month'
        ? 'month'
        : (bookingRate === 'week' ? 'week' : 'day');
    const toYmd = (date) => formatDateOnlyInput(date);
    const parseYmd = (ymd) => parseDateOnlyLocal(ymd);
    const calculateDateNights = (startYmd, endYmd) => calculateDateOnlyDiffDays(startYmd, endYmd);
    const selectedDurationNights = calculateDateNights(startDate, endDate);
    const effectiveQuoteRequestPricingMode = normalizedPricingMode === 'month'
        ? 'month'
        : ((normalizedPricingMode === 'week' || selectedDurationNights >= 7) ? 'week' : 'day');
    const availableAddons = getAvailableCaravanAddons(caravan);
    const calendarLocale = language === 'da' ? 'da-DK' : 'en-US';

    useEffect(() => {
        setStartDate(String(initialStartDate || '').trim());
        setEndDate(String(initialEndDate || '').trim());
    }, [initialStartDate, initialEndDate, caravanId]);

    useEffect(() => {
        setFestivalSuggestions([]);
        setFestivalSuggestionsError('');
        setIsLoadingFestivalSuggestions(false);
        setShowFestivalSuggestions(false);
        setSelectedFestivalSuggestionId(null);
    }, [caravanId, startDate, endDate]);

    useEffect(() => {
        const referenceDate = parseYmd(startDate) || parseYmd(endDate);
        if (!referenceDate) {
            return;
        }

        setCalendarAnchorMonth(new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1));
    }, [startDate, endDate]);

    useEffect(() => {
        let ignore = false;
        const loadBlocked = async () => {
            if (!caravanId) {
                setBlockedRanges([]);
                setBlockedDates(new Set());
                return;
            }

            setIsLoadingBlockedRanges(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/bookings/caravan/${caravanId}/blocked`);
                if (!res.ok) {
                    throw new Error(t('bookingCouldNotLoadBlockedRanges'));
                }
                const data = await res.json();
                if (ignore) return;
                const rows = Array.isArray(data) ? data : [];
                setBlockedRanges(rows);

                const nextBlocked = new Set();
                rows.forEach((row) => {
                    const start = new Date(row.startAt);
                    const end = new Date(row.endAt);
                    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return;

                    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                    while (d < endDay) {
                        nextBlocked.add(toYmd(d));
                        d.setDate(d.getDate() + 1);
                    }
                });
                setBlockedDates(nextBlocked);
            } catch (err) {
                if (!ignore) {
                    setBlockedRanges([]);
                    setBlockedDates(new Set());
                }
            } finally {
                if (!ignore) {
                    setIsLoadingBlockedRanges(false);
                }
            }
        };

        loadBlocked();
        return () => {
            ignore = true;
        };
    }, [caravanId, t]);

    useEffect(() => {
        if (longTermEnabled && months < minimumBookingMonths) {
            setMonths(minimumBookingMonths);
        }
        if (!longTermEnabled && bookingRate === 'month') {
            setBookingRate('day');
        }
    }, [bookingRate, longTermEnabled, minimumBookingMonths, months]);

    useEffect(() => {
        if (bookingRate === 'month') {
            if (!startDate || months <= 0) {
                return;
            }
            const computedEndDate = calculateMonthBookingEndDate(startDate, months, extraDays);
            if (computedEndDate && computedEndDate !== endDate) {
                setEndDate(computedEndDate);
            }
            return;
        }

        if (bookingRate !== 'week' || !startDate || weeks <= 0) {
            return;
        }
        const totalDays = (weeks * 7) + Math.max(0, Number(extraDays) || 0);
        if (totalDays <= 0) {
            return;
        }
        const start = parseDateOnlyLocal(startDate);
        if (!start) return;
        const computedEnd = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        computedEnd.setDate(computedEnd.getDate() + totalDays);
        const formattedEnd = formatDateOnlyInput(computedEnd);
        if (formattedEnd !== endDate) {
            setEndDate(formattedEnd);
        }
    }, [bookingRate, startDate, weeks, extraDays, months, endDate]);

    useEffect(() => {
        if (isDoorstepRequired) {
            if (!doorstepEnabled) {
                setDoorstepEnabled(true);
            }
            return;
        }

        if (!isDoorstepAvailable && doorstepEnabled) {
            setDoorstepEnabled(false);
        }
    }, [isDoorstepAvailable, isDoorstepRequired, doorstepEnabled]);

    useEffect(() => {
        const debounceCheck = setTimeout(() => {
            const startLocal = parseDateOnlyLocal(startDate);
            const endLocal = parseDateOnlyLocal(endDate);
            if (startLocal && endLocal && endLocal > startLocal) {
                const normalizedDoorstepAddress = (doorstepDeliveryAddress || '').trim();
                if (effectiveDoorstepEnabled && !normalizedDoorstepAddress) {
                    setIsAvailable(null);
                    setQuote(null);
                    setError(t('bookingDeliveryAddressRequired'));
                    setIsChecking(false);
                    return;
                }
                const checkAvailabilityAndQuote = async () => {
                    const startAt = `${startDate}T${startTime || '00:00'}:00`;
                    const endAt = `${endDate}T${endTime || '00:00'}:00`;
                    setIsChecking(true);
                    setError('');
                    setIsAvailable(null);
                    setQuote(null);
                    try {
                        const [availabilityRes, quoteRes] = await Promise.all([
                            fetch(`${API_BASE_URL}/api/bookings/check-availability?caravanId=${caravanId}&startDate=${encodeURIComponent(startAt)}&endDate=${encodeURIComponent(endAt)}`),
                            fetch(`${API_BASE_URL}/api/bookings/quote`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    caravanId,
                                    renterId: user?.userId || null,
                                    startDate: startAt,
                                    endDate: endAt,
                                    pricingMode: effectiveQuoteRequestPricingMode,
                                    doorstepEnabled: effectiveDoorstepEnabled,
                                    doorstepDeliveryAddress: effectiveDoorstepEnabled ? normalizedDoorstepAddress : null,
                                    addonGasSelected: Boolean(selectedAddons.gas),
                                    addonSideMirrorsSelected: Boolean(selectedAddons.sideMirrors),
                                    addonTentSetupSelected: Boolean(selectedAddons.tentSetup),
                                    addonAdapter613Selected: Boolean(selectedAddons.adapter613),
                                    addonCleaningSelected: Boolean(selectedAddons.cleaning),
                                    addonToiletEmptyingSelected: Boolean(selectedAddons.toiletEmptying),
                                    addonTentRentalSelected: Boolean(selectedAddons.tentRental),
                                    addonTentDryingSelected: Boolean(selectedAddons.tentDrying)
                                }),
                            })
                        ]);

                        if (!availabilityRes.ok) {
                            throw new Error(t('bookingCouldNotCheckAvailability'));
                        }
                        if (!quoteRes.ok) {
                            const text = await quoteRes.text();
                            throw new Error(text || t('bookingCouldNotCalculateQuote'));
                        }

                        const availabilityData = await availabilityRes.json();
                        const quoteData = await quoteRes.json();
                        setIsAvailable(availabilityData.isAvailable);
                        setQuote(quoteData);
                        if (!availabilityData.isAvailable) {
                            setError(t('bookingDatesUnavailable'));
                        }
                    } catch (err) {
                        setError(err.message);
                    } finally {
                        setIsChecking(false);
                    }
                };
                checkAvailabilityAndQuote();
            } else {
                setIsAvailable(null);
                setQuote(null);
            }
        }, 500);

        return () => clearTimeout(debounceCheck);
    }, [startDate, endDate, startTime, endTime, caravanId, effectiveQuoteRequestPricingMode, effectiveDoorstepEnabled, doorstepDeliveryAddress, selectedAddons.gas, selectedAddons.sideMirrors, selectedAddons.tentSetup, selectedAddons.adapter613, selectedAddons.cleaning, selectedAddons.toiletEmptying, selectedAddons.tentRental, selectedAddons.tentDrying, user?.userId, t]);

    const calculateNights = () => {
        if (startDate && endDate) {
            return calculateDateOnlyDiffDays(startDate, endDate);
        }
        return 0;
    };

    const nights = calculateNights();
    const depositAmount = Number(caravan?.deposit_amount || 0);
    const rentalAmount = Number(quote?.rental_amount || 0);
    const monthlyComponentAmount = Number(quote?.monthly_component_amount ?? quote?.monthlyComponentAmount ?? 0);
    const weeklyComponentAmount = Number(quote?.weekly_component_amount ?? quote?.weeklyComponentAmount ?? 0);
    const dailyComponentAmount = Number(quote?.daily_component_amount ?? quote?.dailyComponentAmount ?? 0);
    const doorstepFee = Number(quote?.doorstep_fee || 0);
    const doorstepRatePerKm = Number(quote?.doorstep_rate_per_km ?? caravan?.doorstep_rate_per_km ?? 0);
    const doorstepDistanceKm = Number(quote?.doorstep_distance_km || 0);
    const doorstepChargeableDistanceKm = Number(quote?.doorstep_chargeable_distance_km || (doorstepDistanceKm * 4));
    const doorstepChargeMultiplier = Number(quote?.doorstep_charge_multiplier || 4);
    const quoteAddons = getSelectedQuoteAddons(quote);
    const addonAmount = Number(quote?.addon_amount || 0);
    const totalCost = Number(quote?.total_price || 0);
    const totalDueIncludingDeposit = totalCost + depositAmount;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tripStartDate = startDate ? parseDateOnlyLocal(startDate) : null;
    const msUntilTripStart = tripStartDate ? (tripStartDate.getTime() - today.getTime()) : null;
    const isFullPaymentDueNow = msUntilTripStart !== null && msUntilTripStart <= (14 * ONE_DAY_MS);
    const currentPaymentDueAmount = depositAmount > 0
        ? (isFullPaymentDueNow ? totalDueIncludingDeposit : depositAmount)
        : totalCost;
    const nightLabel = nights === 1 ? t('bookingNight') : t('bookingNights');
    const dayLabel = t('bookingDay');
    const daysLabel = t('bookingDays');
    const rawQuotePricingMode = String(quote?.pricing_mode || quote?.pricingMode || effectiveQuoteRequestPricingMode).trim().toLowerCase();
    const quotePricingMode = rawQuotePricingMode === 'month' ? 'month' : (rawQuotePricingMode === 'week' ? 'week' : 'day');
    const isMonthQuote = quotePricingMode === 'month';
    const isWeekQuote = quotePricingMode === 'week';
    const quoteMonths = Number(quote?.months || (isMonthQuote ? months : 0));
    const quoteWeeks = Number(quote?.weeks || (isWeekQuote ? Math.floor(nights / 7) : 0));
    const quoteDays = Number(quote?.days || (isWeekQuote ? Math.max(nights - (quoteWeeks * 7), 0) : 0));
    const quoteWeekLabel = quoteWeeks === 1 ? t('bookingWeek') : t('bookingWeeksPlural');
    const remainingDaysAfterWeekRate = isWeekQuote ? quoteDays : 0;
    const weekDayLabel = remainingDaysAfterWeekRate === 1 ? dayLabel : daysLabel;
    const monthRateParts = isMonthQuote
        ? [
            quoteMonths > 0 ? `${quoteMonths} ${language === 'da' ? (quoteMonths === 1 ? 'måned' : 'måneder') : `month${quoteMonths === 1 ? '' : 's'}`} x ${language === 'da' ? 'månedspris' : 'monthly rate'}` : '',
            quoteWeeks > 0 ? `${quoteWeeks} ${quoteWeekLabel} x ${t('bookingWeeklyRate')}` : '',
            quoteDays > 0 ? `${quoteDays} ${weekDayLabel} x ${t('bookingDailyRate')}` : ''
        ].filter(Boolean)
        : [];
    const pricingCalculationText = isMonthQuote
        ? monthRateParts.join(' + ')
        : (isWeekQuote
        ? [
            quoteWeeks > 0 ? `${quoteWeeks} ${quoteWeekLabel} x ${t('bookingWeeklyRate')}` : '',
            remainingDaysAfterWeekRate > 0 ? `${remainingDaysAfterWeekRate} ${weekDayLabel} x ${t('bookingDailyRate')}` : ''
        ].filter(Boolean).join(' + ')
        : `${nights} ${nights === 1 ? dayLabel : daysLabel} x ${t('bookingDailyRate')}`);
    const monthBifurcationParts = isMonthQuote
        ? [
            monthlyComponentAmount > 0 ? `DKK ${monthlyComponentAmount.toLocaleString('da-DK')}` : '',
            weeklyComponentAmount > 0 ? `DKK ${weeklyComponentAmount.toLocaleString('da-DK')}` : '',
            dailyComponentAmount > 0 ? `DKK ${dailyComponentAmount.toLocaleString('da-DK')}` : ''
        ].filter(Boolean)
        : [];
    const pricingBifurcationFormulaText = isMonthQuote
        ? (monthBifurcationParts.length > 1
            ? `${monthBifurcationParts.join(' + ')} = DKK ${Number(rentalAmount).toLocaleString('da-DK')}`
            : (monthBifurcationParts[0] || `DKK ${Number(rentalAmount).toLocaleString('da-DK')}`))
        : (isWeekQuote
        ? `DKK ${weeklyComponentAmount.toLocaleString('da-DK')} + DKK ${dailyComponentAmount.toLocaleString('da-DK')} = DKK ${Number(rentalAmount).toLocaleString('da-DK')}`
        : `DKK ${dailyComponentAmount.toLocaleString('da-DK')}`);
    const formatQuoteLineDuration = (line) => {
        const lineMonths = Number(line?.months || 0);
        const lineWeeks = Number(line?.weeks || 0);
        const lineDays = Number(line?.nights || 0);
        const parts = [];
        if (lineMonths > 0) {
            parts.push(`${lineMonths} ${language === 'da' ? (lineMonths === 1 ? 'mÃ¥ned' : 'mÃ¥neder') : `month${lineMonths === 1 ? '' : 's'}`}`);
        }
        if (lineWeeks > 0) {
            parts.push(`${lineWeeks} ${lineWeeks === 1 ? t('bookingWeek') : t('bookingWeeksPlural')}`);
        }
        if (lineDays > 0) {
            parts.push(`${lineDays} ${lineDays === 1 ? t('bookingDay') : t('bookingDays')}`);
        }
        return parts.length > 0 ? parts.join(' + ') : `0 ${t('bookingDays')}`;
    };

    const shiftCalendarMonths = (offset) => {
        setCalendarAnchorMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() + offset, 1));
    };
    const calendarMonth1 = new Date(calendarAnchorMonth.getFullYear(), calendarAnchorMonth.getMonth(), 1);
    const calendarMonth2 = new Date(calendarAnchorMonth.getFullYear(), calendarAnchorMonth.getMonth() + 1, 1);

    const isBlocked = (ymd) => blockedDates.has(String(ymd || ''));
    const rangeIncludesBlocked = (startYmd, endYmd) => {
        const start = parseYmd(startYmd);
        const end = parseYmd(endYmd);
        if (!start || !end || end <= start) return false;

        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        while (d < endDay) {
            if (blockedDates.has(toYmd(d))) {
                return true;
            }
            d.setDate(d.getDate() + 1);
        }
        return false;
    };

    const handleStartDateChange = (nextYmd) => {
        if (!nextYmd) {
            setStartDate('');
            if (bookingRate === 'day') {
                setEndDate('');
            }
            setError('');
            return;
        }

        if (isBlocked(nextYmd)) {
            setError(t('bookingDateBlocked'));
            return;
        }

        setError('');
        setStartDate(nextYmd);

        if (bookingRate === 'day' && endDate) {
            const startLocal = parseYmd(nextYmd);
            const endLocal = parseYmd(endDate);
            if (!startLocal || !endLocal || endLocal <= startLocal || rangeIncludesBlocked(nextYmd, endDate)) {
                setEndDate('');
            }
        }
    };

    const handleEndDateChange = (nextYmd) => {
        if (!nextYmd) {
            setEndDate('');
            setError('');
            return;
        }

        if (startDate && rangeIncludesBlocked(startDate, nextYmd)) {
            setError(t('bookingRangeBlocked'));
            return;
        }

        setError('');
        setEndDate(nextYmd);
    };

    const handleCalendarPick = (ymd) => {
        if (!ymd || isBlocked(ymd)) return;
        setError('');

        if (bookingRate === 'week') {
            setStartDate(ymd);
            return;
        }
        if (bookingRate === 'month') {
            setStartDate(ymd);
            return;
        }

        if (!startDate || (startDate && endDate)) {
            setStartDate(ymd);
            setEndDate('');
            return;
        }

        const startLocal = parseYmd(startDate);
        const pickedLocal = parseYmd(ymd);
        if (!startLocal || !pickedLocal) {
            setStartDate(ymd);
            setEndDate('');
            return;
        }

        if (pickedLocal <= startLocal) {
            setStartDate(ymd);
            setEndDate('');
            return;
        }

        if (rangeIncludesBlocked(startDate, ymd)) {
            setError(t('bookingRangeBlocked'));
            return;
        }

        setEndDate(ymd);
    };

    const renderMonth = (monthDate) => {
        const monthLabel = monthDate.toLocaleString(calendarLocale, { month: 'long', year: 'numeric' });
        const weekday = language === 'da'
            ? ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']
            : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const calendarWeeks = getCalendarWeeksForMonth(monthDate);
        const startLocal = startDate ? parseYmd(startDate) : null;
        const endLocal = endDate ? parseYmd(endDate) : null;

        return (
            <div className="w-full min-w-0">
                <p className="text-xs font-semibold text-gray-700 mb-2 capitalize">{monthLabel}</p>
                <div className="grid grid-cols-[2.5rem_repeat(7,minmax(0,1fr))] gap-1 text-center text-[11px] text-gray-500 mb-1">
                    <div className="flex items-center justify-center rounded bg-gray-100 font-semibold text-gray-600">{t('bookingCalendarWeekNumber')}</div>
                    {weekday.map((w) => <div key={w}>{w}</div>)}
                </div>
                <div className="space-y-1">
                    {calendarWeeks.map((week) => (
                        <div key={`${monthLabel}-week-${week.weekNumber}-${formatDateOnlyInput(week.days[0]?.date)}`} className="grid grid-cols-[2.5rem_repeat(7,minmax(0,1fr))] gap-1">
                            <div className="flex h-8 items-center justify-center rounded-md bg-gray-100 text-xs font-semibold text-gray-600">
                                {week.weekNumber}
                            </div>
                            {week.days.map(({ date, inMonth }) => {
                                const ymd = toYmd(date);
                                const blocked = inMonth && isBlocked(ymd);

                                const isStart = startDate === ymd;
                                const isEnd = endDate === ymd;
                                const inRange = Boolean(startLocal && endLocal && date >= startLocal && date <= endLocal);

                                const baseClass = 'flex h-8 w-full min-w-[2rem] items-center justify-center rounded-md border text-xs font-medium leading-none transition-colors';
                                const stateClass = !inMonth
                                    ? 'bg-gray-50 border-gray-100 text-gray-300'
                                    : blocked
                                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed line-through'
                                        : (isStart || isEnd)
                                            ? 'bg-green-600 border-green-600 text-white'
                                            : inRange
                                                ? 'bg-green-50 border-green-200 text-green-800'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-green-400';

                                return (
                                    <button
                                        key={ymd}
                                        type="button"
                                        disabled={!inMonth || blocked}
                                        onClick={() => handleCalendarPick(ymd)}
                                        className={`${baseClass} ${stateClass}`}
                                        title={blocked ? 'Not available' : 'Available'}
                                    >
                                        {date.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const loadFestivalSuggestions = async () => {
        if (!startDate || !endDate) {
            setFestivalSuggestions([]);
            setFestivalSuggestionsError('');
            setShowFestivalSuggestions(false);
            return;
        }

        setIsLoadingFestivalSuggestions(true);
        setFestivalSuggestionsError('');
        setShowFestivalSuggestions(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/caravans?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
            if (!response.ok) {
                throw new Error(t('bookingFestivalSuggestionsError'));
            }

            const data = await response.json();
            const suggestions = (Array.isArray(data) ? data : [])
                .filter((item) => Number(item?.caravan_id) !== Number(caravan?.caravan_id))
                .filter((item) => isFestivalAvailable(item));
            setFestivalSuggestions(suggestions);
        } catch (err) {
            setFestivalSuggestions([]);
            setFestivalSuggestionsError(err?.message || t('bookingFestivalSuggestionsError'));
        } finally {
            setIsLoadingFestivalSuggestions(false);
        }
    };

    const handleBookSuggestedCaravan = (suggestion) => {
        const nextCaravanId = Number(suggestion?.caravan_id ?? suggestion?.caravanId);
        if (!Number.isFinite(nextCaravanId) || nextCaravanId <= 0) {
            return;
        }
        setSelectedFestivalSuggestionId(null);
        onNavigate({ page: 'booking', caravanId: nextCaravanId, startDate, endDate });
    };

    const handleBookingSubmit = async () => {
        if (isSubmitting) {
            return;
        }
        if (!isAvailable) {
            setError('Cannot book unavailable dates.');
            return;
        }
        const normalizedDoorstepAddress = (doorstepDeliveryAddress || '').trim();
        if (effectiveDoorstepEnabled && !normalizedDoorstepAddress) {
            setError(t('bookingDeliveryAddressRequired'));
            return;
        }
        const normalizedRemarks = (remarks || '').trim();
        if (!normalizedRemarks) {
            setError(t('bookingRemarksRequired'));
            return;
        }
        if (!festivalAvailable) {
            const wantsFestivalBooking = window.confirm(t('bookingFestivalPrompt'));
            if (wantsFestivalBooking) {
                await loadFestivalSuggestions();
                return;
            }
        }
        setError('');
        const startAt = `${startDate}T${startTime || '00:00'}:00`;
        const endAt = `${endDate}T${endTime || '00:00'}:00`;
        const bookingDto = {
            renterId: user.userId,
            caravanId: caravan.caravan_id,
            startDate: startAt,
            endDate: endAt,
            totalPrice: totalCost,
            pricingMode: quotePricingMode,
            remarks: normalizedRemarks,
            doorstepEnabled: effectiveDoorstepEnabled,
            doorstepDeliveryAddress: effectiveDoorstepEnabled ? normalizedDoorstepAddress : null,
            addonGasSelected: Boolean(selectedAddons.gas),
            addonSideMirrorsSelected: Boolean(selectedAddons.sideMirrors),
            addonTentSetupSelected: Boolean(selectedAddons.tentSetup),
            addonAdapter613Selected: Boolean(selectedAddons.adapter613),
            addonCleaningSelected: Boolean(selectedAddons.cleaning),
            addonToiletEmptyingSelected: Boolean(selectedAddons.toiletEmptying),
            addonTentRentalSelected: Boolean(selectedAddons.tentRental),
            addonTentDryingSelected: Boolean(selectedAddons.tentDrying)
        };
        setIsSubmitting(true);
        try {
            const createdBooking = await onCreateBooking(bookingDto);
            onNavigate({
                page: 'booking-success',
                caravanName: caravan.name,
                ownerContactNumber: createdBooking?.ownerContactNumber ?? createdBooking?.OwnerContactNumber ?? ''
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!caravan) return <div className="text-center py-20">Loading booking details...</div>;

    return (
        <>
        <ActionLoaderOverlay
            show={isSubmitting}
            title="Please wait..."
            subtitle="Confirming booking and sending email notifications."
        />
        <SuggestedCaravanDetailModal
            caravanId={selectedFestivalSuggestionId}
            startDate={startDate}
            endDate={endDate}
            isOpen={Boolean(selectedFestivalSuggestionId)}
            onClose={() => setSelectedFestivalSuggestionId(null)}
            onBook={handleBookSuggestedCaravan}
        />
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-center mb-8">{t('bookingTitle')}</h1>
            <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 bg-white p-8 rounded-lg shadow-xl">
                <div>
                    <h2 className="text-2xl font-bold">{caravan.name}</h2>
                    <p className="text-gray-600">{caravan.location}</p>
                    <div className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${festivalAvailable ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                        {festivalAvailable ? t('cardFestivalAvailable') : t('cardFestivalUnavailable')}
                    </div>
                    <CaravanWeightSummary caravan={caravan} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" />
                    <img
                        src={getCaravanMainImage(caravan)}
                        alt={caravan.name}
                        className="rounded-lg mt-4 w-full object-cover h-48"
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                    />
                </div>
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold border-b pb-2">{t('bookingTrip')}</h3>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('bookingRate')}</label>
                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={() => setBookingRate('day')}
                                className={`px-4 py-2 rounded-full border transition-colors ${bookingRate === 'day' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'}`}
                            >
                                {t('bookingPerDay')}
                            </button>
                            <button
                                onClick={() => setBookingRate('week')}
                                className={`px-4 py-2 rounded-full border transition-colors ${bookingRate === 'week' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'}`}
                            >
                                {t('bookingPerWeek')}
                            </button>
                            {longTermEnabled && (
                                <button
                                    onClick={() => setBookingRate('month')}
                                    className={`px-4 py-2 rounded-full border transition-colors ${bookingRate === 'month' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'}`}
                                >
                                    {language === 'da' ? 'Pr. mÃ¥ned' : 'Per Month'}
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            {bookingRate === 'month'
                                ? (language === 'da'
                                    ? `Minimum ${minimumBookingMonths} mÃ¥ned(er). TilfÃ¸j ekstra dage, sÃ¥ udfyldes slutdatoen automatisk.`
                                    : `Minimum ${minimumBookingMonths} month(s). Add extra days and the end date is calculated automatically.`)
                                : t('bookingWeekNote')}
                        </p>
                    </div>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('detailCheckIn')}</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => handleStartDateChange(e.target.value)}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('bookingCheckInTime')}</label>
                                <input
                                    type="time"
                                    value={startTime}
                                    readOnly
                                    disabled
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                                />
                            </div>
                        </div>

                        {bookingRate === 'day' ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{t('detailCheckOut')}</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        min={startDate || undefined}
                                        onChange={(e) => handleEndDateChange(e.target.value)}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{t('bookingCheckOutTime')}</label>
                                    <input
                                        type="time"
                                        value={endTime}
                                        readOnly
                                        disabled
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>
                            </div>
                        ) : bookingRate === 'week' ? (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('bookingWeeks')}</label>
                                        <select value={weeks} onChange={e => setWeeks(Number(e.target.value))} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2">
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(value => (
                                                <option key={value} value={value}>{value} {value > 1 ? t('bookingWeeksPlural') : t('bookingWeek')}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('bookingExtraDays')}</label>
                                        <select value={extraDays} onChange={e => setExtraDays(Number(e.target.value))} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2">
                                            {[0, 1, 2, 3, 4, 5, 6].map(value => (
                                                <option key={value} value={value}>
                                                    {value} {value === 1 ? t('bookingDay') : t('bookingDays')}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('detailCheckOut')}</label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            readOnly
                                            disabled
                                            className="mt-1 block w-full border-gray-200 rounded-md shadow-sm p-2 bg-gray-50 text-gray-600"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{t('bookingCheckOutTime')}</label>
                                    <input
                                        type="time"
                                        value={endTime}
                                        readOnly
                                        disabled
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{language === 'da' ? 'MÃ¥neder' : 'Months'}</label>
                                        <select
                                            value={months}
                                            onChange={e => setMonths(Number(e.target.value))}
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                                        >
                                            {Array.from({ length: Math.max(24, minimumBookingMonths + 11) - minimumBookingMonths + 1 }, (_, index) => minimumBookingMonths + index).map((value) => (
                                                <option key={value} value={value}>
                                                    {value} {language === 'da' ? (value === 1 ? 'mÃ¥ned' : 'mÃ¥neder') : `month${value === 1 ? '' : 's'}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('bookingExtraDays')}</label>
                                        <select value={extraDays} onChange={e => setExtraDays(Number(e.target.value))} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2">
                                            {Array.from({ length: 31 }, (_, index) => index).map(value => (
                                                <option key={value} value={value}>
                                                    {value} {value === 1 ? t('bookingDay') : t('bookingDays')}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">{t('detailCheckOut')}</label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            readOnly
                                            disabled
                                            className="mt-1 block w-full border-gray-200 rounded-md shadow-sm p-2 bg-gray-50 text-gray-600"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{t('bookingCheckOutTime')}</label>
                                    <input
                                        type="time"
                                        value={endTime}
                                        readOnly
                                        disabled
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>
                            </>
                        )}

                        <div className="rounded-lg border border-gray-200 bg-stone-50 p-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <p className="text-sm font-semibold text-gray-800">{t('bookingCalendarTitle')}</p>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => shiftCalendarMonths(-1)}
                                        className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:border-green-400 hover:text-green-700"
                                        aria-label={t('bookingCalendarPrevious')}
                                    >
                                        {language === 'da' ? 'Forrige' : 'Prev'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => shiftCalendarMonths(1)}
                                        className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:border-green-400 hover:text-green-700"
                                        aria-label={t('bookingCalendarNext')}
                                    >
                                        {language === 'da' ? 'Næste' : 'Next'}
                                    </button>
                                    {isLoadingBlockedRanges ? (
                                        <p className="text-xs text-gray-500">{t('bookingCalendarLoading')}</p>
                                    ) : (
                                        <p className="text-xs text-gray-500">{t('bookingBlockedCount').replace('{count}', blockedRanges.length)}</p>
                                    )}
                                </div>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">{t('bookingCalendarHint')}</p>
                            <div className="mt-3 grid grid-cols-1 gap-4">
                                {renderMonth(calendarMonth1)}
                                {renderMonth(calendarMonth2)}
                            </div>
                            <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-sm bg-white border border-gray-200" />
                                    <span>{t('bookingCalendarAvailable')}</span>
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-sm bg-gray-100 border border-gray-200" />
                                    <span>{t('bookingCalendarUnavailable')}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    {isDoorstepAvailable && (
                        <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                            <div>
                                <p className="font-medium text-gray-800">{t('bookingDoorstepTitle')}</p>
                                <p className="text-xs text-gray-500">{t('bookingDoorstepSubtitle')}</p>
                            </div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={doorstepEnabled}
                                    onChange={(e) => setDoorstepEnabled(e.target.checked)}
                                    className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                />
                                {doorstepEnabled ? t('bookingRequested') : t('bookingOptional')}
                            </label>
                        </div>
                    )}
                    {effectiveDoorstepEnabled && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('bookingDeliveryAddress')}</label>
                            <textarea
                                rows="3"
                                value={doorstepDeliveryAddress}
                                onChange={(e) => setDoorstepDeliveryAddress(e.target.value)}
                                placeholder=""
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                            />
                        </div>
                    )}
                    {availableAddons.length > 0 && (
                        <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                            <div>
                                <p className="font-medium text-gray-800">{t('bookingOptionalAddonsTitle')}</p>
                                <p className="text-xs text-gray-500">{t('bookingOptionalAddonsSubtitle')}</p>
                            </div>
                            <div className="space-y-2">
                                {availableAddons.map((addon) => (
                                    <label key={addon.key} className="flex items-center justify-between gap-3 rounded-md border border-gray-100 px-3 py-2">
                                        <span className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(selectedAddons[addon.key])}
                                                onChange={(e) => setSelectedAddons((prev) => ({ ...prev, [addon.key]: e.target.checked }))}
                                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                            />
                                            {addon.label}
                                        </span>
                                        <span className="text-sm font-medium text-gray-700">{formatInr(addon.price)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('bookingRemarksLabel')}</label>
                        <textarea
                            rows="3"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder=""
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2"
                        />
                    </div>

                    <div className="h-6 text-center">
                        {isChecking && <p className="text-blue-600 text-sm">{t('bookingChecking')}</p>}
                        {error && <p className="text-red-500 text-sm">{error}</p>}
                        {isAvailable === true && !isChecking && <p className="text-green-600 text-sm font-semibold">{t('bookingAvailable')}</p>}
                    </div>

                    {showFestivalSuggestions && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                            <h4 className="text-base font-semibold text-amber-900">{t('bookingFestivalSuggestionsTitle')}</h4>
                            <p className="mt-1 text-sm text-amber-800">{t('bookingFestivalSuggestionsSubtitle')}</p>
                            {isLoadingFestivalSuggestions && <p className="mt-3 text-sm text-amber-800">{t('bookingFestivalSuggestionsLoading')}</p>}
                            {!isLoadingFestivalSuggestions && festivalSuggestionsError && <p className="mt-3 text-sm text-red-600">{festivalSuggestionsError}</p>}
                            {!isLoadingFestivalSuggestions && !festivalSuggestionsError && festivalSuggestions.length === 0 && (
                                <p className="mt-3 text-sm text-amber-800">{t('bookingFestivalSuggestionsEmpty')}</p>
                            )}
                            {!isLoadingFestivalSuggestions && !festivalSuggestionsError && festivalSuggestions.length > 0 && (
                                <div className="mt-4 space-y-3">
                                    {festivalSuggestions.map((suggestion) => {
                                        const suggestionDailyPrice = Number(suggestion?.price_per_day || 0);
                                        const suggestionWeeklyPrice = Number(suggestion?.price_per_week || (suggestionDailyPrice * 7));
                                        return (
                                            <div key={suggestion.caravan_id} className="rounded-xl border border-amber-200 bg-white p-4 shadow-sm">
                                                <div className="grid gap-4 md:grid-cols-[11rem_minmax(0,1fr)]">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedFestivalSuggestionId(suggestion.caravan_id)}
                                                        className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 md:w-44"
                                                    >
                                                        <img
                                                            src={getCaravanMainImage(suggestion)}
                                                            alt={suggestion.name}
                                                            className="h-28 w-full object-cover"
                                                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                        />
                                                    </button>
                                                    <div className="min-w-0">
                                                        <div className="min-w-0 break-words">
                                                            <p className="text-base font-semibold text-gray-900">{suggestion.name}</p>
                                                            <p className="text-sm text-gray-600">{suggestion.location || t('cardLocationUnavailable')}</p>
                                                            <p className="mt-1 text-sm text-gray-700">
                                                                {formatInr(suggestionDailyPrice)} / {t('bookingPerDay')} · {formatInr(suggestionWeeklyPrice)} / {t('bookingPerWeek')}
                                                            </p>
                                                        </div>
                                                        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedFestivalSuggestionId(suggestion.caravan_id)}
                                                                className={`${SECONDARY_BUTTON_CLASS} w-full sm:w-auto`}
                                                            >
                                                                {t('bookingFestivalSuggestionView')}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleBookSuggestedCaravan(suggestion)}
                                                                className={`${PRIMARY_BUTTON_CLASS} w-full sm:w-auto`}
                                                            >
                                                                {t('bookingFestivalSuggestionCta')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {nights > 0 && quote && (
                        <div className="border-t pt-4 space-y-2">
                            {isMonthQuote ? (
                                <div className="flex justify-between">
                                    <span>{formatPricingDurationDisplay(startDate, endDate, 'month', language)}</span>
                                    <span>DKK {Number(rentalAmount).toLocaleString('da-DK')}</span>
                                </div>
                            ) : !isWeekQuote ? (
                                <div className="flex justify-between">
                                    <span>{nights} {nightLabel}</span>
                                    <span>DKK {Number(rentalAmount).toLocaleString('da-DK')}</span>
                                </div>
                            ) : (
                                <div className="flex justify-between">
                                    <span>
                                        {quoteWeeks > 0 ? `${quoteWeeks} ${quoteWeekLabel}` : ''}
                                        {remainingDaysAfterWeekRate > 0 ? `${quoteWeeks > 0 ? ' + ' : ''}${remainingDaysAfterWeekRate} ${weekDayLabel}` : ''}
                                    </span>
                                    <span>DKK {Number(rentalAmount).toLocaleString('da-DK')}</span>
                                </div>
                            )}
                            <p className="text-xs text-gray-500">
                                <span className="font-semibold">{t('bookingCalculation')}:</span> {pricingCalculationText}
                            </p>
                            {isMonthQuote && (
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{language === 'da' ? 'MÃ¥nedsbetaling' : 'Monthly Charge'} ({quoteMonths} {language === 'da' ? (quoteMonths === 1 ? 'mÃ¥ned' : 'mÃ¥neder') : `month${quoteMonths === 1 ? '' : 's'}`})</span>
                                    <span>DKK {monthlyComponentAmount.toLocaleString('da-DK')}</span>
                                </div>
                            )}
                            {isMonthQuote && quoteWeeks > 0 && (
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{t('bookingWeeklyCharge')} ({quoteWeeks} {quoteWeekLabel})</span>
                                    <span>DKK {weeklyComponentAmount.toLocaleString('da-DK')}</span>
                                </div>
                            )}
                            {isMonthQuote && quoteDays > 0 && (
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{t('bookingDailyCharge')} ({quoteDays} {quoteDays === 1 ? t('bookingDay') : t('bookingDays')})</span>
                                    <span>DKK {dailyComponentAmount.toLocaleString('da-DK')}</span>
                                </div>
                            )}
                            {isWeekQuote && quoteWeeks > 0 && (
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{t('bookingWeeklyCharge')} ({quoteWeeks} {quoteWeekLabel})</span>
                                    <span>DKK {weeklyComponentAmount.toLocaleString('da-DK')}</span>
                                </div>
                            )}
                            {isWeekQuote && remainingDaysAfterWeekRate > 0 && (
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{t('bookingDailyCharge')} ({remainingDaysAfterWeekRate} {weekDayLabel})</span>
                                    <span>DKK {dailyComponentAmount.toLocaleString('da-DK')}</span>
                                </div>
                            )}
                            {isWeekQuote && (
                                <p className="text-xs text-gray-500">
                                    <span className="font-semibold">{t('bookingBifurcation')}:</span> {pricingBifurcationFormulaText}
                                </p>
                            )}
                            {isMonthQuote && (
                                <p className="text-xs text-gray-500">
                                    <span className="font-semibold">{t('bookingBifurcation')}:</span> {pricingBifurcationFormulaText}
                                </p>
                            )}
                            {!!(quote.breakdown && quote.breakdown.length > 0) && (
                                <div className="text-xs text-gray-500 space-y-1">
                                    {quote.breakdown.map((line) => (
                                        <p key={line.season_code}>
                                            {line.season_name}: {formatQuoteLineDuration(line)} (DKK {Number(line.amount || 0).toLocaleString('da-DK')})
                                        </p>
                                    ))}
                                </div>
                            )}
                            {effectiveDoorstepEnabled && (
                                quote?.doorstep_price_pending ? (
                                    <div className="flex justify-between text-amber-700">
                                        <span>{t('bookingDoorstepLine')}</span>
                                        <span>{t('bookingDoorstepPendingLine')}</span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between">
                                        <span>
                                            {t('bookingDoorstepLine')} ({doorstepDistanceKm.toLocaleString('da-DK', { maximumFractionDigits: 2 })} km x {doorstepChargeMultiplier} x {doorstepRatePerKm.toLocaleString('da-DK', { maximumFractionDigits: 2 })}/km)
                                        </span>
                                        <span>DKK {doorstepFee.toLocaleString('da-DK')}</span>
                                    </div>
                                )
                            )}
                            {quoteAddons.map((addon) => (
                                <div key={addon.key} className="flex justify-between">
                                    <span>{addon.label}</span>
                                    <span>{formatInr(addon.price)}</span>
                                </div>
                            ))}
                            {quoteAddons.length > 0 && (
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{t('bookingAddonsTotal')}</span>
                                    <span>{formatInr(addonAmount)}</span>
                                </div>
                            )}
                            {effectiveDoorstepEnabled && !quote?.doorstep_price_pending && (
                                <p className="text-xs text-amber-700">
                                    {t('bookingDoorstepChargeNote').replace('{km}', doorstepChargeableDistanceKm.toLocaleString('da-DK', { maximumFractionDigits: 2 }))}
                                </p>
                            )}
                            {currentPaymentDueAmount > 0 && (depositAmount > 0 || isFullPaymentDueNow) && (
                                <div className="flex justify-between text-amber-700">
                                    <span>{isFullPaymentDueNow ? t('bookingPaymentDueFull') : t('bookingPaymentDueDeposit')}</span>
                                    <span>DKK {currentPaymentDueAmount.toLocaleString('da-DK')}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                                <span>{depositAmount > 0 ? t('bookingTotalDueWithDeposit') : t('bookingTotal')}</span>
                                <span>DKK {(depositAmount > 0 ? totalDueIncludingDeposit : totalCost).toLocaleString('da-DK')}</span>
                            </div>
                            {depositAmount > 0 && (
                                <p className="text-xs text-amber-700">{t('bookingPendingUntilDeposit')}</p>
                            )}
                        </div>
                    )}
                    <button onClick={handleBookingSubmit} className="w-full mt-4 bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed" disabled={!isAvailable || isChecking || nights <= 0 || !quote || isSubmitting}>
                        {isSubmitting ? t('bookingConfirming') : (isChecking ? t('bookingCheckingShort') : t('bookingConfirm'))}
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

const OwnerAvailabilityPage = ({ user, onNavigate }) => {
    const { t } = useContext(LanguageContext);
    const [ownerCaravans, setOwnerCaravans] = useState([]);
    const [selectedCaravanId, setSelectedCaravanId] = useState('');
    const [blocks, setBlocks] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const [seasons, setSeasons] = useState([]);
    const [seasonPrices, setSeasonPrices] = useState([]);
    const [seasonPriceError, setSeasonPriceError] = useState('');
    const [seasonPriceSuccess, setSeasonPriceSuccess] = useState('');
    const [isSeasonSaving, setIsSeasonSaving] = useState(false);
    const [basePricing, setBasePricing] = useState({
        price_per_day: '',
        price_per_week: '',
        deposit_amount: '',
        self_pick_enabled: true,
        doorstep_available: false,
        doorstep_rate_per_km: '',
        long_term_enabled: false,
        minimum_booking_months: '',
        price_per_month: '',
        addon_gas_price: '',
        addon_side_mirrors_price: '',
        addon_tent_setup_price: '',
        addon_adapter_6_13_price: '',
        addon_cleaning_price: '',
        no_smoking: false
    });
    const [listingUpdateError, setListingUpdateError] = useState('');
    const [listingUpdateSuccess, setListingUpdateSuccess] = useState('');
    const [isListingSaving, setIsListingSaving] = useState(false);
    const [isListingStatusSaving, setIsListingStatusSaving] = useState(false);
    const [updateImageFiles, setUpdateImageFiles] = useState([]);
    const [isImageUpdating, setIsImageUpdating] = useState(false);
    const [isImageRemoving, setIsImageRemoving] = useState(false);
    const [removingImageUrl, setRemovingImageUrl] = useState('');
    const [mainImageUpdatingUrl, setMainImageUpdatingUrl] = useState('');
    const [ownerAmenityOptions, setOwnerAmenityOptions] = useState([]);
    const [ownerTypeAmenityMappings, setOwnerTypeAmenityMappings] = useState([]);
    const [ownerAmenityIds, setOwnerAmenityIds] = useState([]);
    const [amenityUpdateError, setAmenityUpdateError] = useState('');
    const [amenityUpdateSuccess, setAmenityUpdateSuccess] = useState('');
    const [isAmenitySaving, setIsAmenitySaving] = useState(false);

    const getSelectedCaravan = useCallback(() => ownerCaravans.find(c => String(c.caravan_id) === String(selectedCaravanId)), [ownerCaravans, selectedCaravanId]);
    const getSanitizedCaravanImageUrls = useCallback((caravan) => {
        const raw = Array.isArray(caravan?.images) ? caravan.images : [];
        const normalized = raw
            .filter((item) => typeof item === 'string' && item.trim() !== '')
            .map((item) => item.trim());
        return [...new Set(normalized)];
    }, []);

    const loadOwnerAmenityMaster = useCallback(async () => {
        try {
            const [amenitiesRes, mappingsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/amenities`),
                fetch(`${API_BASE_URL}/api/caravantypes/amenity-mappings`)
            ]);
            if (!amenitiesRes.ok) throw new Error('Failed to load amenities');
            if (!mappingsRes.ok) throw new Error('Failed to load type-amenity mappings');

            const amenitiesData = await amenitiesRes.json();
            const mappingsData = await mappingsRes.json();
            setOwnerAmenityOptions(Array.isArray(amenitiesData) ? amenitiesData : []);
            setOwnerTypeAmenityMappings(Array.isArray(mappingsData) ? mappingsData : []);
        } catch (err) {
            setAmenityUpdateError(err.message || 'Failed to load amenities.');
        }
    }, []);

    const loadOwnerSelectedAmenityIds = useCallback(async (caravanId) => {
        if (!caravanId || !user?.userId) {
            setOwnerAmenityIds([]);
            return;
        }

        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${caravanId}/owner-amenities?ownerId=${user.userId}`);
            if (!res.ok) throw new Error('Failed to load selected amenities');
            const data = await res.json();
            const normalized = Array.isArray(data)
                ? data.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)
                : [];
            setOwnerAmenityIds([...new Set(normalized)]);
        } catch (err) {
            setAmenityUpdateError(err.message || 'Failed to load selected amenities.');
            setOwnerAmenityIds([]);
        }
    }, [user?.userId]);

    const loadSeasonMaster = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/seasons`);
            if (!res.ok) throw new Error('Failed to load seasons');
            const data = await res.json();
            setSeasons(data);
        } catch (err) {
            setSeasonPriceError(err.message || 'Failed to load seasons.');
        }
    }, []);

    const normalizeSeasonPrices = useCallback((rawPrices) => {
        const selectedCaravan = getSelectedCaravan();
        const fallbackDay = Number(selectedCaravan?.price_per_day || 0);
        const fallbackWeek = Number(selectedCaravan?.price_per_week || (fallbackDay * 7));
        const fallbackMonth = selectedCaravan?.price_per_month ?? selectedCaravan?.pricePerMonth ?? '';
        const incoming = rawPrices || [];

        return seasons.map((season) => {
            const existing = incoming.find(x => Number(x.season_id) === Number(season.season_id));
            return {
                season_id: Number(season.season_id),
                season_code: season.season_code,
                season_name: season.season_name,
                price_per_day: Number(existing?.price_per_day ?? fallbackDay),
                price_per_week: Number(existing?.price_per_week ?? fallbackWeek),
                price_per_month: existing?.price_per_month ?? fallbackMonth ?? ''
            };
        });
    }, [seasons, getSelectedCaravan]);

    const loadSeasonPrices = useCallback(async (caravanId) => {
        if (!caravanId || seasons.length === 0) return;
        setSeasonPriceError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/seasons/caravans/${caravanId}/prices`);
            if (!res.ok) throw new Error('Failed to load seasonal prices');
            const data = await res.json();
            setSeasonPrices(normalizeSeasonPrices(data));
        } catch (err) {
            setSeasonPriceError(err.message || 'Failed to load seasonal prices.');
            setSeasonPrices(normalizeSeasonPrices([]));
        }
    }, [seasons, normalizeSeasonPrices]);

    const loadOwnerCaravans = useCallback(async () => {
        if (!user?.userId) return;
        setIsLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/owner/${user.userId}`);
            if (!res.ok) throw new Error('Failed to load owner caravans');
            const data = await res.json();
            setOwnerCaravans(data);
            if (data.length > 0) {
                setSelectedCaravanId(String(data[0].caravan_id));
            }
        } catch (err) {
            setError(err.message || 'Failed to load owner caravans.');
        } finally {
            setIsLoading(false);
        }
    }, [user?.userId]);

    const loadBlocks = useCallback(async (caravanId) => {
        if (!caravanId || !user?.userId) return;
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${caravanId}/unavailability?ownerId=${user.userId}`);
            if (!res.ok) throw new Error('Failed to load unavailable dates');
            const data = await res.json();
            setBlocks(data);
        } catch (err) {
            setError(err.message || 'Failed to load unavailable dates.');
        }
    }, [user?.userId]);

    useEffect(() => {
        loadOwnerCaravans();
        loadSeasonMaster();
        loadOwnerAmenityMaster();
    }, [loadOwnerCaravans, loadSeasonMaster, loadOwnerAmenityMaster]);

    useEffect(() => {
        if (selectedCaravanId) {
            loadBlocks(selectedCaravanId);
            loadSeasonPrices(selectedCaravanId);
            loadOwnerSelectedAmenityIds(selectedCaravanId);
        } else {
            setBlocks([]);
            setSeasonPrices([]);
            setOwnerAmenityIds([]);
        }
    }, [selectedCaravanId, loadBlocks, loadSeasonPrices, loadOwnerSelectedAmenityIds]);

    useEffect(() => {
        const selected = getSelectedCaravan();
        if (!selected) return;
        const selfPickEnabled = Boolean(selected.self_pick_enabled ?? selected.selfPickEnabled ?? true);
        const isDoorstepAvailable = Boolean(selected.doorstep_available ?? selected.doorstepAvailable ?? false);
        const doorstepRatePerKm = selected.doorstep_rate_per_km ?? selected.doorstepRatePerKm;
        const minimumBookingMonths = selected.minimum_booking_months ?? selected.minimumBookingMonths;
        const pricePerMonth = selected.price_per_month ?? selected.pricePerMonth;
        const addonGasPrice = selected.addon_gas_price;
        const addonSideMirrorsPrice = selected.addon_side_mirrors_price;
        const addonTentSetupPrice = selected.addon_tent_setup_price;
        const addonAdapter613Price = selected.addon_adapter_6_13_price;
        const addonCleaningPrice = selected.addon_cleaning_price;
        const noSmoking = Boolean(selected.no_smoking ?? selected.noSmoking ?? false);
        setBasePricing({
            price_per_day: String(Number(selected.price_per_day || 0)),
            price_per_week: String(Number(selected.price_per_week || 0)),
            deposit_amount: String(Number(selected.deposit_amount || 0)),
            self_pick_enabled: selfPickEnabled,
            doorstep_available: isDoorstepAvailable,
            doorstep_rate_per_km: doorstepRatePerKm === null || doorstepRatePerKm === undefined ? '' : String(Number(doorstepRatePerKm)),
            long_term_enabled: Boolean(selected.long_term_enabled ?? selected.longTermEnabled ?? false),
            minimum_booking_months: minimumBookingMonths === null || minimumBookingMonths === undefined ? '' : String(Number(minimumBookingMonths)),
            price_per_month: pricePerMonth === null || pricePerMonth === undefined ? '' : String(Number(pricePerMonth)),
            addon_gas_price: addonGasPrice === null || addonGasPrice === undefined ? '' : String(Number(addonGasPrice)),
            addon_side_mirrors_price: addonSideMirrorsPrice === null || addonSideMirrorsPrice === undefined ? '' : String(Number(addonSideMirrorsPrice)),
            addon_tent_setup_price: addonTentSetupPrice === null || addonTentSetupPrice === undefined ? '' : String(Number(addonTentSetupPrice)),
            addon_adapter_6_13_price: addonAdapter613Price === null || addonAdapter613Price === undefined ? '' : String(Number(addonAdapter613Price)),
            addon_cleaning_price: addonCleaningPrice === null || addonCleaningPrice === undefined ? '' : String(Number(addonCleaningPrice)),
            no_smoking: noSmoking
        });
    }, [getSelectedCaravan]);

    useEffect(() => {
        setListingUpdateError('');
        setListingUpdateSuccess('');
        setSeasonPriceError('');
        setSeasonPriceSuccess('');
        setAmenityUpdateError('');
        setAmenityUpdateSuccess('');
        setUpdateImageFiles([]);
        setRemovingImageUrl('');
        setMainImageUpdatingUrl('');
    }, [selectedCaravanId]);

    const hasOverlap = useCallback((start, end, existingBlocks) => {
        const startAt = new Date(start);
        const endAt = new Date(end);
        return existingBlocks.some((b) => {
            const blockStart = new Date(b.start_date || b.startDate);
            const blockEnd = new Date(b.end_date || b.endDate);
            return blockStart < endAt && blockEnd > startAt;
        });
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        if (!selectedCaravanId) {
            setError(t('ownerNoCaravans'));
            return;
        }
        if (!startDate || !endDate || new Date(startDate) >= new Date(endDate)) {
            setError(t('ownerDateValidation'));
            return;
        }
        if (hasOverlap(startDate, endDate, blocks)) {
            setError(t('ownerBlockConflict'));
            return;
        }

        setIsSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/unavailability`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    owner_id: user.userId,
                    start_date: startDate,
                    end_date: endDate,
                    reason: reason.trim() || null
                })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to block dates');
            }
            setStartDate('');
            setEndDate('');
            setReason('');
            await loadBlocks(selectedCaravanId);
        } catch (err) {
            setError(err.message || 'Failed to block dates.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteBlock = async (unavailabilityId) => {
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/unavailability/${unavailabilityId}?ownerId=${user.userId}`, {
                method: 'DELETE'
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to remove blocked dates');
            }
            await loadBlocks(selectedCaravanId);
        } catch (err) {
            setError(err.message || 'Failed to remove blocked dates.');
        }
    };

    const handleSeasonPriceInputChange = (seasonId, field, value) => {
        setSeasonPriceSuccess('');
        setSeasonPrices(prev => prev.map(item => (
            Number(item.season_id) === Number(seasonId)
                ? { ...item, [field]: value === '' ? '' : Number(value) }
                : item
        )));
    };

    const refreshOwnerCaravans = useCallback(async () => {
        if (!user?.userId) return;
        const res = await fetch(`${API_BASE_URL}/api/caravans/owner/${user.userId}`);
        if (!res.ok) throw new Error('Failed to refresh caravans');
        const data = await res.json();
        setOwnerCaravans(data);
    }, [user?.userId]);

    const handleToggleListingStatus = async () => {
        if (!selectedCaravanId || !user?.userId) return;
        if (ownerRelistBlockedByAdmin) {
            setListingUpdateError('This caravan was unlisted by admin due to complaint action. Only admin can relist it.');
            setListingUpdateSuccess('');
            return;
        }
        setListingUpdateError('');
        setListingUpdateSuccess('');
        setIsListingStatusSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/listing-status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    actorUserId: user.userId,
                    isListed: !selectedIsListed
                })
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to update listing status.');
            }
            await refreshOwnerCaravans();
            setListingUpdateSuccess(!selectedIsListed ? 'Caravan relisted successfully.' : 'Caravan unlisted successfully.');
        } catch (err) {
            setListingUpdateError(err.message || 'Failed to update listing status.');
        } finally {
            setIsListingStatusSaving(false);
        }
    };

    const handleSaveBasePricing = async () => {
        if (!selectedCaravanId) return;
        const pricePerDay = Number(basePricing.price_per_day);
        const pricePerWeek = Number(basePricing.price_per_week);
        const depositAmount = Number(basePricing.deposit_amount || 0);
        const selfPickEnabled = selectedIsStationary ? false : Boolean(basePricing.self_pick_enabled);
        const doorstepAvailable = selectedIsStationary
            ? false
            : (!selfPickEnabled ? true : Boolean(basePricing.doorstep_available));
        const doorstepRatePerKm = basePricing.doorstep_rate_per_km === '' ? null : Number(basePricing.doorstep_rate_per_km);
        const longTermEnabled = Boolean(basePricing.long_term_enabled);
        const minimumBookingMonths = basePricing.minimum_booking_months === '' ? null : Number(basePricing.minimum_booking_months);
        const pricePerMonth = basePricing.price_per_month === '' ? null : Number(basePricing.price_per_month);
        const addonGasPrice = basePricing.addon_gas_price === '' ? null : Number(basePricing.addon_gas_price);
        const addonSideMirrorsPrice = basePricing.addon_side_mirrors_price === '' ? null : Number(basePricing.addon_side_mirrors_price);
        const addonTentSetupPrice = basePricing.addon_tent_setup_price === '' ? null : Number(basePricing.addon_tent_setup_price);
        const addonAdapter613Price = basePricing.addon_adapter_6_13_price === '' ? null : Number(basePricing.addon_adapter_6_13_price);
        const addonCleaningPrice = basePricing.addon_cleaning_price === '' ? null : Number(basePricing.addon_cleaning_price);

        if (Number.isNaN(pricePerDay) || Number.isNaN(pricePerWeek) || Number.isNaN(depositAmount) || pricePerDay < 0 || pricePerWeek < 0 || depositAmount < 0) {
            setListingUpdateError('Please enter valid non-negative pricing values.');
            setListingUpdateSuccess('');
            return;
        }
        if (pricePerWeek > (pricePerDay * 7)) {
            setListingUpdateError('Price per week must be <= 7 x price per day.');
            setListingUpdateSuccess('');
            return;
        }
        if (doorstepAvailable) {
            if (doorstepRatePerKm === null || Number.isNaN(doorstepRatePerKm) || doorstepRatePerKm <= 0) {
                setListingUpdateError('Doorstep rate per km must be greater than 0 when doorstep delivery is enabled.');
                setListingUpdateSuccess('');
                return;
            }
        } else if (doorstepRatePerKm !== null && (Number.isNaN(doorstepRatePerKm) || doorstepRatePerKm < 0)) {
            setListingUpdateError('Doorstep rate per km must be non-negative.');
            setListingUpdateSuccess('');
            return;
        }
        if (longTermEnabled) {
            if (!Number.isFinite(minimumBookingMonths) || minimumBookingMonths <= 0) {
                setListingUpdateError('Minimum booking months must be greater than 0 when long-term booking is enabled.');
                setListingUpdateSuccess('');
                return;
            }
            if (!Number.isFinite(pricePerMonth) || pricePerMonth <= 0) {
                setListingUpdateError('Price per month must be greater than 0 when long-term booking is enabled.');
                setListingUpdateSuccess('');
                return;
            }
        }
        if ([addonGasPrice, addonSideMirrorsPrice, addonTentSetupPrice, addonAdapter613Price, addonCleaningPrice].some((value) => value !== null && (Number.isNaN(value) || value < 0))) {
            setListingUpdateError('Add-on prices must be blank or non-negative.');
            setListingUpdateSuccess('');
            return;
        }

        setListingUpdateError('');
        setListingUpdateSuccess('');
        setIsListingSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/owner-pricing`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    owner_id: user.userId,
                    price_per_day: pricePerDay,
                    price_per_week: pricePerWeek,
                    deposit_amount: depositAmount,
                    self_pick_enabled: selfPickEnabled,
                    doorstep_available: doorstepAvailable,
                    doorstep_rate_per_km: doorstepAvailable ? doorstepRatePerKm : null,
                    long_term_enabled: longTermEnabled,
                    minimum_booking_months: longTermEnabled ? minimumBookingMonths : null,
                    price_per_month: longTermEnabled ? pricePerMonth : null,
                    addon_gas_price: addonGasPrice,
                    addon_side_mirrors_price: addonSideMirrorsPrice,
                    addon_tent_setup_price: addonTentSetupPrice,
                    addon_adapter_6_13_price: addonAdapter613Price,
                    addon_cleaning_price: addonCleaningPrice,
                    no_smoking: Boolean(basePricing.no_smoking)
                })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to update pricing.');
            }
            await refreshOwnerCaravans();
            await loadSeasonPrices(selectedCaravanId);
            setListingUpdateSuccess('Pricing and doorstep settings updated successfully.');
        } catch (err) {
            setListingUpdateError(err.message || 'Failed to update pricing.');
        } finally {
            setIsListingSaving(false);
        }
    };

    const handleUpdateImages = async () => {
        if (!selectedCaravanId || updateImageFiles.length === 0) return;
        setListingUpdateError('');
        setListingUpdateSuccess('');
        const selected = getSelectedCaravan();
        const existingImageCount = getSanitizedCaravanImageUrls(selected).length;
        if ((existingImageCount + updateImageFiles.length) > MAX_CARAVAN_IMAGES) {
            setListingUpdateError(`You can have a maximum of ${MAX_CARAVAN_IMAGES} images. Remove some existing photos first.`);
            return;
        }
        const imageSizeError = buildImageSizeError(updateImageFiles);
        if (imageSizeError) {
            setListingUpdateError(imageSizeError);
            return;
        }
        setIsImageUpdating(true);
        try {
            const formData = new FormData();
            formData.append('ownerId', String(user.userId));
            updateImageFiles.forEach((file) => formData.append('files', file));

            const res = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/images`, {
                method: 'POST',
                body: formData
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to upload images.');
            }
            setUpdateImageFiles([]);
            await refreshOwnerCaravans();
            setListingUpdateSuccess('Images uploaded successfully.');
        } catch (err) {
            setListingUpdateError(err.message || 'Failed to upload images.');
        } finally {
            setIsImageUpdating(false);
        }
    };

    const handleUpdateImageChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > MAX_CARAVAN_IMAGES) {
            setListingUpdateError(`You can upload a maximum of ${MAX_CARAVAN_IMAGES} images.`);
            setUpdateImageFiles([]);
            return;
        }
        const imageSizeError = buildImageSizeError(files);
        if (imageSizeError) {
            setListingUpdateError(imageSizeError);
            setUpdateImageFiles([]);
            return;
        }
        const selected = getSelectedCaravan();
        const existingImageCount = getSanitizedCaravanImageUrls(selected).length;
        if ((existingImageCount + files.length) > MAX_CARAVAN_IMAGES) {
            setListingUpdateError(`You can have a maximum of ${MAX_CARAVAN_IMAGES} images. Remove some existing photos first.`);
            setUpdateImageFiles([]);
            return;
        }
        setListingUpdateError('');
        setUpdateImageFiles(files);
    };

    const handleRemoveExistingImage = async (imageUrl) => {
        if (!selectedCaravanId || !user?.userId || !imageUrl) return;

        const confirmed = window.confirm('Are you sure you want to remove this photo?');
        if (!confirmed) return;

        setListingUpdateError('');
        setListingUpdateSuccess('');
        setIsImageRemoving(true);
        setRemovingImageUrl(imageUrl);
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/images/remove`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ownerId: user.userId,
                    imageUrl
                })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to remove image.');
            }
            setUpdateImageFiles([]);
            await refreshOwnerCaravans();
            setListingUpdateSuccess('Photo removed successfully.');
        } catch (err) {
            setListingUpdateError(err.message || 'Failed to remove image.');
        } finally {
            setIsImageRemoving(false);
            setRemovingImageUrl('');
        }
    };

    const handleSetMainImage = async (imageUrl) => {
        if (!selectedCaravanId || !user?.userId || !imageUrl) return;

        setListingUpdateError('');
        setListingUpdateSuccess('');
        setMainImageUpdatingUrl(imageUrl);
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/images/main`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ownerId: user.userId,
                    imageUrl
                })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to update main image.');
            }
            await refreshOwnerCaravans();
            setListingUpdateSuccess('Main photo updated successfully.');
        } catch (err) {
            setListingUpdateError(err.message || 'Failed to update main image.');
        } finally {
            setMainImageUpdatingUrl('');
        }
    };

    const toggleOwnerAmenity = (amenityId, checked) => {
        const parsedId = Number(amenityId);
        if (!Number.isFinite(parsedId) || parsedId <= 0) return;
        setOwnerAmenityIds((prev) => (
            checked
                ? [...new Set([...prev, parsedId])]
                : prev.filter((item) => Number(item) !== parsedId)
        ));
    };

    const handleSaveOwnerAmenities = async () => {
        if (!selectedCaravanId || !user?.userId) return;
        setAmenityUpdateError('');
        setAmenityUpdateSuccess('');

        const selected = getSelectedCaravan();
        const selectedTypeId = Number(selected?.type_id);
        const allowedAmenityIdSet = new Set(
            (ownerTypeAmenityMappings || [])
                .filter((mapRow) => Number(mapRow.type_id) === selectedTypeId)
                .map((mapRow) => Number(mapRow.amenity_id))
        );
        const selectedAmenityIds = (ownerAmenityIds || [])
            .map((item) => Number(item))
            .filter((item) => Number.isFinite(item) && item > 0);

        if (selectedAmenityIds.length === 0) {
            setAmenityUpdateError('At least one amenity is required.');
            return;
        }
        if (selectedAmenityIds.some((id) => !allowedAmenityIdSet.has(id))) {
            setAmenityUpdateError('One or more selected amenities are not allowed for this caravan type.');
            return;
        }

        setIsAmenitySaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/caravans/${selectedCaravanId}/owner-amenities`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    owner_id: user.userId,
                    amenities: [...new Set(selectedAmenityIds)]
                })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to update amenities.');
            }
            const updatedIds = await res.json();
            const normalizedUpdated = Array.isArray(updatedIds)
                ? updatedIds.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)
                : [];
            setOwnerAmenityIds([...new Set(normalizedUpdated)]);
            await refreshOwnerCaravans();
            setAmenityUpdateSuccess('Amenities updated successfully.');
        } catch (err) {
            setAmenityUpdateError(err.message || 'Failed to update amenities.');
        } finally {
            setIsAmenitySaving(false);
        }
    };

    const handleSaveSeasonPrices = async () => {
        if (!selectedCaravanId) return;
        const longTermEnabled = Boolean(basePricing.long_term_enabled);
        const invalid = seasonPrices.some(row => {
            const day = Number(row.price_per_day);
            const week = Number(row.price_per_week);
            const month = row.price_per_month === '' ? null : Number(row.price_per_month);
            return Number.isNaN(day)
                || Number.isNaN(week)
                || day <= 0
                || week <= 0
                || week > (day * 7)
                || (longTermEnabled && (month === null || Number.isNaN(month) || month <= 0));
        });
        if (invalid) {
            setSeasonPriceError(longTermEnabled
                ? 'Each season must have valid day, week, and month prices. Week price must be <= 7 x day price.'
                : 'Each season must have prices greater than 0 and week price must be <= 7 x day price.');
            setSeasonPriceSuccess('');
            return;
        }

        setSeasonPriceError('');
        setSeasonPriceSuccess('');
        setIsSeasonSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/seasons/caravans/${selectedCaravanId}/prices`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    owner_id: user.userId,
                    prices: seasonPrices.map(row => ({
                        season_id: Number(row.season_id),
                        price_per_day: Number(row.price_per_day),
                        price_per_week: Number(row.price_per_week),
                        price_per_month: longTermEnabled ? Number(row.price_per_month) : null
                    }))
                })
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to save seasonal prices');
            }

            const data = await res.json();
            setSeasonPrices(normalizeSeasonPrices(data));
            setSeasonPriceSuccess('Seasonal prices updated for the selected caravan.');
        } catch (err) {
            setSeasonPriceError(err.message || 'Failed to save seasonal prices.');
            setSeasonPriceSuccess('');
        } finally {
            setIsSeasonSaving(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
    };

    const selectedCaravan = getSelectedCaravan();
    const selectedIsStationary = isStationaryCaravan(selectedCaravan);
    const selectedCaravanTypeId = Number(selectedCaravan?.type_id);
    const allowedAmenityIdSet = new Set(
        (ownerTypeAmenityMappings || [])
            .filter((mapRow) => Number(mapRow.type_id) === selectedCaravanTypeId)
            .map((mapRow) => Number(mapRow.amenity_id))
    );
    const availableOwnerAmenities = (ownerAmenityOptions || [])
        .filter((item) => allowedAmenityIdSet.has(Number(item.amenity_id)));
    const existingImageUrls = getSanitizedCaravanImageUrls(selectedCaravan);
    const selectedApprovalRaw = selectedCaravan?.isApproved ?? selectedCaravan?.is_approved;
    const selectedIsApproved = selectedApprovalRaw === true
        || selectedApprovalRaw === 1
        || selectedApprovalRaw === '1'
        || String(selectedApprovalRaw).toLowerCase() === 'true';
    const selectedApprovalStatus = String(selectedCaravan?.approvalStatus ?? selectedCaravan?.approval_status ?? '').trim().toLowerCase();
    const selectedApprovalRemarks = String(selectedCaravan?.approvalRemarks ?? selectedCaravan?.approval_remarks ?? '').trim();
    const selectedIsRejected = selectedApprovalStatus === 'rejected' || (!selectedIsApproved && !!selectedApprovalRemarks);
    const selectedStatusLabel = selectedIsApproved ? 'Approved' : (selectedIsRejected ? 'Rejected' : 'Pending Approval');
    const selectedStatusClass = selectedIsApproved ? 'text-green-700' : (selectedIsRejected ? 'text-red-700' : 'text-amber-700');
    const selectedListedRaw = selectedCaravan?.is_listed ?? selectedCaravan?.isListed ?? true;
    const selectedIsListed = isTruthyValue(selectedListedRaw);
    const selectedAdminListingLockRaw = selectedCaravan?.admin_listing_lock ?? selectedCaravan?.adminListingLock ?? false;
    const selectedAdminListingLock = isTruthyValue(selectedAdminListingLockRaw);
    const ownerRelistBlockedByAdmin = !selectedIsListed && selectedAdminListingLock;

    useEffect(() => {
        if (selectedIsStationary) {
            setBasePricing((prev) => {
                if (!prev.self_pick_enabled && !prev.doorstep_available && prev.doorstep_rate_per_km === '') {
                    return prev;
                }

                return {
                    ...prev,
                    self_pick_enabled: false,
                    doorstep_available: false,
                    doorstep_rate_per_km: ''
                };
            });
            return;
        }

        if (!basePricing.self_pick_enabled && !basePricing.doorstep_available) {
            setBasePricing((prev) => ({
                ...prev,
                doorstep_available: true
            }));
        }
    }, [selectedIsStationary, basePricing.self_pick_enabled, basePricing.doorstep_available]);

    if (isLoading) {
        return <div className="container mx-auto px-4 py-8">Loading...</div>;
    }

    if (ownerCaravans.length === 0) {
        return (
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl font-bold mb-4">{t('ownerCalendarTitle')}</h1>
                <p className="text-gray-600">{t('ownerNoCaravans')}</p>
                <button onClick={() => onNavigate({ page: 'become-owner' })} className="mt-4 bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700">
                    {t('navBecomeOwner')}
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-2">{t('ownerCalendarTitle')}</h1>
            <p className="text-gray-600 mb-6">{t('ownerCalendarSubtitle')}</p>

            {error && <p className="text-red-600 mb-4">{error}</p>}

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Manage Caravan</h2>
                <label htmlFor="owner-caravan-manage" className="block text-sm font-medium text-gray-700 mb-1">{t('ownerSelectCaravan')}</label>
                <select
                    id="owner-caravan-manage"
                    value={selectedCaravanId}
                    onChange={(e) => setSelectedCaravanId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                    {ownerCaravans.map((c) => (
                        <option key={c.caravan_id} value={c.caravan_id}>
                            {c.name} ({c.location})
                        </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">All updates below apply only to the selected caravan.</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-1">My Listing Updates</h2>
                <p className="text-sm text-gray-600 mb-3">
                    Managing: <span className="font-medium">{selectedCaravan?.name || '-'}</span>
                </p>
                <p className="text-sm mb-3">
                    Current Status:{' '}
                    <span className={`font-semibold ${selectedStatusClass}`}>
                        {selectedStatusLabel}
                    </span>
                </p>
                {selectedIsRejected && selectedApprovalRemarks && (
                    <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Admin Remarks</p>
                        <p className="mt-1 text-sm text-red-800 whitespace-pre-wrap">{selectedApprovalRemarks}</p>
                    </div>
                )}
                <p className="text-sm mb-3">
                    Listing Status:{' '}
                    <span className={`font-semibold ${selectedIsListed ? 'text-green-700' : 'text-red-700'}`}>
                        {selectedIsListed ? 'Listed' : 'Unlisted'}
                    </span>
                </p>
                <CaravanWeightSummary caravan={selectedCaravan} className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2" />
                <button
                    onClick={handleToggleListingStatus}
                    disabled={isListingStatusSaving || !selectedCaravanId || ownerRelistBlockedByAdmin}
                    className={`${selectedIsListed ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-full disabled:bg-gray-400 mb-3`}
                >
                    {isListingStatusSaving
                        ? 'Saving...'
                        : (selectedIsListed
                            ? 'Unlist Caravan'
                            : (ownerRelistBlockedByAdmin ? 'Relist Restricted by Admin' : 'Relist Caravan'))}
                </button>
                {ownerRelistBlockedByAdmin && (
                    <p className="text-sm text-red-600 mb-3">Admin unlisted this caravan after a customer complaint. Only admin can relist it.</p>
                )}
                {listingUpdateError && <p className="text-red-600 mb-3">{listingUpdateError}</p>}
                {listingUpdateSuccess && <p className="text-green-700 mb-3">{listingUpdateSuccess}</p>}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <InputField
                        label="Price per Day (DKK)"
                        name="owner-price-day"
                        type="number"
                        value={basePricing.price_per_day}
                        onChange={(e) => setBasePricing(prev => ({ ...prev, price_per_day: e.target.value }))}
                    />
                    <InputField
                        label="Price per Week (DKK)"
                        name="owner-price-week"
                        type="number"
                        value={basePricing.price_per_week}
                        onChange={(e) => setBasePricing(prev => ({ ...prev, price_per_week: e.target.value }))}
                    />
                    <InputField
                        label="Deposit (DKK)"
                        name="owner-price-deposit"
                        type="number"
                        value={basePricing.deposit_amount}
                        onChange={(e) => setBasePricing(prev => ({ ...prev, deposit_amount: e.target.value }))}
                    />
                </div>
                <div className="border rounded-md p-4 bg-gray-50 mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Add-ons</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                        <InputField
                            label="Gas Add-on (DKK)"
                            name="owner-addon-gas"
                            type="number"
                            required={false}
                            min="0"
                            step="0.01"
                            value={basePricing.addon_gas_price}
                            onChange={(e) => setBasePricing(prev => ({ ...prev, addon_gas_price: e.target.value }))}
                        />
                        <InputField
                            label="Side Mirrors (DKK)"
                            name="owner-addon-side-mirrors"
                            type="number"
                            required={false}
                            min="0"
                            step="0.01"
                            value={basePricing.addon_side_mirrors_price}
                            onChange={(e) => setBasePricing(prev => ({ ...prev, addon_side_mirrors_price: e.target.value }))}
                        />
                        <InputField
                            label="Raising the Tent (DKK)"
                            name="owner-addon-tent-setup"
                            type="number"
                            required={false}
                            min="0"
                            step="0.01"
                            value={basePricing.addon_tent_setup_price}
                            onChange={(e) => setBasePricing(prev => ({ ...prev, addon_tent_setup_price: e.target.value }))}
                        />
                        <InputField
                            label="Adapter 6/13 Pole (DKK)"
                            name="owner-addon-adapter-613"
                            type="number"
                            required={false}
                            min="0"
                            step="0.01"
                            value={basePricing.addon_adapter_6_13_price}
                            onChange={(e) => setBasePricing(prev => ({ ...prev, addon_adapter_6_13_price: e.target.value }))}
                        />
                        <InputField
                            label="Cleaning (DKK)"
                            name="owner-addon-cleaning"
                            type="number"
                            required={false}
                            min="0"
                            step="0.01"
                            value={basePricing.addon_cleaning_price}
                            onChange={(e) => setBasePricing(prev => ({ ...prev, addon_cleaning_price: e.target.value }))}
                        />
                    </div>
                </div>
                <div className="border rounded-md p-4 bg-gray-50 mb-4 space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">Settings</h3>
                    <div className="rounded-md border border-gray-200 p-3 bg-white">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                checked={Boolean(basePricing.self_pick_enabled)}
                                onChange={(e) => setBasePricing(prev => ({
                                    ...prev,
                                    self_pick_enabled: e.target.checked
                                }))}
                                disabled={selectedIsStationary}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            Self pickup enabled
                        </label>
                    </div>
                    <div className="rounded-md border border-gray-200 p-3 bg-white">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                checked={Boolean(!selectedIsStationary && (!basePricing.self_pick_enabled || basePricing.doorstep_available))}
                                onChange={(e) => setBasePricing(prev => ({
                                    ...prev,
                                    doorstep_available: e.target.checked,
                                    doorstep_rate_per_km: e.target.checked ? prev.doorstep_rate_per_km : ''
                                }))}
                                disabled={selectedIsStationary || !basePricing.self_pick_enabled}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            Offer doorstep delivery option for this caravan
                        </label>
                    </div>
                    <div className="rounded-md border border-gray-200 p-3 bg-white">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                checked={Boolean(basePricing.long_term_enabled)}
                                onChange={(e) => setBasePricing(prev => ({ ...prev, long_term_enabled: e.target.checked }))}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            Long-term booking enabled
                        </label>
                    </div>
                    {Boolean(basePricing.long_term_enabled) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <InputField
                                label="Minimum Booking Months"
                                name="owner-min-booking-months"
                                type="number"
                                required={true}
                                min="1"
                                value={basePricing.minimum_booking_months}
                                onChange={(e) => setBasePricing(prev => ({ ...prev, minimum_booking_months: e.target.value }))}
                            />
                            <InputField
                                label="Price per Month (DKK)"
                                name="owner-price-month"
                                type="number"
                                required={true}
                                min="0"
                                step="0.01"
                                value={basePricing.price_per_month}
                                onChange={(e) => setBasePricing(prev => ({ ...prev, price_per_month: e.target.value }))}
                            />
                        </div>
                    )}
                    <div className="rounded-md border border-gray-200 p-3 bg-white">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                checked={Boolean(basePricing.no_smoking)}
                                onChange={(e) => setBasePricing(prev => ({ ...prev, no_smoking: e.target.checked }))}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            No Smoking
                        </label>
                    </div>
                    {Boolean(!selectedIsStationary && (!basePricing.self_pick_enabled || basePricing.doorstep_available)) && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <InputField
                                label="Doorstep Rate per km (DKK)"
                                name="owner-doorstep-rate"
                                type="number"
                                min="0"
                                step="0.01"
                                value={basePricing.doorstep_rate_per_km}
                                onChange={(e) => setBasePricing(prev => ({ ...prev, doorstep_rate_per_km: e.target.value }))}
                            />
                        </div>
                    )}
                </div>
                <button
                    onClick={handleSaveBasePricing}
                    disabled={isListingSaving || !selectedCaravanId}
                    className="bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700 disabled:bg-gray-400"
                >
                    {isListingSaving ? 'Saving...' : 'Update Pricing/Settings'}
                </button>

                <div className="mt-5 border-t pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Manage Amenities</h3>
                    {amenityUpdateError && <p className="text-red-600 mb-2 text-sm">{amenityUpdateError}</p>}
                    {amenityUpdateSuccess && <p className="text-green-700 mb-2 text-sm">{amenityUpdateSuccess}</p>}
                    {availableOwnerAmenities.length === 0 ? (
                        <p className="text-xs text-gray-500">No amenities are configured for this caravan type.</p>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border border-gray-200 rounded-md p-3 bg-gray-50">
                            {availableOwnerAmenities.map((amenity) => {
                                const amenityId = Number(amenity.amenity_id);
                                return (
                                    <label key={amenityId} className="flex items-center gap-2 text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            checked={ownerAmenityIds.includes(amenityId)}
                                            onChange={(e) => toggleOwnerAmenity(amenityId, e.target.checked)}
                                            className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                        />
                                        <span>{amenity.amenity_name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    <button
                        onClick={handleSaveOwnerAmenities}
                        disabled={isAmenitySaving || !selectedCaravanId || availableOwnerAmenities.length === 0}
                        className="mt-3 bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700 disabled:bg-gray-400"
                    >
                        {isAmenitySaving ? 'Saving...' : 'Update Amenities'}
                    </button>
                </div>

                <div className="mt-5 border-t pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Existing Photos ({existingImageUrls.length})</h3>
                    {existingImageUrls.length === 0 ? (
                        <p className="text-xs text-gray-500">No photos uploaded yet.</p>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {existingImageUrls.map((imgUrl, idx) => {
                                const imageSource = resolveImageUrl(imgUrl) || imgUrl || PLACEHOLDER_IMG;
                                const isRemovingThis = isImageRemoving && removingImageUrl === imgUrl;
                                const isMainImage = idx === 0;
                                const isUpdatingMainImage = mainImageUpdatingUrl === imgUrl;
                                return (
                                    <div key={`${imgUrl}-${idx}`} className="border rounded-md p-2 bg-gray-50">
                                        <img
                                            src={imageSource}
                                            alt={`Caravan ${idx + 1}`}
                                            className="w-full h-24 object-cover rounded-md"
                                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                        />
                                        <div className="mt-2 flex items-center justify-between gap-2">
                                            <span className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${isMainImage ? 'text-green-700' : 'text-gray-500'}`}>
                                                {isMainImage ? 'Main Photo' : 'Photo'}
                                            </span>
                                            {!isMainImage && (
                                                <button
                                                    onClick={() => handleSetMainImage(imgUrl)}
                                                    disabled={Boolean(mainImageUpdatingUrl) || isImageRemoving || isImageUpdating}
                                                    className="text-xs font-semibold text-green-700 hover:text-green-800 disabled:text-gray-400"
                                                >
                                                    {isUpdatingMainImage ? 'Saving...' : 'Set as Main'}
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleRemoveExistingImage(imgUrl)}
                                            disabled={isImageRemoving || isImageUpdating || Boolean(mainImageUpdatingUrl)}
                                            className="mt-2 w-full bg-red-600 text-white text-xs py-1 rounded hover:bg-red-700 disabled:bg-gray-400"
                                        >
                                            {isRemovingThis ? 'Removing...' : 'Remove'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="mt-5 border-t pt-4">
                    <label htmlFor="owner-update-images" className="block text-sm font-medium text-gray-700 mb-1">Upload New Photos (max 10 total, 10 MB each)</label>
                    <LocalizedFileInput
                        id="owner-update-images"
                        selectedFiles={updateImageFiles}
                        accept="image/png,image/jpeg,image/webp"
                        multiple
                        onChange={handleUpdateImageChange}
                        className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">{existingImageUrls.length} existing + {updateImageFiles.length} new file(s) selected</p>
                    <button
                        onClick={handleUpdateImages}
                        disabled={isImageUpdating || updateImageFiles.length === 0 || !selectedCaravanId}
                        className="mt-3 bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700 disabled:bg-gray-400"
                    >
                        {isImageUpdating ? 'Uploading...' : 'Upload Photos'}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-1">{t('ownerCalendarTitle')}</h2>
                <p className="text-sm text-gray-600 mb-4">
                    Managing: <span className="font-medium">{selectedCaravan?.name || '-'}</span>
                </p>
                <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="owner-start" className="block text-sm font-medium text-gray-700 mb-1">{t('ownerStartDate')}</label>
                            <input
                                id="owner-start"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="owner-end" className="block text-sm font-medium text-gray-700 mb-1">{t('ownerEndDate')}</label>
                            <input
                                id="owner-end"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="owner-reason" className="block text-sm font-medium text-gray-700 mb-1">{t('ownerReason')}</label>
                        <input
                            id="owner-reason"
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder=""
                        />
                    </div>
                    <button type="submit" disabled={isSaving} className="bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : t('ownerSaveBlock')}
                    </button>
                </form>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-1">Seasonal Pricing</h2>
                <p className="text-sm text-gray-600 mb-3">
                    Managing: <span className="font-medium">{selectedCaravan?.name || '-'}</span>
                </p>
                {seasonPriceError && <p className="text-red-600 mb-3">{seasonPriceError}</p>}
                {seasonPriceSuccess && <p className="text-green-700 mb-3">{seasonPriceSuccess}</p>}
                <div className="space-y-3">
                    {seasonPrices.map((row) => (
                        <div key={row.season_id} className={`grid grid-cols-1 gap-3 border rounded-md p-3 ${basePricing.long_term_enabled ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                            <p className="font-semibold text-gray-800 flex items-center">{row.season_name}</p>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Price per Day (DKK)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={row.price_per_day}
                                    onChange={(e) => handleSeasonPriceInputChange(row.season_id, 'price_per_day', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Price per Week (DKK)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={row.price_per_week}
                                    onChange={(e) => handleSeasonPriceInputChange(row.season_id, 'price_per_week', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            {Boolean(basePricing.long_term_enabled) && (
                                <div>
                                    <label className="block text-xs text-gray-600 mb-1">Price per Month (DKK)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.price_per_month}
                                        onChange={(e) => handleSeasonPriceInputChange(row.season_id, 'price_per_month', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <button
                    onClick={handleSaveSeasonPrices}
                    disabled={isSeasonSaving || seasonPrices.length === 0}
                    className="mt-4 bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700 disabled:bg-gray-400"
                >
                    {isSeasonSaving ? 'Saving...' : 'Save Seasonal Prices'}
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">{t('ownerExistingBlocks')}</h2>
                {blocks.length === 0 ? (
                    <p className="text-gray-500">No blocked dates yet.</p>
                ) : (
                    <div className="space-y-3">
                        {blocks.map((block) => (
                            <div key={block.unavailability_id || block.unavailabilityId} className="flex items-center justify-between border rounded-md p-3">
                                <div>
                                    <p className="font-medium">
                                        {formatDate(block.start_date || block.startDate)} - {formatDate(block.end_date || block.endDate)}
                                    </p>
                                    {!!(block.reason) && <p className="text-sm text-gray-600">{block.reason}</p>}
                                </div>
                                <button
                                    onClick={() => handleDeleteBlock(block.unavailability_id || block.unavailabilityId)}
                                    className="text-red-600 hover:text-red-800 font-semibold"
                                >
                                    {t('ownerDeleteBlock')}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const OwnerBusinessPage = ({ user, onNavigate }) => {
    const { language } = useContext(LanguageContext);
    const [dashboard, setDashboard] = useState({ summary: null, bookings: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [workingBookingId, setWorkingBookingId] = useState(null);
    const [expandedCheckoutBookingId, setExpandedCheckoutBookingId] = useState(null);
    const [damageCategories, setDamageCategories] = useState([]);
    const [damageCategoriesLoading, setDamageCategoriesLoading] = useState(false);
    const [checkoutForms, setCheckoutForms] = useState({});
    const [checkInPhotoFilesByBookingId, setCheckInPhotoFilesByBookingId] = useState({});
    const [checkInPhotoUrlsByBookingId, setCheckInPhotoUrlsByBookingId] = useState({});
    const [, setAssessmentByBookingId] = useState({});
    const [imageSliderState, setImageSliderState] = useState({
        open: false,
        images: [],
        index: 0,
        title: ''
    });
    const MAX_CHECKIN_PHOTOS = 10;
    const MAX_CHECKIN_PHOTO_SIZE_MB = 8;
    const MIN_REQUIRED_CHECKIN_PHOTOS = 1;

    const fetchOwnerBusiness = useCallback(async () => {
        if (!user?.userId) return;
        setIsLoading(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/owner/${user.userId}/business`);
            if (!response.ok) {
                throw new Error('Could not fetch owner bookings and business summary.');
            }
            const data = await response.json();
            setDashboard({
                summary: data.summary || null,
                bookings: Array.isArray(data.bookings) ? data.bookings : []
            });
        } catch (err) {
            setError(err.message || 'Could not fetch owner business data.');
        } finally {
            setIsLoading(false);
        }
    }, [user?.userId]);

    useEffect(() => {
        fetchOwnerBusiness();
    }, [fetchOwnerBusiness]);

    useEffect(() => {
        const fetchDamageCategories = async () => {
            setDamageCategoriesLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/bookings/damage-categories`);
                if (!response.ok) {
                    throw new Error('Could not load damage categories.');
                }
                const data = await response.json();
                setDamageCategories(Array.isArray(data) ? data : []);
            } catch (err) {
                setActionError(err.message || 'Could not load damage categories.');
            } finally {
                setDamageCategoriesLoading(false);
            }
        };

        fetchDamageCategories();
    }, []);

    const fetchCheckInPhotosForBooking = useCallback(async (bookingId) => {
        if (!bookingId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/check-in-photos`);
            if (!response.ok) return;
            const data = await response.json();
            setCheckInPhotoUrlsByBookingId((prev) => ({
                ...prev,
                [bookingId]: Array.isArray(data) ? data.filter(Boolean) : []
            }));
        } catch {
            // Background fetch - ignore transient errors.
        }
    }, []);

    useEffect(() => {
        const bookingIdsToLoad = (dashboard.bookings || [])
            .map((booking) => booking?.bookingId)
            .filter((bookingId) => bookingId && !Object.prototype.hasOwnProperty.call(checkInPhotoUrlsByBookingId, bookingId));

        if (bookingIdsToLoad.length === 0) {
            return;
        }

        bookingIdsToLoad.forEach((bookingId) => {
            fetchCheckInPhotosForBooking(bookingId);
        });
    }, [dashboard.bookings, checkInPhotoUrlsByBookingId, fetchCheckInPhotosForBooking]);

    const openImageSlider = useCallback((images, startIndex = 0, title = 'Booking Images') => {
        const normalized = (Array.isArray(images) ? images : [])
            .map((img) => resolveImageUrl(img) || img)
            .filter(Boolean);

        if (normalized.length === 0) {
            setImageSliderState({
                open: true,
                images: [PLACEHOLDER_IMG],
                index: 0,
                title
            });
            return;
        }

        const safeIndex = Math.max(0, Math.min(startIndex, normalized.length - 1));
        setImageSliderState({
            open: true,
            images: normalized,
            index: safeIndex,
            title
        });
    }, []);

    const closeImageSlider = useCallback(() => {
        setImageSliderState((prev) => ({ ...prev, open: false }));
    }, []);

    const showPrevImage = useCallback(() => {
        setImageSliderState((prev) => {
            if (!prev.images.length) return prev;
            const nextIndex = (prev.index - 1 + prev.images.length) % prev.images.length;
            return { ...prev, index: nextIndex };
        });
    }, []);

    const showNextImage = useCallback(() => {
        setImageSliderState((prev) => {
            if (!prev.images.length) return prev;
            const nextIndex = (prev.index + 1) % prev.images.length;
            return { ...prev, index: nextIndex };
        });
    }, []);

    useEffect(() => {
        if (!imageSliderState.open) return;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeImageSlider();
            } else if (event.key === 'ArrowLeft') {
                showPrevImage();
            } else if (event.key === 'ArrowRight') {
                showNextImage();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [imageSliderState.open, closeImageSlider, showPrevImage, showNextImage]);

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('da-DK');
    };

    const getStatusBadgeClass = (status) => {
        if (status === 'confirmed') return 'bg-green-100 text-green-800';
        if (status === 'checked_in') return 'bg-blue-100 text-blue-800';
        if (status === 'checkout_pending_customer') return 'bg-orange-100 text-orange-800';
        if (status === 'checkout_pending_approval') return 'bg-amber-100 text-amber-800';
        if (status === 'checkout_rejected') return 'bg-red-100 text-red-800';
        if (status === 'checked_out' || status === 'completed') return 'bg-purple-100 text-purple-800';
        if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
        return 'bg-gray-200 text-gray-700';
    };

    const getInitialCheckoutForm = () => {
        const firstCategory = damageCategories[0];
        return {
            notes: '',
            customerReviewComment: '',
            photoUrls: [],
            newPhotoFiles: [],
            items: [
                {
                    categoryId: firstCategory ? String(firstCategory.categoryId) : '',
                    quantity: 1,
                    unitPrice: firstCategory ? String(firstCategory.standardPrice) : '',
                    notes: '',
                    adminRejectComment: ''
                }
            ]
        };
    };

    const mapAssessmentToCheckoutForm = (assessment) => {
        const mappedItems = (assessment?.items || []).map((item) => ({
            categoryId: String(item.categoryId || ''),
            quantity: Number(item.quantity || 1),
            unitPrice: String(item.unitPrice ?? ''),
            notes: item.notes || '',
            adminRejectComment: item.adminRejectComment || ''
        }));
        return {
            notes: assessment?.notes || '',
            customerReviewComment: assessment?.customerReviewComment || assessment?.CustomerReviewComment || assessment?.customer_review_comment || '',
            photoUrls: Array.isArray(assessment?.photoUrls) ? assessment.photoUrls.filter(Boolean) : [],
            newPhotoFiles: [],
            items: mappedItems.length > 0 ? mappedItems : getInitialCheckoutForm().items
        };
    };

    const openCheckoutForm = async (booking) => {
        const bookingId = booking.bookingId;
        setActionError('');
        setActionSuccess('');

        if (expandedCheckoutBookingId === bookingId) {
            setExpandedCheckoutBookingId(null);
            return;
        }

        setExpandedCheckoutBookingId(bookingId);

        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/damage-assessment`);
            if (response.ok) {
                const assessment = await response.json();
                setAssessmentByBookingId((prev) => ({ ...prev, [bookingId]: assessment }));
                setCheckoutForms((prev) => ({ ...prev, [bookingId]: mapAssessmentToCheckoutForm(assessment) }));
                return;
            }
        } catch (err) {
            setActionError(err.message || 'Could not load previous deduction form.');
        }

        setCheckoutForms((prev) => {
            if (prev[bookingId]) return prev;
            return { ...prev, [bookingId]: getInitialCheckoutForm() };
        });
    };

    const updateCheckoutNotes = (bookingId, value) => {
        setCheckoutForms((prev) => ({
            ...prev,
            [bookingId]: {
                ...(prev[bookingId] || getInitialCheckoutForm()),
                notes: value
            }
        }));
    };

    const updateCheckoutPhotoFiles = (bookingId, files) => {
        setCheckoutForms((prev) => {
            const form = prev[bookingId] || getInitialCheckoutForm();
            return {
                ...prev,
                [bookingId]: {
                    ...form,
                    newPhotoFiles: files
                }
            };
        });
    };

    const removeCheckoutPhotoUrl = (bookingId, photoIndex) => {
        setCheckoutForms((prev) => {
            const form = prev[bookingId] || getInitialCheckoutForm();
            const photoUrls = [...(form.photoUrls || [])];
            photoUrls.splice(photoIndex, 1);
            return {
                ...prev,
                [bookingId]: {
                    ...form,
                    photoUrls
                }
            };
        });
    };

    const updateCheckoutLine = (bookingId, index, field, value) => {
        setCheckoutForms((prev) => {
            const form = prev[bookingId] || getInitialCheckoutForm();
            const items = [...(form.items || [])];
            const current = { ...(items[index] || { categoryId: '', quantity: 1, unitPrice: '', notes: '' }) };
            current[field] = value;

            if (field === 'categoryId') {
                const selected = damageCategories.find((c) => String(c.categoryId) === String(value));
                if (selected && (current.unitPrice === '' || Number.isNaN(Number(current.unitPrice)))) {
                    current.unitPrice = String(selected.standardPrice);
                }
            }

            items[index] = current;
            return {
                ...prev,
                [bookingId]: {
                    ...form,
                    items
                }
            };
        });
    };

    const addCheckoutLine = (bookingId) => {
        setCheckoutForms((prev) => {
            const form = prev[bookingId] || getInitialCheckoutForm();
            const firstCategory = damageCategories[0];
            const items = [...(form.items || []), {
                categoryId: firstCategory ? String(firstCategory.categoryId) : '',
                quantity: 1,
                unitPrice: firstCategory ? String(firstCategory.standardPrice) : '',
                notes: '',
                adminRejectComment: ''
            }];
            return {
                ...prev,
                [bookingId]: {
                    ...form,
                    items
                }
            };
        });
    };

    const removeCheckoutLine = (bookingId, index) => {
        setCheckoutForms((prev) => {
            const form = prev[bookingId] || getInitialCheckoutForm();
            const items = [...(form.items || [])];
            items.splice(index, 1);
            return {
                ...prev,
                [bookingId]: {
                    ...form,
                    items: items.length > 0 ? items : [{
                        categoryId: '',
                        quantity: 1,
                        unitPrice: '',
                        notes: '',
                        adminRejectComment: ''
                    }]
                }
            };
        });
    };

    const getCheckoutDeductionTotal = (form) => {
        if (!form || !Array.isArray(form.items)) return 0;
        return form.items.reduce((sum, line) => {
            const quantity = Math.max(Number(line.quantity) || 0, 0);
            const unitPrice = Math.max(Number(line.unitPrice) || 0, 0);
            return sum + (quantity * unitPrice);
        }, 0);
    };

    const uploadDamagePhotos = async (bookingId, files) => {
        const formData = new FormData();
        formData.append('ownerId', String(user.userId));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/damage-photos`, {
            method: 'POST',
            body: formData
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Could not upload damage photos.');
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    };

    const updateCheckInPhotoFiles = (bookingId, fileList) => {
        const files = Array.from(fileList || []);
        const existingPhotoCount = Array.isArray(checkInPhotoUrlsByBookingId[bookingId])
            ? checkInPhotoUrlsByBookingId[bookingId].length
            : 0;

        if ((existingPhotoCount + files.length) > MAX_CHECKIN_PHOTOS) {
            setActionError(`You can keep at most ${MAX_CHECKIN_PHOTOS} check-in photos per booking.`);
            setActionSuccess('');
            return;
        }

        if (files.some((file) => file.size > (MAX_CHECKIN_PHOTO_SIZE_MB * 1024 * 1024))) {
            setActionError(`Each check-in photo must be ${MAX_CHECKIN_PHOTO_SIZE_MB} MB or smaller.`);
            setActionSuccess('');
            return;
        }

        setActionError('');
        setCheckInPhotoFilesByBookingId((prev) => ({ ...prev, [bookingId]: files }));
    };

    const uploadCheckInPhotos = async (bookingId, files) => {
        if (!Array.isArray(files) || files.length === 0) {
            return [];
        }

        const formData = new FormData();
        formData.append('ownerId', String(user.userId));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/check-in-photos`, {
            method: 'POST',
            body: formData
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Could not upload check-in photos.');
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    };

    const handleCheckIn = async (booking) => {
        if (!user?.userId) return;
        const bookingId = booking?.bookingId;
        if (!bookingId) return;
        const hasFullPayment = isBookingDepositPaid(booking) && isBookingRentPaid(booking);
        if (!hasFullPayment) {
            setActionError('Check-in is blocked until full rent + deposit is paid.');
            setActionSuccess('');
            return;
        }
        const existingCheckInPhotos = Array.isArray(checkInPhotoUrlsByBookingId[bookingId]) ? checkInPhotoUrlsByBookingId[bookingId] : [];
        const selectedCheckInFiles = Array.isArray(checkInPhotoFilesByBookingId[bookingId])
            ? checkInPhotoFilesByBookingId[bookingId]
            : [];
        const totalCheckInPhotos = existingCheckInPhotos.length + selectedCheckInFiles.length;
        if (totalCheckInPhotos < MIN_REQUIRED_CHECKIN_PHOTOS) {
            setActionError(`Upload at least ${MIN_REQUIRED_CHECKIN_PHOTOS} check-in photo before check-in.`);
            setActionSuccess('');
            return;
        }

        setWorkingBookingId(bookingId);
        setActionError('');
        setActionSuccess('');
        try {
            if (selectedCheckInFiles.length > 0) {
                const uploadedPhotoUrls = await uploadCheckInPhotos(bookingId, selectedCheckInFiles);
                setCheckInPhotoUrlsByBookingId((prev) => ({ ...prev, [bookingId]: uploadedPhotoUrls }));
                setCheckInPhotoFilesByBookingId((prev) => ({ ...prev, [bookingId]: [] }));
            }

            const actionUrl = `${API_BASE_URL}/api/bookings/${bookingId}/check-in`;
            const payload = JSON.stringify({ ownerId: user.userId });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Could not complete check-in.'));
            }
            setActionSuccess(`Check-in completed for ${formatBookingNumber(bookingId)}.`);
            await fetchOwnerBusiness();
        } catch (err) {
            setActionError(err.message || 'Could not complete check-in.');
        } finally {
            setWorkingBookingId(null);
        }
    };

    const handleCheckout = async (booking) => {
        if (!user?.userId) return;
        const form = checkoutForms[booking.bookingId] || getInitialCheckoutForm();
        const payloadItems = (form.items || [])
            .filter((line) => Number(line.categoryId) > 0)
            .map((line) => ({
                categoryId: Number(line.categoryId),
                quantity: Math.max(Number(line.quantity) || 1, 1),
                unitPrice: Math.max(Number(line.unitPrice) || 0, 0),
                notes: line.notes || ''
            }));
        const existingPhotoUrls = Array.isArray(form.photoUrls) ? form.photoUrls.filter(Boolean) : [];
        const newPhotoFiles = Array.isArray(form.newPhotoFiles) ? form.newPhotoFiles : [];

        setWorkingBookingId(booking.bookingId);
        setActionError('');
        setActionSuccess('');
        try {
            let uploadedPhotoUrls = [];
            if (newPhotoFiles.length > 0) {
                uploadedPhotoUrls = await uploadDamagePhotos(booking.bookingId, newPhotoFiles);
            }

            const actionUrl = `${API_BASE_URL}/api/bookings/${booking.bookingId}/check-out`;
            const payload = JSON.stringify({
                ownerId: user.userId,
                notes: form.notes || '',
                items: payloadItems,
                photoUrls: [...existingPhotoUrls, ...uploadedPhotoUrls]
            });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Could not submit deduction form.'));
            }

            const data = await response.json();
            setActionSuccess(`Deduction form submitted for customer review. Submitted amount: DKK ${Number(data.totalDeduction || 0).toLocaleString('da-DK')}.`);
            setExpandedCheckoutBookingId(null);
            await fetchOwnerBusiness();
        } catch (err) {
            setActionError(err.message || 'Could not submit deduction form.');
        } finally {
            setWorkingBookingId(null);
        }
    };

    return (
        <>
        <ActionLoaderOverlay
            show={workingBookingId !== null}
            title="Please wait..."
            subtitle="Completing action and sending booking emails."
        />
        {imageSliderState.open && (
            <div className="fixed inset-0 z-[10000] bg-black/90 px-4 py-6">
                <div className="mx-auto max-w-6xl h-full flex flex-col">
                    <div className="flex items-center justify-between text-white mb-3">
                        <h3 className="text-lg font-semibold truncate pr-4">{imageSliderState.title || 'Booking Images'}</h3>
                        <button
                            type="button"
                            onClick={closeImageSlider}
                            className="bg-white/20 hover:bg-white/30 rounded-md px-3 py-1 text-sm"
                        >
                            Close
                        </button>
                    </div>

                    <div className="flex-1 min-h-0 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={showPrevImage}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                            aria-label="Previous image"
                        >
                            &lt;
                        </button>
                        <img
                            src={imageSliderState.images[imageSliderState.index] || PLACEHOLDER_IMG}
                            alt={`Booking ${imageSliderState.index + 1}`}
                            className="max-h-full max-w-full object-contain rounded-lg border border-white/20"
                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                        />
                        <button
                            type="button"
                            onClick={showNextImage}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                            aria-label="Next image"
                        >
                            &gt;
                        </button>
                    </div>

                    <div className="mt-3">
                        <p className="text-xs text-white/80 mb-2">
                            {imageSliderState.index + 1} / {imageSliderState.images.length}
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {imageSliderState.images.map((img, idx) => (
                                <button
                                    key={`${img}-${idx}`}
                                    type="button"
                                    onClick={() => setImageSliderState((prev) => ({ ...prev, index: idx }))}
                                    className={`shrink-0 border rounded-md ${idx === imageSliderState.index ? 'border-green-400' : 'border-white/30'}`}
                                >
                                    <img
                                        src={img}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className="w-20 h-14 object-cover rounded-md"
                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )}
        <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
                <h1 className="text-3xl font-bold">Owner Bookings & Business</h1>
                <button
                    onClick={() => onNavigate({ page: 'owner-availability' })}
                    className="bg-green-600 text-white px-5 py-2 rounded-full hover:bg-green-700"
                >
                    Open Calendar Manager
                </button>
            </div>

            {isLoading && <p>Loading owner business data...</p>}
            {error && <p className="text-red-600 mb-3">{error}</p>}
            {actionError && <p className="text-red-600 mb-3">{actionError}</p>}
            {actionSuccess && <p className="text-green-700 mb-3">{actionSuccess}</p>}

            {!isLoading && !error && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-500">Total Bookings</p>
                            <p className="text-2xl font-bold">{Number(dashboard.summary?.totalBookings || 0)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-500">Confirmed</p>
                            <p className="text-2xl font-bold text-green-700">{Number(dashboard.summary?.confirmedBookings || 0)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-500">Checked-in</p>
                            <p className="text-2xl font-bold text-blue-700">{Number(dashboard.summary?.checkedInBookings || 0)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-500">Checked-out</p>
                            <p className="text-2xl font-bold text-purple-700">{Number(dashboard.summary?.checkedOutBookings || 0)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-500">Business Done</p>
                            <p className="text-2xl font-bold">{formatInr(dashboard.summary?.totalBusinessAmount || 0)}</p>
                        </div>
                    </div>

                    {dashboard.bookings.length === 0 ? (
                        <div className="text-center py-10 bg-gray-50 rounded-lg">
                            <h3 className="text-xl font-semibold">No bookings received yet</h3>
                            <p className="text-gray-600 mt-2">Once customers book your caravan, details will appear here.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {dashboard.bookings.map((booking) => {
                                const form = checkoutForms[booking.bookingId] || getInitialCheckoutForm();
                                const checkoutTotal = getCheckoutDeductionTotal(form);
                                const depositAmount = toAmount(booking.depositAmount || 0);
                                const customerReviewComment = String(
                                    (typeof form.customerReviewComment === 'string' && form.customerReviewComment.trim())
                                    || booking.customerReviewComment
                                    || booking.CustomerReviewComment
                                    || booking.customer_review_comment
                                    || ''
                                ).trim();
                                const pricingModeLabel = getPricingModeLabel(booking.pricingMode, language);
                                const rentalAmount = toAmount(booking.rentalAmount ?? booking.totalPrice);
                                const pricingDurationDisplay = formatPricingDurationDisplay(booking.startDate, booking.endDate, booking.pricingMode, language);
                                const pricingCalculationRule = formatPricingCalculationRule(booking.startDate, booking.endDate, booking.pricingMode, language);
                                const doorstepFee = toAmount(booking.doorstepFee);
                                const bookingAddons = getSelectedBookingAddons(booking);
                                const addonAmount = toAmount(booking.addonAmount);
                                const bookingAmount = toAmount(booking.totalPrice);
                                const totalCustomerDue = getTotalCustomerDue(booking);
                                const currentCustomerDue = getBookingCurrentDue(booking);
                                const depositPaid = isBookingDepositPaid(booking);
                                const rentPaid = isBookingRentPaid(booking);
                                const checkInPhotos = Array.isArray(checkInPhotoUrlsByBookingId[booking.bookingId])
                                    ? checkInPhotoUrlsByBookingId[booking.bookingId]
                                    : [];
                                const selectedCheckInFiles = Array.isArray(checkInPhotoFilesByBookingId[booking.bookingId])
                                    ? checkInPhotoFilesByBookingId[booking.bookingId]
                                    : [];
                                const totalCheckInPhotos = checkInPhotos.length + selectedCheckInFiles.length;
                                const remainingCheckInPhotos = Math.max(0, MIN_REQUIRED_CHECKIN_PHOTOS - totalCheckInPhotos);
                                return (
                                    <div key={booking.bookingId} className="bg-white p-4 rounded-lg shadow border border-gray-100">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <h2 className="text-lg font-bold">{booking.caravanName}</h2>
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusBadgeClass(booking.bookingStatus)}`}>
                                                {booking.bookingStatus}
                                            </span>
                                        </div>
                                        <p className="text-gray-600">{booking.caravanLocation}</p>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                            <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(booking.bookingId)}</p>
                                            <p><span className="font-semibold">Booking ID:</span> {booking.bookingId}</p>
                                            <p><span className="font-semibold">Customer:</span> {booking.renterName} ({booking.renterEmail})</p>
                                            <p><span className="font-semibold">Dates:</span> {formatDate(booking.startDate)} to {formatDate(booking.endDate)}</p>
                                            <p><span className="font-semibold">Pricing Mode:</span> {pricingModeLabel}</p>
                                            <p><span className="font-semibold">Rental Charge ({pricingModeLabel}):</span> {formatInr(rentalAmount)}</p>
                                            <p><span className="font-semibold">Pricing Duration:</span> {pricingDurationDisplay}</p>
                                            <p><span className="font-semibold">Pricing Calculation:</span> {pricingCalculationRule}</p>
                                            <p><span className="font-semibold">Doorstep Fee:</span> {formatInr(doorstepFee)}</p>
                                            {booking.doorstepEnabled && (booking.doorstepDeliveryAddress || booking.DoorstepDeliveryAddress) && (
                                                <p><span className="font-semibold">Delivery Address:</span> {booking.doorstepDeliveryAddress || booking.DoorstepDeliveryAddress}</p>
                                            )}
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons:</span> {formatAddonSummary(bookingAddons)}</p>}
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons Total:</span> {formatInr(addonAmount)}</p>}
                                            <p><span className="font-semibold">Booking Amount:</span> {formatInr(bookingAmount)}</p>
                                            <p><span className="font-semibold">Deposit:</span> {formatInr(depositAmount)}</p>
                                            <p><span className="font-semibold">Net Due Amount:</span> {formatInr(depositPaid ? currentCustomerDue : totalCustomerDue)}</p>
                                            <p><span className="font-semibold">Check-in:</span> {formatDateTime(booking.checkInAt)}</p>
                                            <p><span className="font-semibold">Check-out:</span> {formatDateTime(booking.checkOutAt)}</p>
                                            <p><span className="font-semibold">Booking Remarks:</span> {booking.bookingRemarks || booking.BookingRemarks || 'N/A'}</p>
                                            <p><span className="font-semibold">Deduction:</span> {formatInr(booking.damageDeductionAmount || 0)}</p>
                                            <p><span className="font-semibold">Deposit Refund:</span> {formatInr(booking.depositRefundAmount || 0)}</p>
                                            <p><span className="font-semibold">Checkout Review:</span> {booking.checkoutReviewStatus || 'N/A'}</p>
                                            <p><span className="font-semibold">Admin Comment:</span> {booking.checkoutAdminComment || 'N/A'}</p>
                                            <p><span className="font-semibold">Customer Rejection Remark:</span> {customerReviewComment || 'N/A'}</p>
                                        </div>

                                        {checkInPhotos.length > 0 && (
                                            <div className="mt-3">
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Check-in Photos</p>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                    {checkInPhotos.map((photoUrl, photoIndex) => (
                                                        <button
                                                            type="button"
                                                            key={`owner-checkin-photo-${booking.bookingId}-${photoIndex}`}
                                                            className="block border rounded-md overflow-hidden bg-white"
                                                            onClick={() => openImageSlider(
                                                                checkInPhotos,
                                                                photoIndex,
                                                                `Check-in Photos - ${formatBookingNumber(booking.bookingId)}`
                                                            )}
                                                        >
                                                            <img
                                                                src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                                alt={`Check-in photo ${photoIndex + 1}`}
                                                                className="h-20 w-full object-cover"
                                                                onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-4 flex flex-wrap gap-3">
                                            {booking.bookingStatus === 'confirmed' && depositPaid && rentPaid && (
                                                <div className="space-y-2">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Upload Check-in Photos (minimum 1, maximum 10, up to 8 MB each)</label>
                                                        <LocalizedFileInput
                                                            id={`owner-checkin-upload-${booking.bookingId}`}
                                                            selectedFiles={selectedCheckInFiles}
                                                            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                                            multiple
                                                            onChange={(e) => updateCheckInPhotoFiles(booking.bookingId, e.target.files)}
                                                            className="w-full"
                                                            statusClassName="text-xs"
                                                            buttonClassName="text-xs px-3 py-1.5"
                                                        />
                                                        <p className="text-xs text-gray-500 mt-1">{selectedCheckInFiles.length} new photo(s) selected.</p>
                                                        <p className="text-xs text-amber-700 mt-1">Note: Minimum 1 photo is compulsory. You can upload up to 10 photos and cover all sides of the caravan.</p>
                                                        {remainingCheckInPhotos > 0 && (
                                                            <p className="text-xs text-red-700 mt-1">{remainingCheckInPhotos} check-in photo required before check-in.</p>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => handleCheckIn(booking)}
                                                        disabled={workingBookingId === booking.bookingId || remainingCheckInPhotos > 0}
                                                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                                    >
                                                        {workingBookingId === booking.bookingId ? 'Processing...' : 'Mark Check-in'}
                                                    </button>
                                                </div>
                                            )}
                                            {booking.bookingStatus === 'confirmed' && !(depositPaid && rentPaid) && (
                                                <p className="text-sm text-red-700 font-medium">Check-in is blocked until full rent + deposit is paid.</p>
                                            )}
                                            {(booking.bookingStatus === 'checked_out' || booking.bookingStatus === 'checkout_rejected')
                                                && booking.checkoutReviewStatus !== 'approved' && (
                                                <button
                                                    onClick={() => openCheckoutForm(booking)}
                                                    className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700"
                                                >
                                                    {expandedCheckoutBookingId === booking.bookingId
                                                        ? 'Close Checkout Form'
                                                        : booking.bookingStatus === 'checkout_rejected'
                                                            ? 'Edit & Resubmit Deductions'
                                                            : 'Open Checkout Form'}
                                                </button>
                                            )}
                                            {booking.bookingStatus === 'checkout_pending_approval' && (
                                                <p className="text-sm text-amber-700 font-medium">Deduction form submitted. Waiting for admin approval.</p>
                                            )}
                                            {booking.bookingStatus === 'checkout_pending_customer' && (
                                                <p className="text-sm text-orange-700 font-medium">Deduction form submitted. Waiting for customer agreement before admin approval.</p>
                                            )}
                                            {booking.bookingStatus === 'checkout_rejected' && !!customerReviewComment && (
                                                <p className="text-sm text-red-700 font-medium">Customer rejected the deduction request: {customerReviewComment}</p>
                                            )}
                                        </div>

                                        {expandedCheckoutBookingId === booking.bookingId
                                            && (booking.bookingStatus === 'checked_out' || booking.bookingStatus === 'checkout_rejected')
                                            && booking.checkoutReviewStatus !== 'approved' && (
                                            <div className="mt-4 border border-amber-200 rounded-md p-4 bg-amber-50">
                                                <h3 className="text-lg font-semibold mb-3">Checkout Damage & Penalty Form</h3>
                                                {booking.bookingStatus === 'checkout_rejected' && (
                                                    <div className="mb-3 space-y-1">
                                                        {customerReviewComment ? (
                                                            <p className="text-sm text-red-700">Customer rejected the deduction request. Review remarks and resubmit.</p>
                                                        ) : (
                                                            <p className="text-sm text-red-700">Admin rejected the previous submission. Update the flagged categories and resubmit.</p>
                                                        )}
                                                        {!!customerReviewComment && (
                                                            <p className="text-xs text-red-700">Customer remark: {customerReviewComment}</p>
                                                        )}
                                                        {!!booking.checkoutAdminComment && (
                                                            <p className="text-xs text-red-700">Admin note: {booking.checkoutAdminComment}</p>
                                                        )}
                                                    </div>
                                                )}
                                                {damageCategoriesLoading ? (
                                                    <p className="text-sm text-gray-700">Loading damage categories...</p>
                                                ) : (
                                                    <>
                                                        <div className="space-y-3">
                                                            {(form.items || []).map((line, index) => (
                                                                <div key={`${booking.bookingId}-line-${index}`} className="space-y-1">
                                                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                                                                        <div className="md:col-span-2">
                                                                            <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                                                                            <select
                                                                                value={line.categoryId}
                                                                                onChange={(e) => updateCheckoutLine(booking.bookingId, index, 'categoryId', e.target.value)}
                                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                                            >
                                                                                <option value="">Select category</option>
                                                                                {damageCategories.map((cat) => (
                                                                                    <option key={cat.categoryId} value={cat.categoryId}>
                                                                                        {cat.categoryType} - {cat.categoryName}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-gray-700 mb-1">Qty</label>
                                                                            <input
                                                                                type="number"
                                                                                min="1"
                                                                                value={line.quantity}
                                                                                onChange={(e) => updateCheckoutLine(booking.bookingId, index, 'quantity', e.target.value)}
                                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-gray-700 mb-1">Unit Price</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                step="0.01"
                                                                                value={line.unitPrice}
                                                                                onChange={(e) => updateCheckoutLine(booking.bookingId, index, 'unitPrice', e.target.value)}
                                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeCheckoutLine(booking.bookingId, index)}
                                                                                className="w-full bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700"
                                                                            >
                                                                                Remove
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {!!line.adminRejectComment && (
                                                                        <p className="text-xs text-red-700">
                                                                            Admin rejection note: {line.adminRejectComment}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => addCheckoutLine(booking.bookingId)}
                                                            className="mt-3 bg-gray-700 text-white px-3 py-2 rounded-md hover:bg-gray-800"
                                                        >
                                                            Add Damage Line
                                                        </button>

                                                        <div className="mt-4">
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Damage Photos</label>
                                                            <LocalizedFileInput
                                                                id={`damage-photos-${booking.bookingId}`}
                                                                selectedFiles={form.newPhotoFiles || []}
                                                                accept="image/jpeg,image/png,image/webp"
                                                                multiple
                                                                onChange={(e) => {
                                                                    const files = Array.from(e.target.files || []);
                                                                    if (files.length > 10) {
                                                                        setActionError('You can upload a maximum of 10 photos at once.');
                                                                        return;
                                                                    }
                                                                    updateCheckoutPhotoFiles(booking.bookingId, files);
                                                                }}
                                                                className="w-full"
                                                            />
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {(form.newPhotoFiles || []).length} new photo(s) selected. Supported: JPG, PNG, WEBP.
                                                            </p>
                                                            {(form.photoUrls || []).length > 0 && (
                                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                                                                    {(form.photoUrls || []).map((photoUrl, photoIndex) => (
                                                                        <div key={`${booking.bookingId}-photo-${photoIndex}`} className="relative border rounded-md overflow-hidden bg-white">
                                                                            <img
                                                                                src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                                                alt={`Damage ${photoIndex + 1}`}
                                                                                className="h-20 w-full object-cover"
                                                                                onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeCheckoutPhotoUrl(booking.bookingId, photoIndex)}
                                                                                className="absolute top-1 right-1 text-xs bg-red-600 text-white px-2 py-0.5 rounded"
                                                                            >
                                                                                Remove
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="mt-4">
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Owner Notes</label>
                                                            <textarea
                                                                rows="3"
                                                                value={form.notes || ''}
                                                                onChange={(e) => updateCheckoutNotes(booking.bookingId, e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                            />
                                                        </div>

                                                        <div className="mt-4 space-y-1 text-sm">
                                                            <p><span className="font-semibold">Deposit Amount:</span> {formatInr(depositAmount)}</p>
                                                            <p><span className="font-semibold">Current Deduction Total:</span> {formatInr(checkoutTotal || 0)}</p>
                                                            <p><span className="font-semibold">Estimated Refund:</span> {formatInr(Math.max(depositAmount - checkoutTotal, 0))}</p>
                                                            <p className="text-xs text-amber-700">Deductions can exceed deposit amount. Extra due will be invoiced to customer after admin approval.</p>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleCheckout(booking)}
                                                            disabled={workingBookingId === booking.bookingId}
                                                            className="mt-4 bg-green-700 text-white px-5 py-2 rounded-md hover:bg-green-800 disabled:bg-gray-400"
                                                        >
                                                            {workingBookingId === booking.bookingId ? 'Submitting...' : 'Submit Deductions For Customer Review'}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
        </>
    );
};
// --- My Bookings Page Component ---
// This component fetches and displays a list of bookings for the logged-in user.
const MyBookingsPage = ({ user, onNavigate }) => {
    const { t, language } = useContext(LanguageContext);
    const [bookings, setBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [workingBookingId, setWorkingBookingId] = useState(null);
    const [expandedComplaintBookingId, setExpandedComplaintBookingId] = useState(null);
    const [complaintFormsByBookingId, setComplaintFormsByBookingId] = useState({});
    const [checkoutRejectRemarksByBookingId, setCheckoutRejectRemarksByBookingId] = useState({});
    const [checkoutAssessmentByBookingId, setCheckoutAssessmentByBookingId] = useState({});
    const [checkInPhotoUrlsByBookingId, setCheckInPhotoUrlsByBookingId] = useState({});
    const [checkInPhotoFilesByBookingId, setCheckInPhotoFilesByBookingId] = useState({});
    const [imageSliderState, setImageSliderState] = useState({
        open: false,
        images: [],
        index: 0,
        title: ''
    });
    const MAX_CHECKIN_PHOTOS = 10;
    const MAX_CHECKIN_PHOTO_SIZE_MB = 8;
    const MAX_CHECKIN_PHOTO_SIZE_BYTES = MAX_CHECKIN_PHOTO_SIZE_MB * 1024 * 1024;
    const MIN_REQUIRED_CHECKIN_PHOTOS = 1;

    const fetchBookings = useCallback(async () => {
        if (!user?.userId) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/my-bookings/${user.userId}`);
            if (!response.ok) {
                throw new Error('Could not fetch your bookings.');
            }
            const data = await response.json();
            setBookings(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || 'Could not fetch your bookings.');
        } finally {
            setIsLoading(false);
        }
    }, [user?.userId]);

    const fetchCheckoutAssessmentForCustomer = useCallback(async (bookingId) => {
        if (!bookingId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/damage-assessment`);
            if (!response.ok) return;
            const data = await response.json();
            setCheckoutAssessmentByBookingId((prev) => ({ ...prev, [bookingId]: data }));
        } catch {
            // Ignore background fetch errors for optional checkout media.
        }
    }, []);

    const fetchCheckInPhotosForCustomer = useCallback(async (bookingId) => {
        if (!bookingId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/check-in-photos`);
            if (!response.ok) return;
            const data = await response.json();
            setCheckInPhotoUrlsByBookingId((prev) => ({
                ...prev,
                [bookingId]: Array.isArray(data) ? data.filter(Boolean) : []
            }));
        } catch {
            // Ignore optional media fetch failures.
        }
    }, []);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    useEffect(() => {
        const bookingIdsNeedingAssessment = bookings
            .filter((booking) => {
                const status = String(booking?.bookingStatus || '').trim().toLowerCase();
                return status === 'checkout_pending_customer' || status === 'checkout_pending_approval' || status === 'checkout_rejected';
            })
            .map((booking) => booking?.bookingId)
            .filter((bookingId) => bookingId && !checkoutAssessmentByBookingId[bookingId]);

        if (bookingIdsNeedingAssessment.length === 0) {
            return;
        }

        bookingIdsNeedingAssessment.forEach((bookingId) => {
            fetchCheckoutAssessmentForCustomer(bookingId);
        });
    }, [bookings, checkoutAssessmentByBookingId, fetchCheckoutAssessmentForCustomer]);

    useEffect(() => {
        const bookingIdsToLoad = bookings
            .map((booking) => booking?.bookingId)
            .filter((bookingId) => bookingId && !Object.prototype.hasOwnProperty.call(checkInPhotoUrlsByBookingId, bookingId));

        if (bookingIdsToLoad.length === 0) {
            return;
        }

        bookingIdsToLoad.forEach((bookingId) => {
            fetchCheckInPhotosForCustomer(bookingId);
        });
    }, [bookings, checkInPhotoUrlsByBookingId, fetchCheckInPhotosForCustomer]);

    const openImageSlider = useCallback((images, startIndex = 0, title = 'Booking Images') => {
        const normalized = (Array.isArray(images) ? images : [])
            .map((img) => resolveImageUrl(img) || img)
            .filter(Boolean);

        if (normalized.length === 0) {
            setImageSliderState({
                open: true,
                images: [PLACEHOLDER_IMG],
                index: 0,
                title
            });
            return;
        }

        const safeIndex = Math.max(0, Math.min(startIndex, normalized.length - 1));
        setImageSliderState({
            open: true,
            images: normalized,
            index: safeIndex,
            title
        });
    }, []);

    const closeImageSlider = useCallback(() => {
        setImageSliderState((prev) => ({ ...prev, open: false }));
    }, []);

    const showPrevImage = useCallback(() => {
        setImageSliderState((prev) => {
            if (!prev.images.length) return prev;
            const nextIndex = (prev.index - 1 + prev.images.length) % prev.images.length;
            return { ...prev, index: nextIndex };
        });
    }, []);

    const showNextImage = useCallback(() => {
        setImageSliderState((prev) => {
            if (!prev.images.length) return prev;
            const nextIndex = (prev.index + 1) % prev.images.length;
            return { ...prev, index: nextIndex };
        });
    }, []);

    useEffect(() => {
        if (!imageSliderState.open) return;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeImageSlider();
            } else if (event.key === 'ArrowLeft') {
                showPrevImage();
            } else if (event.key === 'ArrowRight') {
                showNextImage();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [imageSliderState.open, closeImageSlider, showPrevImage, showNextImage]);

    // Helper function to format dates for display
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('da-DK');
    };

    const getStatusBadgeClass = (status) => {
        if (status === 'confirmed') return 'bg-green-100 text-green-800';
        if (status === 'checked_in') return 'bg-blue-100 text-blue-800';
        if (status === 'checkout_pending_customer') return 'bg-orange-100 text-orange-800';
        if (status === 'checkout_pending_approval') return 'bg-amber-100 text-amber-800';
        if (status === 'checkout_rejected') return 'bg-red-100 text-red-800';
        if (status === 'checked_out' || status === 'completed') return 'bg-purple-100 text-purple-800';
        if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    const updateCustomerCheckInFiles = (bookingId, fileList) => {
        const files = Array.from(fileList || []);
        const existingPhotoCount = Array.isArray(checkInPhotoUrlsByBookingId[bookingId])
            ? checkInPhotoUrlsByBookingId[bookingId].length
            : 0;

        if ((existingPhotoCount + files.length) > MAX_CHECKIN_PHOTOS) {
            setActionError(`You can keep at most ${MAX_CHECKIN_PHOTOS} check-in photos per booking.`);
            setActionSuccess('');
            return;
        }

        if (files.some((file) => Number(file?.size || 0) > MAX_CHECKIN_PHOTO_SIZE_BYTES)) {
            setActionError(`Each check-in photo must be ${MAX_CHECKIN_PHOTO_SIZE_MB} MB or smaller.`);
            setActionSuccess('');
            return;
        }

        setActionError('');
        setCheckInPhotoFilesByBookingId((prev) => ({ ...prev, [bookingId]: files }));
    };

    const uploadCustomerCheckInPhotos = async (bookingId, files) => {
        if (!user?.userId || !Array.isArray(files) || files.length === 0) {
            return [];
        }

        const formData = new FormData();
        formData.append('renterId', String(user.userId));
        files.forEach((file) => formData.append('files', file));

        const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/customer-check-in-photos`, {
            method: 'POST',
            body: formData
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Could not upload check-in photos.');
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    };

    const handleCustomerCheckIn = async (booking) => {
        if (!user?.userId) return;
        const bookingId = booking?.bookingId;
        if (!bookingId) return;
        const hasFullPayment = isBookingDepositPaid(booking) && isBookingRentPaid(booking);
        if (!hasFullPayment) {
            setActionError('Check-in is blocked until full rent + deposit is paid.');
            setActionSuccess('');
            return;
        }
        const existingCheckInPhotos = Array.isArray(checkInPhotoUrlsByBookingId[bookingId]) ? checkInPhotoUrlsByBookingId[bookingId] : [];
        const selectedCheckInFiles = Array.isArray(checkInPhotoFilesByBookingId[bookingId])
            ? checkInPhotoFilesByBookingId[bookingId]
            : [];
        const totalCheckInPhotos = existingCheckInPhotos.length + selectedCheckInFiles.length;
        if (totalCheckInPhotos < MIN_REQUIRED_CHECKIN_PHOTOS) {
            setActionError(`Upload at least ${MIN_REQUIRED_CHECKIN_PHOTOS} check-in photo before check-in.`);
            setActionSuccess('');
            return;
        }

        setWorkingBookingId(bookingId);
        setActionError('');
        setActionSuccess('');
        try {
            if (selectedCheckInFiles.length > 0) {
                const savedPhotoUrls = await uploadCustomerCheckInPhotos(bookingId, selectedCheckInFiles);
                setCheckInPhotoUrlsByBookingId((prev) => ({
                    ...prev,
                    [bookingId]: Array.isArray(savedPhotoUrls) ? savedPhotoUrls : (prev[bookingId] || [])
                }));
            }

            const actionUrl = `${API_BASE_URL}/api/bookings/${bookingId}/customer-check-in`;
            const payload = JSON.stringify({ renterId: user.userId });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Could not complete customer check-in.'));
            }

            setActionSuccess(`Check-in completed for ${formatBookingNumber(bookingId)}.`);
            setCheckInPhotoFilesByBookingId((prev) => ({ ...prev, [bookingId]: [] }));
            await fetchCheckInPhotosForCustomer(bookingId);
            await fetchBookings();
        } catch (err) {
            setActionError(err.message || 'Could not complete customer check-in.');
        } finally {
            setWorkingBookingId(null);
        }
    };

    const handleCustomerCheckOut = async (bookingId) => {
        if (!user?.userId) return;
        setWorkingBookingId(bookingId);
        setActionError('');
        setActionSuccess('');
        try {
            const actionUrl = `${API_BASE_URL}/api/bookings/${bookingId}/customer-check-out`;
            const payload = JSON.stringify({ renterId: user.userId });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Could not complete customer check-out.'));
            }

            setActionSuccess(`Check-out completed for ${formatBookingNumber(bookingId)}. Owner can now submit deductions.`);
            await fetchBookings();
        } catch (err) {
            setActionError(err.message || 'Could not complete customer check-out.');
        } finally {
            setWorkingBookingId(null);
        }
    };

    const updateCheckoutRejectRemark = (bookingId, value) => {
        setCheckoutRejectRemarksByBookingId((prev) => ({
            ...prev,
            [bookingId]: value
        }));
    };

    const handleCustomerCheckoutReview = async (bookingId, decision) => {
        if (!user?.userId) return;
        const normalizedDecision = String(decision || '').trim().toLowerCase();
        if (normalizedDecision !== 'agree' && normalizedDecision !== 'disagree') return;

        let remarks = '';
        if (normalizedDecision === 'disagree') {
            remarks = String(checkoutRejectRemarksByBookingId[bookingId] || '').trim();
            if (!remarks) {
                setActionError('Remarks are required when disagreeing with checkout deductions.');
                return;
            }
        }

        setWorkingBookingId(bookingId);
        setActionError('');
        setActionSuccess('');
        try {
            const actionUrl = `${API_BASE_URL}/api/bookings/${bookingId}/checkout-customer-review`;
            const payload = JSON.stringify({
                renterId: user.userId,
                decision: normalizedDecision,
                remarks
            });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Could not submit checkout review.'));
            }

            setActionSuccess(
                normalizedDecision === 'agree'
                    ? `You agreed with deductions for ${formatBookingNumber(bookingId)}. Request moved to admin approval.`
                    : `You disagreed with deductions for ${formatBookingNumber(bookingId)}. Request moved back to owner.`
            );
            if (normalizedDecision === 'disagree') {
                setCheckoutRejectRemarksByBookingId((prev) => ({
                    ...prev,
                    [bookingId]: ''
                }));
            }
            await fetchBookings();
        } catch (err) {
            setActionError(err.message || 'Could not submit checkout review.');
        } finally {
            setWorkingBookingId(null);
        }
    };

    const updateComplaintExplanation = (bookingId, value) => {
        setComplaintFormsByBookingId((prev) => ({
            ...prev,
            [bookingId]: {
                ...(prev[bookingId] || { explanation: '', files: [] }),
                explanation: value
            }
        }));
    };

    const updateComplaintFiles = (bookingId, files) => {
        const selected = Array.from(files || []);
        if (selected.length > MAX_COMPLAINT_IMAGES) {
            setActionError(`You can upload at most ${MAX_COMPLAINT_IMAGES} complaint photos.`);
            return;
        }
        const oversized = selected.filter((file) => Number(file?.size || 0) > MAX_COMPLAINT_IMAGE_SIZE_BYTES);
        if (oversized.length > 0) {
            setActionError(`Each complaint photo must be ${MAX_COMPLAINT_IMAGE_SIZE_MB} MB or smaller.`);
            return;
        }

        setComplaintFormsByBookingId((prev) => ({
            ...prev,
            [bookingId]: {
                ...(prev[bookingId] || { explanation: '', files: [] }),
                files: selected
            }
        }));
    };

    const toggleComplaintForm = (bookingId) => {
        setActionError('');
        setActionSuccess('');
        setExpandedComplaintBookingId((prev) => (prev === bookingId ? null : bookingId));
    };

    const handleRaiseComplaint = async (booking) => {
        if (!user?.userId) return;
        const bookingStatus = String(booking?.bookingStatus || '').trim().toLowerCase();
        const complaintBlockedStatuses = new Set(['rejected', 'checked_out', 'completed', 'checkout_pending_customer', 'checkout_pending_approval', 'checkout_rejected']);
        if (complaintBlockedStatuses.has(bookingStatus)) {
            setActionError('Complaint cannot be raised for rejected or closed bookings.');
            setActionSuccess('');
            return;
        }

        const form = complaintFormsByBookingId[booking.bookingId] || { explanation: '', files: [] };
        const explanation = (form.explanation || '').trim();
        const files = Array.isArray(form.files) ? form.files : [];

        if (!explanation) {
            setActionError('Complaint explanation is required.');
            return;
        }
        if (files.length === 0) {
            setActionError('At least one complaint photo is required.');
            return;
        }

        setWorkingBookingId(booking.bookingId);
        setActionError('');
        setActionSuccess('');
        try {
            const formData = new FormData();
            formData.append('renterId', String(user.userId));
            formData.append('explanation', explanation);
            files.forEach((file) => formData.append('files', file));

            const response = await fetch(`${API_BASE_URL}/api/caravans/${booking.caravanId}/complaints/with-evidence`, {
                method: 'POST',
                body: formData
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Could not submit complaint.'));
            }

            setActionSuccess(`Complaint submitted for ${formatBookingNumber(booking.bookingId)}. Admin and owner were notified.`);
            setExpandedComplaintBookingId(null);
            setComplaintFormsByBookingId((prev) => ({
                ...prev,
                [booking.bookingId]: { explanation: '', files: [] }
            }));
        } catch (err) {
            setActionError(err.message || 'Could not submit complaint.');
        } finally {
            setWorkingBookingId(null);
        }
    };

    return (
        <>
        <ActionLoaderOverlay
            show={workingBookingId !== null}
            title="Please wait..."
            subtitle="Completing action and sending booking emails."
        />
        {imageSliderState.open && (
            <div className="fixed inset-0 z-[10000] bg-black/90 px-4 py-6">
                <div className="mx-auto max-w-6xl h-full flex flex-col">
                    <div className="flex items-center justify-between text-white mb-3">
                        <h3 className="text-lg font-semibold truncate pr-4">{imageSliderState.title || 'Booking Images'}</h3>
                        <button
                            type="button"
                            onClick={closeImageSlider}
                            className="bg-white/20 hover:bg-white/30 rounded-md px-3 py-1 text-sm"
                        >
                            Close
                        </button>
                    </div>

                    <div className="flex-1 min-h-0 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={showPrevImage}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                            aria-label="Previous image"
                        >
                            &lt;
                        </button>
                        <img
                            src={imageSliderState.images[imageSliderState.index] || PLACEHOLDER_IMG}
                            alt={`Booking ${imageSliderState.index + 1}`}
                            className="max-h-full max-w-full object-contain rounded-lg border border-white/20"
                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                        />
                        <button
                            type="button"
                            onClick={showNextImage}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                            aria-label="Next image"
                        >
                            &gt;
                        </button>
                    </div>

                    <div className="mt-3">
                        <p className="text-xs text-white/80 mb-2">
                            {imageSliderState.index + 1} / {imageSliderState.images.length}
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {imageSliderState.images.map((img, idx) => (
                                <button
                                    key={`${img}-${idx}`}
                                    type="button"
                                    onClick={() => setImageSliderState((prev) => ({ ...prev, index: idx }))}
                                    className={`shrink-0 border rounded-md ${idx === imageSliderState.index ? 'border-green-400' : 'border-white/30'}`}
                                >
                                    <img
                                        src={img}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className="w-20 h-14 object-cover rounded-md"
                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )}
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6">{t('myBookingsTitle')}</h1>
            {isLoading && <p>{t('myBookingsLoading')}</p>}
            {error && <p className="text-red-500">{error}</p>}
            {actionError && <p className="text-red-500 mb-2">{actionError}</p>}
            {actionSuccess && <p className="text-green-700 mb-2">{actionSuccess}</p>}
            {!isLoading && bookings.length === 0 && (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                    <h3 className="text-xl font-semibold">{t('myBookingsEmptyTitle')}</h3>
                    <p className="text-gray-600 mt-2">{t('myBookingsEmptyText')}</p>
                    <button onClick={() => onNavigate({ page: 'list' })} className="mt-4 bg-green-600 text-white font-bold py-2 px-5 rounded-full hover:bg-green-700">
                        {t('myBookingsExplore')}
                    </button>
                </div>
            )}
            {!isLoading && bookings.length > 0 && (
                <div className="space-y-6">
                    {bookings.map(booking => {
                        const pricingModeLabel = getPricingModeLabel(booking.pricingMode, language);
                        const rentalAmount = toAmount(booking.rentalAmount ?? booking.totalPrice);
                        const pricingDurationDisplay = formatPricingDurationDisplay(booking.startDate, booking.endDate, booking.pricingMode, language);
                        const pricingCalculationRule = formatPricingCalculationRule(booking.startDate, booking.endDate, booking.pricingMode, language);
                        const doorstepFee = toAmount(booking.doorstepFee);
                        const bookingAddons = getSelectedBookingAddons(booking);
                        const addonAmount = toAmount(booking.addonAmount);
                        const bookingAmount = toAmount(booking.totalPrice);
                        const depositAmount = toAmount(booking.depositAmount);
                        const totalCustomerDue = getTotalCustomerDue(booking);
                        const currentCustomerDue = getBookingCurrentDue(booking);
                        const depositPaid = isBookingDepositPaid(booking);
                        const rentPaid = isBookingRentPaid(booking);
                        const checkoutAssessment = checkoutAssessmentByBookingId[booking.bookingId];
                        const checkoutPhotos = Array.isArray(checkoutAssessment?.photoUrls) ? checkoutAssessment.photoUrls.filter(Boolean) : [];
                        const checkInPhotos = Array.isArray(checkInPhotoUrlsByBookingId[booking.bookingId]) ? checkInPhotoUrlsByBookingId[booking.bookingId] : [];
                        const selectedCheckInFiles = Array.isArray(checkInPhotoFilesByBookingId[booking.bookingId])
                            ? checkInPhotoFilesByBookingId[booking.bookingId]
                            : [];
                        const totalCheckInPhotos = checkInPhotos.length + selectedCheckInFiles.length;
                        const remainingCheckInPhotos = Math.max(0, MIN_REQUIRED_CHECKIN_PHOTOS - totalCheckInPhotos);
                        const complaintForm = complaintFormsByBookingId[booking.bookingId] || { explanation: '', files: [] };
                        const bookingStatusNormalized = String(booking.bookingStatus || '').trim().toLowerCase();
                        const canRaiseComplaint = !(
                            bookingStatusNormalized === 'rejected'
                            || bookingStatusNormalized === 'checked_out'
                            || bookingStatusNormalized === 'completed'
                            || bookingStatusNormalized === 'checkout_pending_customer'
                            || bookingStatusNormalized === 'checkout_pending_approval'
                            || bookingStatusNormalized === 'checkout_rejected'
                        );

                        return (
                        <div key={booking.bookingId} className="bg-white p-4 rounded-lg shadow-md flex flex-col md:flex-row items-center gap-4">
                            <img
                                src={resolveImageUrl(booking.caravanImageUrl) || PLACEHOLDER_IMG}
                                alt={booking.caravanName}
                                className="w-full md:w-48 h-32 object-cover rounded-md"
                                onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                            />
                            <div className="flex-grow">
                                <h2 className="text-xl font-bold">{booking.caravanName}</h2>
                                <p className="text-gray-600">{booking.caravanLocation}</p>
                                <p className="text-sm text-gray-500 mt-1">
                                    <span className="font-semibold">Booking No:</span> {formatBookingNumber(booking.bookingId)}
                                </p>
                                <p className="text-sm text-gray-500 mt-2">
                                    <span className="font-semibold">{t('myBookingsDates')}:</span> {formatDate(booking.startDate)} to {formatDate(booking.endDate)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Pricing Mode:</span> {pricingModeLabel}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Rental Charge ({pricingModeLabel}):</span> {formatInr(rentalAmount)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Pricing Duration:</span> {pricingDurationDisplay}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Pricing Calculation:</span> {pricingCalculationRule}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Doorstep Fee:</span> {formatInr(doorstepFee)}
                                </p>
                                {bookingAddons.length > 0 && (
                                    <p className="text-sm text-gray-500">
                                        <span className="font-semibold">Add-ons:</span> {formatAddonSummary(bookingAddons)}
                                    </p>
                                )}
                                {bookingAddons.length > 0 && (
                                    <p className="text-sm text-gray-500">
                                        <span className="font-semibold">Add-ons Total:</span> {formatInr(addonAmount)}
                                    </p>
                                )}
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">{t('myBookingsTotal')}:</span> {formatInr(bookingAmount)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Deposit:</span> {formatInr(depositAmount)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Net Due Amount:</span> {formatInr(depositPaid ? currentCustomerDue : totalCustomerDue)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Check-in:</span> {formatDateTime(booking.checkInAt)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Check-out:</span> {formatDateTime(booking.checkOutAt)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Booking Remarks:</span> {booking.bookingRemarks || booking.BookingRemarks || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Caravan Owner Contact:</span> {booking.ownerMobile || booking.OwnerMobile || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Checkout Review:</span> {booking.checkoutReviewStatus || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Admin Comment:</span> {booking.checkoutAdminComment || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Deposit Deduction:</span> {formatInr(booking.damageDeductionAmount || 0)}
                                </p>
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Deposit Refund:</span> {formatInr(booking.depositRefundAmount || 0)}
                                </p>
                                {checkInPhotos.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-sm font-semibold text-gray-700 mb-1">Check-in Photos</p>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                            {checkInPhotos.map((photoUrl, photoIndex) => (
                                                <button
                                                    type="button"
                                                    key={`customer-checkin-photo-${booking.bookingId}-${photoIndex}`}
                                                    className="block border rounded-md overflow-hidden bg-white"
                                                    onClick={() => openImageSlider(
                                                        checkInPhotos,
                                                        photoIndex,
                                                        `Check-in Photos - ${formatBookingNumber(booking.bookingId)}`
                                                    )}
                                                >
                                                    <img
                                                        src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                        alt={`Check-in photo ${photoIndex + 1}`}
                                                        className="h-20 w-full object-cover"
                                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="mt-3">
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => onNavigate({ page: 'detail', caravan_id: booking.caravanId })}
                                            className="bg-gray-700 text-white px-3 py-2 rounded-md hover:bg-gray-800"
                                        >
                                            View Caravan Details
                                        </button>
                                        {canRaiseComplaint && (
                                            <button
                                                onClick={() => toggleComplaintForm(booking.bookingId)}
                                                disabled={workingBookingId === booking.bookingId}
                                                className="bg-red-700 text-white px-3 py-2 rounded-md hover:bg-red-800 disabled:bg-gray-400"
                                            >
                                                {expandedComplaintBookingId === booking.bookingId ? 'Close Complaint Form' : 'Raise Complaint'}
                                            </button>
                                        )}
                                        {booking.bookingStatus === 'confirmed' && depositPaid && rentPaid && (
                                            <div className="space-y-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Upload Check-in Photos (minimum 1, maximum 10, up to 8 MB each)</label>
                                                    <LocalizedFileInput
                                                        id={`customer-checkin-upload-${booking.bookingId}`}
                                                        selectedFiles={selectedCheckInFiles}
                                                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                                        multiple
                                                        onChange={(e) => updateCustomerCheckInFiles(booking.bookingId, e.target.files)}
                                                        className="w-full"
                                                        statusClassName="text-xs"
                                                        buttonClassName="text-xs px-3 py-1.5"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">{selectedCheckInFiles.length} new photo(s) selected.</p>
                                                    <p className="text-xs text-amber-700 mt-1">Note: Minimum 1 photo is compulsory. You can upload up to 10 photos and cover all sides of the caravan.</p>
                                                    {remainingCheckInPhotos > 0 && (
                                                        <p className="text-xs text-red-700 mt-1">{remainingCheckInPhotos} check-in photo required before check-in.</p>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleCustomerCheckIn(booking)}
                                                    disabled={workingBookingId === booking.bookingId || remainingCheckInPhotos > 0}
                                                    className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                                >
                                                    {workingBookingId === booking.bookingId ? 'Processing...' : 'Check-in'}
                                                </button>
                                            </div>
                                        )}
                                        {booking.bookingStatus === 'confirmed' && !(depositPaid && rentPaid) && (
                                            <p className="text-xs text-red-700 font-semibold">Check-in is blocked until full rent + deposit is paid.</p>
                                        )}
                                        {booking.bookingStatus === 'checked_in' && depositPaid && rentPaid && (
                                            <button
                                                onClick={() => handleCustomerCheckOut(booking.bookingId)}
                                                disabled={workingBookingId === booking.bookingId}
                                                className="bg-amber-600 text-white px-3 py-2 rounded-md hover:bg-amber-700 disabled:bg-gray-400"
                                            >
                                                {workingBookingId === booking.bookingId ? 'Processing...' : 'Check-out'}
                                            </button>
                                        )}
                                        {booking.bookingStatus === 'checked_in' && !(depositPaid && rentPaid) && (
                                            <p className="text-xs text-red-700 font-semibold">Check-out is blocked until full rent + deposit is paid.</p>
                                        )}
                                        {booking.bookingStatus === 'checkout_pending_customer' && (
                                            <div className="w-full space-y-2">
                                                {checkoutPhotos.length > 0 && (
                                                    <div className="rounded-md border border-gray-200 bg-white p-3">
                                                        <p className="text-sm font-semibold text-gray-700 mb-2">Owner Uploaded Checkout Photos</p>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                            {checkoutPhotos.map((photoUrl, photoIndex) => (
                                                                <button
                                                                    type="button"
                                                                    key={`customer-checkout-photo-${booking.bookingId}-${photoIndex}`}
                                                                    className="block border rounded-md overflow-hidden bg-white"
                                                                    onClick={() => openImageSlider(
                                                                        checkoutPhotos,
                                                                        photoIndex,
                                                                        `Checkout Proof - ${formatBookingNumber(booking.bookingId)}`
                                                                    )}
                                                                >
                                                                    <img
                                                                        src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                                        alt={`Checkout evidence ${photoIndex + 1}`}
                                                                        className="h-24 w-full object-cover"
                                                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                                    />
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => handleCustomerCheckoutReview(booking.bookingId, 'agree')}
                                                    disabled={workingBookingId === booking.bookingId}
                                                    className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                >
                                                    {workingBookingId === booking.bookingId ? 'Processing...' : 'Agree Deductions'}
                                                </button>
                                                <div className="rounded-md border border-red-200 bg-red-50 p-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Remarks (Required to Disagree)</label>
                                                    <textarea
                                                        rows="2"
                                                        value={checkoutRejectRemarksByBookingId[booking.bookingId] || ''}
                                                        onChange={(e) => updateCheckoutRejectRemark(booking.bookingId, e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                                                        placeholder=""
                                                    />
                                                    <button
                                                        onClick={() => handleCustomerCheckoutReview(booking.bookingId, 'disagree')}
                                                        disabled={workingBookingId === booking.bookingId}
                                                        className="mt-2 bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                                                    >
                                                        {workingBookingId === booking.bookingId ? 'Processing...' : 'Disagree & Return'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {canRaiseComplaint && expandedComplaintBookingId === booking.bookingId && (
                                        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Complaint Explanation</label>
                                            <textarea
                                                rows="3"
                                                value={complaintForm.explanation || ''}
                                                onChange={(e) => updateComplaintExplanation(booking.bookingId, e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                                                placeholder=""
                                            />
                                            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Upload Complaint Photos</label>
                                            <LocalizedFileInput
                                                id={`complaint-photos-${booking.bookingId}`}
                                                selectedFiles={complaintForm.files || []}
                                                accept="image/jpeg,image/png,image/webp"
                                                multiple
                                                onChange={(e) => updateComplaintFiles(booking.bookingId, e.target.files)}
                                                className="w-full"
                                            />
                                            <p className="mt-1 text-xs text-gray-600">
                                                {(complaintForm.files || []).length} photo(s) selected. Max {MAX_COMPLAINT_IMAGES}, up to {MAX_COMPLAINT_IMAGE_SIZE_MB} MB each.
                                            </p>
                                            <div className="mt-3 flex gap-2">
                                                <button
                                                    onClick={() => handleRaiseComplaint(booking)}
                                                    disabled={workingBookingId === booking.bookingId}
                                                    className="bg-red-700 text-white px-4 py-2 rounded-md hover:bg-red-800 disabled:bg-gray-400"
                                                >
                                                    {workingBookingId === booking.bookingId ? 'Submitting...' : 'Submit Complaint'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedComplaintBookingId(null)}
                                                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex-shrink-0">
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${getStatusBadgeClass(booking.bookingStatus)}`}>
                                    {booking.bookingStatus}
                                </span>
                            </div>
                        </div>
                    );
                    })}
                </div>
            )}
        </div>
        </>
    );
};


// --- Main App Component Function ---
// This function would be inside your main App.js file and passed down as a prop.
const handleCreateBooking = async (bookingDto) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/bookings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bookingDto),
        });
        if (!response.ok) {
            // If the API returns a 409 Conflict, it means the dates are not available.
            if (response.status === 409) {
                const errorData = await response.json();
                throw new Error(errorData.message);
            }
            const errorText = await response.text();
            throw new Error(`Failed to create booking: ${errorText}`);
        }
    } catch (err) {
        console.error("Booking creation error:", err);
        // Re-throw the error so the BookingPage component can catch and display it.
        throw err;
    }
};

const BookingSuccessPage = ({ caravanName, ownerContactNumber, onNavigate }) => (
    <div className="container mx-auto px-4 py-12 text-center">
        <div className="max-w-lg mx-auto bg-white p-8 rounded-lg shadow-lg">
            <svg className="w-16 h-16 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            <h1 className="text-3xl font-bold text-gray-900 mt-4">Booking Request Sent!</h1>
            <p className="text-gray-600 mt-2">Your request for <strong>{caravanName}</strong> has been sent to the owner. You will receive an email confirmation once it's approved.</p>
            {ownerContactNumber && (
                <p className="text-gray-700 mt-4">
                    <span className="font-semibold">Caravan Owner Contact:</span> {ownerContactNumber}
                </p>
            )}
            <button onClick={() => onNavigate({ page: 'home' })} className="mt-6 bg-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-700 transition-colors duration-300">
                Back to Home
            </button>
        </div>
    </div>
);
const BecomeOwnerPage = ({ onNavigate, onAddCaravan, user, masterAmenities, masterTypes, typeAmenityMappings, onRefreshMasterData }) => {
    const { t, language } = useContext(LanguageContext);
    const ownerFormText = language === 'da'
        ? {
            title: 'Registrer dit køretøj',
            type: 'Type',
            model: 'Model',
            year: 'År',
            gearbox: 'Gearkasse',
            automatic: 'Automatisk',
            manual: 'Manuel',
            fuelType: 'Brændstoftype',
            diesel: 'Diesel',
            petrol: 'Benzin',
            gas: 'Gas',
            width: 'Bredde (m)',
            length: 'Længde (m)',
            height: 'Højde (m)',
            curbWeight: 'Egenvægt (kg)',
            allowedTotalWeight: 'Tilladt totalvægt (kg)',
            vehicleName: 'Navn på køretøj',
            registrationNumber: 'Registreringsnummer',
            location: 'Lokation (visning)',
            street: 'Vej',
            city: 'By',
            zipcode: 'Postnummer',
            pricePerDay: 'Pris pr. dag (DKK)',
            pricePerWeek: 'Pris pr. uge (DKK)',
            depositAmount: 'Depositum (DKK)',
            sitting: 'Siddepladser',
            beds: 'Senge',
            addonsSection: 'Tilvalg',
            settingsSection: 'Indstillinger',
            gasAddon: 'Gas (DKK)',
            sideMirrors: 'Sidespejle (DKK)',
            tentSetup: 'Opsætning af telt (DKK)',
            adapter: 'Adapter 6/13 pol (DKK)',
            cleaningAddon: 'Rengoring (DKK)',
            toiletAvailable: 'Toilettet kan bruges',
            waterAvailable: 'Vand i campingvognen',
            toiletEmptyingAddon: 'Tømning af toilet (DKK)',
            tentRentalAddon: 'Leje af telt (DKK)',
            tentDryingAddon: 'Tørring af telt (DKK)',
            selfPickEnabled: 'Selvafhentning aktiveret',
            doorstepAvailable: 'Tilbyd levering og afhentning',
            festivalAvailability: 'Tilgaengelig til festival',
            petsAllowed: 'Kæledyr tilladt',
            noSmoking: 'Rygning ikke tilladt',
            doorstepRate: 'Pris for leverings service pr. km (DKK)',
            longTermEnabled: 'Langtidsbooking aktiveret',
            minimumBookingMonths: 'Minimum måneder',
            pricePerMonth: 'Pris pr. måned (DKK)',
            ownWeight: 'Egenvægt (kg)',
            totalWeight: 'Totalvægt (kg)',
            seasonPricing: 'Sæsonpriser',
            seasonPerDay: 'Pr. dag (DKK)',
            seasonPerWeek: 'Pr. uge (DKK)',
            seasonPerMonth: 'Pr. måned (DKK)',
            amenities: 'Faciliteter',
            noAmenities: 'Ingen faciliteter er koblet til denne type.',
            description: 'Beskrivelse',
            images: 'Billeder af køretøj (maks. 10, 10 MB hver)',
            selectedFiles: (count) => `${count} fil(er) valgt`,
            submit: 'Indsend til godkendelse'
        }
        : {
            title: 'Register Your Vehicle',
            type: 'Type',
            model: 'Model',
            year: 'Year',
            gearbox: 'Gearbox',
            automatic: 'Automatic',
            manual: 'Manual',
            fuelType: 'Fuel Type',
            diesel: 'Diesel',
            petrol: 'Petrol',
            gas: 'Gas',
            width: 'Width (m)',
            length: 'Length (m)',
            height: 'Height (m)',
            curbWeight: 'Curb Weight (kg)',
            allowedTotalWeight: 'Allowed Total Weight (kg)',
            vehicleName: 'Vehicle Name',
            registrationNumber: 'Registration Number',
            location: 'Location (Display)',
            street: 'Street',
            city: 'City',
            zipcode: 'Zipcode',
            pricePerDay: 'Price per Day (DKK)',
            pricePerWeek: 'Price per Week (DKK)',
            depositAmount: 'Deposit Amount (DKK)',
            sitting: 'Sitting',
            beds: 'Beds',
            addonsSection: 'Add-ons',
            settingsSection: 'Settings',
            gasAddon: 'Gas Add-on (DKK)',
            sideMirrors: 'Side Mirrors (DKK)',
            tentSetup: 'Raising the Tent (DKK)',
            adapter: 'Adapter 6/13 Pole (DKK)',
            cleaningAddon: 'Cleaning (DKK)',
            toiletAvailable: 'Can you use the toilet?',
            waterAvailable: 'Water in the caravan',
            toiletEmptyingAddon: 'Emptying toilet (DKK)',
            tentRentalAddon: 'Rent the tent (DKK)',
            tentDryingAddon: 'Drying the tent (DKK)',
            selfPickEnabled: 'Self pickup enabled',
            doorstepAvailable: 'Offer delivery and pickup',
            festivalAvailability: 'Available for Festival',
            petsAllowed: 'Pets Allowed',
            noSmoking: 'No Smoking',
            doorstepRate: 'Doorstep service per km (DKK)',
            longTermEnabled: 'Long-term booking enabled',
            minimumBookingMonths: 'Minimum booking months',
            pricePerMonth: 'Price per Month (DKK)',
            ownWeight: 'Own Weight (kg)',
            totalWeight: 'Total Weight (kg)',
            seasonPricing: 'Season-wise Pricing',
            seasonPerDay: 'Per Day (DKK)',
            seasonPerWeek: 'Per Week (DKK)',
            seasonPerMonth: 'Per Month (DKK)',
            amenities: 'Amenities',
            noAmenities: 'No amenities mapped for this caravan type.',
            description: 'Description',
            images: 'Vehicle Images (max 10, 10 MB each)',
            selectedFiles: (count) => `${count} file(s) selected`,
            submit: 'Submit for Approval'
        };
    const [formData, setFormData] = useState({
        name: '', location: '', registration_number: '', street: '', city: '', zipcode: '', price_per_day: '', price_per_week: '', deposit_amount: '', capacity: '', beds: '',
        own_weight_kg: '', total_weight_kg: '',
        motorhome_model: '',
        motorhome_year: '',
        motorhome_gearbox: '',
        motorhome_fuel_type: '',
        motorhome_width_m: '',
        motorhome_length_m: '',
        motorhome_height_m: '',
        motorhome_curb_weight_kg: '',
        motorhome_allowed_total_weight_kg: '',
        type_id: masterTypes[0]?.type_id || '', description: '', amenities: [], self_pick_enabled: true, doorstep_available: false, doorstep_rate_per_km: '', available_for_festival: true, pets_allowed: false, no_smoking: false, toilet_available: false, water_available: false,
        long_term_enabled: false, minimum_booking_months: '', price_per_month: '',
        addon_gas_price: '', addon_side_mirrors_price: '', addon_tent_setup_price: '', addon_adapter_6_13_price: '', addon_cleaning_price: '',
        addon_toilet_emptying_price: '', addon_tent_rental_price: '', addon_tent_drying_price: ''
    });
    const [imageFiles, setImageFiles] = useState([]);
    const [addressError, setAddressError] = useState('');
    const [rateError, setRateError] = useState('');
    const [doorstepRateError, setDoorstepRateError] = useState('');
    const [depositError, setDepositError] = useState('');
    const [weightError, setWeightError] = useState('');
    const [imageError, setImageError] = useState('');
    const [seasonRates, setSeasonRates] = useState([]);
    const [seasonRateError, setSeasonRateError] = useState('');
    const [formError, setFormError] = useState('');

    useEffect(() => {
        if (typeof onRefreshMasterData === 'function') {
            onRefreshMasterData();
        }
    }, [onRefreshMasterData]);

    useEffect(() => {
        let ignore = false;
        const loadSeasons = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/seasons`);
                if (!response.ok) {
                    throw new Error('Failed to load seasons');
                }
                const data = await response.json();
                if (ignore) return;
                setSeasonRates(data.map((s) => ({
                    season_id: Number(s.season_id),
                    season_name: s.season_name,
                    price_per_day: 0,
                    price_per_week: 0,
                    price_per_month: ''
                })));
            } catch (err) {
                if (!ignore) {
                    setSeasonRateError(err.message || 'Failed to load seasons.');
                }
            }
        };
        loadSeasons();
        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (!masterTypes || masterTypes.length === 0) {
            return;
        }

        setFormData(prev => {
            const hasCurrentType = masterTypes.some(t => String(t.type_id) === String(prev.type_id));
            if (hasCurrentType) return prev;

            const nextTypeId = Number(masterTypes[0].type_id);
            const allowedForType = new Set(
                (typeAmenityMappings || [])
                    .filter(m => Number(m.type_id) === nextTypeId)
                    .map(m => String(m.amenity_id))
            );

            return {
                ...prev,
                type_id: String(masterTypes[0].type_id),
                amenities: prev.amenities.filter(a => allowedForType.has(String(a)))
            };
        });
    }, [masterTypes, typeAmenityMappings]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;
        setFormData(prev => {
            if (name === 'doorstep_available') {
                return {
                    ...prev,
                    [name]: nextValue,
                    doorstep_rate_per_km: nextValue ? prev.doorstep_rate_per_km : ''
                };
            }
            if (name !== 'type_id') {
                return { ...prev, [name]: nextValue };
            }
            const nextTypeId = Number(value);
            const allowedForType = new Set(
                (typeAmenityMappings || [])
                    .filter(m => Number(m.type_id) === nextTypeId)
                    .map(m => String(m.amenity_id))
            );
            const filteredAmenities = prev.amenities.filter(a => allowedForType.has(String(a)));
            return { ...prev, [name]: value, amenities: filteredAmenities };
        });
        if (name === 'doorstep_available' && !nextValue) {
            setDoorstepRateError('');
        }
    };

    const handleAmenityChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            amenities: checked ? [...prev.amenities, value] : prev.amenities.filter(a => a !== value)
        }));
    };

    const handleSeasonRateChange = (seasonId, field, value) => {
        setSeasonRates(prev => prev.map(row => (
            Number(row.season_id) === Number(seasonId)
                ? { ...row, [field]: value === '' ? '' : Number(value) }
                : row
        )));
    };

    const allowedAmenityIdSet = new Set(
        (typeAmenityMappings || [])
            .filter(m => Number(m.type_id) === Number(formData.type_id))
            .map(m => Number(m.amenity_id))
    );
    const filteredAmenities = masterAmenities.filter(a => allowedAmenityIdSet.has(Number(a.amenity_id)));
    const selectedTypeName = String(
        (masterTypes || []).find(t => String(t.type_id) === String(formData.type_id))?.type_name || ''
    );
    const isMotorhomeVehicle = isMotorhomeSpecType(selectedTypeName);
    const isStationaryVehicle = isStationarySpecType(selectedTypeName);

    useEffect(() => {
        if (!isStationaryVehicle) {
            return;
        }

        setFormData((prev) => {
            if (!prev.self_pick_enabled && !prev.doorstep_available && prev.doorstep_rate_per_km === '') {
                return prev;
            }

            return {
                ...prev,
                self_pick_enabled: false,
                doorstep_available: false,
                doorstep_rate_per_km: ''
            };
        });
    }, [isStationaryVehicle]);

    useEffect(() => {
        if (isStationaryVehicle) {
            return;
        }

        if (!formData.self_pick_enabled && !formData.doorstep_available) {
            setFormData((prev) => ({
                ...prev,
                doorstep_available: true
            }));
        }
    }, [formData.self_pick_enabled, formData.doorstep_available, isStationaryVehicle]);

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > MAX_CARAVAN_IMAGES) {
            setImageError(`You can upload a maximum of ${MAX_CARAVAN_IMAGES} images.`);
            setImageFiles([]);
            return;
        }
        const imageSizeError = buildImageSizeError(files);
        if (imageSizeError) {
            setImageError(imageSizeError);
            setImageFiles([]);
            return;
        }
        setImageError('');
        setImageFiles(files);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError('');
        if (!formData.name.trim()
            || !formData.location.trim()
            || !formData.registration_number.trim()
            || !formData.description.trim()
            || !String(formData.type_id || '').trim()
            || formData.amenities.length === 0) {
            setFormError('Caravan name, location, registration number, type, amenities, and description are required.');
            return;
        }
        if (!formData.street.trim() || !formData.city.trim() || !formData.zipcode.trim()) {
            setAddressError(t('addressRequired'));
            return;
        }
        setAddressError('');
        const dailyRate = Number(formData.price_per_day);
        const weeklyRate = Number(formData.price_per_week);
        const depositAmount = Number(formData.deposit_amount);
        const capacity = Number(formData.capacity);
        const beds = Number(formData.beds);
        if (!Number.isFinite(dailyRate) || dailyRate <= 0 || !Number.isFinite(weeklyRate) || weeklyRate <= 0) {
            setRateError('Price per day and price per week are required and must be greater than 0.');
            return;
        }
        if (weeklyRate > (dailyRate * 7)) {
            setRateError(t('weeklyRateValidation'));
            return;
        }
        setRateError('');
        if (!Number.isFinite(depositAmount) || depositAmount < 0) {
            setDepositError('Deposit amount is required and must be 0 or more.');
            return;
        }
        setDepositError('');
        const addonGasPrice = formData.addon_gas_price === '' ? null : Number(formData.addon_gas_price);
        const addonSideMirrorsPrice = formData.addon_side_mirrors_price === '' ? null : Number(formData.addon_side_mirrors_price);
        const addonTentSetupPrice = formData.addon_tent_setup_price === '' ? null : Number(formData.addon_tent_setup_price);
        const addonAdapter613Price = formData.addon_adapter_6_13_price === '' ? null : Number(formData.addon_adapter_6_13_price);
        const addonCleaningPrice = formData.addon_cleaning_price === '' ? null : Number(formData.addon_cleaning_price);
        const addonToiletEmptyingPrice = formData.addon_toilet_emptying_price === '' ? null : Number(formData.addon_toilet_emptying_price);
        const addonTentRentalPrice = formData.addon_tent_rental_price === '' ? null : Number(formData.addon_tent_rental_price);
        const addonTentDryingPrice = formData.addon_tent_drying_price === '' ? null : Number(formData.addon_tent_drying_price);
        if ([addonGasPrice, addonSideMirrorsPrice, addonTentSetupPrice, addonAdapter613Price, addonCleaningPrice, addonToiletEmptyingPrice, addonTentRentalPrice, addonTentDryingPrice].some((value) => value !== null && (Number.isNaN(value) || value < 0))) {
            setRateError('Add-on prices must be blank or non-negative.');
            return;
        }
        if (formData.toilet_available && (!Number.isFinite(addonToiletEmptyingPrice) || addonToiletEmptyingPrice < 0)) {
            setRateError('Emptying toilet price is required when the toilet can be used.');
            return;
        }
        if (!Number.isFinite(capacity) || capacity <= 0) {
            setFormError('Sitting is required and must be greater than 0.');
            return;
        }
        if (!Number.isFinite(beds) || beds <= 0) {
            setFormError('Beds are required and must be greater than 0.');
            return;
        }
        const longTermEnabled = Boolean(formData.long_term_enabled);
        const minimumBookingMonths = formData.minimum_booking_months === '' ? null : Number(formData.minimum_booking_months);
        const pricePerMonth = formData.price_per_month === '' ? null : Number(formData.price_per_month);
        const effectiveDoorstepAvailable = isStationaryVehicle
            ? false
            : (!formData.self_pick_enabled ? true : Boolean(formData.doorstep_available));
        const doorstepRatePerKm = formData.doorstep_rate_per_km === '' ? null : Number(formData.doorstep_rate_per_km);
        if (effectiveDoorstepAvailable) {
            if (doorstepRatePerKm === null || Number.isNaN(doorstepRatePerKm) || doorstepRatePerKm <= 0) {
                setDoorstepRateError('Doorstep rate per km must be greater than 0 when doorstep delivery is enabled.');
                return;
            }
        } else if (doorstepRatePerKm !== null && (Number.isNaN(doorstepRatePerKm) || doorstepRatePerKm < 0)) {
            setDoorstepRateError('Doorstep rate per km cannot be negative.');
            return;
        }
        if (longTermEnabled) {
            if (!Number.isFinite(minimumBookingMonths) || minimumBookingMonths <= 0) {
                setRateError('Minimum booking months must be greater than 0 when long-term booking is enabled.');
                return;
            }
            if (!Number.isFinite(pricePerMonth) || pricePerMonth <= 0) {
                setRateError('Price per month must be greater than 0 when long-term booking is enabled.');
                return;
            }
        }
        setDoorstepRateError('');
        let ownWeightKg = null;
        let totalWeightKg = null;
        let motorhomeYear = null;
        let motorhomeWidthM = null;
        let motorhomeLengthM = null;
        let motorhomeHeightM = null;

        if (isMotorhomeVehicle) {
            const model = (formData.motorhome_model || '').trim();
            const gearbox = (formData.motorhome_gearbox || '').trim();
            const fuelType = (formData.motorhome_fuel_type || '').trim();
            motorhomeYear = formData.motorhome_year === '' ? null : Number(formData.motorhome_year);
            motorhomeWidthM = formData.motorhome_width_m === '' ? null : Number(formData.motorhome_width_m);
            motorhomeLengthM = formData.motorhome_length_m === '' ? null : Number(formData.motorhome_length_m);
            motorhomeHeightM = formData.motorhome_height_m === '' ? null : Number(formData.motorhome_height_m);
            ownWeightKg = formData.motorhome_curb_weight_kg === '' ? null : Number(formData.motorhome_curb_weight_kg);
            totalWeightKg = formData.motorhome_allowed_total_weight_kg === '' ? null : Number(formData.motorhome_allowed_total_weight_kg);
            const minYear = 1900;
            const maxYear = new Date().getFullYear() + 1;

            if (!model || !gearbox || !fuelType) {
                setFormError('Model, year, gearbox, fuel type, dimensions, and weight details are required for Rooftop Camper, Rooftop, Campervan, and Motorhome types.');
                return;
            }
            if (motorhomeYear === null || !Number.isInteger(motorhomeYear) || motorhomeYear < minYear || motorhomeYear > maxYear) {
                setFormError(`Year must be between ${minYear} and ${maxYear}.`);
                return;
            }
            if (motorhomeWidthM === null || motorhomeLengthM === null || motorhomeHeightM === null) {
                setFormError('Width, length, and height are required.');
                return;
            }
            if ((Number.isNaN(motorhomeWidthM) || motorhomeWidthM <= 0)
                || (Number.isNaN(motorhomeLengthM) || motorhomeLengthM <= 0)
                || (Number.isNaN(motorhomeHeightM) || motorhomeHeightM <= 0)) {
                setFormError('Dimensions must be greater than 0.');
                return;
            }
            if (ownWeightKg === null || totalWeightKg === null) {
                setWeightError('Curb weight and allowed total weight are required.');
                return;
            }
            if ((Number.isNaN(ownWeightKg) || ownWeightKg <= 0)
                || (Number.isNaN(totalWeightKg) || totalWeightKg <= 0)) {
                setWeightError('Weights must be greater than 0.');
                return;
            }
            if (totalWeightKg < ownWeightKg) {
                setWeightError('Allowed total weight must be greater than or equal to curb weight.');
                return;
            }
        } else {
            ownWeightKg = formData.own_weight_kg === '' ? null : Number(formData.own_weight_kg);
            totalWeightKg = formData.total_weight_kg === '' ? null : Number(formData.total_weight_kg);
            if (ownWeightKg === null || totalWeightKg === null) {
                setWeightError('Own weight and total weight are required.');
                return;
            }
            if ((Number.isNaN(ownWeightKg) || ownWeightKg <= 0)
                || (Number.isNaN(totalWeightKg) || totalWeightKg <= 0)) {
                setWeightError('Weights must be greater than 0.');
                return;
            }
            if (totalWeightKg < ownWeightKg) {
                setWeightError('Total weight must be greater than or equal to own weight.');
                return;
            }
        }
        setWeightError('');
        if (!seasonRates.length) {
            setSeasonRateError('Season pricing is required.');
            return;
        }
        const hasInvalidSeasonRate = seasonRates.some((row) => {
            const day = Number(row.price_per_day);
            const week = Number(row.price_per_week);
            const month = row.price_per_month === '' ? null : Number(row.price_per_month);
            return Number.isNaN(day)
                || Number.isNaN(week)
                || day <= 0
                || week <= 0
                || week > (day * 7)
                || (longTermEnabled && (month === null || Number.isNaN(month) || month <= 0));
        });
        if (hasInvalidSeasonRate) {
            setSeasonRateError(longTermEnabled
                ? 'Each season must have valid day, week, and month prices. Week price must be <= 7 x day price.'
                : 'Each season must have day and week price greater than 0, and week price must be <= 7 x day price.');
            return;
        }
        setSeasonRateError('');
        if (imageFiles.length === 0) {
            setImageError('At least one image is required.');
            return;
        }
        if (imageFiles.length > MAX_CARAVAN_IMAGES) {
            setImageError(`You can upload a maximum of ${MAX_CARAVAN_IMAGES} images.`);
            return;
        }
        const imageSizeError = buildImageSizeError(imageFiles);
        if (imageSizeError) {
            setImageError(imageSizeError);
            return;
        }
        setImageError('');
        const address = {
            street: formData.street.trim(),
            city: formData.city.trim(),
            zipcode: formData.zipcode.trim()
        };
        const derivedLocation = address.street && address.city
            ? `${address.street}, ${address.city}${address.zipcode ? ` ${address.zipcode}` : ''}`
            : formData.location;
        const newCaravan = {
            id: crypto.randomUUID(), ...formData,
            location: derivedLocation,
            registration_number: (formData.registration_number || '').trim(),
            address,
            vehicle_category: isStationaryVehicle ? 'stationary' : (isMotorhomeVehicle ? 'motorhome' : 'caravan'),
            type_id: Number(formData.type_id),
            price_per_day: dailyRate,
            price_per_week: weeklyRate,
            deposit_amount: depositAmount,
            own_weight_kg: ownWeightKg,
            total_weight_kg: totalWeightKg,
            motorhome_model: isMotorhomeVehicle ? (formData.motorhome_model || '').trim() : null,
            motorhome_year: isMotorhomeVehicle ? motorhomeYear : null,
            motorhome_gearbox: isMotorhomeVehicle ? (formData.motorhome_gearbox || '').trim() : null,
            motorhome_fuel_type: isMotorhomeVehicle ? (formData.motorhome_fuel_type || '').trim() : null,
            motorhome_width_m: isMotorhomeVehicle ? motorhomeWidthM : null,
            motorhome_length_m: isMotorhomeVehicle ? motorhomeLengthM : null,
            motorhome_height_m: isMotorhomeVehicle ? motorhomeHeightM : null,
            motorhome_curb_weight_kg: isMotorhomeVehicle ? ownWeightKg : null,
            motorhome_allowed_total_weight_kg: isMotorhomeVehicle ? totalWeightKg : null,
            self_pick_enabled: isStationaryVehicle ? false : Boolean(formData.self_pick_enabled),
            doorstep_available: effectiveDoorstepAvailable,
            doorstep_rate_per_km: effectiveDoorstepAvailable ? doorstepRatePerKm : null,
            long_term_enabled: longTermEnabled,
            minimum_booking_months: longTermEnabled ? minimumBookingMonths : null,
            price_per_month: longTermEnabled ? pricePerMonth : null,
            addon_gas_price: addonGasPrice,
            addon_side_mirrors_price: addonSideMirrorsPrice,
            addon_tent_setup_price: addonTentSetupPrice,
            addon_adapter_6_13_price: addonAdapter613Price,
            addon_cleaning_price: addonCleaningPrice,
            toilet_available: Boolean(formData.toilet_available),
            water_available: Boolean(formData.water_available),
            addon_toilet_emptying_price: formData.toilet_available ? addonToiletEmptyingPrice : null,
            addon_tent_rental_price: addonTentRentalPrice,
            addon_tent_drying_price: addonTentDryingPrice,
            available_for_festival: Boolean(formData.available_for_festival),
            pets_allowed: Boolean(formData.pets_allowed),
            no_smoking: Boolean(formData.no_smoking),
            capacity: Number(formData.capacity),
            beds: Number(formData.beds),
            amenities: formData.amenities.map(a => Number(a)),
            seasonPrices: seasonRates.map((row) => ({
                season_id: Number(row.season_id),
                price_per_day: Number(row.price_per_day),
                price_per_week: Number(row.price_per_week),
                price_per_month: longTermEnabled ? Number(row.price_per_month) : null
            })),
            imageFiles,
            owner_id: user?.userId || 1,
            rating: BASE_CARAVAN_RATING, reviewCount: 0, isApproved: false
        };
        onAddCaravan(newCaravan);
        onNavigate({ page: 'list' });
    };

    return (
        <AuthPageWrapper title={ownerFormText.title} maxWidthClass="max-w-5xl" showLogo={false}>
            <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                    <label htmlFor="type_id" className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.type}</label>
                    <select name="type_id" id="type_id" required value={formData.type_id} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500">
                        {masterTypes.map(t => <option key={t.type_id} value={t.type_id}>{t.type_name}</option>)}
                    </select>
                </div>
                {isMotorhomeVehicle && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField label={ownerFormText.model} name="motorhome_model" value={formData.motorhome_model} onChange={handleChange} placeholder="" />
                            <InputField label={ownerFormText.year} name="motorhome_year" type="number" min="1900" max={new Date().getFullYear() + 1} value={formData.motorhome_year} onChange={handleChange} placeholder="" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="motorhome_gearbox" className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.gearbox}</label>
                                <select
                                    id="motorhome_gearbox"
                                    name="motorhome_gearbox"
                                    required
                                    value={formData.motorhome_gearbox}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                    <option value=""></option>
                                    <option value="Automatic">{ownerFormText.automatic}</option>
                                    <option value="Manual">{ownerFormText.manual}</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="motorhome_fuel_type" className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.fuelType}</label>
                                <select
                                    id="motorhome_fuel_type"
                                    name="motorhome_fuel_type"
                                    required
                                    value={formData.motorhome_fuel_type}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                    <option value=""></option>
                                    <option value="Diesel">{ownerFormText.diesel}</option>
                                    <option value="Patrol">{ownerFormText.petrol}</option>
                                    <option value="Gas">{ownerFormText.gas}</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <InputField label={ownerFormText.width} name="motorhome_width_m" type="number" step="0.01" min="0" value={formData.motorhome_width_m} onChange={handleChange} placeholder="" />
                            <InputField label={ownerFormText.length} name="motorhome_length_m" type="number" step="0.01" min="0" value={formData.motorhome_length_m} onChange={handleChange} placeholder="" />
                            <InputField label={ownerFormText.height} name="motorhome_height_m" type="number" step="0.01" min="0" value={formData.motorhome_height_m} onChange={handleChange} placeholder="" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField label={ownerFormText.curbWeight} name="motorhome_curb_weight_kg" type="number" value={formData.motorhome_curb_weight_kg} onChange={handleChange} placeholder="" />
                            <InputField label={ownerFormText.allowedTotalWeight} name="motorhome_allowed_total_weight_kg" type="number" value={formData.motorhome_allowed_total_weight_kg} onChange={handleChange} placeholder="" />
                        </div>
                        {weightError && <p className="text-sm text-red-600">{weightError}</p>}
                    </>
                )}
                <InputField label={ownerFormText.vehicleName} name="name" value={formData.name} onChange={handleChange} placeholder="" />
                <InputField label={ownerFormText.registrationNumber} name="registration_number" value={formData.registration_number} onChange={handleChange} placeholder="" />
                <InputField label={ownerFormText.location} name="location" value={formData.location} onChange={handleChange} placeholder="" />
                {formError && <p className="text-sm text-red-600">{formError}</p>}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.street}</label>
                        <textarea
                            id="street"
                            name="street"
                            rows="2"
                            maxLength={200}
                            value={formData.street}
                            onChange={handleChange}
                            placeholder=""
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.city}</label>
                        <textarea
                            id="city"
                            name="city"
                            rows="2"
                            maxLength={200}
                            value={formData.city}
                            onChange={handleChange}
                            placeholder=""
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>
                    <InputField label={ownerFormText.zipcode} name="zipcode" value={formData.zipcode} onChange={handleChange} placeholder="" />
                </div>
                {addressError && <p className="text-sm text-red-600">{addressError}</p>}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <InputField label={ownerFormText.pricePerDay} name="price_per_day" type="number" value={formData.price_per_day} onChange={handleChange} placeholder="" />
                    <InputField label={ownerFormText.pricePerWeek} name="price_per_week" type="number" value={formData.price_per_week} onChange={handleChange} placeholder="" />
                    <InputField label={ownerFormText.depositAmount} name="deposit_amount" type="number" value={formData.deposit_amount} onChange={handleChange} placeholder="" />
                    <InputField label={ownerFormText.sitting} name="capacity" type="number" value={formData.capacity} onChange={handleChange} placeholder="" />
                    <InputField label={ownerFormText.beds} name="beds" type="number" value={formData.beds} onChange={handleChange} placeholder="" />
                </div>
                <div className="rounded-md border-2 border-green-200 bg-green-50 p-4">
                    <div className="mb-4 rounded-md border border-green-200 bg-white px-3 py-2">
                        <h3 className="text-sm font-semibold text-green-800">{ownerFormText.addonsSection}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        <InputField label={ownerFormText.gasAddon} name="addon_gas_price" type="number" required={false} min="0" step="0.01" value={formData.addon_gas_price} onChange={handleChange} placeholder="" />
                        <InputField label={ownerFormText.sideMirrors} name="addon_side_mirrors_price" type="number" required={false} min="0" step="0.01" value={formData.addon_side_mirrors_price} onChange={handleChange} placeholder="" />
                        <InputField label={ownerFormText.tentSetup} name="addon_tent_setup_price" type="number" required={false} min="0" step="0.01" value={formData.addon_tent_setup_price} onChange={handleChange} placeholder="" />
                        <InputField label={ownerFormText.adapter} name="addon_adapter_6_13_price" type="number" required={false} min="0" step="0.01" value={formData.addon_adapter_6_13_price} onChange={handleChange} placeholder="" />
                        <InputField label={ownerFormText.cleaningAddon} name="addon_cleaning_price" type="number" required={false} min="0" step="0.01" value={formData.addon_cleaning_price} onChange={handleChange} placeholder="" />
                        {Boolean(formData.toilet_available) && <InputField label={ownerFormText.toiletEmptyingAddon} name="addon_toilet_emptying_price" type="number" required={true} min="0" step="0.01" value={formData.addon_toilet_emptying_price} onChange={handleChange} placeholder="" />}
                        <InputField label={ownerFormText.tentRentalAddon} name="addon_tent_rental_price" type="number" required={false} min="0" step="0.01" value={formData.addon_tent_rental_price} onChange={handleChange} placeholder="" />
                        <InputField label={ownerFormText.tentDryingAddon} name="addon_tent_drying_price" type="number" required={false} min="0" step="0.01" value={formData.addon_tent_drying_price} onChange={handleChange} placeholder="" />
                    </div>
                </div>
                <div className="border rounded-md p-4 bg-white space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">{ownerFormText.settingsSection}</h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm font-medium text-gray-700">
                            <input type="checkbox" name="toilet_available" checked={Boolean(formData.toilet_available)} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                            {ownerFormText.toiletAvailable}
                        </label>
                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm font-medium text-gray-700">
                            <input type="checkbox" name="water_available" checked={Boolean(formData.water_available)} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                            {ownerFormText.waterAvailable}
                        </label>
                    </div>
                    <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                name="self_pick_enabled"
                                checked={Boolean(formData.self_pick_enabled)}
                                onChange={handleChange}
                                disabled={isStationaryVehicle}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            {ownerFormText.selfPickEnabled}
                        </label>
                    </div>
                    <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                name="doorstep_available"
                                checked={Boolean(!isStationaryVehicle && (!formData.self_pick_enabled || formData.doorstep_available))}
                                onChange={handleChange}
                                disabled={isStationaryVehicle || !formData.self_pick_enabled}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            {ownerFormText.doorstepAvailable}
                        </label>
                    </div>
                    <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                name="long_term_enabled"
                                checked={Boolean(formData.long_term_enabled)}
                                onChange={handleChange}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            {ownerFormText.longTermEnabled}
                        </label>
                    </div>
                    {Boolean(formData.long_term_enabled) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField label={ownerFormText.minimumBookingMonths} name="minimum_booking_months" type="number" required={true} min="1" value={formData.minimum_booking_months} onChange={handleChange} placeholder="" />
                            <InputField label={ownerFormText.pricePerMonth} name="price_per_month" type="number" required={true} min="0" step="0.01" value={formData.price_per_month} onChange={handleChange} placeholder="" />
                        </div>
                    )}
                    <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                name="available_for_festival"
                                checked={Boolean(formData.available_for_festival)}
                                onChange={handleChange}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            {ownerFormText.festivalAvailability}
                        </label>
                    </div>
                    <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                name="pets_allowed"
                                checked={Boolean(formData.pets_allowed)}
                                onChange={handleChange}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            {ownerFormText.petsAllowed}
                        </label>
                    </div>
                    <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                name="no_smoking"
                                checked={Boolean(formData.no_smoking)}
                                onChange={handleChange}
                                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            {ownerFormText.noSmoking}
                        </label>
                    </div>
                    {Boolean(!isStationaryVehicle && (!formData.self_pick_enabled || formData.doorstep_available)) && (
                        <InputField
                            label={ownerFormText.doorstepRate}
                            name="doorstep_rate_per_km"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.doorstep_rate_per_km}
                            onChange={handleChange}
                            placeholder=""
                        />
                    )}
                </div>
                {doorstepRateError && <p className="text-sm text-red-600">{doorstepRateError}</p>}
                {rateError && <p className="text-sm text-red-600">{rateError}</p>}
                {depositError && <p className="text-sm text-red-600">{depositError}</p>}
                {!isMotorhomeVehicle && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField label={ownerFormText.ownWeight} name="own_weight_kg" type="number" value={formData.own_weight_kg} onChange={handleChange} placeholder="" />
                        <InputField label={ownerFormText.totalWeight} name="total_weight_kg" type="number" value={formData.total_weight_kg} onChange={handleChange} placeholder="" />
                    </div>
                )}
                {!isMotorhomeVehicle && weightError && <p className="text-sm text-red-600">{weightError}</p>}
                <div className="border rounded-md p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">{ownerFormText.seasonPricing}</h3>
                    <div className="space-y-3">
                        {seasonRates.map((row) => (
                            <div key={row.season_id} className={`grid grid-cols-1 gap-3 ${formData.long_term_enabled ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                                <p className="font-medium text-gray-700 flex items-center">{row.season_name}</p>
                                <InputField
                                    label={ownerFormText.seasonPerDay}
                                    name={`season_day_${row.season_id}`}
                                    type="number"
                                    min="1"
                                    value={row.price_per_day}
                                    onChange={(e) => handleSeasonRateChange(row.season_id, 'price_per_day', e.target.value)}
                                    required={true}
                                />
                                <InputField
                                    label={ownerFormText.seasonPerWeek}
                                    name={`season_week_${row.season_id}`}
                                    type="number"
                                    min="1"
                                    value={row.price_per_week}
                                    onChange={(e) => handleSeasonRateChange(row.season_id, 'price_per_week', e.target.value)}
                                    required={true}
                                />
                                {Boolean(formData.long_term_enabled) && (
                                    <InputField
                                        label={ownerFormText.seasonPerMonth}
                                        name={`season_month_${row.season_id}`}
                                        type="number"
                                        min="1"
                                        value={row.price_per_month}
                                        onChange={(e) => handleSeasonRateChange(row.season_id, 'price_per_month', e.target.value)}
                                        required={true}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    {seasonRateError && <p className="text-sm text-red-600 mt-2">{seasonRateError}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.amenities}</label>
                    <div className="grid grid-cols-2 gap-2 border p-2 rounded-md max-h-32 overflow-y-auto">
                        {filteredAmenities.map(amenity => (
                            <label key={amenity.amenity_id} className="flex items-center space-x-2">
                                <input type="checkbox" value={amenity.amenity_id} checked={formData.amenities.includes(String(amenity.amenity_id))} onChange={handleAmenityChange} className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                                <span>{amenity.amenity_name}</span>
                            </label>
                        ))}
                        {filteredAmenities.length === 0 && <p className="text-sm text-gray-500 col-span-2">{ownerFormText.noAmenities}</p>}
                    </div>
                </div>
                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.description}</label>
                    <textarea name="description" id="description" required value={formData.description} onChange={handleChange} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder=""></textarea>
                </div>
                <div>
                    <label htmlFor="caravan-images" className="block text-sm font-medium text-gray-700 mb-1">{ownerFormText.images}</label>
                    <LocalizedFileInput
                        id="caravan-images"
                        selectedFiles={imageFiles}
                        accept="image/png,image/jpeg,image/webp"
                        multiple
                        onChange={handleImageChange}
                        className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">{ownerFormText.selectedFiles(imageFiles.length)}</p>
                    {imageError && <p className="text-sm text-red-600 mt-1">{imageError}</p>}
                </div>

                <div>
                    <button type="submit" className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                        {ownerFormText.submit}
                    </button>
                </div>
            </form>
        </AuthPageWrapper>
    );
};

// --- NEW: Reusable CRUD Management Component ---
const CrudManager = ({ title, items, idKey, nameKey, onAdd, onUpdate, onDelete, isLoading, error }) => {
    const [newItemName, setNewItemName] = useState('');
    const [editingItem, setEditingItem] = useState(null); // { id: 1, name: 'current name' }

    const handleAdd = () => {
        if (newItemName.trim()) {
            onAdd(newItemName.trim());
            setNewItemName('');
        }
    };

    const handleStartEdit = (item) => {
        setEditingItem({ id: item[idKey], name: item[nameKey] });
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
    };

    const handleSaveEdit = () => {
        if (editingItem && editingItem.name.trim()) {
            onUpdate(editingItem.id, editingItem.name.trim());
            setEditingItem(null);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-2xl font-semibold mb-4">{title}</h3>
            {isLoading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}

            <ul className="space-y-2 mb-4">
                {items.map(item => (
                    <li key={item[idKey]} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        {editingItem?.id === item[idKey] ? (
                            <input
                                type="text"
                                value={editingItem.name}
                                onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                                className="flex-grow px-2 py-1 border border-gray-300 rounded-md"
                            />
                        ) : (
                            <span>{item[nameKey]}</span>
                        )}
                        <div className="space-x-2">
                            {editingItem?.id === item[idKey] ? (
                                <>
                                    <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800 font-semibold">Save</button>
                                    <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">Cancel</button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => handleStartEdit(item)} className="text-blue-600 hover:text-blue-800">Edit</button>
                                    <button onClick={() => onDelete(item[idKey])} className="text-red-600 hover:text-red-800">Delete</button>
                                </>
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            <div className="flex space-x-2 border-t pt-4">
                <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder=""
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button onClick={handleAdd} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Add</button>
            </div>
        </div>
    );
};

const AmenityManager = ({ items, onAdd, onUpdate, onSetStatus, isLoading, error }) => {
    const [newItemName, setNewItemName] = useState('');
    const [editingItem, setEditingItem] = useState(null); // { id, name }

    const handleAdd = () => {
        if (!newItemName.trim()) return;
        onAdd(newItemName.trim());
        setNewItemName('');
    };

    const handleStartEdit = (item) => {
        setEditingItem({ id: item.amenity_id, name: item.amenity_name || '' });
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
    };

    const handleSaveEdit = () => {
        if (!editingItem || !editingItem.name.trim()) return;
        onUpdate(editingItem.id, editingItem.name.trim());
        setEditingItem(null);
    };

    const handleToggleStatus = (item, isActive) => {
        const nextActive = !isActive;
        const actionText = nextActive ? 'activate' : 'deactivate';
        const targetName = item?.amenity_name || `Amenity ${item?.amenity_id ?? ''}`;
        const confirmed = window.confirm(`Are you sure you want to ${actionText} ${targetName}?`);
        if (!confirmed) {
            return;
        }
        onSetStatus(item.amenity_id, nextActive);
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-2xl font-semibold mb-4">Amenities</h3>
            {isLoading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}

            <ul className="space-y-2 mb-4">
                {items.map(item => {
                    const isActive = item?.is_active !== false;
                    return (
                        <li key={item.amenity_id} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2 min-w-0">
                                {editingItem?.id === item.amenity_id ? (
                                    <input
                                        type="text"
                                        value={editingItem.name}
                                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                                        className="px-2 py-1 border border-gray-300 rounded-md"
                                    />
                                ) : (
                                    <span className="truncate">{item.amenity_name}</span>
                                )}
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className="space-x-2 shrink-0">
                                {editingItem?.id === item.amenity_id ? (
                                    <>
                                        <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800 font-semibold">Save</button>
                                        <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => handleStartEdit(item)} className="text-blue-600 hover:text-blue-800">Edit</button>
                                        <button
                                            onClick={() => handleToggleStatus(item, isActive)}
                                            className={isActive ? 'text-amber-700 hover:text-amber-900' : 'text-green-700 hover:text-green-900'}
                                        >
                                            {isActive ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            <div className="flex space-x-2 border-t pt-4">
                <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder=""
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button onClick={handleAdd} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Add</button>
            </div>
        </div>
    );
};

const CaravanTypeManager = ({ items, onAdd, onUpdate, onSetStatus, isLoading, error }) => {
    const [newItemName, setNewItemName] = useState('');
    const [editingItem, setEditingItem] = useState(null); // { id, name }

    const handleAdd = () => {
        if (!newItemName.trim()) return;
        onAdd(newItemName.trim());
        setNewItemName('');
    };

    const handleStartEdit = (item) => {
        setEditingItem({ id: item.type_id, name: item.type_name || '' });
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
    };

    const handleSaveEdit = () => {
        if (!editingItem || !editingItem.name.trim()) return;
        onUpdate(editingItem.id, editingItem.name.trim());
        setEditingItem(null);
    };

    const handleToggleStatus = (item, isActive) => {
        const nextActive = !isActive;
        const actionText = nextActive ? 'activate' : 'deactivate';
        const targetName = item?.type_name || `Type ${item?.type_id ?? ''}`;
        const confirmed = window.confirm(`Are you sure you want to ${actionText} ${targetName}?`);
        if (!confirmed) {
            return;
        }
        onSetStatus(item.type_id, nextActive);
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-2xl font-semibold mb-4">Caravan Types</h3>
            {isLoading && <p>Loading...</p>}
            {error && <p className="text-red-500">{error}</p>}

            <ul className="space-y-2 mb-4">
                {items.map(item => {
                    const isActive = item?.is_active !== false;
                    return (
                        <li key={item.type_id} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2 min-w-0">
                                {editingItem?.id === item.type_id ? (
                                    <input
                                        type="text"
                                        value={editingItem.name}
                                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                                        className="px-2 py-1 border border-gray-300 rounded-md"
                                    />
                                ) : (
                                    <span className="truncate">{item.type_name}</span>
                                )}
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className="space-x-2 shrink-0">
                                {editingItem?.id === item.type_id ? (
                                    <>
                                        <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800 font-semibold">Save</button>
                                        <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => handleStartEdit(item)} className="text-blue-600 hover:text-blue-800">Edit</button>
                                        <button
                                            onClick={() => handleToggleStatus(item, isActive)}
                                            className={isActive ? 'text-amber-700 hover:text-amber-900' : 'text-green-700 hover:text-green-900'}
                                        >
                                            {isActive ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            <div className="flex space-x-2 border-t pt-4">
                <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder=""
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button onClick={handleAdd} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Add</button>
            </div>
        </div>
    );
};

const TypeAmenityMappingManager = ({ caravanTypes, amenities, mappings, onSave }) => {
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [selectedAmenityIds, setSelectedAmenityIds] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!selectedTypeId && caravanTypes.length > 0) {
            setSelectedTypeId(String(caravanTypes[0].type_id));
        }
    }, [caravanTypes, selectedTypeId]);

    useEffect(() => {
        if (!selectedTypeId) return;
        const mapped = mappings
            .filter(m => Number(m.type_id) === Number(selectedTypeId))
            .map(m => Number(m.amenity_id));
        setSelectedAmenityIds(mapped);
    }, [selectedTypeId, mappings]);

    const toggleAmenity = (amenityId, checked) => {
        setSelectedAmenityIds(prev => checked ? [...new Set([...prev, amenityId])] : prev.filter(id => id !== amenityId));
    };

    const save = async () => {
        if (!selectedTypeId) return;
        setIsSaving(true);
        try {
            await onSave(Number(selectedTypeId), selectedAmenityIds);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-2xl font-semibold mb-4">Type-Amenity Mapping</h3>
            <div className="mb-4">
                <label htmlFor="mappingType" className="block text-sm font-medium text-gray-700 mb-1">Caravan Type</label>
                <select id="mappingType" value={selectedTypeId} onChange={(e) => setSelectedTypeId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500">
                    {caravanTypes.map(t => <option key={t.type_id} value={t.type_id}>{t.type_name}</option>)}
                </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border p-3 rounded-md max-h-72 overflow-y-auto">
                {amenities.map(a => (
                    <label key={a.amenity_id} className="flex items-center space-x-2">
                        <input type="checkbox" checked={selectedAmenityIds.includes(Number(a.amenity_id))} onChange={(e) => toggleAmenity(Number(a.amenity_id), e.target.checked)} className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500" />
                        <span>{a.amenity_name}</span>
                    </label>
                ))}
            </div>
            <button onClick={save} disabled={isSaving || !selectedTypeId} className="mt-4 bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700 disabled:bg-gray-400">
                {isSaving ? 'Saving...' : 'Save Mapping'}
            </button>
        </div>
    );
};

const SeasonMonthConfigManager = ({ seasons, monthConfig, onSave, isSaving }) => {
    const [draft, setDraft] = useState([]);

    useEffect(() => {
        setDraft((monthConfig || []).map(item => ({
            month_number: Number(item.month_number),
            season_id: Number(item.season_id)
        })));
    }, [monthConfig]);

    const changeMonthSeason = (monthNumber, seasonId) => {
        setDraft(prev => prev.map(item => (
            Number(item.month_number) === Number(monthNumber)
                ? { ...item, season_id: Number(seasonId) }
                : item
        )));
    };

    const save = () => {
        if (draft.length === 12) {
            onSave(draft);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-2xl font-semibold mb-4">Season Month Configuration</h3>
            <p className="text-sm text-gray-600 mb-4">Assign each month to High, Medium, or Low season.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {draft
                    .sort((a, b) => Number(a.month_number) - Number(b.month_number))
                    .map((item) => (
                        <div key={item.month_number} className="grid grid-cols-2 gap-3 border rounded-md p-3">
                            <p className="font-medium">{MONTH_NAMES[item.month_number - 1]}</p>
                            <select
                                value={item.season_id}
                                onChange={(e) => changeMonthSeason(item.month_number, e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md"
                            >
                                {seasons.map((season) => (
                                    <option key={season.season_id} value={season.season_id}>
                                        {season.season_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}
            </div>
            <button onClick={save} disabled={isSaving || draft.length !== 12} className="mt-4 bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700 disabled:bg-gray-400">
                {isSaving ? 'Saving...' : 'Save Month Mapping'}
            </button>
        </div>
    );
};


const AdminPage = ({ user, caravans, onApproveCaravan, initialTypeAmenityMappings, onSaveTypeAmenityMapping }) => {
    const { language, t } = useContext(LanguageContext);
    const [activeTab, setActiveTab] = useState('approvals');

    //1
    const [unapprovedCaravans, setUnapprovedCaravans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pendingBookings, setPendingBookings] = useState([]);
    const [bookingHistory, setBookingHistory] = useState([]);

    // State for Amenities
    const [amenities, setAmenities] = useState([]);
    const [amenitiesLoading, setAmenitiesLoading] = useState(true);
    const [amenitiesError, setAmenitiesError] = useState(null);

    // State for Caravan Types
    const [caravanTypes, setCaravanTypes] = useState([]);
    const [typesLoading, setTypesLoading] = useState(true);
    const [typesError, setTypesError] = useState(null);
    const [typeAmenityMappings, setTypeAmenityMappings] = useState(initialTypeAmenityMappings || []);
    const [seasons, setSeasons] = useState([]);
    const [monthConfig, setMonthConfig] = useState([]);
    const [seasonConfigError, setSeasonConfigError] = useState(null);
    const [isSeasonConfigSaving, setIsSeasonConfigSaving] = useState(false);
    const [emailSettings, setEmailSettings] = useState({ email_language: 'da' });
    const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
    const [emailSettingsError, setEmailSettingsError] = useState('');
    const [emailSettingsSuccess, setEmailSettingsSuccess] = useState('');
    const [isEmailSettingsSaving, setIsEmailSettingsSaving] = useState(false);
    const [termsDocuments, setTermsDocuments] = useState([]);
    const [termsDocumentsLoading, setTermsDocumentsLoading] = useState(false);
    const [termsDocumentsError, setTermsDocumentsError] = useState('');
    const [termsDocumentsSuccess, setTermsDocumentsSuccess] = useState('');
    const [termsUploadFiles, setTermsUploadFiles] = useState({ customer: null, owner: null });
    const [termsUploadInputKeys, setTermsUploadInputKeys] = useState({ customer: 0, owner: 0 });
    const [uploadingTermsAudience, setUploadingTermsAudience] = useState('');
    const [restoringTermsDocumentId, setRestoringTermsDocumentId] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState('');
    const [deliveryAdminPartners, setDeliveryAdminPartners] = useState([]);
    const [deliveryAdminJobs, setDeliveryAdminJobs] = useState([]);
    const [deliveryAdminLoading, setDeliveryAdminLoading] = useState(false);
    const [deliveryAdminError, setDeliveryAdminError] = useState('');
    const [deliveryAdminPartnerActionUserId, setDeliveryAdminPartnerActionUserId] = useState(null);
    const [deliveryAdminActionJobId, setDeliveryAdminActionJobId] = useState(null);
    const [deliveryAdminPaymentJobId, setDeliveryAdminPaymentJobId] = useState(null);
    const [deliveryJobPaymentDrafts, setDeliveryJobPaymentDrafts] = useState({});
    const [deliveryJobPaymentErrors, setDeliveryJobPaymentErrors] = useState({});
    const [savingRoleUserId, setSavingRoleUserId] = useState(null);
    const [savingUserStatusId, setSavingUserStatusId] = useState(null);
    const [damageCategories, setDamageCategories] = useState([]);
    const [damageCategoriesLoading, setDamageCategoriesLoading] = useState(false);
    const [damageCategoriesError, setDamageCategoriesError] = useState('');
    const [damageCategoryDraft, setDamageCategoryDraft] = useState({
        categoryType: 'Damage',
        categoryName: '',
        standardPrice: ''
    });
    const [savingDamageCategoryId, setSavingDamageCategoryId] = useState(null);
    const [pendingCheckoutReviews, setPendingCheckoutReviews] = useState([]);
    const [checkoutReviewError, setCheckoutReviewError] = useState('');
    const [checkoutReviewSuccess, setCheckoutReviewSuccess] = useState('');
    const [loadingReviewBookingId, setLoadingReviewBookingId] = useState(null);
    const [submittingReviewBookingId, setSubmittingReviewBookingId] = useState(null);
    const [approvingBookingId, setApprovingBookingId] = useState(null);
    const [rejectingBookingId, setRejectingBookingId] = useState(null);
    const [bookingRejectRemarksById, setBookingRejectRemarksById] = useState({});
    const [bookingCancelRemarksById, setBookingCancelRemarksById] = useState({});
    const [cancellingBookingId, setCancellingBookingId] = useState(null);
    const [markingCancellationRefundKey, setMarkingCancellationRefundKey] = useState('');
    const [bookingCancellationError, setBookingCancellationError] = useState('');
    const [bookingCancellationSuccess, setBookingCancellationSuccess] = useState('');
    const [rejectingCaravanId, setRejectingCaravanId] = useState(null);
    const [caravanRejectRemarksById, setCaravanRejectRemarksById] = useState({});
    const [doorstepApprovalByBookingId, setDoorstepApprovalByBookingId] = useState({});
    const [fullPaymentApprovalByBookingId, setFullPaymentApprovalByBookingId] = useState({});
    const [markingRentPaidBookingId, setMarkingRentPaidBookingId] = useState(null);
    const [expandedReviewBookingId, setExpandedReviewBookingId] = useState(null);
    const [assessmentByBookingId, setAssessmentByBookingId] = useState({});
    const [reviewFormsByBookingId, setReviewFormsByBookingId] = useState({});
    const [dueInvoices, setDueInvoices] = useState([]);
    const [dueInvoicesLoading, setDueInvoicesLoading] = useState(false);
    const [dueInvoicesError, setDueInvoicesError] = useState('');
    const [clearingDueInvoiceId, setClearingDueInvoiceId] = useState(null);
    const [dueClearCommentById, setDueClearCommentById] = useState({});
    const [rentReminderQueue, setRentReminderQueue] = useState([]);
    const [rentReminderQueueError, setRentReminderQueueError] = useState('');
    const [sendingRentReminderBookingId, setSendingRentReminderBookingId] = useState(null);
    const [rentPendingReport, setRentPendingReport] = useState([]);
    const [rentPendingReportError, setRentPendingReportError] = useState('');
    const [adminCaravans, setAdminCaravans] = useState([]);
    const [adminCaravansError, setAdminCaravansError] = useState('');
    const [bookingOverviewDraftStartDate, setBookingOverviewDraftStartDate] = useState(() => getAdminBookingOverviewDefaultStartDate());
    const [bookingOverviewDraftEndDate, setBookingOverviewDraftEndDate] = useState(() => getAdminBookingOverviewDefaultEndDate());
    const [bookingOverviewAppliedStartDate, setBookingOverviewAppliedStartDate] = useState(() => getAdminBookingOverviewDefaultStartDate());
    const [bookingOverviewAppliedEndDate, setBookingOverviewAppliedEndDate] = useState(() => getAdminBookingOverviewDefaultEndDate());
    const [bookingOverviewBlocks, setBookingOverviewBlocks] = useState([]);
    const [bookingOverviewLoading, setBookingOverviewLoading] = useState(false);
    const [bookingOverviewError, setBookingOverviewError] = useState('');
    const [bookingOverviewInitialized, setBookingOverviewInitialized] = useState(false);
    const [bookingOverviewLayout, setBookingOverviewLayout] = useState('matrix');
    const [bookingOverviewGranularity, setBookingOverviewGranularity] = useState('day');
    const [bookingOverviewShowWeekDates, setBookingOverviewShowWeekDates] = useState(false);
    const [bookingHistoryStatusFilter, setBookingHistoryStatusFilter] = useState('all');
    const [bookingHistorySort, setBookingHistorySort] = useState('bookingDateDesc');
    const [selectedOverviewBookingId, setSelectedOverviewBookingId] = useState(null);
    const [unlistedFutureBookingsReport, setUnlistedFutureBookingsReport] = useState([]);
    const [unlistedFutureBookingsError, setUnlistedFutureBookingsError] = useState('');
    const [updatingListingCaravanId, setUpdatingListingCaravanId] = useState(null);
    const [openComplaints, setOpenComplaints] = useState([]);
    const [openComplaintsError, setOpenComplaintsError] = useState('');
    const [openComplaintsLoading, setOpenComplaintsLoading] = useState(false);
    const [actingComplaintId, setActingComplaintId] = useState(null);
    const [checkInPhotoUrlsByBookingId, setCheckInPhotoUrlsByBookingId] = useState({});
    const [imageSliderState, setImageSliderState] = useState({
        open: false,
        images: [],
        index: 0,
        title: ''
    });
    const termsDocumentsByAudience = useMemo(() => groupTermsDocumentsByAudience(termsDocuments), [termsDocuments]);
    const cancelledBookings = useMemo(() => (
        (Array.isArray(bookingHistory) ? bookingHistory : []).filter((booking) => {
            const status = String(booking?.bookingStatus || '').trim().toLowerCase();
            return status === 'cancelled';
        })
    ), [bookingHistory]);
    const bookingHistoryStatuses = useMemo(() => (
        [...new Set((Array.isArray(bookingHistory) ? bookingHistory : [])
            .map((booking) => String(booking?.bookingStatus || '').trim().toLowerCase())
            .filter(Boolean))]
            .sort()
    ), [bookingHistory]);
    const filteredSortedBookingHistory = useMemo(() => {
        const rows = (Array.isArray(bookingHistory) ? bookingHistory : []).filter((booking) => (
            bookingHistoryStatusFilter === 'all'
            || String(booking?.bookingStatus || '').trim().toLowerCase() === bookingHistoryStatusFilter
        ));
        const dateValue = (booking, field) => {
            const value = booking?.[field] ?? booking?.[field.charAt(0).toUpperCase() + field.slice(1)];
            const parsed = value ? new Date(value).getTime() : 0;
            return Number.isFinite(parsed) ? parsed : 0;
        };
        const sortConfig = {
            bookingDateAsc: ['createdAt', 1],
            bookingDateDesc: ['createdAt', -1],
            startDateAsc: ['startDate', 1],
            startDateDesc: ['startDate', -1],
            endDateAsc: ['endDate', 1],
            endDateDesc: ['endDate', -1]
        }[bookingHistorySort] || ['createdAt', -1];
        return [...rows].sort((left, right) => (
            (dateValue(left, sortConfig[0]) - dateValue(right, sortConfig[0])) * sortConfig[1]
        ));
    }, [bookingHistory, bookingHistorySort, bookingHistoryStatusFilter]);
    const selectedOverviewBooking = useMemo(() => (
        (Array.isArray(bookingHistory) ? bookingHistory : []).find(
            (booking) => Number(booking?.bookingId) === Number(selectedOverviewBookingId)
        ) || null
    ), [bookingHistory, selectedOverviewBookingId]);
    const pendingCancellationRefundCount = useMemo(() => (
        cancelledBookings.filter((booking) => {
            const rentPending = toAmount(booking?.cancellationRentRefundAmount) > 0 && !Boolean(booking?.cancellationRentRefundPaid);
            const depositPending = toAmount(booking?.cancellationDepositRefundAmount) > 0 && !Boolean(booking?.cancellationDepositRefundPaid);
            return rentPending || depositPending;
        }).length
    ), [cancelledBookings]);

    const getAdminBookingStatusLabel = (status) => {
        const normalizedStatus = String(status || '').trim().toLowerCase();
        switch (normalizedStatus) {
            case 'pending':
                return t('adminBookingStatusPending');
            case 'confirmed':
                return t('adminBookingStatusConfirmed');
            case 'checked_in':
                return t('adminBookingStatusCheckedIn');
            case 'checkout_pending_customer':
                return t('adminBookingStatusCheckoutPendingCustomer');
            case 'checkout_pending_approval':
                return t('adminBookingStatusCheckoutPendingApproval');
            case 'checkout_rejected':
                return t('adminBookingStatusCheckoutRejected');
            case 'checked_out':
                return t('adminBookingStatusCheckedOut');
            case 'completed':
                return t('adminBookingStatusCompleted');
            case 'expired':
                return t('adminBookingStatusExpired');
            case 'cancelled':
                return t('adminBookingStatusCancelled');
            default:
                return status
                    ? String(status).replace(/_/g, ' ')
                    : t('adminNotAvailable');
        }
    };

    const getAdminCheckoutReviewStatusLabel = (status) => {
        const normalizedStatus = String(status || '').trim().toLowerCase();
        switch (normalizedStatus) {
            case '':
                return t('adminNotAvailable');
            case 'pending':
                return t('adminCheckoutReviewStatusPending');
            case 'approved':
                return t('adminCheckoutReviewStatusApproved');
            case 'rejected':
                return t('adminCheckoutReviewStatusRejected');
            default:
                return String(status).replace(/_/g, ' ');
        }
    };

    const getDefaultReviewFormState = useCallback(() => ({
        comment: '',
        itemComments: {},
        markOwnerTransferPaid: false,
        ownerTransferReference: '',
        markCustomerRefundPaid: false,
        customerRefundReference: ''
    }), []);

    const fetchUnapproved = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/caravans/unapproved`);
            if (!response.ok) throw new Error('Failed to fetch approval queue');
            const data = await response.json();
            setUnapprovedCaravans(data);
        } catch (err) {
            setError("Could not fetch approval queue. Is the API running?");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchPendingBookings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/pending`);
            if (!response.ok) throw new Error('Failed to fetch pending bookings');
            const data = await response.json();
            setPendingBookings(data);
        } catch (err) {
            setError("Could not fetch pending bookings. Is the API running?");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchBookingHistory = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/history`);
            if (!response.ok) throw new Error('Failed to fetch booking history');
            const data = await response.json();
            setBookingHistory(data);
        } catch (err) {
            setError("Could not fetch booking history. Is the API running?");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchPendingCheckoutReviews = useCallback(async () => {
        setCheckoutReviewError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/checkout-reviews/pending`);
            if (!response.ok) throw new Error('Failed to fetch pending checkout reviews');
            const data = await response.json();
            setPendingCheckoutReviews(Array.isArray(data) ? data : []);
        } catch (err) {
            setCheckoutReviewError(err.message || 'Could not fetch pending checkout reviews.');
        }
    }, []);

    const fetchSeasonConfig = useCallback(async () => {
        setSeasonConfigError(null);
        try {
            const [seasonsRes, configRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/seasons`),
                fetch(`${API_BASE_URL}/api/seasons/month-config`)
            ]);
            if (!seasonsRes.ok) throw new Error('Failed to fetch seasons');
            if (!configRes.ok) throw new Error('Failed to fetch month mapping');

            const seasonsData = await seasonsRes.json();
            const configData = await configRes.json();
            setSeasons(seasonsData);
            setMonthConfig(configData);
        } catch (err) {
            setSeasonConfigError(err.message || 'Failed to load season config.');
        }

    }, []);
    const fetchEmailSettings = useCallback(async () => {
        setEmailSettingsLoading(true);
        setEmailSettingsError('');
        setEmailSettingsSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin-settings/email`);
            if (!response.ok) throw new Error(await readApiErrorMessage(response, 'Failed to load email settings.'));
            const data = await response.json();
            setEmailSettings({
                email_language: data?.email_language === 'en' ? 'en' : 'da'
            });
        } catch (err) {
            setEmailSettingsError(err.message || 'Failed to load email settings.');
        } finally {
            setEmailSettingsLoading(false);
        }
    }, []);
    const fetchTermsDocuments = useCallback(async () => {
        setTermsDocumentsLoading(true);
        setTermsDocumentsError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/terms-documents`);
            if (!response.ok) throw new Error(await readApiErrorMessage(response, 'Failed to load terms PDFs.'));
            const data = await response.json();
            setTermsDocuments(Array.isArray(data) ? data : []);
        } catch (err) {
            setTermsDocumentsError(err.message || 'Failed to load terms PDFs.');
        } finally {
            setTermsDocumentsLoading(false);
        }
    }, []);
    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        setUsersError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/users`);
            if (!response.ok) throw new Error('Failed to fetch users');
            const data = await response.json();
            setUsers(Array.isArray(data) ? data : []);
        } catch (err) {
            setUsersError(err.message || 'Could not fetch users.');
        } finally {
            setUsersLoading(false);
        }
    }, []);
    const fetchDeliveryAdmin = useCallback(async () => {
        setDeliveryAdminLoading(true);
        setDeliveryAdminError('');
        try {
            const [partnersResponse, jobsResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/api/delivery-partners/admin/partners`),
                fetch(`${API_BASE_URL}/api/delivery-partners/admin/jobs`)
            ]);
            if (!partnersResponse.ok) {
                throw new Error(await readApiErrorMessage(partnersResponse, 'Failed to fetch delivery partners.'));
            }
            if (!jobsResponse.ok) {
                throw new Error(await readApiErrorMessage(jobsResponse, 'Failed to fetch delivery jobs.'));
            }

            const [partnersData, jobsData] = await Promise.all([
                partnersResponse.json(),
                jobsResponse.json()
            ]);

            setDeliveryAdminPartners(Array.isArray(partnersData) ? partnersData : []);
            setDeliveryAdminJobs(Array.isArray(jobsData) ? jobsData : []);
        } catch (err) {
            setDeliveryAdminError(err.message || 'Could not fetch delivery center data.');
        } finally {
            setDeliveryAdminLoading(false);
        }
    }, []);

    const fetchDamageCategories = useCallback(async () => {
        setDamageCategoriesLoading(true);
        setDamageCategoriesError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/damage-categories?includeInactive=true`);
            if (!response.ok) throw new Error('Failed to fetch damage categories');
            const data = await response.json();
            setDamageCategories(Array.isArray(data) ? data : []);
        } catch (err) {
            setDamageCategoriesError(err.message || 'Could not fetch damage categories.');
        } finally {
            setDamageCategoriesLoading(false);
        }
    }, []);

    const fetchDueInvoices = useCallback(async () => {
        setDueInvoicesLoading(true);
        setDueInvoicesError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/due-invoices`);
            if (!response.ok) throw new Error('Failed to fetch pending due invoices');
            const data = await response.json();
            setDueInvoices(Array.isArray(data) ? data : []);
        } catch (err) {
            setDueInvoicesError(err.message || 'Could not fetch due invoices.');
        } finally {
            setDueInvoicesLoading(false);
        }
    }, []);

    const fetchRentReminderQueue = useCallback(async () => {
        setRentReminderQueueError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/rent-reminders`);
            if (!response.ok) throw new Error('Failed to fetch rent reminder queue');
            const data = await response.json();
            setRentReminderQueue(Array.isArray(data) ? data : []);
        } catch (err) {
            setRentReminderQueueError(err.message || 'Could not fetch rent reminder queue.');
        }
    }, []);

    const fetchRentPendingReport = useCallback(async () => {
        setRentPendingReportError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/rent-pending-report`);
            if (!response.ok) throw new Error('Failed to fetch rent pending report');
            const data = await response.json();
            setRentPendingReport(Array.isArray(data) ? data : []);
        } catch (err) {
            setRentPendingReportError(err.message || 'Could not fetch rent pending report.');
        }
    }, []);

    const fetchAdminCaravans = useCallback(async () => {
        setAdminCaravansError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/caravans/admin/all`);
            if (!response.ok) throw new Error('Failed to fetch caravans for admin');
            const data = await response.json();
            setAdminCaravans(Array.isArray(data) ? data : []);
        } catch (err) {
            setAdminCaravansError(err.message || 'Could not fetch caravans for admin.');
        }
    }, []);

    const fetchBookingOverview = useCallback(async (startDateValue, endDateValue) => {
        const normalizedStartDate = formatDateOnlyInput(parseDateOnlyLocal(startDateValue));
        const normalizedEndDate = formatDateOnlyInput(parseDateOnlyLocal(endDateValue));
        const startDate = parseDateOnlyLocal(normalizedStartDate);
        const endDate = parseDateOnlyLocal(normalizedEndDate);

        if (!normalizedStartDate || !normalizedEndDate || !startDate || !endDate) {
            setBookingOverviewError('Start date and end date are required.');
            return false;
        }
        if (endDate < startDate) {
            setBookingOverviewError('End date must be on or after start date.');
            return false;
        }

        setBookingOverviewLoading(true);
        setBookingOverviewError('');
        try {
            const params = new URLSearchParams({
                startDate: normalizedStartDate,
                endDate: normalizedEndDate
            });

            const [caravansResponse, blocksResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/api/caravans/admin/all`),
                fetch(`${API_BASE_URL}/api/bookings/admin/caravan-blocks?${params.toString()}`)
            ]);

            if (!caravansResponse.ok) {
                throw new Error(await readApiErrorMessage(caravansResponse, 'Failed to fetch caravans for admin.'));
            }
            if (!blocksResponse.ok) {
                throw new Error(await readApiErrorMessage(blocksResponse, 'Failed to fetch caravan booking overview.'));
            }

            const [caravansData, blocksData] = await Promise.all([
                caravansResponse.json(),
                blocksResponse.json()
            ]);

            setAdminCaravans(Array.isArray(caravansData) ? caravansData : []);
            setBookingOverviewBlocks(Array.isArray(blocksData) ? blocksData : []);
            setBookingOverviewAppliedStartDate(normalizedStartDate);
            setBookingOverviewAppliedEndDate(normalizedEndDate);
            return true;
        } catch (err) {
            setBookingOverviewError(err.message || 'Could not load caravan booking overview.');
            return false;
        } finally {
            setBookingOverviewLoading(false);
        }
    }, []);

    const fetchUnlistedFutureBookingsReport = useCallback(async () => {
        setUnlistedFutureBookingsError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/caravans/admin/unlisted-future-bookings`);
            if (!response.ok) throw new Error('Failed to fetch unlisted future bookings report');
            const data = await response.json();
            setUnlistedFutureBookingsReport(Array.isArray(data) ? data : []);
        } catch (err) {
            setUnlistedFutureBookingsError(err.message || 'Could not fetch unlisted future bookings report.');
        }
    }, []);

    const fetchOpenComplaints = useCallback(async () => {
        setOpenComplaintsLoading(true);
        setOpenComplaintsError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/caravans/admin/complaints/open`);
            if (!response.ok) throw new Error('Failed to fetch open complaints');
            const data = await response.json();
            setOpenComplaints(Array.isArray(data) ? data : []);
        } catch (err) {
            setOpenComplaintsError(err.message || 'Could not fetch open complaints.');
        } finally {
            setOpenComplaintsLoading(false);
        }
    }, []);

    const fetchCheckInPhotosForAdmin = useCallback(async (bookingId) => {
        if (!bookingId) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/check-in-photos`);
            if (!response.ok) return;
            const data = await response.json();
            setCheckInPhotoUrlsByBookingId((prev) => ({
                ...prev,
                [bookingId]: Array.isArray(data) ? data.filter(Boolean) : []
            }));
        } catch {
            // Optional media fetch; ignore transient errors.
        }
    }, []);

    const handleLoadBookingOverview = useCallback(async () => {
        await fetchBookingOverview(bookingOverviewDraftStartDate, bookingOverviewDraftEndDate);
    }, [fetchBookingOverview, bookingOverviewDraftStartDate, bookingOverviewDraftEndDate]);

    const shiftBookingOverviewMonths = useCallback(async (offset) => {
        const nextStartDate = formatDateOnlyInput(addCalendarMonthsLocal(bookingOverviewAppliedStartDate, offset));
        const nextEndDate = formatDateOnlyInput(addCalendarMonthsLocal(bookingOverviewAppliedEndDate, offset));
        if (!nextStartDate || !nextEndDate) {
            return;
        }

        setBookingOverviewDraftStartDate(nextStartDate);
        setBookingOverviewDraftEndDate(nextEndDate);
        await fetchBookingOverview(nextStartDate, nextEndDate);
    }, [bookingOverviewAppliedEndDate, bookingOverviewAppliedStartDate, fetchBookingOverview]);
    const handleLoadDefaultBookingOverview = useCallback(async () => {
        const nextStartDate = getAdminBookingOverviewDefaultStartDate();
        const nextEndDate = getAdminBookingOverviewDefaultEndDate();
        setBookingOverviewDraftStartDate(nextStartDate);
        setBookingOverviewDraftEndDate(nextEndDate);
        await fetchBookingOverview(nextStartDate, nextEndDate);
    }, [fetchBookingOverview]);

    const bookingOverviewMonthAnchors = useMemo(
        () => getCalendarMonthsInRange(bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate),
        [bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate]
    );
    const bookingOverviewRangeDayCount = useMemo(
        () => calculateInclusiveDateCount(bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate),
        [bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate]
    );
    const bookingOverviewDayStatusByCaravanId = useMemo(
        () => buildBookingOverviewDayStatusMap(
            bookingOverviewBlocks,
            bookingOverviewAppliedStartDate,
            bookingOverviewAppliedEndDate
        ),
        [bookingOverviewBlocks, bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate]
    );
    const bookingOverviewCalendarDataByCaravanId = useMemo(() => {
        const next = {};
        (Array.isArray(adminCaravans) ? adminCaravans : []).forEach((caravan) => {
            const caravanId = Number(caravan?.caravan_id ?? caravan?.caravanId ?? caravan?.id ?? 0);
            if (!caravanId) return;
            const statusByDate = bookingOverviewDayStatusByCaravanId[caravanId] || {};
            const blockedDates = new Set(
                Object.entries(statusByDate)
                    .filter(([, status]) => String(status || '').trim().toLowerCase() !== 'free')
                    .map(([ymd]) => ymd)
            );
            const bookedCount = Object.values(statusByDate).filter((status) => status === 'booked').length;
            const doorstepCount = Object.values(statusByDate).filter((status) => status === 'doorstep').length;
            const assignedDoorstepCount = Object.values(statusByDate).filter((status) => status === 'doorstep_assigned').length;
            const unavailableCount = Object.values(statusByDate).filter((status) => status === 'unavailable').length;

            next[caravanId] = {
                blockedDates,
                bookedCount,
                doorstepCount,
                assignedDoorstepCount,
                unavailableCount,
                freeCount: Math.max(bookingOverviewRangeDayCount - blockedDates.size, 0)
            };
        });
        return next;
    }, [adminCaravans, bookingOverviewDayStatusByCaravanId, bookingOverviewRangeDayCount]);
    const sortedAdminCaravans = useMemo(() => (
        [...(Array.isArray(adminCaravans) ? adminCaravans : [])].sort((a, b) => {
            const left = String(a?.name || '').trim();
            const right = String(b?.name || '').trim();
            return left.localeCompare(right, undefined, { sensitivity: 'base' });
        })
    ), [adminCaravans]);
    const bookingOverviewRangeStartLocal = parseDateOnlyLocal(bookingOverviewAppliedStartDate);
    const bookingOverviewRangeEndLocal = parseDateOnlyLocal(bookingOverviewAppliedEndDate);
    const bookingOverviewCalendarLocale = language === 'da' ? 'da-DK' : 'en-US';
    const bookingOverviewWeekdayLabels = language === 'da'
        ? ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']
        : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const getBookingOverviewStatusLabel = useCallback((status) => {
        switch (String(status || '').trim().toLowerCase()) {
            case 'doorstep_assigned':
                return language === 'da' ? 'Doorstep-booking tildelt leveringscenter' : 'Doorstep booking assigned to delivery center';
            case 'doorstep':
                return language === 'da' ? 'Doorstep-booking' : 'Doorstep booking';
            case 'booked':
                return language === 'da' ? 'Booket' : 'Booked';
            case 'unavailable':
                return language === 'da' ? 'Blokeret' : 'Unavailable';
            default:
                return language === 'da' ? 'Ledig' : 'Free';
        }
    }, [language]);
    const getBookingOverviewCellClassName = useCallback((status) => {
        switch (String(status || '').trim().toLowerCase()) {
            case 'doorstep_assigned':
                return 'border-green-300 bg-green-500 text-white';
            case 'doorstep':
                return 'border-blue-300 bg-blue-500 text-white';
            case 'booked':
                return 'border-red-300 bg-red-500 text-white';
            case 'unavailable':
                return 'border-gray-300 bg-gray-300 text-gray-800';
            default:
                return 'border-gray-200 bg-white text-gray-400';
        }
    }, []);
    const getOverviewBookingIdForDate = useCallback((caravanId, ymd) => {
        const targetDate = parseDateOnlyLocal(ymd);
        if (!targetDate) return null;
        const targetTime = targetDate.getTime();
        const block = (Array.isArray(bookingOverviewBlocks) ? bookingOverviewBlocks : []).find((item) => {
            if (Number(item?.caravanId) !== Number(caravanId) || !item?.bookingId) return false;
            const start = new Date(item.startAt);
            const end = new Date(item.endAt);
            return Number.isFinite(start.getTime()) && Number.isFinite(end.getTime())
                && targetTime >= new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime()
                && targetTime < new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
        });
        return block?.bookingId || null;
    }, [bookingOverviewBlocks]);
    const bookingOverviewDayRows = useMemo(
        () => getBookingOverviewDayRows(
            bookingOverviewAppliedStartDate,
            bookingOverviewAppliedEndDate,
            bookingOverviewCalendarLocale
        ),
        [bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate, bookingOverviewCalendarLocale]
    );
    const bookingOverviewWeekRows = useMemo(
        () => getBookingOverviewWeekRows(
            bookingOverviewAppliedStartDate,
            bookingOverviewAppliedEndDate,
            bookingOverviewCalendarLocale
        ),
        [bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate, bookingOverviewCalendarLocale]
    );
    const bookingOverviewWeekStatusByCaravanId = useMemo(() => {
        const next = {};
        sortedAdminCaravans.forEach((caravan) => {
            const caravanId = Number(caravan?.caravan_id ?? caravan?.caravanId ?? caravan?.id ?? 0);
            if (!caravanId) return;

            const statusByDate = bookingOverviewDayStatusByCaravanId[caravanId] || {};
            next[caravanId] = {};
            bookingOverviewWeekRows.forEach((row) => {
                let rowStatus = 'free';
                (Array.isArray(row?.dates) ? row.dates : []).forEach((ymd) => {
                    rowStatus = pickBookingOverviewStatus(rowStatus, statusByDate[ymd] || 'free');
                });
                next[caravanId][row.key] = rowStatus;
            });
        });
        return next;
    }, [sortedAdminCaravans, bookingOverviewDayStatusByCaravanId, bookingOverviewWeekRows]);
    const bookingOverviewMatrixRows = bookingOverviewGranularity === 'week'
        ? bookingOverviewWeekRows
        : bookingOverviewDayRows;
    const isCompactBookingOverviewMatrix = bookingOverviewLayout === 'matrix';
    const bookingOverviewMatrixWeekColumnWidthClassName = isCompactBookingOverviewMatrix
        ? (bookingOverviewGranularity === 'week'
            ? (bookingOverviewShowWeekDates ? 'min-w-[56px] w-[56px]' : 'min-w-[40px] w-[40px]')
            : 'min-w-[52px] w-[52px]')
        : 'min-w-[128px]';
    const bookingOverviewMatrixCaravanHeaderClassName = isCompactBookingOverviewMatrix
        ? 'sticky left-0 top-0 z-20 min-w-[104px] max-w-[104px] border-b border-r border-gray-200 bg-gray-50 px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-600'
        : 'sticky left-0 top-0 z-20 min-w-[150px] border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600';
    const bookingOverviewMatrixHeaderCellClassName = isCompactBookingOverviewMatrix
        ? `sticky top-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-1 py-1.5 text-left align-top ${bookingOverviewMatrixWeekColumnWidthClassName}`
        : `sticky top-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-3 py-3 text-left align-top ${bookingOverviewMatrixWeekColumnWidthClassName}`;
    const bookingOverviewMatrixCaravanCellClassName = isCompactBookingOverviewMatrix
        ? 'sticky left-0 z-10 min-w-[136px] max-w-[136px] border-b border-r border-gray-200 bg-white px-2 py-1.5 align-top'
        : 'sticky left-0 z-10 min-w-[220px] border-b border-r border-gray-200 bg-white px-4 py-3 align-top';
    const bookingOverviewMatrixStatusCellClassName = isCompactBookingOverviewMatrix
        ? `h-8 border-b border-r border-gray-200 px-0.5 py-0.5 text-center align-middle ${bookingOverviewMatrixWeekColumnWidthClassName}`
        : `h-12 border-b border-r border-gray-200 px-2 py-1 text-center align-middle ${bookingOverviewMatrixWeekColumnWidthClassName}`;
    const bookingOverviewMatrixPrimaryLabelClassName = isCompactBookingOverviewMatrix
        ? 'text-[11px] font-semibold leading-tight text-gray-900 whitespace-nowrap'
        : 'text-sm font-semibold text-gray-900';
    const bookingOverviewMatrixSecondaryLabelClassName = isCompactBookingOverviewMatrix
        ? 'mt-0.5 text-[9px] leading-tight text-gray-500'
        : 'mt-1 text-xs text-gray-500';
    const bookingOverviewMatrixNameClassName = isCompactBookingOverviewMatrix
        ? 'truncate text-[11px] font-semibold leading-tight text-gray-900'
        : 'truncate text-sm font-semibold text-gray-900';
    const bookingOverviewMatrixLocationClassName = isCompactBookingOverviewMatrix
        ? 'mt-0.5 truncate text-[10px] leading-tight text-gray-500'
        : 'mt-1 truncate text-xs text-gray-500';
    const bookingOverviewMatrixStatsClassName = isCompactBookingOverviewMatrix
        ? 'mt-1 flex flex-wrap gap-0.5 text-[9px]'
        : 'mt-2 flex flex-wrap gap-1 text-[11px]';

    const openImageSlider = useCallback((images, startIndex = 0, title = 'Caravan Images') => {
        const normalized = (Array.isArray(images) ? images : [])
            .map((img) => resolveImageUrl(img) || img)
            .filter(Boolean);

        if (normalized.length === 0) {
            setImageSliderState({
                open: true,
                images: [PLACEHOLDER_IMG],
                index: 0,
                title
            });
            return;
        }

        const safeIndex = Math.max(0, Math.min(startIndex, normalized.length - 1));
        setImageSliderState({
            open: true,
            images: normalized,
            index: safeIndex,
            title
        });
    }, []);

    const closeImageSlider = useCallback(() => {
        setImageSliderState((prev) => ({ ...prev, open: false }));
    }, []);

    const showPrevImage = useCallback(() => {
        setImageSliderState((prev) => {
            if (!prev.images.length) return prev;
            const nextIndex = (prev.index - 1 + prev.images.length) % prev.images.length;
            return { ...prev, index: nextIndex };
        });
    }, []);

    const showNextImage = useCallback(() => {
        setImageSliderState((prev) => {
            if (!prev.images.length) return prev;
            const nextIndex = (prev.index + 1) % prev.images.length;
            return { ...prev, index: nextIndex };
        });
    }, []);

    useEffect(() => {
        if (!imageSliderState.open) return;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeImageSlider();
            } else if (event.key === 'ArrowLeft') {
                showPrevImage();
            } else if (event.key === 'ArrowRight') {
                showNextImage();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [imageSliderState.open, closeImageSlider, showPrevImage, showNextImage]);
    // Generic fetch handler
    const fetchData = async (url, setData, setLoading, setError) => {
        setLoading(true);
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            setData(data);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };
    const prefetchAdminBadgeCounts = useCallback(async () => {
        const loadArray = async (url) => {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    return null;
                }

                const data = await response.json();
                return Array.isArray(data) ? data : [];
            } catch {
                return null;
            }
        };

        const [
            pendingBookingsData,
            bookingHistoryData,
            checkoutReviewsData,
            dueInvoicesData,
            rentReminderQueueData,
            rentPendingData,
            adminCaravansData,
            openComplaintsData,
            deliveryJobsData
        ] = await Promise.all([
            loadArray(`${API_BASE_URL}/api/bookings/admin/pending`),
            loadArray(`${API_BASE_URL}/api/bookings/admin/history`),
            loadArray(`${API_BASE_URL}/api/bookings/admin/checkout-reviews/pending`),
            loadArray(`${API_BASE_URL}/api/bookings/admin/due-invoices`),
            loadArray(`${API_BASE_URL}/api/bookings/admin/rent-reminders`),
            loadArray(`${API_BASE_URL}/api/bookings/admin/rent-pending-report`),
            loadArray(`${API_BASE_URL}/api/caravans/admin/all`),
            loadArray(`${API_BASE_URL}/api/caravans/admin/complaints/open`),
            loadArray(`${API_BASE_URL}/api/delivery-partners/admin/jobs`)
        ]);

        if (pendingBookingsData !== null) {
            setPendingBookings(pendingBookingsData);
        }
        if (bookingHistoryData !== null) {
            setBookingHistory(bookingHistoryData);
        }
        if (checkoutReviewsData !== null) {
            setPendingCheckoutReviews(checkoutReviewsData);
        }
        if (dueInvoicesData !== null) {
            setDueInvoices(dueInvoicesData);
        }
        if (rentReminderQueueData !== null) {
            setRentReminderQueue(rentReminderQueueData);
        }
        if (rentPendingData !== null) {
            setRentPendingReport(rentPendingData);
        }
        if (adminCaravansData !== null) {
            setAdminCaravans(adminCaravansData);
        }
        if (openComplaintsData !== null) {
            setOpenComplaints(openComplaintsData);
        }
        if (deliveryJobsData !== null) {
            setDeliveryAdminJobs(deliveryJobsData);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'approvals') {
            fetchUnapproved();
        };
        if (activeTab === 'bookings') {
            fetchPendingBookings();
        };
        if (activeTab === 'booking-history') {
            fetchBookingHistory();
        };
        if (activeTab === 'cancellation-refunds') {
            fetchBookingHistory();
        };
        if (activeTab === 'checkout-reviews') {
            fetchPendingCheckoutReviews();
        };
        if (activeTab === 'seasons') {
            fetchSeasonConfig();
        };
        if (activeTab === 'settings') {
            fetchEmailSettings();
            fetchTermsDocuments();
        };
        if (activeTab === 'users') {
            fetchUsers();
        };
        if (activeTab === 'delivery-center') {
            fetchDeliveryAdmin();
        };
        if (activeTab === 'damage-catalog') {
            fetchDamageCategories();
        };
        if (activeTab === 'dues') {
            fetchDueInvoices();
        };
        if (activeTab === 'rent-reminders') {
            fetchRentReminderQueue();
        };
        if (activeTab === 'rent-report') {
            fetchRentPendingReport();
        };
        if (activeTab === 'booking-overview' && !bookingOverviewInitialized) {
            setBookingOverviewInitialized(true);
            fetchBookingOverview(bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate);
        };
        if (activeTab === 'listings') {
            fetchAdminCaravans();
            fetchUnlistedFutureBookingsReport();
        };
        if (activeTab === 'complaints') {
            fetchOpenComplaints();
        };
        fetchData(`${API_BASE_URL}/api/Amenities?includeInactive=true`, setAmenities, setAmenitiesLoading, setAmenitiesError);
        fetchData(`${API_BASE_URL}/api/CaravanTypes?includeInactive=true`, setCaravanTypes, setTypesLoading, setTypesError);
        fetchData(`${API_BASE_URL}/api/CaravanTypes/amenity-mappings`, setTypeAmenityMappings, () => { }, () => { });
    }, [activeTab, fetchUnapproved, fetchPendingBookings, fetchBookingHistory, fetchPendingCheckoutReviews, fetchSeasonConfig, fetchEmailSettings, fetchTermsDocuments, fetchUsers, fetchDeliveryAdmin, fetchDamageCategories, fetchDueInvoices, fetchRentReminderQueue, fetchRentPendingReport, bookingOverviewInitialized, fetchBookingOverview, bookingOverviewAppliedStartDate, bookingOverviewAppliedEndDate, fetchAdminCaravans, fetchUnlistedFutureBookingsReport, fetchOpenComplaints]);

    useEffect(() => {
        prefetchAdminBadgeCounts();
    }, [prefetchAdminBadgeCounts]);

    useEffect(() => {
        const bookingIdsToLoad = [
            ...(pendingBookings || []),
            ...(pendingCheckoutReviews || []),
            ...(rentPendingReport || []),
            ...(bookingHistory || [])
        ]
            .map((booking) => booking?.bookingId)
            .filter((bookingId) => bookingId && !Object.prototype.hasOwnProperty.call(checkInPhotoUrlsByBookingId, bookingId));

        if (bookingIdsToLoad.length === 0) {
            return;
        }

        bookingIdsToLoad.forEach((bookingId) => {
            fetchCheckInPhotosForAdmin(bookingId);
        });
    }, [
        pendingBookings,
        pendingCheckoutReviews,
        rentPendingReport,
        bookingHistory,
        checkInPhotoUrlsByBookingId,
        fetchCheckInPhotosForAdmin
    ]);

    useEffect(() => {
        setTypeAmenityMappings(initialTypeAmenityMappings || []);
    }, [initialTypeAmenityMappings]);


    const handleApprove = async (caravanId) => {
        const parsedCaravanId = Number(caravanId);
        if (!Number.isFinite(parsedCaravanId) || parsedCaravanId <= 0) {
            setError('Invalid caravan id for approval.');
            return;
        }
        await onApproveCaravan(parsedCaravanId);
        if (activeTab === 'approvals') {
            fetchUnapproved();
        };
    };

    const handleCaravanRejectRemarksChange = (caravanId, value) => {
        setCaravanRejectRemarksById((prev) => ({ ...prev, [caravanId]: value }));
    };

    const handleRejectCaravan = async (caravanId) => {
        const remarks = (caravanRejectRemarksById[caravanId] || '').trim();
        if (!remarks) {
            setError('Rejection remarks are required.');
            return;
        }
        if (!user?.userId) {
            setError('Admin user context is missing.');
            return;
        }

        setRejectingCaravanId(caravanId);
        setError(null);
        try {
            const rejectUrl = `${API_BASE_URL}/api/caravans/${caravanId}/reject`;
            const payload = JSON.stringify({
                adminId: user.userId,
                remarks
            });
            let response = await fetch(rejectUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(rejectUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to reject caravan'));
            }
            await fetchUnapproved();
        } catch (err) {
            setError(err.message || 'Failed to reject caravan.');
        } finally {
            setRejectingCaravanId(null);
        }
    };

    const handleDoorstepApprovalChange = (bookingId, value) => {
        setDoorstepApprovalByBookingId((prev) => ({ ...prev, [bookingId]: value }));
    };

    const handleBookingRejectRemarksChange = (bookingId, value) => {
        setBookingRejectRemarksById((prev) => ({ ...prev, [bookingId]: value }));
    };

    const handleBookingCancelRemarksChange = (bookingId, value) => {
        setBookingCancelRemarksById((prev) => ({ ...prev, [bookingId]: value }));
    };

    const handleFullPaymentApprovalChange = (bookingId, checked) => {
        setFullPaymentApprovalByBookingId((prev) => ({ ...prev, [bookingId]: checked }));
    };

    const handleApproveBooking = async (bookingId, doorstepFee, markRentPaid) => {
        if (!user?.userId) {
            setError('Admin user context is missing.');
            return;
        }

        setApprovingBookingId(bookingId);
        try {
            const approveUrl = `${API_BASE_URL}/api/bookings/${bookingId}/approve`;
            const approvePayload = JSON.stringify({
                adminId: user.userId,
                doorstepFee: doorstepFee,
                markRentPaid: Boolean(markRentPaid)
            });

            // Prefer POST because some IIS setups block PUT (405 via WebDAV/verb restrictions).
            let response = await fetch(approveUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: approvePayload
            });

            if (response.status === 405) {
                response = await fetch(approveUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: approvePayload
                });
            }

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to approve booking');
            }
            if (activeTab === 'bookings') {
                await fetchPendingBookings();
            }
            await fetchBookingHistory();
            await fetchRentPendingReport();
        } catch (err) {
            setError(err.message || 'Failed to approve booking.');
        } finally {
            setApprovingBookingId(null);
        }
    };

    const handleRejectBooking = async (bookingId) => {
        if (!user?.userId) {
            setError('Admin user context is missing.');
            return;
        }

        const remarks = (bookingRejectRemarksById[bookingId] || '').trim();
        if (!remarks) {
            setError('Rejection remarks are required.');
            return;
        }

        setRejectingBookingId(bookingId);
        try {
            const rejectUrl = `${API_BASE_URL}/api/bookings/${bookingId}/reject`;
            const payload = JSON.stringify({
                adminId: user.userId,
                remarks
            });
            let response = await fetch(rejectUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(rejectUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to reject booking'));
            }
            if (activeTab === 'bookings') {
                await fetchPendingBookings();
            }
        } catch (err) {
            setError(err.message || 'Failed to reject booking.');
        } finally {
            setRejectingBookingId(null);
        }
    };

    const handleCancelBooking = async (bookingId) => {
        if (!user?.userId) {
            setBookingCancellationError('Admin user context is missing.');
            return;
        }

        const bookingToCancel = (Array.isArray(bookingHistory) ? bookingHistory : []).find((item) => Number(item?.bookingId) === Number(bookingId));
        const bookingStatus = String(bookingToCancel?.bookingStatus || '').trim().toLowerCase();
        const remarks = (bookingCancelRemarksById[bookingId] || '').trim();
        if (!remarks) {
            setBookingCancellationError('Cancellation remarks are required.');
            return;
        }

        if (!window.confirm('Do you want to cancel this booking?')) {
            return;
        }

        setCancellingBookingId(bookingId);
        setBookingCancellationError('');
        setBookingCancellationSuccess('');
        try {
            const cancelUrl = `${API_BASE_URL}/api/bookings/${bookingId}/cancel`;
            const payload = JSON.stringify({
                adminId: user.userId,
                remarks
            });
            let response = await fetch(cancelUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(cancelUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to cancel booking.'));
            }

            setBookingCancelRemarksById((prev) => ({ ...prev, [bookingId]: '' }));
            setBookingCancellationSuccess(`Booking ${formatBookingNumber(bookingId)} was cancelled.`);
            await fetchPendingBookings();
            await fetchBookingHistory();
            await fetchRentPendingReport();
            if (bookingStatus === 'confirmed') {
                setActiveTab('cancellation-refunds');
            }
        } catch (err) {
            setBookingCancellationError(err.message || 'Failed to cancel booking.');
        } finally {
            setCancellingBookingId(null);
        }
    };

    const handleMarkRentPaid = async (bookingId) => {
        if (!user?.userId) {
            setRentPendingReportError('Admin user context is missing.');
            setRentReminderQueueError('Admin user context is missing.');
            return;
        }

        if (!window.confirm('Do you want to mark this rent as paid?')) {
            return;
        }

        setMarkingRentPaidBookingId(bookingId);
        setRentPendingReportError('');
        setRentReminderQueueError('');
        try {
            const actionUrl = `${API_BASE_URL}/api/bookings/admin/${bookingId}/rent-paid`;
            const payload = JSON.stringify({ adminId: user.userId });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to mark rent as paid.'));
            }
            await fetchRentReminderQueue();
            await fetchRentPendingReport();
            await fetchBookingHistory();
        } catch (err) {
            const errorMessage = err.message || 'Failed to mark rent as paid.';
            setRentPendingReportError(errorMessage);
            setRentReminderQueueError(errorMessage);
        } finally {
            setMarkingRentPaidBookingId(null);
        }
    };

    const handleSendRentReminder = async (bookingId) => {
        if (!user?.userId) {
            setRentReminderQueueError('Admin user context is missing.');
            return;
        }

        if (!window.confirm('Do you want to send this rent reminder email now?')) {
            return;
        }

        setSendingRentReminderBookingId(bookingId);
        setRentReminderQueueError('');
        try {
            const actionUrl = `${API_BASE_URL}/api/bookings/admin/${bookingId}/rent-reminder`;
            const payload = JSON.stringify({ adminId: user.userId });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to send rent reminder.'));
            }
            await fetchRentReminderQueue();
            await fetchRentPendingReport();
        } catch (err) {
            setRentReminderQueueError(err.message || 'Failed to send rent reminder.');
        } finally {
            setSendingRentReminderBookingId(null);
        }
    };

    const handleMarkCancellationRefundPaid = async (bookingId, refundType) => {
        if (!user?.userId) {
            setBookingCancellationError('Admin user context is missing.');
            return;
        }

        const normalizedRefundType = refundType === 'deposit' ? 'deposit' : 'rent';
        const label = normalizedRefundType === 'rent' ? 'rent refund' : 'deposit refund';
        if (!window.confirm(`Do you want to mark this ${label} as paid?`)) {
            return;
        }

        const actionKey = `${bookingId}-${normalizedRefundType}`;
        setMarkingCancellationRefundKey(actionKey);
        setBookingCancellationError('');
        setBookingCancellationSuccess('');
        try {
            const actionUrl = `${API_BASE_URL}/api/bookings/admin/${bookingId}/refunds/${normalizedRefundType}/paid`;
            const payload = JSON.stringify({ adminId: user.userId });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, `Failed to mark ${label} as paid.`));
            }

            setBookingCancellationSuccess(`Booking ${formatBookingNumber(bookingId)} ${label} marked as paid.`);
            await fetchBookingHistory();
        } catch (err) {
            setBookingCancellationError(err.message || `Failed to mark ${label} as paid.`);
        } finally {
            setMarkingCancellationRefundKey('');
        }
    };

    const updateCaravanListingStatus = async (caravanId, isListed) => {
        if (!user?.userId) {
            setAdminCaravansError('Admin user context is missing.');
            return;
        }

        setUpdatingListingCaravanId(caravanId);
        setAdminCaravansError('');
        try {
            const actionUrl = `${API_BASE_URL}/api/caravans/${caravanId}/listing-status`;
            const payload = JSON.stringify({
                actorUserId: user.userId,
                isListed
            });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to update listing status'));
            }
            await fetchAdminCaravans();
            await fetchUnlistedFutureBookingsReport();
        } catch (err) {
            setAdminCaravansError(err.message || 'Failed to update listing status.');
        } finally {
            setUpdatingListingCaravanId(null);
        }
    };

    const applyComplaintAction = async (complaintId, action) => {
        if (!user?.userId) {
            setOpenComplaintsError('Admin user context is missing.');
            return;
        }

        setActingComplaintId(complaintId);
        setOpenComplaintsError('');
        try {
            const actionUrl = `${API_BASE_URL}/api/caravans/admin/complaints/${complaintId}/action`;
            const payload = JSON.stringify({
                adminId: user.userId,
                action
            });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to apply complaint action'));
            }

            await fetchOpenComplaints();
            if (action === 'unlist_caravan') {
                await fetchAdminCaravans();
                await fetchUnlistedFutureBookingsReport();
            }
        } catch (err) {
            setOpenComplaintsError(err.message || 'Failed to apply complaint action.');
        } finally {
            setActingComplaintId(null);
        }
    };

    const handleDueClearCommentChange = (dueInvoiceId, value) => {
        setDueClearCommentById((prev) => ({ ...prev, [dueInvoiceId]: value }));
    };

    const clearDueInvoice = async (dueInvoiceId) => {
        if (!user?.userId) {
            setDueInvoicesError('Admin user context is missing.');
            return;
        }

        setClearingDueInvoiceId(dueInvoiceId);
        setDueInvoicesError('');
        try {
            const clearUrl = `${API_BASE_URL}/api/bookings/admin/due-invoices/${dueInvoiceId}/clear`;
            const payload = JSON.stringify({
                adminId: user.userId,
                comment: dueClearCommentById[dueInvoiceId] || ''
            });
            let response = await fetch(clearUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(clearUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to clear due invoice'));
            }
            await fetchDueInvoices();
            await fetchBookingHistory();
        } catch (err) {
            setDueInvoicesError(err.message || 'Failed to clear due invoice.');
        } finally {
            setClearingDueInvoiceId(null);
        }
    };

    // Generic CRUD handlers
    const handleApiRequest = async (url, method, body, onSuccess) => {
        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: body ? JSON.stringify(body) : null,
            });
            if (!response.ok) throw new Error(`API request failed: ${response.statusText}`);
            const result = method !== 'DELETE' ? await response.json() : null;
            onSuccess(result);
        } catch (error) {
            console.error("API Error:", error);
            // Here you could show an error toast to the user
        }
    };

    // Amenity Handlers
    const addAmenity = (name) => handleApiRequest(`${API_BASE_URL}/api/amenities`, 'POST', { amenity_name: name }, (newItem) => setAmenities(prev => [...prev, newItem]));
    const updateAmenity = (id, name) => handleApiRequest(`${API_BASE_URL}/api/amenities/${id}`, 'POST', { amenity_name: name }, (updatedItem) => setAmenities(prev => prev.map(item => item.amenity_id === id ? updatedItem : item)));
    const setAmenityStatus = (id, isActive) => handleApiRequest(
        `${API_BASE_URL}/api/amenities/${id}/status`,
        'POST',
        { is_active: isActive },
        (updatedItem) => setAmenities(prev => prev.map(item => item.amenity_id === id ? updatedItem : item))
    );

    // Caravan Type Handlers
    const addCaravanType = (name) => handleApiRequest(`${API_BASE_URL}/api/caravantypes`, 'POST', { type_name: name }, (newItem) => setCaravanTypes(prev => [...prev, newItem]));
    const updateCaravanType = (id, name) => handleApiRequest(`${API_BASE_URL}/api/caravantypes/${id}`, 'POST', { type_name: name }, (updatedItem) => setCaravanTypes(prev => prev.map(item => item.type_id === id ? updatedItem : item)));
    const setCaravanTypeStatus = async (id, isActive) => {
        const parsedTypeId = Number(id);
        if (!Number.isFinite(parsedTypeId) || parsedTypeId <= 0) {
            setTypesError('Invalid caravan type id.');
            return;
        }

        setTypesError(null);
        try {
            const actionUrl = `${API_BASE_URL}/api/caravantypes/${parsedTypeId}/status`;
            const payload = JSON.stringify({ is_active: Boolean(isActive) });
            let response = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });

            if (response.status === 405) {
                response = await fetch(actionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }

            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to update caravan type status.'));
            }

            const updatedItem = await response.json();
            setCaravanTypes(prev => prev.map(item => (
                Number(item.type_id) === parsedTypeId
                    ? updatedItem
                    : item
            )));
            fetchData(`${API_BASE_URL}/api/CaravanTypes?includeInactive=true`, setCaravanTypes, setTypesLoading, setTypesError);
        } catch (err) {
            setTypesError(err.message || 'Failed to update caravan type status.');
        }
    };
    const saveTypeAmenityMapping = async (typeId, amenityIds) => {
        const updatedAmenityIds = await onSaveTypeAmenityMapping(typeId, amenityIds);
        setTypeAmenityMappings(prev => {
            const next = prev.filter(m => Number(m.type_id) !== Number(typeId));
            return [...next, ...updatedAmenityIds.map(id => ({ type_id: typeId, amenity_id: id }))];
        });
    };

    const saveMonthConfig = async (months) => {
        setSeasonConfigError(null);
        setIsSeasonConfigSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/seasons/month-config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ months }),
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to save month mapping');
            }
            const data = await response.json();
            setMonthConfig(data);
        } catch (err) {
            setSeasonConfigError(err.message || 'Failed to save month mapping.');
        } finally {
            setIsSeasonConfigSaving(false);
        }
    };

    const saveEmailSettings = async () => {
        setEmailSettingsError('');
        setEmailSettingsSuccess('');
        setIsEmailSettingsSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin-settings/email`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(emailSettings),
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to save email settings.'));
            }

            const data = await response.json();
            setEmailSettings({
                email_language: data?.email_language === 'en' ? 'en' : 'da'
            });
            setEmailSettingsSuccess('Email language setting saved successfully.');
        } catch (err) {
            setEmailSettingsError(err.message || 'Failed to save email settings.');
        } finally {
            setIsEmailSettingsSaving(false);
        }
    };
    const handleTermsFileSelection = (audience, file) => {
        const normalizedAudience = normalizeTermsAudience(audience);
        if (!normalizedAudience) return;

        setTermsDocumentsError('');
        setTermsDocumentsSuccess('');
        setTermsUploadFiles((prev) => ({
            ...prev,
            [normalizedAudience]: file || null
        }));
    };
    const uploadTermsDocument = async (audience) => {
        const normalizedAudience = normalizeTermsAudience(audience);
        if (!normalizedAudience) {
            setTermsDocumentsError('Invalid audience selected for terms upload.');
            return;
        }

        if (!user?.userId) {
            setTermsDocumentsError('Admin user context is missing.');
            return;
        }

        const selectedFile = termsUploadFiles[normalizedAudience];
        if (!selectedFile) {
            setTermsDocumentsError('Please choose a PDF file first.');
            return;
        }

        if (!/\.pdf$/i.test(selectedFile.name || '')) {
            setTermsDocumentsError('Only PDF files are allowed.');
            return;
        }

        if (Number(selectedFile.size || 0) > MAX_TERMS_PDF_SIZE_BYTES) {
            setTermsDocumentsError(`The PDF file must be ${MAX_TERMS_PDF_SIZE_MB} MB or smaller.`);
            return;
        }

        setUploadingTermsAudience(normalizedAudience);
        setTermsDocumentsError('');
        setTermsDocumentsSuccess('');
        try {
            const formData = new FormData();
            formData.append('adminId', String(user.userId));
            formData.append('audience', normalizedAudience);
            formData.append('file', selectedFile);

            const response = await fetch(`${API_BASE_URL}/api/terms-documents/admin/upload`, {
                method: 'POST',
                body: formData
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to upload terms PDF.'));
            }

            setTermsUploadFiles((prev) => ({ ...prev, [normalizedAudience]: null }));
            setTermsUploadInputKeys((prev) => ({ ...prev, [normalizedAudience]: Number(prev[normalizedAudience] || 0) + 1 }));
            setTermsDocumentsSuccess(`${getTermsAudienceLabel(normalizedAudience)} terms PDF uploaded successfully.`);
            await fetchTermsDocuments();
        } catch (err) {
            setTermsDocumentsError(err.message || 'Failed to upload terms PDF.');
        } finally {
            setUploadingTermsAudience('');
        }
    };
    const setTermsVersionAsCurrent = async (document) => {
        const termsDocumentId = Number(document?.termsDocumentId);
        if (!Number.isFinite(termsDocumentId) || termsDocumentId <= 0) {
            setTermsDocumentsError('Invalid terms document selected.');
            return;
        }

        if (!user?.userId) {
            setTermsDocumentsError('Admin user context is missing.');
            return;
        }

        const audienceLabel = getTermsAudienceLabel(document?.audience);
        const confirmed = window.confirm(`Set version ${document?.versionNumber || ''} as the current ${audienceLabel} terms PDF?`);
        if (!confirmed) {
            return;
        }

        setRestoringTermsDocumentId(termsDocumentId);
        setTermsDocumentsError('');
        setTermsDocumentsSuccess('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/terms-documents/admin/${termsDocumentId}/current`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminId: user.userId })
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to set current terms PDF.'));
            }

            setTermsDocumentsSuccess(`${audienceLabel} terms version ${document?.versionNumber || ''} is now current.`);
            await fetchTermsDocuments();
        } catch (err) {
            setTermsDocumentsError(err.message || 'Failed to set current terms PDF.');
        } finally {
            setRestoringTermsDocumentId(null);
        }
    };

    const handleRoleChange = (userId, role) => {
        setUsers(prev => prev.map(item => (
            Number(item.userId) === Number(userId)
                ? { ...item, role }
                : item
        )));
    };

    const saveRole = async (userId, role) => {
        setSavingRoleUserId(userId);
        setUsersError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/role`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role })
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to update role'));
            }
            await fetchUsers();
        } catch (err) {
            setUsersError(err.message || 'Failed to update role.');
        } finally {
            setSavingRoleUserId(null);
        }
    };
    const handleDeliveryJobAdminAction = async (deliveryJobId, action) => {
        if (!user?.userId) return;

        const actionLabel = action === 'reopen' ? 'reopen' : 'cancel';
        const confirmed = window.confirm(`Are you sure you want to ${actionLabel} delivery job ${deliveryJobId}?`);
        if (!confirmed) {
            return;
        }

        setDeliveryAdminActionJobId(deliveryJobId);
        setDeliveryAdminError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/delivery-partners/admin/jobs/${deliveryJobId}/${actionLabel}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adminId: user.userId })
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, `Failed to ${actionLabel} delivery job.`));
            }

            await fetchDeliveryAdmin();
        } catch (err) {
            setDeliveryAdminError(err.message || `Failed to ${actionLabel} delivery job.`);
        } finally {
            setDeliveryAdminActionJobId(null);
        }
    };

    const handleDeliveryPartnerApprovalAction = async (partner, action) => {
        if (!user?.userId || !partner?.userId) return;

        const approvalStatus = action === 'approve' ? 'approved' : 'rejected';
        const remarks = action === 'reject'
            ? window.prompt('Enter rejection remarks (optional):', partner?.approvalRemarks || '')
            : (partner?.approvalRemarks || '');
        if (remarks === null) {
            return;
        }

        setDeliveryAdminPartnerActionUserId(partner.userId);
        setDeliveryAdminError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/delivery-partners/admin/partners/${partner.userId}/approval`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminId: user.userId,
                    approvalStatus,
                    remarks: remarks || ''
                })
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, `Failed to ${action} delivery partner.`));
            }

            await fetchDeliveryAdmin();
        } catch (err) {
            setDeliveryAdminError(err.message || `Failed to ${action} delivery partner.`);
        } finally {
            setDeliveryAdminPartnerActionUserId(null);
        }
    };

    const updateDeliveryJobPaymentDraft = (deliveryJobId, field, value) => {
        setDeliveryJobPaymentDrafts((previous) => ({
            ...previous,
            [deliveryJobId]: {
                ...(previous[deliveryJobId] || {}),
                [field]: value
            }
        }));
        setDeliveryJobPaymentErrors((previous) => {
            if (!previous[deliveryJobId]?.[field]) {
                return previous;
            }

            return {
                ...previous,
                [deliveryJobId]: {
                    ...(previous[deliveryJobId] || {}),
                    [field]: ''
                }
            };
        });
    };

    const handleMarkDeliveryJobPaid = async (job) => {
        if (!user?.userId || !job?.deliveryJobId) return;

        const paymentDraft = deliveryJobPaymentDrafts[job.deliveryJobId] || {};
        const resolvedAmount = getDeliveryJobPayoutAmount(job);
        const committedAmountRaw = paymentDraft.amount ?? (resolvedAmount > 0 ? String(resolvedAmount) : '');
        const paymentReference = String(paymentDraft.reference ?? job.paymentReference ?? '').trim();
        const committedAmount = Number(committedAmountRaw);
        const fieldErrors = {
            amount: '',
            reference: ''
        };

        if (!String(committedAmountRaw ?? '').trim()) {
            fieldErrors.amount = 'Amount is required.';
        } else if (!Number.isFinite(committedAmount) || committedAmount <= 0) {
            fieldErrors.amount = 'Amount must be greater than 0.';
        }
        if (!paymentReference) {
            fieldErrors.reference = 'Reference is required.';
        }

        if (fieldErrors.amount || fieldErrors.reference) {
            setDeliveryJobPaymentErrors((previous) => ({
                ...previous,
                [job.deliveryJobId]: fieldErrors
            }));
            setDeliveryAdminError('');
            return;
        }

        setDeliveryAdminPaymentJobId(job.deliveryJobId);
        setDeliveryAdminError('');
        setDeliveryJobPaymentErrors((previous) => ({
            ...previous,
            [job.deliveryJobId]: {
                amount: '',
                reference: ''
            }
        }));
        try {
            const response = await fetch(`${API_BASE_URL}/api/delivery-partners/admin/jobs/${job.deliveryJobId}/payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminId: user.userId,
                    committedAmount,
                    paymentReference
                })
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to mark delivery job as paid.'));
            }

            await fetchDeliveryAdmin();
        } catch (err) {
            setDeliveryAdminError(err.message || 'Failed to mark delivery job as paid.');
        } finally {
            setDeliveryAdminPaymentJobId(null);
        }
    };

    const setUserActiveStatus = async (userItem, isActive) => {
        const userId = Number(userItem?.userId);
        if (!Number.isFinite(userId) || userId <= 0) return;

        const verb = isActive ? 'unblock' : 'block';
        const targetName = userItem?.name || userItem?.email || `User ${userId}`;
        const confirmed = window.confirm(`Are you sure you want to ${verb} ${targetName}?`);
        if (!confirmed) {
            return;
        }

        setSavingUserStatusId(userId);
        setUsersError('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isActive })
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, `Failed to ${verb} user`));
            }
            await fetchUsers();
        } catch (err) {
            setUsersError(err.message || `Failed to ${verb} user.`);
        } finally {
            setSavingUserStatusId(null);
        }
    };

    const loadCheckoutAssessmentForReview = async (bookingId) => {
        setCheckoutReviewError('');
        setCheckoutReviewSuccess('');
        if (expandedReviewBookingId === bookingId) {
            setExpandedReviewBookingId(null);
            return;
        }

        setExpandedReviewBookingId(bookingId);
        setLoadingReviewBookingId(bookingId);
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/damage-assessment`);
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Could not load checkout assessment.');
            }

            const assessment = await response.json();
            setAssessmentByBookingId((prev) => ({ ...prev, [bookingId]: assessment }));

            const itemComments = {};
            (assessment.items || []).forEach((item) => {
                itemComments[item.itemId] = item.adminRejectComment || '';
            });
            setReviewFormsByBookingId((prev) => ({
                ...prev,
                [bookingId]: {
                    ...getDefaultReviewFormState(),
                    comment: assessment.adminComment || '',
                    itemComments,
                    markOwnerTransferPaid: Boolean(assessment.ownerTransferPaid),
                    ownerTransferReference: assessment.ownerTransferReference || '',
                    markCustomerRefundPaid: Boolean(assessment.customerRefundPaid),
                    customerRefundReference: assessment.customerRefundReference || ''
                }
            }));
        } catch (err) {
            setCheckoutReviewError(err.message || 'Could not load checkout assessment.');
        } finally {
            setLoadingReviewBookingId(null);
        }
    };

    const updateReviewComment = (bookingId, value) => {
        setReviewFormsByBookingId((prev) => ({
            ...prev,
            [bookingId]: {
                ...(prev[bookingId] || getDefaultReviewFormState()),
                comment: value
            }
        }));
    };

    const updateReviewItemComment = (bookingId, itemId, value) => {
        setReviewFormsByBookingId((prev) => {
            const current = prev[bookingId] || getDefaultReviewFormState();
            return {
                ...prev,
                [bookingId]: {
                    ...current,
                    itemComments: {
                        ...(current.itemComments || {}),
                        [itemId]: value
                    }
                }
            };
        });
    };

    const updateReviewTransferField = (bookingId, field, value) => {
        setReviewFormsByBookingId((prev) => ({
            ...prev,
            [bookingId]: {
                ...(prev[bookingId] || getDefaultReviewFormState()),
                [field]: value
            }
        }));
    };

    const submitCheckoutReview = async (booking, decision) => {
        if (!user?.userId) {
            setCheckoutReviewError('Admin user context is missing.');
            return;
        }

        const bookingId = booking.bookingId;
        const assessment = assessmentByBookingId[bookingId];
        if (!assessment) {
            setCheckoutReviewError('Load assessment first.');
            return;
        }

        const form = reviewFormsByBookingId[bookingId] || getDefaultReviewFormState();
        const itemCommentsPayload = (assessment.items || []).map((item) => ({
            itemId: item.itemId,
            comment: (form.itemComments?.[item.itemId] || '').trim()
        }));

        if (decision === 'reject' && !itemCommentsPayload.some((item) => item.comment)) {
            setCheckoutReviewError('At least one category comment is required for rejection.');
            return;
        }

        const ownerTransferReference = (form.ownerTransferReference || '').trim();
        const customerRefundReference = (form.customerRefundReference || '').trim();

        if (decision === 'approve' && form.markOwnerTransferPaid && !ownerTransferReference) {
            setCheckoutReviewError('Owner transfer reference is required when marking owner transfer as paid.');
            return;
        }

        if (decision === 'approve' && form.markCustomerRefundPaid && !customerRefundReference) {
            setCheckoutReviewError('Customer refund reference is required when marking customer refund as paid.');
            return;
        }

        setSubmittingReviewBookingId(bookingId);
        setCheckoutReviewError('');
        setCheckoutReviewSuccess('');
        try {
            const reviewUrl = `${API_BASE_URL}/api/bookings/${bookingId}/checkout-review`;
            const payload = JSON.stringify({
                adminId: user.userId,
                decision,
                comment: form.comment || '',
                markOwnerTransferPaid: Boolean(form.markOwnerTransferPaid),
                ownerTransferReference,
                markCustomerRefundPaid: Boolean(form.markCustomerRefundPaid),
                customerRefundReference,
                itemComments: itemCommentsPayload
            });
            let response = await fetch(reviewUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload
            });
            if (response.status === 405) {
                response = await fetch(reviewUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Could not submit checkout review.'));
            }

            setCheckoutReviewSuccess(decision === 'approve'
                ? `Checkout deductions approved for ${formatBookingNumber(bookingId)}.`
                : `Checkout deductions rejected for ${formatBookingNumber(bookingId)}.`);
            setExpandedReviewBookingId(null);
            await fetchPendingCheckoutReviews();
            await fetchBookingHistory();
        } catch (err) {
            setCheckoutReviewError(err.message || 'Could not submit checkout review.');
        } finally {
            setSubmittingReviewBookingId(null);
        }
    };

    const handleDamageCategoryChange = (categoryId, field, value) => {
        setDamageCategories(prev => prev.map((item) => (
            Number(item.categoryId) === Number(categoryId)
                ? { ...item, [field]: value }
                : item
        )));
    };

    const createDamageCategory = async () => {
        setDamageCategoriesError('');
        try {
            const payload = {
                categoryType: (damageCategoryDraft.categoryType || '').trim(),
                categoryName: (damageCategoryDraft.categoryName || '').trim(),
                standardPrice: Number(damageCategoryDraft.standardPrice || 0),
                isActive: true
            };
            if (!payload.categoryType || !payload.categoryName) {
                throw new Error('Category type and name are required.');
            }
            if (payload.standardPrice < 0) {
                throw new Error('Standard price must be non-negative.');
            }

            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/damage-categories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to create category');
            }

            setDamageCategoryDraft({
                categoryType: payload.categoryType,
                categoryName: '',
                standardPrice: ''
            });
            await fetchDamageCategories();
        } catch (err) {
            setDamageCategoriesError(err.message || 'Failed to create category.');
        }
    };

    const saveDamageCategory = async (category) => {
        setDamageCategoriesError('');
        setSavingDamageCategoryId(category.categoryId);
        try {
            const payload = {
                categoryType: (category.categoryType || '').trim(),
                categoryName: (category.categoryName || '').trim(),
                standardPrice: Number(category.standardPrice || 0),
                isActive: Boolean(category.isActive)
            };
            if (!payload.categoryType || !payload.categoryName) {
                throw new Error('Category type and name are required.');
            }
            if (payload.standardPrice < 0) {
                throw new Error('Standard price must be non-negative.');
            }

            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/damage-categories/${category.categoryId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to save category');
            }

            await fetchDamageCategories();
        } catch (err) {
            setDamageCategoriesError(err.message || 'Failed to save category.');
        } finally {
            setSavingDamageCategoryId(null);
        }
    };

    const deactivateDamageCategory = async (categoryId) => {
        setDamageCategoriesError('');
        setSavingDamageCategoryId(categoryId);
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/admin/damage-categories/${categoryId}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to deactivate category');
            }
            await fetchDamageCategories();
        } catch (err) {
            setDamageCategoriesError(err.message || 'Failed to deactivate category.');
        } finally {
            setSavingDamageCategoryId(null);
        }
    };

    const isEmailActionInProgress =
        approvingBookingId !== null
        || rejectingBookingId !== null
        || rejectingCaravanId !== null
        || submittingReviewBookingId !== null
        || clearingDueInvoiceId !== null;

    return (
        <>
        <ActionLoaderOverlay
            show={isEmailActionInProgress}
            title="Please wait..."
            subtitle="Action submitted. Sending email notifications."
        />
        {imageSliderState.open && (
            <div className="fixed inset-0 z-[10000] bg-black/90 px-4 py-6">
                <div className="mx-auto max-w-6xl h-full flex flex-col">
                    <div className="flex items-center justify-between text-white mb-3">
                        <h3 className="text-lg font-semibold truncate pr-4">{imageSliderState.title || 'Caravan Images'}</h3>
                        <button
                            type="button"
                            onClick={closeImageSlider}
                            className="bg-white/20 hover:bg-white/30 rounded-md px-3 py-1 text-sm"
                        >
                            Close
                        </button>
                    </div>

                    <div className="flex-1 min-h-0 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={showPrevImage}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                            aria-label="Previous image"
                        >
                            &lt;
                        </button>
                        <img
                            src={imageSliderState.images[imageSliderState.index] || PLACEHOLDER_IMG}
                            alt={`Caravan ${imageSliderState.index + 1}`}
                            className="max-h-full max-w-full object-contain rounded-lg border border-white/20"
                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                        />
                        <button
                            type="button"
                            onClick={showNextImage}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                            aria-label="Next image"
                        >
                            &gt;
                        </button>
                    </div>

                    <div className="mt-3">
                        <p className="text-xs text-white/80 mb-2">
                            {imageSliderState.index + 1} / {imageSliderState.images.length}
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {imageSliderState.images.map((img, idx) => (
                                <button
                                    key={`${img}-${idx}`}
                                    type="button"
                                    onClick={() => setImageSliderState((prev) => ({ ...prev, index: idx }))}
                                    className={`shrink-0 border rounded-md ${idx === imageSliderState.index ? 'border-green-400' : 'border-white/30'}`}
                                >
                                    <img
                                        src={img}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className="w-20 h-14 object-cover rounded-md"
                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )}
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6">Admin Panel</h1>
            <div className="flex flex-wrap border-b mb-6">
                <AdminTabButton tabKey="approvals" title={language === 'da' ? 'Godkendelser' : 'Approvals'} count={unapprovedCaravans.length} activeTab={activeTab} onClick={() => setActiveTab('approvals')} />
                <AdminTabButton tabKey="bookings" title={language === 'da' ? 'Bookinger' : 'Bookings'} count={pendingBookings.length} activeTab={activeTab} onClick={() => setActiveTab('bookings')} />
                <AdminTabButton tabKey="booking-history" title={language === 'da' ? 'Alle bookinger' : 'All Bookings'} count={bookingHistory.length} activeTab={activeTab} onClick={() => setActiveTab('booking-history')} />
                <AdminTabButton tabKey="cancellation-refunds" title={language === 'da' ? 'Annulleringsrefusioner' : 'Cancellation Refunds'} count={pendingCancellationRefundCount} activeTab={activeTab} onClick={() => setActiveTab('cancellation-refunds')} />
                <AdminTabButton tabKey="booking-overview" title={language === 'da' ? 'Bookingoversigt' : 'Booking Overview'} count={adminCaravans.length} activeTab={activeTab} onClick={() => setActiveTab('booking-overview')} />
                <AdminTabButton tabKey="rent-reminders" title={language === 'da' ? 'Lejepåmindelser' : 'Rent Reminders'} count={rentReminderQueue.length} activeTab={activeTab} onClick={() => setActiveTab('rent-reminders')} />
                <AdminTabButton tabKey="rent-report" title={language === 'da' ? 'Afventende leje' : 'Rent Pending Report'} count={rentPendingReport.length} activeTab={activeTab} onClick={() => setActiveTab('rent-report')} />
                <AdminTabButton tabKey="checkout-reviews" title={language === 'da' ? 'Checkout-gennemgange' : 'Checkout Reviews'} count={pendingCheckoutReviews.length} activeTab={activeTab} onClick={() => setActiveTab('checkout-reviews')} />
                <AdminTabButton tabKey="dues" title={language === 'da' ? 'Forfaldne betalinger' : 'Due Clearances'} count={dueInvoices.length} activeTab={activeTab} onClick={() => setActiveTab('dues')} />
                <AdminTabButton tabKey="listings" title={language === 'da' ? 'Annoncer' : 'Listings'} count={adminCaravans.length} activeTab={activeTab} onClick={() => setActiveTab('listings')} />
                <AdminTabButton tabKey="complaints" title={language === 'da' ? 'Klager' : 'Complaints'} count={openComplaints.length} activeTab={activeTab} onClick={() => setActiveTab('complaints')} />
                <AdminTabButton tabKey="amenities" title={language === 'da' ? 'Faciliteter' : 'Amenities'} activeTab={activeTab} onClick={() => setActiveTab('amenities')} />
                <AdminTabButton tabKey="types" title={language === 'da' ? 'Campingvogntyper' : 'Caravan Types'} activeTab={activeTab} onClick={() => setActiveTab('types')} />
                <AdminTabButton tabKey="mappings" title={language === 'da' ? 'Typekoblinger' : 'Type Mappings'} activeTab={activeTab} onClick={() => setActiveTab('mappings')} />
                <AdminTabButton tabKey="seasons" title={language === 'da' ? 'Sæsoner' : 'Seasons'} activeTab={activeTab} onClick={() => setActiveTab('seasons')} />
                <AdminTabButton tabKey="settings" title={language === 'da' ? 'Indstillinger' : 'Settings'} activeTab={activeTab} onClick={() => setActiveTab('settings')} />
                <AdminTabButton tabKey="damage-catalog" title={language === 'da' ? 'Skadekatalog' : 'Damage Catalog'} activeTab={activeTab} onClick={() => setActiveTab('damage-catalog')} />
                <AdminTabButton tabKey="delivery-center" title={language === 'da' ? 'Leveringscenter' : 'Delivery Center'} count={deliveryAdminJobs.length} activeTab={activeTab} onClick={() => setActiveTab('delivery-center')} />
                <AdminTabButton tabKey="users" title={language === 'da' ? 'Brugere' : 'Users'} activeTab={activeTab} onClick={() => setActiveTab('users')} />
            </div>

            {activeTab === 'approvals' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Vehicle Approval Queue</h2>
                    {unapprovedCaravans.length > 0 ? (
                        <div className="space-y-4">
                            {unapprovedCaravans.map(caravan => {
                                const images = getCaravanImages(caravan);
                                const pendingPreviousSnapshot = parsePendingUpdateSnapshot(caravan.pendingUpdatePreviousJson);
                                const pendingNewSnapshot = parsePendingUpdateSnapshot(caravan.pendingUpdateNewJson);
                                const pendingPricingChanges = getPendingPricingChanges(pendingPreviousSnapshot, pendingNewSnapshot);
                                const pendingSeasonalChanges = getPendingSeasonalChanges(pendingPreviousSnapshot, pendingNewSnapshot);
                                const previousImageCount = Array.isArray(pendingPreviousSnapshot?.imageUrls) ? pendingPreviousSnapshot.imageUrls.length : null;
                                const nextImageCount = Array.isArray(pendingNewSnapshot?.imageUrls) ? pendingNewSnapshot.imageUrls.length : null;
                                const hasImageCountChange = previousImageCount !== null && nextImageCount !== null && previousImageCount !== nextImageCount;
                                const hasComparisonData = pendingPricingChanges.length > 0 || pendingSeasonalChanges.length > 0 || hasImageCountChange;
                                const submissionDateValue = caravan.pendingUpdateSubmittedAt || caravan.createdAt;
                                const updateScopeLabel = caravan.pendingUpdateScope || 'listing_update';
                                return (
                                    <div key={caravan.id || caravan.caravan_id} className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 bg-white p-8 rounded-lg shadow-xl">
                                            <div>
                                                <h3 className="text-2xl font-bold">{caravan.name}</h3>
                                                <p className="text-gray-600">{caravan.location || 'Location not provided'}</p>
                                                <img
                                                    src={images[0] || PLACEHOLDER_IMG}
                                                    alt={caravan.name}
                                                    className="rounded-lg mt-4 w-full object-cover h-48 cursor-pointer"
                                                    onClick={() => openImageSlider(images, 0, `${caravan.name} Images`)}
                                                    onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                />
                                                {images.length > 1 && (
                                                    <div className="grid grid-cols-4 gap-2 mt-3">
                                                        {images.slice(1, 9).map((img, idx) => (
                                                            <button
                                                                key={`${caravan.caravan_id || caravan.id}-${idx}`}
                                                                type="button"
                                                                onClick={() => openImageSlider(images, idx + 1, `${caravan.name} Images`)}
                                                                className="w-full"
                                                            >
                                                                <img
                                                                    src={img}
                                                                    alt={`${caravan.name} ${idx + 2}`}
                                                                    className="w-full h-14 object-cover rounded-md border"
                                                                    onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                                />
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                <p className="text-xs text-gray-500 mt-3">ID: {caravan.caravan_id || caravan.id}</p>
                                            </div>

                                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 flex flex-col justify-between">
                                                <div>
                                                    <h4 className="text-xl font-bold mb-3">Caravan Details</h4>
                                                    <div className="space-y-2 text-sm text-gray-700">
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Owner:</span> {caravan.ownerName || caravan.owner?.name || 'N/A'}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Type:</span> {caravan.typeName || 'N/A'}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Registration No:</span> {caravan.registration_number || caravan.registrationNumber || 'N/A'}
                                                    </p>
                                                    <CaravanWeightSummary caravan={caravan} className="grid grid-cols-1 gap-2 sm:grid-cols-2" />
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Sitting:</span> {getCaravanSittingCapacity(caravan) || 'N/A'}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Beds:</span> {getCaravanBedCount(caravan) || 'N/A'}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Price/Day:</span> DKK {Number(caravan.price_per_day || 0).toLocaleString('da-DK')}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Price/Week:</span> DKK {Number(caravan.price_per_week || 0).toLocaleString('da-DK')}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Deposit:</span> DKK {Number((caravan.deposit_amount ?? 0)).toLocaleString('da-DK')}
                                                    </p>
                                                    {caravan.description && (
                                                        <p className="text-sm text-gray-700">
                                                            <span className="font-semibold">Description:</span> {caravan.description}
                                                        </p>
                                                    )}
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-semibold">Amenities:</span> {(caravan.amenities && caravan.amenities.length > 0) ? caravan.amenities.join(', ') : 'N/A'}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        <span className="font-semibold">Submitted:</span> {submissionDateValue ? new Date(submissionDateValue).toLocaleString() : 'N/A'}
                                                    </p>
                                                    <p className="text-sm text-gray-500 capitalize">
                                                        <span className="font-semibold">Update Type:</span> {String(updateScopeLabel).replace(/_/g, ' ')}
                                                    </p>
                                                </div>
                                                </div>

                                                <div className="mt-4">
                                                    <h5 className="text-sm font-bold text-gray-800 mb-2">Previous vs New (for review)</h5>
                                                    {hasComparisonData ? (
                                                        <div className="space-y-3 text-xs">
                                                            {pendingPricingChanges.length > 0 && (
                                                                <div className="border rounded-md p-2 bg-white">
                                                                    <p className="font-semibold text-gray-700 mb-1">Pricing/Settings</p>
                                                                    <div className="space-y-1 text-gray-700">
                                                                        {pendingPricingChanges.map((change) => (
                                                                            <p key={change.label}>
                                                                                <span className="font-medium">{change.label}:</span> {change.previous} -> {change.next}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {pendingSeasonalChanges.length > 0 && (
                                                                <div className="border rounded-md p-2 bg-white">
                                                                    <p className="font-semibold text-gray-700 mb-1">Seasonal Rates</p>
                                                                    <div className="space-y-1 text-gray-700">
                                                                        {pendingSeasonalChanges.map((change) => (
                                                                            <p key={change.seasonName}>
                                                                                <span className="font-medium">{change.seasonName}:</span> Day {formatInr(change.previousDay)} -> {formatInr(change.nextDay)}, Week {formatInr(change.previousWeek)} -> {formatInr(change.nextWeek)}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {hasImageCountChange && (
                                                                <div className="border rounded-md p-2 bg-white text-gray-700">
                                                                    <p><span className="font-medium">Images:</span> {previousImageCount} -> {nextImageCount}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-gray-500">No field-level comparison available for this request.</p>
                                                    )}
                                                </div>

                                                <div className="mt-6">
                                                    <div className="space-y-2">
                                                        <button
                                                            onClick={() => handleApprove(caravan.id || caravan.caravan_id)}
                                                            disabled={rejectingCaravanId === (caravan.id || caravan.caravan_id)}
                                                            className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors duration-300 disabled:bg-gray-400"
                                                        >
                                                            Approve
                                                        </button>
                                                        <textarea
                                                            rows="2"
                                                            value={caravanRejectRemarksById[caravan.id || caravan.caravan_id] || ''}
                                                            onChange={(e) => handleCaravanRejectRemarksChange(caravan.id || caravan.caravan_id, e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                            placeholder=""
                                                        />
                                                        <button
                                                            onClick={() => handleRejectCaravan(caravan.id || caravan.caravan_id)}
                                                            disabled={rejectingCaravanId === (caravan.id || caravan.caravan_id)}
                                                            className="w-full bg-red-600 text-white font-semibold py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                                                        >
                                                            {rejectingCaravanId === (caravan.id || caravan.caravan_id) ? 'Rejecting...' : 'Reject'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : <p>No vehicles waiting for approval.</p>}
                </div>
            )}

            {activeTab === 'bookings' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Booking Approval Queue</h2>
                    {pendingBookings.length > 0 ? (
                        <div className="space-y-4">
                            {pendingBookings.map((booking) => {
                                const pricingModeLabel = getPricingModeLabel(booking.pricingMode, language);
                                const rentalAmount = toAmount(booking.rentalAmount ?? booking.totalPrice);
                                const pricingDurationDisplay = formatPricingDurationDisplay(booking.startDate, booking.endDate, booking.pricingMode, language);
                                const pricingCalculationRule = formatPricingCalculationRule(booking.startDate, booking.endDate, booking.pricingMode, language);
                                const doorstepFee = toAmount(booking.doorstepFee);
                                const bookingAddons = getSelectedBookingAddons(booking);
                                const addonAmount = toAmount(booking.addonAmount);
                                const doorstepRequested = Boolean(booking.doorstepEnabled);
                                const bookingAmount = toAmount(booking.totalPrice);
                                const depositAmount = toAmount(booking.depositAmount);
                                const totalCustomerDue = getTotalCustomerDue(booking);
                                const currentCustomerDue = getBookingCurrentDue(booking);
                                const depositPaid = isBookingDepositPaid(booking);
                                const markFullPaymentOnApproval = Boolean(fullPaymentApprovalByBookingId[booking.bookingId]);
                                const doorstepInputRaw = doorstepApprovalByBookingId[booking.bookingId] ?? (doorstepFee > 0 ? String(doorstepFee) : '');
                                const parsedDoorstepInput = doorstepInputRaw === '' ? null : Number(doorstepInputRaw);
                                const doorstepInputInvalid = doorstepInputRaw !== '' && (!Number.isFinite(parsedDoorstepInput) || parsedDoorstepInput < 0);
                                const effectiveDoorstepFee = doorstepInputRaw !== '' && !doorstepInputInvalid
                                    ? parsedDoorstepInput
                                    : (doorstepFee > 0 ? doorstepFee : null);
                                const doorstepFeeMissing = doorstepRequested && effectiveDoorstepFee === null;

                                return (
                                <div key={booking.bookingId} className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 bg-white p-8 rounded-lg shadow-xl">
                                    <div>
                                        <h3 className="text-2xl font-bold">{booking.caravanName}</h3>
                                        <p className="text-gray-600">{booking.caravanLocation}</p>
                                        <img
                                            src={resolveImageUrl(booking.caravanImageUrl) || PLACEHOLDER_IMG}
                                            alt={booking.caravanName}
                                            className="rounded-lg mt-4 w-full object-cover h-48"
                                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                        />
                                        <div className="mt-4 space-y-2 text-sm text-gray-700">
                                            <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(booking.bookingId)}</p>
                                            <p><span className="font-semibold">Booking ID:</span> {booking.bookingId}</p>
                                            <p><span className="font-semibold">Customer:</span> {booking.renterName} ({booking.renterEmail})</p>
                                            <p><span className="font-semibold">Owner:</span> {booking.ownerName} ({booking.ownerEmail})</p>
                                            <p><span className="font-semibold">Dates:</span> {new Date(booking.startDate).toLocaleDateString()} - {new Date(booking.endDate).toLocaleDateString()}</p>
                                            <p><span className="font-semibold">Pricing Mode:</span> {pricingModeLabel}</p>
                                            <p><span className="font-semibold">Rental Charge ({pricingModeLabel}):</span> {formatInr(rentalAmount)}</p>
                                            <p><span className="font-semibold">Pricing Duration:</span> {pricingDurationDisplay}</p>
                                            <p><span className="font-semibold">Pricing Calculation:</span> {pricingCalculationRule}</p>
                                            <p><span className="font-semibold">Doorstep Fee:</span> {formatInr(doorstepFee)}</p>
                                            {booking.doorstepEnabled && (booking.doorstepDeliveryAddress || booking.DoorstepDeliveryAddress) && (
                                                <p><span className="font-semibold">Delivery Address:</span> {booking.doorstepDeliveryAddress || booking.DoorstepDeliveryAddress}</p>
                                            )}
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons:</span> {formatAddonSummary(bookingAddons)}</p>}
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons Total:</span> {formatInr(addonAmount)}</p>}
                                            <p><span className="font-semibold">Doorstep Status:</span> {booking.doorstepPriceStatus || (doorstepRequested ? 'pending' : 'not_requested')}</p>
                                            <p><span className="font-semibold">Booking Amount:</span> {formatInr(bookingAmount)}</p>
                                            <p><span className="font-semibold">Deposit:</span> {formatInr(depositAmount)}</p>
                                            <p><span className="font-semibold">Net Due Amount:</span> {formatInr(depositPaid ? currentCustomerDue : totalCustomerDue)}</p>
                                            <p><span className="font-semibold">Booking Remarks:</span> {booking.bookingRemarks || booking.BookingRemarks || 'N/A'}</p>
                                            <p><span className="font-semibold">Requested:</span> {booking.createdAt ? new Date(booking.createdAt).toLocaleString() : 'N/A'}</p>
                                            <p><span className="font-semibold">Deposit Due:</span> {booking.depositDueAt ? new Date(booking.depositDueAt).toLocaleString() : 'N/A'}</p>
                                            <p><span className="font-semibold">Rent Due Date:</span> {booking.rentDueAt ? new Date(booking.rentDueAt).toLocaleString() : 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 flex flex-col justify-between">
                                        <div>
                                            <h4 className="text-xl font-bold mb-3">Manual Approval</h4>
                                            <p className="text-sm text-gray-700 mb-2">After confirming deposit receipt manually, approve this booking from here.</p>
                                            <p className="text-sm text-gray-700">On approval, customer and caravan owner will receive email notifications.</p>
                                            {doorstepRequested && (
                                                <div className="mt-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Doorstep fee approved by admin (DKK)</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={doorstepInputRaw}
                                                        onChange={(e) => handleDoorstepApprovalChange(booking.bookingId, e.target.value)}
                                                        placeholder=""
                                                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                                                    />
                                                    {doorstepInputInvalid && (
                                                        <p className="text-xs text-red-600 mt-1">Enter a valid non-negative amount.</p>
                                                    )}
                                                    {!doorstepInputInvalid && doorstepFeeMissing && (
                                                        <p className="text-xs text-red-600 mt-1">Doorstep fee is required before approval.</p>
                                                    )}
                                                    <p className="text-xs text-gray-500 mt-1">Customer first shows interest. Admin sets final price after discussion.</p>
                                                </div>
                                            )}
                                            <label className="mt-4 flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3">
                                                <input
                                                    type="checkbox"
                                                    checked={markFullPaymentOnApproval}
                                                    onChange={(e) => handleFullPaymentApprovalChange(booking.bookingId, e.target.checked)}
                                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                                />
                                                <span className="text-sm text-gray-700">
                                                    <span className="block font-medium text-gray-900">Confirm full payment now</span>
                                                    <span className="block text-xs text-gray-500">Marks rent as already received during approval, so this booking will not show in the rent pending report.</span>
                                                </span>
                                            </label>
                                            <div className="mt-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Remarks (mandatory if rejecting)</label>
                                                <textarea
                                                    rows="2"
                                                    value={bookingRejectRemarksById[booking.bookingId] || ''}
                                                    onChange={(e) => handleBookingRejectRemarksChange(booking.bookingId, e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm"
                                                    placeholder=""
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-6 grid grid-cols-1 gap-2">
                                            <button
                                                onClick={() => handleApproveBooking(
                                                    booking.bookingId,
                                                    effectiveDoorstepFee,
                                                    markFullPaymentOnApproval
                                                )}
                                                disabled={approvingBookingId === booking.bookingId || doorstepInputInvalid || doorstepFeeMissing || rejectingBookingId === booking.bookingId}
                                                className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors duration-300 disabled:bg-gray-400"
                                            >
                                                {approvingBookingId === booking.bookingId
                                                    ? 'Processing...'
                                                    : (markFullPaymentOnApproval ? 'Confirm Full Payment & Approve' : 'Confirm Deposit & Approve')}
                                            </button>
                                            <button
                                                onClick={() => handleRejectBooking(booking.bookingId)}
                                                disabled={rejectingBookingId === booking.bookingId || approvingBookingId === booking.bookingId}
                                                className="w-full bg-red-600 text-white font-semibold py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                                            >
                                                {rejectingBookingId === booking.bookingId ? 'Processing...' : 'Reject Booking'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    ) : <p>No pending bookings waiting for approval.</p>}
                </div>
            )}

            {activeTab === 'rent-report' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Rent Pending + Deposit Received Report</h2>
                    {rentPendingReportError && <p className="text-red-600 mb-3">{rentPendingReportError}</p>}
                    {rentPendingReport.length > 0 ? (
                        <div className="space-y-4">
                            {rentPendingReport.map((booking) => {
                                const rentalAmount = toAmount(booking.rentalAmount ?? booking.totalPrice);
                                const doorstepFee = toAmount(booking.doorstepFee);
                                const bookingAddons = getSelectedBookingAddons(booking);
                                const addonAmount = toAmount(booking.addonAmount);
                                const computedBookingAmount = rentalAmount + addonAmount + doorstepFee;
                                const bookingAmount = computedBookingAmount > 0 ? computedBookingAmount : toAmount(booking.totalPrice);
                                const depositAmount = toAmount(booking.depositAmount);
                                return (
                                    <div key={`rent-report-${booking.bookingId}`} className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md border border-gray-100">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <h3 className="text-xl font-bold">{booking.caravanName}</h3>
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 capitalize">
                                                {booking.bookingStatus || 'confirmed'}
                                            </span>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                            <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(booking.bookingId)}</p>
                                            <p><span className="font-semibold">Booking ID:</span> {booking.bookingId}</p>
                                            <p><span className="font-semibold">Customer:</span> {booking.renterName} ({booking.renterEmail})</p>
                                            <p><span className="font-semibold">Owner:</span> {booking.ownerName} ({booking.ownerEmail})</p>
                                            <p><span className="font-semibold">Rental Charge:</span> {formatInr(rentalAmount)}</p>
                                            <p><span className="font-semibold">Doorstep Fee:</span> {formatInr(doorstepFee)}</p>
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons:</span> {formatAddonSummary(bookingAddons)}</p>}
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons Total:</span> {formatInr(addonAmount)}</p>}
                                            <p><span className="font-semibold">Booking Amount:</span> {formatInr(bookingAmount)}</p>
                                            <p><span className="font-semibold">Deposit Received:</span> {booking.depositPaidAt ? formatInr(depositAmount) : 'No'}</p>
                                            <p><span className="font-semibold">Rent Paid:</span> {booking.rentPaidAt ? 'Yes' : 'No'}</p>
                                            <p><span className="font-semibold">Rent Due Date:</span> {booking.rentDueAt ? new Date(booking.rentDueAt).toLocaleString() : 'N/A'}</p>
                                        </div>
                                        {!booking.rentPaidAt && (
                                            <div className="mt-4">
                                                <button
                                                    onClick={() => handleMarkRentPaid(booking.bookingId)}
                                                    disabled={markingRentPaidBookingId === booking.bookingId}
                                                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                >
                                                    {markingRentPaidBookingId === booking.bookingId ? 'Saving...' : 'Mark Rent Received'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : <p>No bookings found where deposit is received and rent is pending.</p>}
                </div>
            )}

            {activeTab === 'rent-reminders' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-2">Manual Rent Due Reminders</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Only unpaid bookings with trip start dates within the next 21 days appear here. Sending a reminder uses the same rent-due email flow and updates the last sent timestamp on the booking.
                    </p>
                    {rentReminderQueueError && <p className="text-red-600 mb-3">{rentReminderQueueError}</p>}
                    {rentReminderQueue.length > 0 ? (
                        <div className="space-y-4">
                            {rentReminderQueue.map((booking) => {
                                const rentalAmount = toAmount(booking.rentalAmount ?? booking.totalPrice);
                                const doorstepFee = toAmount(booking.doorstepFee);
                                const bookingAddons = getSelectedBookingAddons(booking);
                                const addonAmount = toAmount(booking.addonAmount);
                                const computedBookingAmount = rentalAmount + addonAmount + doorstepFee;
                                const bookingAmount = computedBookingAmount > 0 ? computedBookingAmount : toAmount(booking.totalPrice);
                                const depositAmount = toAmount(booking.depositAmount);
                                const tripStart = booking.startDate ? new Date(booking.startDate) : null;
                                const today = new Date();
                                const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                const tripStartDay = tripStart ? new Date(tripStart.getFullYear(), tripStart.getMonth(), tripStart.getDate()) : null;
                                const daysUntilStart = tripStartDay
                                    ? Math.max(Math.round((tripStartDay.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24)), 0)
                                    : null;

                                return (
                                    <div key={`rent-reminder-${booking.bookingId}`} className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md border border-gray-100">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div>
                                                <h3 className="text-xl font-bold">{booking.caravanName}</h3>
                                                <p className="text-sm text-gray-500">{booking.caravanLocation || 'Location not available'}</p>
                                            </div>
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 capitalize">
                                                {booking.bookingStatus || 'confirmed'}
                                            </span>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                            <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(booking.bookingId)}</p>
                                            <p><span className="font-semibold">Booking ID:</span> {booking.bookingId}</p>
                                            <p><span className="font-semibold">Customer:</span> {booking.renterName} ({booking.renterEmail})</p>
                                            <p><span className="font-semibold">Owner:</span> {booking.ownerName} ({booking.ownerEmail})</p>
                                            <p><span className="font-semibold">Trip Dates:</span> {booking.startDate ? new Date(booking.startDate).toLocaleDateString() : 'N/A'} - {booking.endDate ? new Date(booking.endDate).toLocaleDateString() : 'N/A'}</p>
                                            <p><span className="font-semibold">Days Until Trip Start:</span> {daysUntilStart ?? 'N/A'}</p>
                                            <p><span className="font-semibold">Rental Charge:</span> {formatInr(rentalAmount)}</p>
                                            <p><span className="font-semibold">Doorstep Fee:</span> {formatInr(doorstepFee)}</p>
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons:</span> {formatAddonSummary(bookingAddons)}</p>}
                                            {bookingAddons.length > 0 && <p><span className="font-semibold">Add-ons Total:</span> {formatInr(addonAmount)}</p>}
                                            <p><span className="font-semibold">Booking Amount:</span> {formatInr(bookingAmount)}</p>
                                            <p><span className="font-semibold">Deposit Received:</span> {booking.depositPaidAt ? formatInr(depositAmount) : 'No'}</p>
                                            <p><span className="font-semibold">Rent Due Date:</span> {booking.rentDueAt ? new Date(booking.rentDueAt).toLocaleString() : 'N/A'}</p>
                                            <p><span className="font-semibold">Last Reminder Email:</span> {booking.rentReminderSentAt ? new Date(booking.rentReminderSentAt).toLocaleString() : 'Never sent'}</p>
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-3">
                                            <button
                                                onClick={() => handleSendRentReminder(booking.bookingId)}
                                                disabled={sendingRentReminderBookingId === booking.bookingId}
                                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                            >
                                                {sendingRentReminderBookingId === booking.bookingId ? 'Sending...' : 'Send Reminder'}
                                            </button>
                                            <button
                                                onClick={() => handleMarkRentPaid(booking.bookingId)}
                                                disabled={markingRentPaidBookingId === booking.bookingId}
                                                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                            >
                                                {markingRentPaidBookingId === booking.bookingId ? 'Saving...' : 'Mark Rent Received'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : <p>No unpaid bookings starting within the next 21 days are currently eligible for rent reminders.</p>}
                </div>
            )}

            {activeTab === 'cancellation-refunds' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Cancellation Refunds</h2>
                    <p className="text-sm text-gray-600 mb-4">Cancel confirmed bookings from booking history, then use this screen to mark rent and deposit refunds as settled.</p>
                    {bookingCancellationError && <p className="text-red-600 mb-3">{bookingCancellationError}</p>}
                    {bookingCancellationSuccess && <p className="text-green-700 mb-3">{bookingCancellationSuccess}</p>}
                    {cancelledBookings.length > 0 ? (
                        <div className="space-y-4">
                            {cancelledBookings.map((booking) => {
                                const rentRefundAmount = toAmount(booking.cancellationRentRefundAmount);
                                const depositRefundAmount = toAmount(booking.cancellationDepositRefundAmount);
                                const rentRefundPaid = Boolean(booking.cancellationRentRefundPaid);
                                const depositRefundPaid = Boolean(booking.cancellationDepositRefundPaid);
                                const rentActionKey = `${booking.bookingId}-rent`;
                                const depositActionKey = `${booking.bookingId}-deposit`;

                                return (
                                    <div key={`cancellation-refund-${booking.bookingId}`} className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md border border-gray-100">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <h3 className="text-xl font-bold">{booking.caravanName}</h3>
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 capitalize">
                                                {booking.bookingStatus || 'cancelled'}
                                            </span>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                            <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(booking.bookingId)}</p>
                                            <p><span className="font-semibold">Booking ID:</span> {booking.bookingId}</p>
                                            <p><span className="font-semibold">Customer:</span> {booking.renterName} ({booking.renterEmail})</p>
                                            <p><span className="font-semibold">Owner:</span> {booking.ownerName} ({booking.ownerEmail})</p>
                                            <p><span className="font-semibold">Trip Dates:</span> {booking.startDate ? new Date(booking.startDate).toLocaleDateString() : 'N/A'} - {booking.endDate ? new Date(booking.endDate).toLocaleDateString() : 'N/A'}</p>
                                            <p><span className="font-semibold">Cancelled At:</span> {booking.cancelledAt ? new Date(booking.cancelledAt).toLocaleString() : 'N/A'}</p>
                                            <p className="md:col-span-2"><span className="font-semibold">Cancellation Remarks:</span> {booking.cancellationRemarks || 'N/A'}</p>
                                        </div>

                                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="rounded-md border border-gray-200 p-4 bg-gray-50">
                                                <p className="text-sm font-semibold text-gray-900">Rent Refund</p>
                                                <p className="mt-2 text-sm text-gray-700"><span className="font-medium">Amount:</span> {formatInr(rentRefundAmount)}</p>
                                                <p className="text-sm text-gray-700"><span className="font-medium">Status:</span> {rentRefundAmount <= 0 ? 'No refund due' : (rentRefundPaid ? 'Paid' : 'Pending')}</p>
                                                <p className="text-sm text-gray-700"><span className="font-medium">Paid At:</span> {booking.cancellationRentRefundPaidAt ? new Date(booking.cancellationRentRefundPaidAt).toLocaleString() : 'N/A'}</p>
                                                {rentRefundAmount > 0 && !rentRefundPaid && (
                                                    <button
                                                        onClick={() => handleMarkCancellationRefundPaid(booking.bookingId, 'rent')}
                                                        disabled={markingCancellationRefundKey === rentActionKey}
                                                        className="mt-3 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                    >
                                                        {markingCancellationRefundKey === rentActionKey ? 'Saving...' : 'Mark Rent Refund Paid'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="rounded-md border border-gray-200 p-4 bg-gray-50">
                                                <p className="text-sm font-semibold text-gray-900">Deposit Refund</p>
                                                <p className="mt-2 text-sm text-gray-700"><span className="font-medium">Amount:</span> {formatInr(depositRefundAmount)}</p>
                                                <p className="text-sm text-gray-700"><span className="font-medium">Status:</span> {depositRefundAmount <= 0 ? 'No refund due' : (depositRefundPaid ? 'Paid' : 'Pending')}</p>
                                                <p className="text-sm text-gray-700"><span className="font-medium">Paid At:</span> {booking.cancellationDepositRefundPaidAt ? new Date(booking.cancellationDepositRefundPaidAt).toLocaleString() : 'N/A'}</p>
                                                {depositRefundAmount > 0 && !depositRefundPaid && (
                                                    <button
                                                        onClick={() => handleMarkCancellationRefundPaid(booking.bookingId, 'deposit')}
                                                        disabled={markingCancellationRefundKey === depositActionKey}
                                                        className="mt-3 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                    >
                                                        {markingCancellationRefundKey === depositActionKey ? 'Saving...' : 'Mark Deposit Refund Paid'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : <p>No cancelled bookings found.</p>}
                </div>
            )}

            {activeTab === 'listings' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Manage Listing Status</h2>
                    {adminCaravansError && <p className="text-red-600 mb-3">{adminCaravansError}</p>}
                    {unlistedFutureBookingsError && <p className="text-red-600 mb-3">{unlistedFutureBookingsError}</p>}
                    {adminCaravans.length > 0 ? (
                        <div className="space-y-3">
                            {adminCaravans.map((caravan) => {
                                const isListed = isTruthyValue(caravan.is_listed ?? caravan.isListed ?? true);
                                const impactedFutureBookingCount = (unlistedFutureBookingsReport || []).filter((row) => Number(row.caravanId) === Number(caravan.caravan_id)).length;
                                return (
                                    <div key={`admin-listing-${caravan.caravan_id}`} className="max-w-4xl mx-auto bg-white p-4 rounded-lg shadow border border-gray-100 flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-800">{caravan.name}</p>
                                            <p className="text-sm text-gray-600">{caravan.location}</p>
                                            <p className="text-xs text-gray-500">Owner: {caravan.ownerName || 'N/A'} | Status: {isListed ? 'Listed' : 'Unlisted'}</p>
                                            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
                                                <p><span className="font-semibold">Type:</span> {caravan.typeName || 'N/A'}</p>
                                                <p><span className="font-semibold">Registration No:</span> {caravan.registration_number || caravan.registrationNumber || 'N/A'}</p>
                                                <p><span className="font-semibold">Sitting:</span> {getCaravanSittingCapacity(caravan) || 'N/A'}</p>
                                                <p><span className="font-semibold">Beds:</span> {getCaravanBedCount(caravan) || 'N/A'}</p>
                                                <p><span className="font-semibold">Price/Day:</span> DKK {Number(caravan.price_per_day || 0).toLocaleString('da-DK')}</p>
                                                <p><span className="font-semibold">Price/Week:</span> DKK {Number(caravan.price_per_week || 0).toLocaleString('da-DK')}</p>
                                            </div>
                                            <CaravanWeightSummary caravan={caravan} className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" />
                                            {!isListed && impactedFutureBookingCount > 0 && (
                                                <p className="text-xs text-red-700 font-semibold mt-1">Warning: {impactedFutureBookingCount} future booking(s) are still active.</p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => updateCaravanListingStatus(caravan.caravan_id, !isListed)}
                                            disabled={updatingListingCaravanId === caravan.caravan_id}
                                            className={`${isListed ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-md disabled:bg-gray-400`}
                                        >
                                            {updatingListingCaravanId === caravan.caravan_id
                                                ? 'Updating...'
                                                : (isListed ? 'Unlist' : 'Relist')}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ) : <p>No caravans available.</p>}

                    {unlistedFutureBookingsReport.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-xl font-semibold mb-3">Unlisted Caravan Future Booking Report</h3>
                            <div className="space-y-3">
                                {unlistedFutureBookingsReport.map((row) => (
                                    <div key={`unlisted-report-${row.bookingId}`} className="max-w-4xl mx-auto bg-red-50 border border-red-200 rounded-md p-3 text-sm text-gray-800">
                                        <p><span className="font-semibold">Caravan:</span> {row.caravanName} (ID {row.caravanId})</p>
                                        <p><span className="font-semibold">Owner:</span> {row.ownerName} ({row.ownerEmail})</p>
                                        <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(row.bookingId)} | <span className="font-semibold">Status:</span> {row.bookingStatus}</p>
                                        <p><span className="font-semibold">Dates:</span> {row.startDate ? new Date(row.startDate).toLocaleDateString() : 'N/A'} - {row.endDate ? new Date(row.endDate).toLocaleDateString() : 'N/A'}</p>
                                        <p><span className="font-semibold">Customer:</span> {row.renterName} ({row.renterEmail})</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'complaints' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Customer Complaints</h2>
                    <p className="text-sm text-gray-600 mb-4">Review complaint explanation and evidence photos, then unlist caravan if required.</p>
                    {openComplaintsError && <p className="text-red-600 mb-3">{openComplaintsError}</p>}
                    {openComplaintsLoading && <p>Loading complaints...</p>}
                    {!openComplaintsLoading && openComplaints.length > 0 ? (
                        <div className="space-y-4">
                            {openComplaints.map((complaint) => {
                                const complaintPhotos = Array.isArray(complaint.photoUrls) ? complaint.photoUrls : [];
                                const listedStatus = isTruthyValue(complaint.isListed ?? complaint.is_listed ?? true);
                                return (
                                    <div key={`complaint-${complaint.complaintId}`} className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md border border-gray-100">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <h3 className="text-xl font-bold">{complaint.caravanName}</h3>
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 capitalize">
                                                {complaint.status || 'open'}
                                            </span>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                            <p><span className="font-semibold">Complaint ID:</span> {complaint.complaintId}</p>
                                            <p><span className="font-semibold">Caravan ID:</span> {complaint.caravanId}</p>
                                            <p><span className="font-semibold">Customer:</span> {complaint.renterName} ({complaint.renterEmail})</p>
                                            <p><span className="font-semibold">Owner:</span> {complaint.ownerName} ({complaint.ownerEmail})</p>
                                            <p><span className="font-semibold">Created At:</span> {complaint.createdAt ? new Date(complaint.createdAt).toLocaleString() : 'N/A'}</p>
                                            <p><span className="font-semibold">Current Listing:</span> {listedStatus ? 'Listed' : 'Unlisted'}</p>
                                        </div>
                                        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Customer Explanation</p>
                                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{complaint.explanation || 'N/A'}</p>
                                        </div>
                                        {complaintPhotos.length > 0 && (
                                            <div className="mt-4">
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Evidence Photos</p>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    {complaintPhotos.map((photoUrl, photoIndex) => (
                                                        <button
                                                            type="button"
                                                            key={`complaint-${complaint.complaintId}-photo-${photoIndex}`}
                                                            className="block border rounded-md overflow-hidden bg-white"
                                                            onClick={() => openImageSlider(
                                                                complaintPhotos,
                                                                photoIndex,
                                                                `Complaint Evidence - ID ${complaint.complaintId}`
                                                            )}
                                                        >
                                                            <img
                                                                src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                                alt={`Complaint evidence ${photoIndex + 1}`}
                                                                className="h-24 w-full object-cover"
                                                                onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className="mt-4">
                                            <button
                                                onClick={() => applyComplaintAction(complaint.complaintId, 'unlist_caravan')}
                                                disabled={actingComplaintId === complaint.complaintId}
                                                className="bg-red-700 text-white px-4 py-2 rounded-md hover:bg-red-800 disabled:bg-gray-400"
                                            >
                                                {actingComplaintId === complaint.complaintId ? 'Processing...' : 'Unlist Caravan & Notify Owner'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                    {!openComplaintsLoading && openComplaints.length === 0 && (
                        <p>No open complaints found.</p>
                    )}
                </div>
            )}

            {activeTab === 'checkout-reviews' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Checkout Deduction Reviews</h2>
                    <p className="text-sm text-gray-600 mb-4">Review submitted owner deduction forms and approve for final settlement.</p>
                    {checkoutReviewError && <p className="text-red-600 mb-3">{checkoutReviewError}</p>}
                    {checkoutReviewSuccess && <p className="text-green-700 mb-3">{checkoutReviewSuccess}</p>}

                    {pendingCheckoutReviews.length > 0 ? (
                        <div className="space-y-4">
                            {pendingCheckoutReviews.map((booking) => {
                                const assessment = assessmentByBookingId[booking.bookingId];
                                const checkInPhotos = Array.isArray(checkInPhotoUrlsByBookingId[booking.bookingId]) ? checkInPhotoUrlsByBookingId[booking.bookingId] : [];
                                const reviewForm = reviewFormsByBookingId[booking.bookingId] || getDefaultReviewFormState();
                                return (
                                    <div key={booking.bookingId} className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md border border-gray-100">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <h3 className="text-xl font-bold">{booking.caravanName}</h3>
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 capitalize">
                                                {booking.bookingStatus}
                                            </span>
                                        </div>
                                        <p className="text-gray-600">{booking.caravanLocation}</p>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                            <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(booking.bookingId)}</p>
                                            <p><span className="font-semibold">Booking ID:</span> {booking.bookingId}</p>
                                            <p><span className="font-semibold">Customer:</span> {booking.renterName} ({booking.renterEmail})</p>
                                            <p><span className="font-semibold">Owner:</span> {booking.ownerName} ({booking.ownerEmail})</p>
                                            <p><span className="font-semibold">Total Deduction:</span> DKK {Number(booking.damageDeductionAmount || 0).toLocaleString('da-DK')}</p>
                                            <p><span className="font-semibold">Deposit Refund:</span> DKK {Number(booking.depositRefundAmount || 0).toLocaleString('da-DK')}</p>
                                            <p><span className="font-semibold">Submitted At:</span> {booking.checkoutSubmittedAt ? new Date(booking.checkoutSubmittedAt).toLocaleString() : 'N/A'}</p>
                                        </div>

                                        {checkInPhotos.length > 0 && (
                                            <div className="mt-3">
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Check-in Photos</p>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                    {checkInPhotos.map((photoUrl, photoIndex) => (
                                                        <button
                                                            type="button"
                                                            key={`admin-review-checkin-photo-${booking.bookingId}-${photoIndex}`}
                                                            className="block border rounded-md overflow-hidden bg-white"
                                                            onClick={() => openImageSlider(
                                                                checkInPhotos,
                                                                photoIndex,
                                                                `Check-in Photos - ${formatBookingNumber(booking.bookingId)}`
                                                            )}
                                                        >
                                                            <img
                                                                src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                                alt={`Check-in photo ${photoIndex + 1}`}
                                                                className="h-20 w-full object-cover"
                                                                onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-4">
                                            <button
                                                onClick={() => loadCheckoutAssessmentForReview(booking.bookingId)}
                                                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                            >
                                                {expandedReviewBookingId === booking.bookingId ? 'Close Review' : 'Review Deductions'}
                                            </button>
                                        </div>

                                        {expandedReviewBookingId === booking.bookingId && (
                                            <div className="mt-4 border border-amber-200 rounded-md p-4 bg-amber-50">
                                                {loadingReviewBookingId === booking.bookingId && <p>Loading assessment...</p>}
                                                {!loadingReviewBookingId && !assessment && <p className="text-red-600">No assessment found.</p>}
                                                {!loadingReviewBookingId && !!assessment && (
                                                    <>
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full text-sm bg-white border border-gray-200">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left">Category</th>
                                                                        <th className="px-3 py-2 text-left">Type</th>
                                                                        <th className="px-3 py-2 text-right">Qty</th>
                                                                        <th className="px-3 py-2 text-right">Unit Price</th>
                                                                        <th className="px-3 py-2 text-right">Line Total</th>
                                                                        <th className="px-3 py-2 text-left">Reject Comment</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(assessment.items || []).map((item) => (
                                                                        <tr key={item.itemId} className="border-t">
                                                                            <td className="px-3 py-2">{item.categoryName}</td>
                                                                            <td className="px-3 py-2">{item.categoryType}</td>
                                                                            <td className="px-3 py-2 text-right">{item.quantity}</td>
                                                                            <td className="px-3 py-2 text-right">DKK {Number(item.unitPrice || 0).toLocaleString('da-DK')}</td>
                                                                            <td className="px-3 py-2 text-right">DKK {Number(item.lineTotal || 0).toLocaleString('da-DK')}</td>
                                                                            <td className="px-3 py-2">
                                                                                <input
                                                                                    type="text"
                                                                                    value={reviewForm.itemComments?.[item.itemId] || ''}
                                                                                    onChange={(e) => updateReviewItemComment(booking.bookingId, item.itemId, e.target.value)}
                                                                                    className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                                                    placeholder=""
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {(assessment.photoUrls || []).length > 0 && (
                                                            <div className="mt-4">
                                                                <p className="text-sm font-semibold text-gray-700 mb-2">Damage Photos</p>
                                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                                    {(assessment.photoUrls || []).map((photoUrl, photoIndex) => (
                                                                        <button
                                                                            type="button"
                                                                            key={`${booking.bookingId}-review-photo-${photoIndex}`}
                                                                            className="block border rounded-md overflow-hidden bg-white"
                                                                            onClick={() => openImageSlider(
                                                                                assessment.photoUrls || [],
                                                                                photoIndex,
                                                                                `Damage Photos - ${formatBookingNumber(booking.bookingId)}`
                                                                            )}
                                                                        >
                                                                            <img
                                                                                src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                                                alt={`Damage evidence ${photoIndex + 1}`}
                                                                                className="h-24 w-full object-cover"
                                                                                onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                                            />
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="mt-4 rounded-md border border-gray-200 bg-white p-3">
                                                            <p className="text-sm font-semibold text-gray-700 mb-2">Transfer Settlement Details</p>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
                                                                <div className="rounded-md bg-gray-50 p-3 border border-gray-200">
                                                                    <p className="font-semibold text-gray-800">Caravan Owner Bank</p>
                                                                    <p><span className="font-medium">Registration No:</span> {assessment.ownerBankRegistrationNo || 'N/A'}</p>
                                                                    <p><span className="font-medium">Account No:</span> {assessment.ownerBankAccountNo || 'N/A'}</p>
                                                                </div>
                                                                <div className="rounded-md bg-gray-50 p-3 border border-gray-200">
                                                                    <p className="font-semibold text-gray-800">Customer Bank</p>
                                                                    <p><span className="font-medium">Registration No:</span> {assessment.customerBankRegistrationNo || 'N/A'}</p>
                                                                    <p><span className="font-medium">Account No:</span> {assessment.customerBankAccountNo || 'N/A'}</p>
                                                                </div>
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-3">Enter transfer reference above each paid checkbox. Reference is required when checkbox is selected.</p>
                                                            <div className="mt-3 space-y-3">
                                                                <div className="rounded-md border border-gray-200 p-3">
                                                                    <input
                                                                        type="text"
                                                                        value={reviewForm.ownerTransferReference || ''}
                                                                        onChange={(e) => updateReviewTransferField(booking.bookingId, 'ownerTransferReference', e.target.value)}
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                                        placeholder=""
                                                                        disabled={!reviewForm.markOwnerTransferPaid}
                                                                    />
                                                                    <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-800">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={Boolean(reviewForm.markOwnerTransferPaid)}
                                                                            onChange={(e) => updateReviewTransferField(booking.bookingId, 'markOwnerTransferPaid', e.target.checked)}
                                                                        />
                                                                        Deducted amount paid to caravan owner
                                                                    </label>
                                                                </div>
                                                                <div className="rounded-md border border-gray-200 p-3">
                                                                    <input
                                                                        type="text"
                                                                        value={reviewForm.customerRefundReference || ''}
                                                                        onChange={(e) => updateReviewTransferField(booking.bookingId, 'customerRefundReference', e.target.value)}
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                                        placeholder=""
                                                                        disabled={!reviewForm.markCustomerRefundPaid}
                                                                    />
                                                                    <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-800">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={Boolean(reviewForm.markCustomerRefundPaid)}
                                                                            onChange={(e) => updateReviewTransferField(booking.bookingId, 'markCustomerRefundPaid', e.target.checked)}
                                                                        />
                                                                        Deposit refund paid to customer
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="mt-4">
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Overall Admin Comment</label>
                                                            <textarea
                                                                rows="3"
                                                                value={reviewForm.comment || ''}
                                                                onChange={(e) => updateReviewComment(booking.bookingId, e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                            />
                                                        </div>

                                                        <div className="mt-4 flex flex-wrap gap-3">
                                                            <button
                                                                onClick={() => submitCheckoutReview(booking, 'approve')}
                                                                disabled={submittingReviewBookingId === booking.bookingId}
                                                                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                            >
                                                                {submittingReviewBookingId === booking.bookingId ? 'Submitting...' : 'Approve & Send Invoice'}
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : <p>No checkout reviews pending approval.</p>}
                </div>
            )}

            {activeTab === 'dues' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Outstanding Damage Dues</h2>
                    {dueInvoicesError && <p className="text-red-600 mb-3">{dueInvoicesError}</p>}
                    {dueInvoicesLoading ? (
                        <p>Loading due invoices...</p>
                    ) : dueInvoices.length > 0 ? (
                        <div className="space-y-4">
                            {dueInvoices.map((due) => (
                                <div key={due.dueInvoiceId} className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md border border-gray-100">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <h3 className="text-xl font-bold">{due.caravanName}</h3>
                                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 capitalize">
                                            {due.status}
                                        </span>
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                        <p><span className="font-semibold">Booking No:</span> {formatBookingNumber(due.bookingId)}</p>
                                        <p><span className="font-semibold">Booking ID:</span> {due.bookingId}</p>
                                        <p><span className="font-semibold">Customer:</span> {due.renterName} ({due.renterEmail})</p>
                                        <p><span className="font-semibold">Owner:</span> {due.ownerName} ({due.ownerEmail})</p>
                                        <p><span className="font-semibold">Trip Dates:</span> {new Date(due.startDate).toLocaleDateString()} - {new Date(due.endDate).toLocaleDateString()}</p>
                                        <p><span className="font-semibold">Outstanding Due:</span> {formatInr(due.outstandingAmount)}</p>
                                        <p><span className="font-semibold">Reminder Count:</span> {due.reminderCount ?? 0}</p>
                                        <p><span className="font-semibold">Last Reminder:</span> {due.lastReminderSentAt ? new Date(due.lastReminderSentAt).toLocaleString() : 'N/A'}</p>
                                    </div>

                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Admin clearance comment</label>
                                        <textarea
                                            rows="2"
                                            value={dueClearCommentById[due.dueInvoiceId] || ''}
                                            onChange={(e) => handleDueClearCommentChange(due.dueInvoiceId, e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder=""
                                        />
                                    </div>

                                    <div className="mt-4 flex justify-end">
                                        <button
                                            onClick={() => clearDueInvoice(due.dueInvoiceId)}
                                            disabled={clearingDueInvoiceId === due.dueInvoiceId}
                                            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                        >
                                            {clearingDueInvoiceId === due.dueInvoiceId ? 'Saving...' : 'Mark Due Cleared'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>No outstanding due invoices.</p>
                    )}
                </div>
            )}

            {activeTab === 'booking-history' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">{t('adminAllBookingsTitle')}</h2>
                    <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-2">
                        <label className="text-sm font-medium text-gray-700">
                            <span className="mb-1 block">{language === 'da' ? 'Filtrer efter status' : 'Filter by status'}</span>
                            <select value={bookingHistoryStatusFilter} onChange={(event) => setBookingHistoryStatusFilter(event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2">
                                <option value="all">{language === 'da' ? 'Alle statusser' : 'All statuses'}</option>
                                {bookingHistoryStatuses.map((status) => <option key={status} value={status}>{getAdminBookingStatusLabel(status)}</option>)}
                            </select>
                        </label>
                        <label className="text-sm font-medium text-gray-700">
                            <span className="mb-1 block">{language === 'da' ? 'Sorter bookinger' : 'Sort bookings'}</span>
                            <select value={bookingHistorySort} onChange={(event) => setBookingHistorySort(event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2">
                                <option value="bookingDateDesc">{language === 'da' ? 'Bookingdato – nyeste først' : 'Booking date – newest first'}</option>
                                <option value="bookingDateAsc">{language === 'da' ? 'Bookingdato – ældste først' : 'Booking date – oldest first'}</option>
                                <option value="startDateAsc">{language === 'da' ? 'Startdato – tidligste først' : 'Start date – earliest first'}</option>
                                <option value="startDateDesc">{language === 'da' ? 'Startdato – seneste først' : 'Start date – latest first'}</option>
                                <option value="endDateAsc">{language === 'da' ? 'Slutdato – tidligste først' : 'End date – earliest first'}</option>
                                <option value="endDateDesc">{language === 'da' ? 'Slutdato – seneste først' : 'End date – latest first'}</option>
                            </select>
                        </label>
                    </div>
                    {bookingCancellationError && <p className="text-red-600 mb-3">{bookingCancellationError}</p>}
                    {bookingCancellationSuccess && <p className="text-green-700 mb-3">{bookingCancellationSuccess}</p>}
                    {filteredSortedBookingHistory.length > 0 ? (
                        <div className="space-y-4">
                            {filteredSortedBookingHistory.map((booking) => {
                                const pricingModeLabel = getPricingModeLabel(booking.pricingMode, language);
                                const rentalAmount = toAmount(booking.rentalAmount ?? booking.totalPrice);
                                const pricingDurationDisplay = formatPricingDurationDisplay(booking.startDate, booking.endDate, booking.pricingMode, language);
                                const pricingCalculationRule = formatPricingCalculationRule(booking.startDate, booking.endDate, booking.pricingMode, language);
                                const doorstepFee = toAmount(booking.doorstepFee);
                                const bookingAddons = getSelectedBookingAddons(booking);
                                const addonAmount = toAmount(booking.addonAmount);
                                const bookingAmount = toAmount(booking.totalPrice);
                                const depositAmount = toAmount(booking.depositAmount);
                                const totalCustomerDue = getTotalCustomerDue(booking);
                                const currentCustomerDue = getBookingCurrentDue(booking);
                                const depositPaid = isBookingDepositPaid(booking);
                                const normalizedBookingStatus = String(booking.bookingStatus || '').trim().toLowerCase();
                                const canCancelBooking = normalizedBookingStatus === 'pending' || normalizedBookingStatus === 'confirmed';
                                const cancellationRentRefundAmount = toAmount(booking.cancellationRentRefundAmount);
                                const cancellationDepositRefundAmount = toAmount(booking.cancellationDepositRefundAmount);
                                const checkInPhotos = Array.isArray(checkInPhotoUrlsByBookingId[booking.bookingId])
                                    ? checkInPhotoUrlsByBookingId[booking.bookingId]
                                    : [];

                                return (
                                <div key={booking.bookingId} className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow-md border border-gray-100">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <h3 className="text-xl font-bold">{booking.caravanName}</h3>
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${booking.bookingStatus === 'confirmed' ? 'bg-green-100 text-green-800' :
                                            booking.bookingStatus === 'checked_in' ? 'bg-blue-100 text-blue-800' :
                                                booking.bookingStatus === 'checkout_pending_customer' ? 'bg-orange-100 text-orange-800' :
                                                booking.bookingStatus === 'checkout_pending_approval' ? 'bg-amber-100 text-amber-800' :
                                                    booking.bookingStatus === 'checkout_rejected' ? 'bg-red-100 text-red-800' :
                                                (booking.bookingStatus === 'checked_out' || booking.bookingStatus === 'completed') ? 'bg-purple-100 text-purple-800' :
                                                    booking.bookingStatus === 'expired' ? 'bg-gray-200 text-gray-700' :
                                                        'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {getAdminBookingStatusLabel(booking.bookingStatus)}
                                        </span>
                                    </div>
                                    <p className="text-gray-600">{booking.caravanLocation}</p>
                                    <img
                                        src={resolveImageUrl(booking.caravanImageUrl) || PLACEHOLDER_IMG}
                                        alt={booking.caravanName}
                                        className="rounded-lg mt-3 w-full object-cover h-44"
                                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                    />
                                    {checkInPhotos.length > 0 && (
                                        <div className="mt-3">
                                            <p className="text-sm font-semibold text-gray-700 mb-2">{t('adminCheckInPhotos')}</p>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                {checkInPhotos.map((photoUrl, photoIndex) => (
                                                    <button
                                                        type="button"
                                                        key={`admin-history-checkin-photo-${booking.bookingId}-${photoIndex}`}
                                                        className="block border rounded-md overflow-hidden bg-white"
                                                        onClick={() => openImageSlider(
                                                            checkInPhotos,
                                                            photoIndex,
                                                            t('adminCheckInPhotoGalleryTitle').replace('{bookingNo}', formatBookingNumber(booking.bookingId))
                                                        )}
                                                    >
                                                        <img
                                                            src={resolveImageUrl(photoUrl) || PLACEHOLDER_IMG}
                                                            alt={t('adminCheckInPhotoAlt').replace('{count}', String(photoIndex + 1))}
                                                            className="h-20 w-full object-cover"
                                                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                        <p><span className="font-semibold">{t('adminBookingNumber')}:</span> {formatBookingNumber(booking.bookingId)}</p>
                                        <p><span className="font-semibold">{t('adminBookingId')}:</span> {booking.bookingId}</p>
                                        <p><span className="font-semibold">{t('adminCustomer')}:</span> {booking.renterName} ({booking.renterEmail})</p>
                                        <p><span className="font-semibold">{t('adminOwner')}:</span> {booking.ownerName} ({booking.ownerEmail})</p>
                                        <p><span className="font-semibold">{t('adminDates')}:</span> {new Date(booking.startDate).toLocaleDateString()} - {new Date(booking.endDate).toLocaleDateString()}</p>
                                        <p><span className="font-semibold">{t('adminPricingMode')}:</span> {pricingModeLabel}</p>
                                        <p><span className="font-semibold">{t('adminRentalCharge')} ({pricingModeLabel}):</span> {formatInr(rentalAmount)}</p>
                                        <p><span className="font-semibold">{t('adminPricingDuration')}:</span> {pricingDurationDisplay}</p>
                                        <p><span className="font-semibold">{t('adminPricingCalculation')}:</span> {pricingCalculationRule}</p>
                                        <p><span className="font-semibold">{t('adminDoorstepFee')}:</span> {formatInr(doorstepFee)}</p>
                                        {booking.doorstepEnabled && (booking.doorstepDeliveryAddress || booking.DoorstepDeliveryAddress) && (
                                            <p><span className="font-semibold">{t('adminDeliveryAddress')}:</span> {booking.doorstepDeliveryAddress || booking.DoorstepDeliveryAddress}</p>
                                        )}
                                        {bookingAddons.length > 0 && <p><span className="font-semibold">{t('adminAddons')}:</span> {formatAddonSummary(bookingAddons)}</p>}
                                        {bookingAddons.length > 0 && <p><span className="font-semibold">{t('adminAddonsTotal')}:</span> {formatInr(addonAmount)}</p>}
                                        <p><span className="font-semibold">{t('adminBookingAmount')}:</span> {formatInr(bookingAmount)}</p>
                                        <p><span className="font-semibold">{t('adminDeposit')}:</span> {formatInr(depositAmount)}</p>
                                        <p><span className="font-semibold">{t('adminNetDueAmount')}:</span> {formatInr(depositPaid ? currentCustomerDue : totalCustomerDue)}</p>
                                        <p><span className="font-semibold">{t('adminBookingRemarks')}:</span> {booking.bookingRemarks || booking.BookingRemarks || t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminCheckIn')}:</span> {booking.checkInAt ? new Date(booking.checkInAt).toLocaleString() : t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminCheckOut')}:</span> {booking.checkOutAt ? new Date(booking.checkOutAt).toLocaleString() : t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminCheckoutReview')}:</span> {getAdminCheckoutReviewStatusLabel(booking.checkoutReviewStatus)}</p>
                                        <p><span className="font-semibold">{t('adminSubmittedAt')}:</span> {booking.checkoutSubmittedAt ? new Date(booking.checkoutSubmittedAt).toLocaleString() : t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminReviewedAt')}:</span> {booking.checkoutReviewedAt ? new Date(booking.checkoutReviewedAt).toLocaleString() : t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminAdminComment')}:</span> {booking.checkoutAdminComment || t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminDeduction')}:</span> {formatInr(booking.damageDeductionAmount || 0)}</p>
                                        <p><span className="font-semibold">{t('adminDepositRefund')}:</span> {formatInr(booking.depositRefundAmount || 0)}</p>
                                        <p><span className="font-semibold">{t('adminCancelledAt')}:</span> {booking.cancelledAt ? new Date(booking.cancelledAt).toLocaleString() : t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminCancellationRemarks')}:</span> {booking.cancellationRemarks || t('adminNotAvailable')}</p>
                                        <p><span className="font-semibold">{t('adminCancellationRentRefund')}:</span> {formatInr(cancellationRentRefundAmount)}{cancellationRentRefundAmount > 0 ? ` (${booking.cancellationRentRefundPaid ? t('adminPaid') : t('adminPending')})` : ''}</p>
                                        <p><span className="font-semibold">{t('adminCancellationDepositRefund')}:</span> {formatInr(cancellationDepositRefundAmount)}{cancellationDepositRefundAmount > 0 ? ` (${booking.cancellationDepositRefundPaid ? t('adminPaid') : t('adminPending')})` : ''}</p>
                                        <p><span className="font-semibold">{t('adminCreated')}:</span> {booking.createdAt ? new Date(booking.createdAt).toLocaleString() : t('adminNotAvailable')}</p>
                                    </div>
                                    {canCancelBooking && (
                                        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
                                            <p className="text-sm font-semibold text-red-900">{t('adminCancelBookingTitle')}</p>
                                            <p className="mt-1 text-sm text-red-800">{t('adminCancelBookingHelp')}</p>
                                            <textarea
                                                rows="2"
                                                value={bookingCancelRemarksById[booking.bookingId] || ''}
                                                onChange={(e) => handleBookingCancelRemarksChange(booking.bookingId, e.target.value)}
                                                className="mt-3 w-full border border-red-200 rounded-md p-2 text-sm bg-white"
                                                placeholder=""
                                            />
                                            <div className="mt-3">
                                                <button
                                                    onClick={() => handleCancelBooking(booking.bookingId)}
                                                    disabled={cancellingBookingId === booking.bookingId}
                                                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                                                >
                                                    {cancellingBookingId === booking.bookingId ? t('adminCancelling') : t('adminCancelBookingAction')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    ) : <p>{t('adminNoBookingsFound')}</p>}
                </div>
            )}

            {activeTab === 'booking-overview' && (
                <div>
                    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">{language === 'da' ? 'Bookingoversigt for campingvogne' : 'Caravan Booking Overview'}</h2>
                            <p className="mt-1.5 text-xs text-gray-600">
                                {language === 'da'
                                    ? 'Vælg et datointerval for at se hver campingvogns kalender på én skærm. Grå gennemstregede datoer er booket eller blokeret.'
                                    : 'Select a date range to see each caravan calendar from one screen. The calendar uses the same color coding as the matrix view for bookings, doorstep, and unavailable dates.'}
                            </p>
                            <div className="mt-2.5 flex flex-wrap gap-2 text-[11px] font-medium">
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 text-gray-700">
                                    <span className="h-2.5 w-2.5 rounded-full border border-gray-300 bg-white" />
                                    {language === 'da' ? 'Ledige datoer' : 'Free dates'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-red-700">
                                    <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-red-300 bg-red-500 text-[9px] font-semibold leading-none text-white">B</span>
                                    {language === 'da' ? 'Booket' : 'Booked'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-blue-700">
                                    <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-blue-300 bg-blue-500 text-[9px] font-semibold leading-none text-white">D</span>
                                    {language === 'da' ? 'Doorstep' : 'Doorstep'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-green-700">
                                    <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-green-300 bg-green-500 text-[9px] font-semibold leading-none text-white">A</span>
                                    {language === 'da' ? 'Doorstep tildelt' : 'Doorstep assigned'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-gray-700">
                                    <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-gray-300 bg-gray-300 text-[9px] font-semibold leading-none text-gray-800">U</span>
                                    {language === 'da' ? 'Blokeret' : 'Unavailable'}
                                </span>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
                            <button
                                type="button"
                                onClick={() => shiftBookingOverviewMonths(-1)}
                                disabled={bookingOverviewLoading}
                                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:border-green-500 hover:text-green-700 disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                {language === 'da' ? 'Forrige måned' : 'Previous month'}
                            </button>
                            <label className="text-xs text-gray-700">
                                <span className="mb-1 block font-medium">{language === 'da' ? 'Startdato' : 'Start Date'}</span>
                                <input
                                    type="date"
                                    value={bookingOverviewDraftStartDate}
                                    onChange={(e) => setBookingOverviewDraftStartDate(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-2.5 py-2 text-xs"
                                />
                            </label>
                            <label className="text-xs text-gray-700">
                                <span className="mb-1 block font-medium">{language === 'da' ? 'Slutdato' : 'End Date'}</span>
                                <input
                                    type="date"
                                    value={bookingOverviewDraftEndDate}
                                    onChange={(e) => setBookingOverviewDraftEndDate(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-2.5 py-2 text-xs"
                                />
                            </label>
                            <button
                                type="button"
                                onClick={handleLoadBookingOverview}
                                disabled={bookingOverviewLoading}
                                className="rounded-md bg-green-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-green-700 disabled:bg-gray-400"
                            >
                                {bookingOverviewLoading ? (language === 'da' ? 'Indlæser...' : 'Loading...') : (language === 'da' ? 'Indlæs oversigt' : 'Load Overview')}
                            </button>
                            <button
                                type="button"
                                onClick={() => shiftBookingOverviewMonths(1)}
                                disabled={bookingOverviewLoading}
                                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:border-green-500 hover:text-green-700 disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                {language === 'da' ? 'Næste måned' : 'Next month'}
                            </button>
                        </div>
                    </div>

                    <div className="mt-3 flex flex-col gap-2.5 rounded-xl border border-gray-200 bg-white p-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-wrap gap-2">
                            <div className="inline-flex overflow-hidden rounded-lg border border-gray-300 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setBookingOverviewLayout('matrix')}
                                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${bookingOverviewLayout === 'matrix' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {language === 'da' ? 'Matrix' : 'Matrix'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setBookingOverviewLayout('calendar')}
                                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${bookingOverviewLayout === 'calendar' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {language === 'da' ? 'Kalender' : 'Calendar'}
                                </button>
                            </div>
                            <div className="inline-flex overflow-hidden rounded-lg border border-gray-300 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setBookingOverviewGranularity('day')}
                                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${bookingOverviewGranularity === 'day' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {language === 'da' ? 'Dagvis' : 'Daywise'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setBookingOverviewGranularity('week')}
                                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${bookingOverviewGranularity === 'week' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {language === 'da' ? 'Ugevis' : 'Weekwise'}
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={handleLoadDefaultBookingOverview}
                                disabled={bookingOverviewLoading}
                                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-green-500 hover:text-green-700 disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                {language === 'da' ? 'Naeste 12 maaneder' : 'Next 12 months'}
                            </button>
                            {bookingOverviewGranularity === 'week' && (
                                <label className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={bookingOverviewShowWeekDates}
                                        onChange={(event) => setBookingOverviewShowWeekDates(event.target.checked)}
                                        className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                    />
                                    <span>{language === 'da' ? 'Vis ugedatoer' : 'Show week dates'}</span>
                                </label>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 text-[11px] font-medium">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-gray-800 ring-1 ring-gray-200">
                                <span className="h-2.5 w-2.5 rounded-sm border border-gray-300 bg-white" />
                                {language === 'da' ? 'Ledig' : 'Free'}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-red-800">
                                <span className="h-2.5 w-2.5 rounded-sm border border-red-300 bg-red-500" />
                                {language === 'da' ? 'Booket' : 'Booked'}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-blue-800">
                                <span className="h-2.5 w-2.5 rounded-sm border border-blue-300 bg-blue-500" />
                                {language === 'da' ? 'Doorstep-booking' : 'Doorstep booking'}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-green-800">
                                <span className="h-2.5 w-2.5 rounded-sm border border-green-300 bg-green-500" />
                                {language === 'da' ? 'Doorstep-booking tildelt leveringscenter' : 'Doorstep booking assigned to delivery center'}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-gray-700">
                                <span className="h-2.5 w-2.5 rounded-sm border border-gray-300 bg-gray-300" />
                                {language === 'da' ? 'Blokeret / utilgængelig' : 'Blocked / unavailable'}
                            </span>
                        </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <p>
                            {bookingOverviewLayout === 'matrix'
                                ? (language === 'da'
                                    ? `Viser ${sortedAdminCaravans.length} campingvogn${sortedAdminCaravans.length === 1 ? '' : 'e'} pa tværs af ${bookingOverviewMatrixRows.length} ${bookingOverviewGranularity === 'week' ? 'ugekolonner' : 'dagkolonner'}.`
                                    : `Showing ${sortedAdminCaravans.length} caravan${sortedAdminCaravans.length === 1 ? '' : 's'} across ${bookingOverviewMatrixRows.length} ${bookingOverviewGranularity === 'week' ? 'week columns' : 'day columns'}.`)
                                : (language === 'da'
                                    ? `Viser ${sortedAdminCaravans.length} campingvogn${sortedAdminCaravans.length === 1 ? '' : 'e'} for ${bookingOverviewRangeDayCount} dag${bookingOverviewRangeDayCount === 1 ? '' : 'e'}.`
                                    : `Showing ${sortedAdminCaravans.length} caravan${sortedAdminCaravans.length === 1 ? '' : 's'} for ${bookingOverviewRangeDayCount} day${bookingOverviewRangeDayCount === 1 ? '' : 's'}.`)}
                        </p>
                        {bookingOverviewAppliedStartDate && bookingOverviewAppliedEndDate && (
                            <p className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-700">
                                {language === 'da'
                                    ? `Indlæst interval: ${bookingOverviewAppliedStartDate} til ${bookingOverviewAppliedEndDate}`
                                    : `Loaded range: ${bookingOverviewAppliedStartDate} to ${bookingOverviewAppliedEndDate}`}
                            </p>
                        )}
                    </div>

                    {bookingOverviewError && <p className="mt-3 text-sm text-red-600">{bookingOverviewError}</p>}

                    {bookingOverviewLoading && sortedAdminCaravans.length === 0 ? (
                        <p className="mt-4 text-sm">{language === 'da' ? 'Indlæser bookingoversigt for campingvogne...' : 'Loading caravan booking overview...'}</p>
                    ) : sortedAdminCaravans.length > 0 ? (
                        bookingOverviewLayout === 'matrix' ? (
                            <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                                <table className="min-w-max border-separate border-spacing-0">
                                    <thead>
                                        <tr>
                                            <th className={bookingOverviewMatrixCaravanHeaderClassName}>
                                                {language === 'da' ? 'Campingvogn' : 'Caravan'}
                                            </th>
                                            {bookingOverviewMatrixRows.map((row) => {
                                                return (
                                                    <th key={`overview-matrix-head-${row.key}`} className={bookingOverviewMatrixHeaderCellClassName}>
                                                        <p className={bookingOverviewMatrixPrimaryLabelClassName}>{row.primaryLabel}</p>
                                                        {(!(bookingOverviewGranularity === 'week') || bookingOverviewShowWeekDates) && row.secondaryLabel && (
                                                            <p className={bookingOverviewMatrixSecondaryLabelClassName}>{row.secondaryLabel}</p>
                                                        )}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedAdminCaravans.map((caravan) => {
                                            const caravanId = Number(caravan?.caravan_id ?? caravan?.caravanId ?? caravan?.id ?? 0);
                                            const calendarData = bookingOverviewCalendarDataByCaravanId[caravanId] || {
                                                bookedCount: 0,
                                                doorstepCount: 0,
                                                assignedDoorstepCount: 0,
                                                unavailableCount: 0
                                            };

                                            return (
                                                <tr key={`overview-matrix-row-${caravanId}`}>
                                                    <td className={bookingOverviewMatrixCaravanCellClassName}>
                                                        <p className={bookingOverviewMatrixNameClassName} title={caravan.name || `Caravan ${caravanId}`}>
                                                            {caravan.name || `Caravan ${caravanId}`}
                                                        </p>
                                                        <p className={bookingOverviewMatrixLocationClassName} title={caravan.location || ''}>
                                                            {caravan.location || (language === 'da' ? 'Ingen lokation' : 'No location')}
                                                        </p>
                                                        <div className={bookingOverviewMatrixStatsClassName}>
                                                            <span className={isCompactBookingOverviewMatrix ? 'rounded bg-red-50 px-1 py-0.5 text-red-700' : 'rounded bg-red-50 px-1.5 py-0.5 text-red-700'}>{calendarData.bookedCount}</span>
                                                            <span className={isCompactBookingOverviewMatrix ? 'rounded bg-blue-50 px-1 py-0.5 text-blue-700' : 'rounded bg-blue-50 px-1.5 py-0.5 text-blue-700'}>{calendarData.doorstepCount}</span>
                                                            <span className={isCompactBookingOverviewMatrix ? 'rounded bg-green-50 px-1 py-0.5 text-green-700' : 'rounded bg-green-50 px-1.5 py-0.5 text-green-700'}>{calendarData.assignedDoorstepCount}</span>
                                                            <span className={isCompactBookingOverviewMatrix ? 'rounded bg-gray-100 px-1 py-0.5 text-gray-700' : 'rounded bg-gray-100 px-1.5 py-0.5 text-gray-700'}>{calendarData.unavailableCount}</span>
                                                        </div>
                                                    </td>
                                                    {bookingOverviewMatrixRows.map((row) => {
                                                        const status = bookingOverviewGranularity === 'week'
                                                            ? (bookingOverviewWeekStatusByCaravanId[caravanId]?.[row.key] || 'free')
                                                            : (bookingOverviewDayStatusByCaravanId[caravanId]?.[row.ymd] || 'free');
                                                        const overviewBookingId = bookingOverviewGranularity === 'week'
                                                            ? (row.dates || []).map((date) => getOverviewBookingIdForDate(caravanId, date)).find(Boolean)
                                                            : getOverviewBookingIdForDate(caravanId, row.ymd);

                                                        return (
                                                            <td
                                                                key={`overview-matrix-cell-${caravanId}-${row.key}`}
                                                                className={`${bookingOverviewMatrixStatusCellClassName} ${getBookingOverviewCellClassName(status)} ${overviewBookingId ? 'cursor-pointer hover:ring-2 hover:ring-green-600' : ''}`}
                                                                title={`${caravan.name || `Caravan ${caravanId}`} - ${getBookingOverviewStatusLabel(status)}`}
                                                                onClick={() => overviewBookingId && setSelectedOverviewBookingId(overviewBookingId)}
                                                            >
                                                                <span className={isCompactBookingOverviewMatrix ? 'text-[9px] font-semibold tracking-wide' : 'text-[11px] font-semibold tracking-wide'}>
                                                                    {status === 'doorstep_assigned'
                                                                        ? 'A'
                                                                        : status === 'doorstep'
                                                                        ? 'D'
                                                                        : status === 'booked'
                                                                            ? 'B'
                                                                            : status === 'unavailable'
                                                                                ? 'U'
                                                                                : ''}
                                                                </span>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                        <div className="mt-4 space-y-4">
                            {sortedAdminCaravans.map((caravan) => {
                                const caravanId = Number(caravan?.caravan_id ?? caravan?.caravanId ?? caravan?.id ?? 0);
                                const calendarData = bookingOverviewCalendarDataByCaravanId[caravanId] || {
                                    blockedDates: new Set(),
                                    bookedCount: 0,
                                    freeCount: bookingOverviewRangeDayCount
                                };
                                const caravanImages = getCaravanImages(caravan);

                                return (
                                    <div key={caravanId || caravan?.caravan_id || caravan?.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                                            <div className="flex gap-3">
                                                <img
                                                    src={caravanImages[0] || PLACEHOLDER_IMG}
                                                    alt={caravan.name || 'Caravan'}
                                                    className="h-20 w-28 rounded-lg object-cover border border-gray-200"
                                                    onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                                                />
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900">{caravan.name || `Caravan ${caravanId}`}</h3>
                                                    <p className="mt-0.5 text-xs text-gray-600">{caravan.location || 'Location not provided'}</p>
                                                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-medium">
                                                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-800">
                                                            {language === 'da' ? 'Ledige' : 'Free'}: {calendarData.freeCount}
                                                        </span>
                                                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700">
                                                            {language === 'da' ? 'Bookede' : 'Booked'}: {calendarData.bookedCount}
                                                        </span>
                                                        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-800">
                                                            ID: {caravanId || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 gap-1.5 text-xs text-gray-700 sm:grid-cols-2 xl:min-w-[280px]">
                                                <p><span className="font-semibold">{language === 'da' ? 'Ejer' : 'Owner'}:</span> {caravan.ownerName || caravan.owner?.name || 'N/A'}</p>
                                                <p><span className="font-semibold">{language === 'da' ? 'Type' : 'Type'}:</span> {caravan.typeName || caravan.type_name || 'N/A'}</p>
                                                <p><span className="font-semibold">{language === 'da' ? 'Listet' : 'Listed'}:</span> {isTruthyValue(caravan.is_listed ?? caravan.isListed ?? true) ? (language === 'da' ? 'Ja' : 'Yes') : (language === 'da' ? 'Nej' : 'No')}</p>
                                                <p><span className="font-semibold">{language === 'da' ? 'Godkendt' : 'Approved'}:</span> {isTruthyValue(caravan.is_approved ?? caravan.isApproved ?? false) ? (language === 'da' ? 'Ja' : 'Yes') : (language === 'da' ? 'Nej' : 'No')}</p>
                                            </div>
                                            <CaravanWeightSummary caravan={caravan} className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" />
                                        </div>

                                        <div className="mt-4 grid grid-cols-1 gap-3 2xl:grid-cols-3 xl:grid-cols-2">
                                            {bookingOverviewMonthAnchors.map((monthDate) => {
                                                const calendarWeeks = getCalendarWeeksForMonth(monthDate);

                                                return (
                                                    <div key={`${caravanId}-${monthDate.getFullYear()}-${monthDate.getMonth()}`} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                                        <p className="mb-2 text-xs font-semibold capitalize text-gray-800">
                                                            {monthDate.toLocaleString(bookingOverviewCalendarLocale, { month: 'long', year: 'numeric' })}
                                                        </p>
                                                        <div className="mb-1.5 grid grid-cols-[2.1rem_repeat(7,minmax(0,1fr))] gap-0.5 text-center text-[10px] text-gray-500">
                                                            <div className="flex items-center justify-center rounded bg-gray-100 font-semibold text-gray-600">{language === 'da' ? 'Uge' : 'Week'}</div>
                                                            {bookingOverviewWeekdayLabels.map((label) => <div key={`${caravanId}-${monthDate.getFullYear()}-${monthDate.getMonth()}-${label}`}>{label}</div>)}
                                                        </div>
                                                        <div className="space-y-0.5">
                                                            {calendarWeeks.map((week) => (
                                                                <div key={`${caravanId}-${formatDateOnlyInput(week.days[0]?.date)}`} className="grid grid-cols-[2.1rem_repeat(7,minmax(0,1fr))] gap-0.5">
                                                                    <div className="flex h-7 items-center justify-center rounded-md bg-gray-100 text-[10px] font-semibold text-gray-600">
                                                                        {week.weekNumber}
                                                                    </div>
                                                                    {week.days.map(({ date, inMonth }) => {
                                                                        const ymd = formatDateOnlyInput(date);
                                                                        const inSelectedRange = Boolean(
                                                                            inMonth
                                                                            && bookingOverviewRangeStartLocal
                                                                            && bookingOverviewRangeEndLocal
                                                                            && date >= bookingOverviewRangeStartLocal
                                                                            && date <= bookingOverviewRangeEndLocal
                                                                        );
                                                                        const status = inSelectedRange
                                                                            ? (bookingOverviewDayStatusByCaravanId[caravanId]?.[ymd] || 'free')
                                                                            : 'free';
                                                                        const cellClass = !inMonth
                                                                            ? 'border-transparent bg-white text-gray-300'
                                                                            : !inSelectedRange
                                                                                ? 'border-transparent bg-white text-gray-300'
                                                                                : getBookingOverviewCellClassName(status);
                                                                        const statusIndicator = status === 'doorstep_assigned'
                                                                            ? 'A'
                                                                            : status === 'doorstep'
                                                                                ? 'D'
                                                                                : status === 'booked'
                                                                                    ? 'B'
                                                                                    : status === 'unavailable'
                                                                                        ? 'U'
                                                                                        : '';
                                                                        const isBlocked = status !== 'free';
                                                                        const overviewBookingId = getOverviewBookingIdForDate(caravanId, ymd);

                                                                        return (
                                                                            <div
                                                                                key={`${caravanId}-${ymd}`}
                                                                                className={`relative flex h-7 items-center justify-center rounded-md border text-[10px] font-medium ${cellClass} ${overviewBookingId ? 'cursor-pointer hover:ring-2 hover:ring-green-600' : ''}`}
                                                                                title={!inSelectedRange ? (language === 'da' ? 'Uden for valgt interval' : 'Outside selected range') : (isBlocked ? (language === 'da' ? 'Booket eller utilgængelig' : 'Booked or unavailable') : (language === 'da' ? 'Ledig' : 'Free'))}
                                                                                onClick={() => overviewBookingId && setSelectedOverviewBookingId(overviewBookingId)}
                                                                            >
                                                                                {date.getDate()}
                                                                                {statusIndicator && (
                                                                                    <span className="pointer-events-none absolute bottom-0.5 right-0.5 text-[8px] font-bold leading-none opacity-90">
                                                                                        {statusIndicator}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        )
                    ) : (
                        <p className="mt-4 text-sm">{language === 'da' ? 'Ingen campingvogne fundet til oversigten.' : 'No caravans found for overview.'}</p>
                    )}
                    {selectedOverviewBookingId && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSelectedOverviewBookingId(null)}>
                            <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
                                <div className="mb-4 flex items-start justify-between gap-4">
                                    <div>
                                        <h3 className="text-xl font-bold">{language === 'da' ? 'Bookingdetaljer' : 'Booking Details'}</h3>
                                        <p className="text-sm text-gray-500">{formatBookingNumber(selectedOverviewBookingId)}</p>
                                    </div>
                                    <button type="button" onClick={() => setSelectedOverviewBookingId(null)} className="rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50">
                                        {language === 'da' ? 'Luk' : 'Close'}
                                    </button>
                                </div>
                                {selectedOverviewBooking ? (
                                    <>
                                        <div className="grid grid-cols-1 gap-3 text-sm text-gray-700 md:grid-cols-2">
                                            <p><span className="font-semibold">{t('adminBookingId')}:</span> {selectedOverviewBooking.bookingId}</p>
                                            <p><span className="font-semibold">Status:</span> {getAdminBookingStatusLabel(selectedOverviewBooking.bookingStatus)}</p>
                                            <p><span className="font-semibold">{t('adminCustomer')}:</span> {selectedOverviewBooking.renterName} ({selectedOverviewBooking.renterEmail})</p>
                                            <p><span className="font-semibold">{t('adminOwner')}:</span> {selectedOverviewBooking.ownerName} ({selectedOverviewBooking.ownerEmail})</p>
                                            <p><span className="font-semibold">{language === 'da' ? 'Campingvogn' : 'Caravan'}:</span> {selectedOverviewBooking.caravanName}</p>
                                            <p><span className="font-semibold">{language === 'da' ? 'Lokation' : 'Location'}:</span> {selectedOverviewBooking.caravanLocation}</p>
                                            <p><span className="font-semibold">{t('adminDates')}:</span> {new Date(selectedOverviewBooking.startDate).toLocaleString()} – {new Date(selectedOverviewBooking.endDate).toLocaleString()}</p>
                                            <p><span className="font-semibold">{t('adminCreated')}:</span> {selectedOverviewBooking.createdAt ? new Date(selectedOverviewBooking.createdAt).toLocaleString() : t('adminNotAvailable')}</p>
                                            <p><span className="font-semibold">{t('adminBookingAmount')}:</span> {formatInr(selectedOverviewBooking.totalPrice)}</p>
                                            <p><span className="font-semibold">{t('adminDeposit')}:</span> {formatInr(selectedOverviewBooking.depositAmount)}</p>
                                            <p><span className="font-semibold">{t('adminDoorstepFee')}:</span> {formatInr(selectedOverviewBooking.doorstepFee)}</p>
                                            <p><span className="font-semibold">{t('adminNetDueAmount')}:</span> {formatInr(getBookingCurrentDue(selectedOverviewBooking))}</p>
                                            <p><span className="font-semibold">{t('adminAddons')}:</span> {formatAddonSummary(getSelectedBookingAddons(selectedOverviewBooking)) || t('adminNotAvailable')}</p>
                                            <p><span className="font-semibold">{t('adminBookingRemarks')}:</span> {selectedOverviewBooking.bookingRemarks || t('adminNotAvailable')}</p>
                                            <p><span className="font-semibold">{t('adminCheckIn')}:</span> {selectedOverviewBooking.checkInAt ? new Date(selectedOverviewBooking.checkInAt).toLocaleString() : t('adminNotAvailable')}</p>
                                            <p><span className="font-semibold">{t('adminCheckOut')}:</span> {selectedOverviewBooking.checkOutAt ? new Date(selectedOverviewBooking.checkOutAt).toLocaleString() : t('adminNotAvailable')}</p>
                                            <p><span className="font-semibold">{t('adminCheckoutReview')}:</span> {getAdminCheckoutReviewStatusLabel(selectedOverviewBooking.checkoutReviewStatus)}</p>
                                            <p><span className="font-semibold">{t('adminAdminComment')}:</span> {selectedOverviewBooking.checkoutAdminComment || t('adminNotAvailable')}</p>
                                        </div>
                                        <button type="button" onClick={() => { setSelectedOverviewBookingId(null); setActiveTab('booking-history'); }} className="mt-5 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700">
                                            {language === 'da' ? 'Åbn i alle bookinger' : 'Open in All Bookings'}
                                        </button>
                                    </>
                                ) : (
                                    <p className="text-sm text-gray-600">{language === 'da' ? 'Bookingdetaljerne kunne ikke findes i det indlæste bookingarkiv.' : 'Booking details were not found in the loaded booking history.'}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'amenities' && (
                <AmenityManager
                    items={amenities}
                    onAdd={addAmenity}
                    onUpdate={updateAmenity}
                    onSetStatus={setAmenityStatus}
                    isLoading={amenitiesLoading}
                    error={amenitiesError}
                />
            )}

            {activeTab === 'types' && (
                <CaravanTypeManager
                    items={caravanTypes}
                    onAdd={addCaravanType}
                    onUpdate={updateCaravanType}
                    onSetStatus={setCaravanTypeStatus}
                    isLoading={typesLoading}
                    error={typesError}
                />
            )}

            {activeTab === 'mappings' && (
                <TypeAmenityMappingManager
                    caravanTypes={caravanTypes}
                    amenities={amenities.filter((item) => item?.is_active !== false)}
                    mappings={typeAmenityMappings}
                    onSave={saveTypeAmenityMapping}
                />
            )}

            {activeTab === 'seasons' && (
                <div>
                    {seasonConfigError && <p className="text-red-600 mb-3">{seasonConfigError}</p>}
                    <SeasonMonthConfigManager
                        seasons={seasons}
                        monthConfig={monthConfig}
                        onSave={saveMonthConfig}
                        isSaving={isSeasonConfigSaving}
                    />
                </div>
            )}

            {activeTab === 'settings' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Admin Settings</h2>
                    <div className="space-y-5">
                        <div className="bg-white rounded-lg shadow-md p-5 max-w-2xl">
                            <h3 className="text-lg font-semibold mb-2">Email Language</h3>
                            <p className="text-sm text-gray-600 mb-4">This setting controls the language used for all outgoing system emails.</p>
                            {emailSettingsError && <p className="text-red-600 mb-3">{emailSettingsError}</p>}
                            {emailSettingsSuccess && <p className="text-green-700 mb-3">{emailSettingsSuccess}</p>}
                            {emailSettingsLoading ? (
                                <p>Loading email settings...</p>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Default mail language</label>
                                        <select
                                            value={emailSettings.email_language || 'da'}
                                            onChange={(e) => {
                                                const nextLanguage = e.target.value === 'en' ? 'en' : 'da';
                                                setEmailSettings(prev => ({ ...prev, email_language: nextLanguage }));
                                                setEmailSettingsSuccess('');
                                            }}
                                            className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            <option value="da">Danish</option>
                                            <option value="en">English</option>
                                        </select>
                                    </div>
                                    <button
                                        onClick={saveEmailSettings}
                                        disabled={isEmailSettingsSaving}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                    >
                                        {isEmailSettingsSaving ? 'Saving...' : 'Save Settings'}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-5">
                            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold mb-1">Terms and Conditions PDFs</h3>
                                    <p className="text-sm text-gray-600">Upload separate PDF versions for customers and caravan owners. Every upload creates a new version and becomes the current PDF for that audience.</p>
                                </div>
                                <button
                                    onClick={fetchTermsDocuments}
                                    disabled={termsDocumentsLoading}
                                    className="border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50 disabled:bg-gray-100"
                                >
                                    {termsDocumentsLoading ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>

                            {termsDocumentsError && <p className="text-red-600 mb-3">{termsDocumentsError}</p>}
                            {termsDocumentsSuccess && <p className="text-green-700 mb-3">{termsDocumentsSuccess}</p>}

                            <div className="space-y-5">
                                {TERMS_AUDIENCES.map((audience) => {
                                    const audienceDocuments = termsDocumentsByAudience[audience] || [];
                                    const currentDocument = audienceDocuments.find((item) => item.isCurrent) || audienceDocuments[0] || null;
                                    const currentDocumentUrl = currentDocument ? resolveApiAssetUrl(currentDocument.fileUrl) : null;
                                    const selectedFile = termsUploadFiles[audience];

                                    return (
                                        <div key={audience} className="border border-gray-200 rounded-xl p-4">
                                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                                <div>
                                                    <h4 className="text-base font-semibold text-gray-900">{getTermsAudienceLabel(audience)}</h4>
                                                    {currentDocument ? (
                                                        <p className="text-sm text-gray-600 mt-1">
                                                            Current version: v{currentDocument.versionNumber} | Uploaded {formatTermsDateTime(currentDocument.createdAt)}
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-gray-500 mt-1">No PDF uploaded yet.</p>
                                                    )}
                                                </div>
                                                {currentDocumentUrl && (
                                                    <a
                                                        href={currentDocumentUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                                    >
                                                        Open current PDF
                                                    </a>
                                                )}
                                            </div>

                                            <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload new PDF</label>
                                                    <LocalizedFileInput
                                                        key={`${audience}-${termsUploadInputKeys[audience]}`}
                                                        id={`terms-upload-${audience}-${termsUploadInputKeys[audience]}`}
                                                        selectedFiles={selectedFile ? [selectedFile] : []}
                                                        accept=".pdf,application/pdf"
                                                        onChange={(e) => handleTermsFileSelection(audience, e.target.files?.[0] || null)}
                                                        className="w-full"
                                                        statusClassName="text-xs"
                                                        buttonClassName="text-sm"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">Only PDF files are allowed. Max size: {MAX_TERMS_PDF_SIZE_MB} MB.</p>
                                                    {selectedFile && (
                                                        <p className="text-xs text-gray-600 mt-1">
                                                            Selected: {selectedFile.name} ({formatFileSizeLabel(selectedFile.size)})
                                                        </p>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => uploadTermsDocument(audience)}
                                                    disabled={uploadingTermsAudience === audience || !selectedFile}
                                                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                >
                                                    {uploadingTermsAudience === audience ? 'Uploading...' : 'Upload PDF'}
                                                </button>
                                            </div>

                                            <div className="mt-5">
                                                <p className="text-sm font-medium text-gray-700 mb-2">Version history</p>
                                                {termsDocumentsLoading && audienceDocuments.length === 0 ? (
                                                    <p className="text-sm text-gray-500">Loading versions...</p>
                                                ) : audienceDocuments.length === 0 ? (
                                                    <p className="text-sm text-gray-500">No versions available.</p>
                                                ) : (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th className="px-3 py-2 text-left">Version</th>
                                                                    <th className="px-3 py-2 text-left">Uploaded</th>
                                                                    <th className="px-3 py-2 text-left">File</th>
                                                                    <th className="px-3 py-2 text-left">Size</th>
                                                                    <th className="px-3 py-2 text-left">Status</th>
                                                                    <th className="px-3 py-2 text-left">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {audienceDocuments.map((document) => {
                                                                    const documentUrl = resolveApiAssetUrl(document.fileUrl);
                                                                    return (
                                                                        <tr key={document.termsDocumentId} className="border-t">
                                                                            <td className="px-3 py-2 font-medium">v{document.versionNumber}</td>
                                                                            <td className="px-3 py-2">{formatTermsDateTime(document.createdAt)}</td>
                                                                            <td className="px-3 py-2">{document.originalFileName || 'PDF file'}</td>
                                                                            <td className="px-3 py-2">{formatFileSizeLabel(document.fileSizeBytes)}</td>
                                                                            <td className="px-3 py-2">
                                                                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${document.isCurrent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                                                    {document.isCurrent ? 'Current' : 'Archived'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-3 py-2">
                                                                                <div className="flex gap-3 flex-wrap">
                                                                                    {documentUrl && (
                                                                                        <a
                                                                                            href={documentUrl}
                                                                                            target="_blank"
                                                                                            rel="noreferrer"
                                                                                            className="text-blue-600 hover:text-blue-800"
                                                                                        >
                                                                                            Open
                                                                                        </a>
                                                                                    )}
                                                                                    {!document.isCurrent && (
                                                                                        <button
                                                                                            onClick={() => setTermsVersionAsCurrent(document)}
                                                                                            disabled={restoringTermsDocumentId === document.termsDocumentId}
                                                                                            className="text-green-700 hover:text-green-900 disabled:text-gray-400"
                                                                                        >
                                                                                            {restoringTermsDocumentId === document.termsDocumentId ? 'Updating...' : 'Make Current'}
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'delivery-center' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Delivery Center</h2>
                    {deliveryAdminError && <p className="text-red-600 mb-3">{deliveryAdminError}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-lg shadow-md p-4">
                            <p className="text-sm text-gray-500">Partners</p>
                            <p className="mt-2 text-3xl font-bold text-gray-900">{deliveryAdminPartners.length}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-md p-4">
                            <p className="text-sm text-gray-500">Available now</p>
                            <p className="mt-2 text-3xl font-bold text-gray-900">{deliveryAdminPartners.filter((item) => item?.isAvailable).length}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-md p-4">
                            <p className="text-sm text-gray-500">Open jobs</p>
                            <p className="mt-2 text-3xl font-bold text-gray-900">{deliveryAdminJobs.filter((item) => normalizeDeliveryJobStatus(item?.jobStatus) === 'open').length}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-md p-4">
                            <p className="text-sm text-gray-500">Active jobs</p>
                            <p className="mt-2 text-3xl font-bold text-gray-900">{deliveryAdminJobs.filter((item) => ['accepted', 'in_progress'].includes(normalizeDeliveryJobStatus(item?.jobStatus))).length}</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                        <div className="flex items-center justify-between gap-3 mb-4">
                            <h3 className="text-xl font-semibold">Registered Delivery Partners</h3>
                            <button
                                type="button"
                                onClick={fetchDeliveryAdmin}
                                disabled={deliveryAdminLoading}
                                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 disabled:bg-gray-100"
                            >
                                Refresh
                            </button>
                        </div>
                        {deliveryAdminLoading && deliveryAdminPartners.length === 0 ? (
                            <p className="text-gray-500">Loading delivery partners...</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Partner</th>
                                            <th className="px-4 py-3 text-left">Contact</th>
                                            <th className="px-4 py-3 text-left">Vehicle / Rules</th>
                                            <th className="px-4 py-3 text-left">License</th>
                                            <th className="px-4 py-3 text-left">Approval</th>
                                            <th className="px-4 py-3 text-left">Bank Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deliveryAdminPartners.map((partner) => {
                                            const isActive = partner?.isActive !== false;
                                            const isAvailable = Boolean(partner?.isAvailable);
                                            const approvalStatus = String(partner?.approvalStatus || 'pending').trim().toLowerCase();
                                            const licenseUrl = partner?.drivingLicenseCopyUrl ? resolveApiAssetUrl(partner.drivingLicenseCopyUrl) : '';
                                            const bankSummary = partner?.bankRegistrationNo && partner?.bankAccountNo
                                                ? `Reg ${partner.bankRegistrationNo} / Acc ${partner.bankAccountNo}`
                                                : 'Missing bank details';
                                            return (
                                                <tr key={partner.userId} className="border-t">
                                                    <td className="px-4 py-3">
                                                        <p className="font-semibold">{partner.name || 'N/A'}</p>
                                                        <p className="text-xs text-gray-500">User ID: {partner.userId}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p>{partner.email || 'N/A'}</p>
                                                        <p className="text-xs text-gray-500">{partner.mobile || 'No mobile number'}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p>{partner.vehicleType || 'N/A'}</p>
                                                        <p className="text-xs text-gray-500">{partner.vehicleRegistrationNumber || 'No registration'}</p>
                                                        <p className="text-xs text-gray-500 mt-1">Tow limit: {partner.maximumTowWeightKg ? `${partner.maximumTowWeightKg} kg` : 'Not set'}</p>
                                                        <p className="text-xs text-gray-500">Range: {partner.pickupLocationRangeKm ? `${partner.pickupLocationRangeKm} km` : 'Not set'}</p>
                                                        <p className="text-xs text-gray-500">Area: {partner.serviceArea || 'N/A'}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {licenseUrl ? (
                                                            <a
                                                                href={licenseUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-blue-600 hover:text-blue-800 font-medium"
                                                            >
                                                                Open uploaded copy
                                                            </a>
                                                        ) : (
                                                            <span className="text-gray-500">No file uploaded</span>
                                                        )}
                                                        {partner.drivingLicenseNumber && (
                                                            <p className="text-xs text-gray-500 mt-1">License No: {partner.drivingLicenseNumber}</p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-wrap gap-2">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${isAvailable ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                                                {isAvailable ? 'Available' : 'Unavailable'}
                                                            </span>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${isActive ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-700'}`}>
                                                                {isActive ? 'Active user' : 'Blocked user'}
                                                            </span>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                                approvalStatus === 'approved'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : approvalStatus === 'rejected'
                                                                        ? 'bg-red-100 text-red-800'
                                                                        : 'bg-amber-100 text-amber-800'
                                                            }`}>
                                                                {approvalStatus}
                                                            </span>
                                                        </div>
                                                        {partner.approvalRemarks && (
                                                            <p className="mt-2 text-xs text-gray-500">Remarks: {partner.approvalRemarks}</p>
                                                        )}
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeliveryPartnerApprovalAction(partner, 'approve')}
                                                                disabled={deliveryAdminPartnerActionUserId === partner.userId || approvalStatus === 'approved'}
                                                                className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-300"
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeliveryPartnerApprovalAction(partner, 'reject')}
                                                                disabled={deliveryAdminPartnerActionUserId === partner.userId}
                                                                className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-300"
                                                            >
                                                                Reject
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p>{bankSummary}</p>
                                                        {partner.bankAccountDetails && (
                                                            <p className="text-xs text-gray-500 mt-1">{partner.bankAccountDetails}</p>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {deliveryAdminPartners.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="px-4 py-4 text-gray-500">No delivery partners found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-4">
                        <h3 className="text-xl font-semibold mb-4">Delivery Jobs</h3>
                        {deliveryAdminLoading && deliveryAdminJobs.length === 0 ? (
                            <p className="text-gray-500">Loading delivery jobs...</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Job</th>
                                            <th className="px-4 py-3 text-left">Schedule</th>
                                            <th className="px-4 py-3 text-left">Route</th>
                                            <th className="px-4 py-3 text-left">Customer</th>
                                            <th className="px-4 py-3 text-left">Assigned / Bank</th>
                                            <th className="px-4 py-3 text-left">Payout</th>
                                            <th className="px-4 py-3 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deliveryAdminJobs.map((job) => {
                                            const status = normalizeDeliveryJobStatus(job?.jobStatus);
                                            const canReopen = canAdminReopenDeliveryJob(status);
                                            const canCancel = canAdminCancelDeliveryJob(status);
                                            const isPaid = Boolean(job?.isPaid || job?.paidAt);
                                            const resolvedAmount = getDeliveryJobPayoutAmount(job);
                                            const paymentDraft = deliveryJobPaymentDrafts[job.deliveryJobId] || {};
                                            const paymentErrors = deliveryJobPaymentErrors[job.deliveryJobId] || {};
                                            const amountInput = paymentDraft.amount ?? (resolvedAmount > 0 ? String(resolvedAmount) : '');
                                            const referenceInput = paymentDraft.reference ?? (job.paymentReference || '');
                                            return (
                                                <tr key={job.deliveryJobId} className="border-t align-top">
                                                    <td className="px-4 py-3">
                                                        <p className="font-semibold">#{job.deliveryJobId} · Booking {job.bookingId}</p>
                                                        <p className="text-xs text-gray-500">{getDeliveryJobTypeLabel(job.jobType, language)} · {job.caravanName || 'N/A'}</p>
                                                        <p className="text-xs text-gray-500 mt-1">Caravan weight: {job.caravanTotalWeightKg ? `${job.caravanTotalWeightKg} kg` : 'N/A'}</p>
                                                        <span className={`mt-2 inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getDeliveryJobStatusBadgeClass(job.jobStatus)}`}>
                                                            {getDeliveryJobStatusLabel(job.jobStatus, language)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">{formatDeliveryDateTime(job.scheduledAt, language)}</td>
                                                    <td className="px-4 py-3">
                                                        <p><span className="font-semibold">Pickup:</span> {job.pickupLocation || 'N/A'}</p>
                                                        <p className="mt-1"><span className="font-semibold">Drop-off:</span> {job.dropoffLocation || 'N/A'}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p>{job.customerName || 'N/A'}</p>
                                                        <p className="text-xs text-gray-500">{job.customerEmail || 'N/A'}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p>{job.acceptedByName || 'Unassigned'}</p>
                                                        {job.acceptedByName && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {job.acceptedByBankRegistrationNo && job.acceptedByBankAccountNo
                                                                    ? `Reg ${job.acceptedByBankRegistrationNo} / Acc ${job.acceptedByBankAccountNo}`
                                                                    : 'Bank details missing'}
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">{isPaid ? 'Paid' : 'Pending'}</p>
                                                        <p className="mt-1 text-sm text-gray-700">Amount: {resolvedAmount > 0 ? formatInr(resolvedAmount) : 'Not set'}</p>
                                                        <p className="text-sm text-gray-700">Reference: {job.paymentReference || 'Not set'}</p>
                                                        {status === 'completed' && !isPaid && job.acceptedByUserId && (
                                                            <div className="mt-3 space-y-2">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={amountInput}
                                                                    onChange={(event) => updateDeliveryJobPaymentDraft(job.deliveryJobId, 'amount', event.target.value)}
                                                                    className={`w-full rounded-md px-3 py-2 ${paymentErrors.amount ? 'border border-red-400 bg-red-50' : 'border border-gray-300'}`}
                                                                    placeholder="Committed amount"
                                                                    aria-invalid={paymentErrors.amount ? 'true' : 'false'}
                                                                />
                                                                {paymentErrors.amount && (
                                                                    <p className="text-xs text-red-600">{paymentErrors.amount}</p>
                                                                )}
                                                                <input
                                                                    type="text"
                                                                    value={referenceInput}
                                                                    onChange={(event) => updateDeliveryJobPaymentDraft(job.deliveryJobId, 'reference', event.target.value)}
                                                                    className={`w-full rounded-md px-3 py-2 ${paymentErrors.reference ? 'border border-red-400 bg-red-50' : 'border border-gray-300'}`}
                                                                    placeholder="Payment reference"
                                                                    aria-invalid={paymentErrors.reference ? 'true' : 'false'}
                                                                />
                                                                {paymentErrors.reference && (
                                                                    <p className="text-xs text-red-600">{paymentErrors.reference}</p>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleMarkDeliveryJobPaid(job)}
                                                                    disabled={deliveryAdminPaymentJobId === job.deliveryJobId}
                                                                    className="bg-emerald-600 text-white px-3 py-2 rounded-md hover:bg-emerald-700 disabled:bg-gray-300"
                                                                >
                                                                    Mark Paid
                                                                </button>
                                                            </div>
                                                        )}
                                                        {isPaid && job.paidAt && (
                                                            <p className="mt-2 text-xs text-green-700">Paid on {formatDeliveryDateTime(job.paidAt, language)}</p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeliveryJobAdminAction(job.deliveryJobId, 'reopen')}
                                                                disabled={!canReopen || deliveryAdminActionJobId === job.deliveryJobId}
                                                                className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                                                            >
                                                                Reopen
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeliveryJobAdminAction(job.deliveryJobId, 'cancel')}
                                                                disabled={!canCancel || deliveryAdminActionJobId === job.deliveryJobId}
                                                                className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-300"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {deliveryAdminJobs.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="px-4 py-4 text-gray-500">No delivery jobs found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'damage-catalog' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">Damage / Penalty Categories</h2>
                    <p className="text-sm text-gray-600 mb-4">Admin defines standard categories and base prices. Owners use these in checkout deduction forms.</p>
                    {damageCategoriesError && <p className="text-red-600 mb-3">{damageCategoriesError}</p>}

                    <div className="bg-white rounded-lg shadow-md p-4 mb-5">
                        <h3 className="font-semibold mb-3">Add Category</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <input
                                type="text"
                                value={damageCategoryDraft.categoryType}
                                onChange={(e) => setDamageCategoryDraft(prev => ({ ...prev, categoryType: e.target.value }))}
                                className="px-3 py-2 border border-gray-300 rounded-md"
                                placeholder=""
                            />
                            <input
                                type="text"
                                value={damageCategoryDraft.categoryName}
                                onChange={(e) => setDamageCategoryDraft(prev => ({ ...prev, categoryName: e.target.value }))}
                                className="px-3 py-2 border border-gray-300 rounded-md"
                                placeholder=""
                            />
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={damageCategoryDraft.standardPrice}
                                onChange={(e) => setDamageCategoryDraft(prev => ({ ...prev, standardPrice: e.target.value }))}
                                className="px-3 py-2 border border-gray-300 rounded-md"
                                placeholder=""
                            />
                            <button
                                onClick={createDamageCategory}
                                className="bg-green-600 text-white rounded-md px-4 py-2 hover:bg-green-700"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    {damageCategoriesLoading ? <p>Loading damage categories...</p> : (
                        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Type</th>
                                        <th className="px-4 py-3 text-left">Category</th>
                                        <th className="px-4 py-3 text-left">Standard Price</th>
                                        <th className="px-4 py-3 text-left">Active</th>
                                        <th className="px-4 py-3 text-left">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {damageCategories.map((item) => (
                                        <tr key={item.categoryId} className="border-t">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="text"
                                                    value={item.categoryType || ''}
                                                    onChange={(e) => handleDamageCategoryChange(item.categoryId, 'categoryType', e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-md w-full"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="text"
                                                    value={item.categoryName || ''}
                                                    onChange={(e) => handleDamageCategoryChange(item.categoryId, 'categoryName', e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-md w-full"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={item.standardPrice}
                                                    onChange={(e) => handleDamageCategoryChange(item.categoryId, 'standardPrice', e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-md w-full"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(item.isActive)}
                                                    onChange={(e) => handleDamageCategoryChange(item.categoryId, 'isActive', e.target.checked)}
                                                    className="h-4 w-4"
                                                />
                                            </td>
                                            <td className="px-4 py-3 space-x-2">
                                                <button
                                                    onClick={() => saveDamageCategory(item)}
                                                    disabled={savingDamageCategoryId === item.categoryId}
                                                    className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => deactivateDamageCategory(item.categoryId)}
                                                    disabled={savingDamageCategoryId === item.categoryId || !item.isActive}
                                                    className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                                                >
                                                    Deactivate
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {damageCategories.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-4 text-gray-500">No categories found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'users' && (
                <div>
                    <h2 className="text-2xl font-semibold mb-4">User Role Management</h2>
                    {usersError && <p className="text-red-600 mb-3">{usersError}</p>}
                    {usersLoading ? <p>Loading users...</p> : (
                        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left">User</th>
                                        <th className="px-4 py-3 text-left">Email</th>
                                        <th className="px-4 py-3 text-left">Mobile</th>
                                        <th className="px-4 py-3 text-left">Address</th>
                                        <th className="px-4 py-3 text-left">Bank Details</th>
                                        <th className="px-4 py-3 text-left">Role</th>
                                        <th className="px-4 py-3 text-left">Status</th>
                                        <th className="px-4 py-3 text-left">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((item) => {
                                        const isActive = item?.isActive !== false;
                                        const isSavingRole = savingRoleUserId === item.userId;
                                        const isSavingStatus = savingUserStatusId === item.userId;
                                        const addressLine = item?.address || [
                                            item?.street,
                                            [item?.zipcode, item?.city].filter(Boolean).join(' ')
                                        ].filter(Boolean).join(', ');
                                        const legacyBankDetails = String(item?.bankAccountDetails || '').trim();
                                        const bankRegistrationNo = String(item?.bankRegistrationNo || '').trim();
                                        const bankAccountNo = String(item?.bankAccountNo || '').trim();
                                        const hasStructuredBankDetails = bankRegistrationNo && bankAccountNo;

                                        return (
                                            <tr key={item.userId} className="border-t">
                                                <td className="px-4 py-3">
                                                    <p className="font-semibold">{item.name}</p>
                                                    <p className="text-xs text-gray-500">ID: {item.userId}</p>
                                                </td>
                                                <td className="px-4 py-3">{item.email}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{item?.mobile || 'N/A'}</td>
                                                <td className="px-4 py-3 min-w-[220px]">{addressLine || 'N/A'}</td>
                                                <td className="px-4 py-3 min-w-[180px]">
                                                    {hasStructuredBankDetails ? (
                                                        <div className="leading-5">
                                                            <p className="whitespace-nowrap"><span className="font-medium">Reg:</span> {bankRegistrationNo}</p>
                                                            <p className="whitespace-nowrap"><span className="font-medium">Acc:</span> {bankAccountNo}</p>
                                                        </div>
                                                    ) : legacyBankDetails ? (
                                                        <p>{legacyBankDetails}</p>
                                                    ) : (
                                                        <span className="text-gray-500">N/A</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <select
                                                        value={item.role || 'user'}
                                                        onChange={(e) => handleRoleChange(item.userId, e.target.value)}
                                                        className="px-3 py-2 border border-gray-300 rounded-md"
                                                    >
                                                        <option value="user">user</option>
                                                        <option value="delivery_partner">delivery_partner</option>
                                                        <option value="admin">admin</option>
                                                    </select>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                                                        {isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 space-x-2">
                                                    <button
                                                        onClick={() => saveRole(item.userId, item.role || 'user')}
                                                        disabled={isSavingRole || isSavingStatus}
                                                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                    >
                                                        {isSavingRole ? 'Saving...' : 'Save Role'}
                                                    </button>
                                                    <button
                                                        onClick={() => setUserActiveStatus(item, !isActive)}
                                                        disabled={isSavingRole || isSavingStatus}
                                                        className={`${isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-md disabled:bg-gray-400`}
                                                    >
                                                        {isSavingStatus ? 'Saving...' : (isActive ? 'Block' : 'Unblock')}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

        </div>
        </>
    );
};

const AdminTabButton = ({ title, count, activeTab, tabKey, ...props }) => {
    const normalize = (value) => String(value || '').toLowerCase().replace(/\s+/g, '-');
    const isActive = normalize(activeTab) === normalize(tabKey || title);
    return (
        <button className={`py-2 px-4 font-semibold transition-colors duration-200 ${isActive ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500 hover:text-green-600'}`} {...props}>
            {title} {count > 0 && <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">{count}</span>}
        </button>
    );
};

const InputField = ({ label, name, required = true, placeholder: _placeholder, ...props }) => (
    <div>
        <label htmlFor={name} className={CONTROL_LABEL_CLASS}>{label}</label>
        <input id={name} name={name} required={required} className={CONTROL_INPUT_CLASS} {...props} placeholder="" />
    </div>
);


// --- MAIN APP COMPONENT (Router) ---
export default function App() {
    const [route, setRoute] = useState(() => getRouteFromLocation());
    const [user, setUser] = useState(null);
    const [caravans, setCaravans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [language, setLanguage] = useState(() => getInitialLanguage());

    const [masterAmenities, setMasterAmenities] = useState([]);
    const [masterCaravanTypes, setMasterCaravanTypes] = useState([]);
    const [masterTypeAmenityMappings, setMasterTypeAmenityMappings] = useState([]);
    const [preLoginRoute, setPreLoginRoute] = useState(null);
    const [postProfileRoute, setPostProfileRoute] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [isProfileComplete, setIsProfileComplete] = useState(false);
    const [hasOwnerCaravans, setHasOwnerCaravans] = useState(false);
    const [hasDeliveryPartnerProfile, setHasDeliveryPartnerProfile] = useState(false);
    const isOwnerProfileComplete = isProfileComplete && hasValidOwnerBankDetails(userProfile);
    const seoMetadata = useMemo(() => buildSeoMetadata({ route, caravans, language }), [caravans, language, route]);

    useSeo(seoMetadata);

    const fetchUserProfile = useCallback(async (userId) => {
        if (!userId) return null;
        try {
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/profile`);
            if (!response.ok) {
                setIsProfileComplete(false);
                setUserProfile(null);
                return null;
            }
            const profile = await response.json();
            setUserProfile(profile);
            setIsProfileComplete(Boolean(profile.isProfileComplete));
            return profile;
        } catch (err) {
            console.error("Profile fetch error:", err);
            setIsProfileComplete(false);
            setUserProfile(null);
            return null;
        }
    }, []);

    const fetchOwnerCapability = useCallback(async (userId) => {
        if (!userId) {
            setHasOwnerCaravans(false);
            return false;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/caravans/owner/${userId}`);
            if (!response.ok) {
                setHasOwnerCaravans(false);
                return false;
            }

            const data = await response.json();
            const ownsCaravan = Array.isArray(data) && data.length > 0;
            setHasOwnerCaravans(ownsCaravan);
            return ownsCaravan;
        } catch (err) {
            console.error("Owner capability fetch error:", err);
            setHasOwnerCaravans(false);
            return false;
        }
    }, []);

    const saveUserProfile = useCallback(async (userId, profilePayload) => {
        const response = await fetch(`${API_BASE_URL}/api/users/${userId}/profile`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(profilePayload),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Failed to save profile');
        }
        const profile = await response.json();
        setUserProfile(profile);
        setIsProfileComplete(Boolean(profile.isProfileComplete));
        setPostProfileRoute(null);
        setUser(prev => {
            if (!prev) return prev;
            const updated = { ...prev, name: profile.name };
            localStorage.setItem('user', JSON.stringify(updated));
            return updated;
        });
        return profile;
    }, []);

    const syncDeliveryPartnerProfile = useCallback((deliveryProfile) => {
        if (!deliveryProfile) {
            return;
        }

        setHasDeliveryPartnerProfile(true);
        const normalizedProfile = {
            name: deliveryProfile?.name ?? deliveryProfile?.Name ?? '',
            street: deliveryProfile?.street ?? deliveryProfile?.Street ?? '',
            city: deliveryProfile?.city ?? deliveryProfile?.City ?? '',
            zipcode: deliveryProfile?.zipcode ?? deliveryProfile?.Zipcode ?? '',
            mobile: deliveryProfile?.mobile ?? deliveryProfile?.Mobile ?? '',
            bankRegistrationNo: deliveryProfile?.bankRegistrationNo ?? deliveryProfile?.BankRegistrationNo ?? '',
            bankAccountNo: deliveryProfile?.bankAccountNo ?? deliveryProfile?.BankAccountNo ?? ''
        };
        const address = [normalizedProfile.street, normalizedProfile.city, normalizedProfile.zipcode]
            .filter(Boolean)
            .join(normalizedProfile.city ? ', ' : ' ');
        const isComplete = Boolean(
            normalizedProfile.name
            && normalizedProfile.street
            && normalizedProfile.city
            && normalizedProfile.zipcode
            && normalizedProfile.mobile
        );

        setUserProfile((previous) => ({
            ...(previous || {}),
            ...normalizedProfile,
            address,
            isProfileComplete: isComplete
        }));
        setIsProfileComplete(isComplete);
        setUser((previous) => {
            if (!previous) return previous;
            const updated = {
                ...previous,
                name: normalizedProfile.name || previous.name
            };
            localStorage.setItem('user', JSON.stringify(updated));
            return updated;
        });
    }, []);

    const fetchDeliveryPartnerCapability = useCallback(async (userId) => {
        if (!userId) {
            setHasDeliveryPartnerProfile(false);
            return false;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/delivery-partners/${userId}/profile`);
            if (!response.ok) {
                setHasDeliveryPartnerProfile(false);
                return false;
            }

            const profile = await response.json();
            setHasDeliveryPartnerProfile(true);
            syncDeliveryPartnerProfile(profile);
            return true;
        } catch (err) {
            console.error('Delivery partner capability fetch error:', err);
            setHasDeliveryPartnerProfile(false);
            return false;
        }
    }, [syncDeliveryPartnerProfile]);

    const fetchMasterData = useCallback(async () => {
        try {
            const [amenitiesRes, typesRes, mappingsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/amenities`),
                fetch(`${API_BASE_URL}/api/caravantypes`),
                fetch(`${API_BASE_URL}/api/caravantypes/amenity-mappings`)
            ]);

            if (!amenitiesRes.ok) throw new Error('Failed to fetch amenities');
            if (!typesRes.ok) throw new Error('Failed to fetch caravan types');
            if (!mappingsRes.ok) throw new Error('Failed to fetch type-amenity mappings');

            const amenitiesData = await amenitiesRes.json();
            const typesData = await typesRes.json();
            const mappingsData = await mappingsRes.json();

            setMasterAmenities(amenitiesData);
            setMasterCaravanTypes(typesData);
            setMasterTypeAmenityMappings(mappingsData);
        } catch (err) {
            setError(err.message);
            console.error("Master data fetch error:", err);
        }
    }, []);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Fetch all data concurrently
            const caravansRes = await fetch(`${API_BASE_URL}/api/caravans`);

            if (!caravansRes.ok) throw new Error('Failed to fetch caravans');

            const caravansData = await caravansRes.json();
            setCaravans(caravansData);
            await fetchMasterData();

        } catch (err) {
            setError(err.message);
            console.error("Fetch error:", err);
        } finally {
            setIsLoading(false);
        }
    }, [fetchMasterData]);

    useEffect(() => {

        try {
            const savedUser = localStorage.getItem('user');
            if (savedUser) {
                const parsedUser = JSON.parse(savedUser);
                setUser(parsedUser);
                if (parsedUser?.requiresPasswordChange) {
                    setRoute({ page: 'change-password' });
                }
                fetchUserProfile(parsedUser.userId);
                fetchOwnerCapability(parsedUser.userId);
                fetchDeliveryPartnerCapability(parsedUser.userId);
            }
        } catch (err) {
            console.error("Failed to parse user from localStorage", err);
            localStorage.removeItem('user');
        }

        fetchAllData();
    }, [fetchUserProfile, fetchAllData, fetchOwnerCapability, fetchDeliveryPartnerCapability]);

    useEffect(() => {
        const syncRouteFromLocation = () => {
            setRoute(getRouteFromLocation());
        };

        window.addEventListener('popstate', syncRouteFromLocation);
        return () => window.removeEventListener('popstate', syncRouteFromLocation);
    }, []);

    useEffect(() => {
        const detailCaravan = route?.page === 'detail'
            ? caravans.find((item) => Number(item?.caravan_id) === Number(route?.caravan_id)) || null
            : null;
        const targetHref = buildHrefFromRoute(route, detailCaravan);
        const currentHref = `${window.location.pathname}${window.location.search}`;

        if (targetHref !== currentHref) {
            window.history.pushState({ path: targetHref }, '', targetHref);
        }
    }, [caravans, route]);

    useEffect(() => {
        const protectedPages = ['booking', 'become-owner', 'owner-availability', 'owner-business', 'admin', 'change-password', 'delivery-portal', 'my-bookings', 'profile-details'];
        const profileRequiredPages = ['booking', 'become-owner', 'owner-availability', 'owner-business'];
        const ownerOnlyPages = ['owner-availability', 'owner-business'];

        if (protectedPages.includes(route.page) && !user) {
            setPreLoginRoute(route);
            setRoute({ page: 'login' });
            return;
        }
        if (user?.requiresPasswordChange && route.page !== 'change-password') {
            setRoute({ page: 'change-password' });
            return;
        }
        if (isOwnerProfileRoute(route.page) && user && !isOwnerProfileComplete) {
            setPostProfileRoute(route);
            setRoute({ page: 'profile-details' });
            return;
        }
        if (profileRequiredPages.includes(route.page) && user && !isProfileComplete) {
            setPostProfileRoute(route);
            setRoute({ page: 'profile-details' });
            return;
        }
        if (route.page === 'admin' && user?.role !== 'admin') {
            setRoute({ page: 'home' });
            return;
        }
        if (route.page === 'delivery-portal' && user && !hasDeliveryPartnerProfile) {
            setRoute({ page: 'home' });
            return;
        }
        if (ownerOnlyPages.includes(route.page) && user && !hasOwnerCaravans) {
            setRoute({ page: 'become-owner' });
        }
    }, [hasDeliveryPartnerProfile, hasOwnerCaravans, isOwnerProfileComplete, isProfileComplete, route, user]);

    const getCaravanById = useCallback((id) => {
        return caravans.find(c => c.caravan_id === id);
    }, [caravans]);

    const addCaravan = async (newCaravan) => {
        try {
            const { imageFiles = [], seasonPrices = [], ...caravanPayload } = newCaravan;
            if (imageFiles.length > MAX_CARAVAN_IMAGES) {
                throw new Error(`You can upload a maximum of ${MAX_CARAVAN_IMAGES} images.`);
            }
            const imageSizeError = buildImageSizeError(imageFiles);
            if (imageSizeError) {
                throw new Error(imageSizeError);
            }
            const response = await fetch(`${API_BASE_URL}/api/Caravans`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(caravanPayload),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to create caravan: ${errorText}`);
            }
            const createdCaravan = await response.json();
            const createdCaravanId = Number(
                createdCaravan?.caravan_id
                ?? createdCaravan?.caravanId
                ?? createdCaravan?.id
            );
            if (!Number.isFinite(createdCaravanId) || createdCaravanId <= 0) {
                throw new Error('Caravan created, but API did not return a valid caravan id.');
            }

            if (imageFiles.length > 0) {
                const formData = new FormData();
                formData.append('ownerId', String(newCaravan.owner_id));
                imageFiles.forEach((file) => formData.append('files', file));

                const uploadResponse = await fetch(`${API_BASE_URL}/api/caravans/${createdCaravanId}/images`, {
                    method: 'POST',
                    body: formData,
                });
                if (!uploadResponse.ok) {
                    throw new Error(`Caravan created, but image upload failed: ${await readApiErrorMessage(uploadResponse, 'Image upload failed.')}`);
                }
            }

            if (Array.isArray(seasonPrices) && seasonPrices.length > 0) {
                const seasonPriceResponse = await fetch(`${API_BASE_URL}/api/seasons/caravans/${createdCaravanId}/prices`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        owner_id: Number(newCaravan.owner_id),
                        prices: seasonPrices.map((row) => ({
                            season_id: Number(row.season_id),
                            price_per_day: Number(row.price_per_day),
                            price_per_week: Number(row.price_per_week),
                            price_per_month: row.price_per_month === null || row.price_per_month === undefined ? null : Number(row.price_per_month)
                        }))
                    })
                });
                if (!seasonPriceResponse.ok) {
                    throw new Error(`Caravan created, but seasonal pricing failed: ${await readApiErrorMessage(seasonPriceResponse, 'Seasonal pricing failed.')}`);
                }
            }
            await fetchAllData();
            await fetchOwnerCapability(newCaravan.owner_id);
        } catch (err) {
            console.error("Error creating caravan:", err);
            setError(err.message);
        }


    };

    const approveCaravan = async (caravanId) => {
        try {
            const approveUrl = `${API_BASE_URL}/api/caravans/${caravanId}/approve`;
            let response = await fetch(approveUrl, {
                method: 'POST',
            });
            if (response.status === 405) {
                response = await fetch(approveUrl, {
                    method: 'PUT',
                });
            }
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Failed to approve caravan'));
            }

            // No need to call fetchAllData here, as the AdminPage will refresh its own list.
        } catch (err) {
            setError(err.message || 'Could not approve the vehicle. Please check the API server.');
            console.error("Error approving caravan:", err);
        }
    };

    const saveTypeAmenityMapping = async (typeId, amenityIds) => {
        const response = await fetch(`${API_BASE_URL}/api/caravantypes/${typeId}/amenities`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ amenityIds }),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Failed to save type-amenity mapping');
        }
        const updatedAmenityIds = await response.json();
        setMasterTypeAmenityMappings(prev => {
            const next = prev.filter(m => Number(m.type_id) !== Number(typeId));
            return [...next, ...updatedAmenityIds.map(id => ({ type_id: typeId, amenity_id: id }))];
        });
        return updatedAmenityIds;
    };

    const handleNavigate = (newRoute) => {
        const protectedPages = ['booking', 'become-owner', 'owner-availability', 'owner-business', 'admin', 'change-password', 'delivery-portal'];
        const profileRequiredPages = ['booking', 'become-owner', 'owner-availability', 'owner-business'];
        const ownerOnlyPages = ['owner-availability', 'owner-business'];
        if (protectedPages.includes(newRoute.page) && !user) {
            setPreLoginRoute(newRoute);
            setRoute({ page: 'login' });
            return;
        }
        if (user?.requiresPasswordChange && newRoute.page !== 'change-password') {
            setRoute({ page: 'change-password' });
            return;
        }
        if (isOwnerProfileRoute(newRoute.page) && user && !isOwnerProfileComplete) {
            setPostProfileRoute(newRoute);
            setRoute({ page: 'profile-details' });
            return;
        }
        if (profileRequiredPages.includes(newRoute.page) && user && !isProfileComplete) {
            setPostProfileRoute(newRoute);
            setRoute({ page: 'profile-details' });
            return;
        }
        if (newRoute.page === 'admin' && user?.role !== 'admin') {
            setRoute({ page: 'home' });
            return;
        }
        if (newRoute.page === 'delivery-portal' && !hasDeliveryPartnerProfile) {
            setRoute({ page: 'home' });
            return;
        }
        if (ownerOnlyPages.includes(newRoute.page) && user && !hasOwnerCaravans) {
            setRoute({ page: 'become-owner' });
            return;
        }
        if (newRoute.page === 'become-owner') {
            fetchMasterData();
        }
        setRoute(newRoute);
    };

    const handleLogin = async (credentials) => {
        const authenticatedUser = {
            ...credentials,
            requiresPasswordChange: Boolean(credentials?.requiresPasswordChange)
        };
        localStorage.setItem('user', JSON.stringify(authenticatedUser)); // Save user to localStorage
        setUser(authenticatedUser);
        if (authenticatedUser.requiresPasswordChange) {
            setRoute({ page: 'change-password' });
            return;
        }
        const profile = await fetchUserProfile(credentials.userId);
        const ownerCapability = await fetchOwnerCapability(credentials.userId);
        const deliveryPartnerCapability = await fetchDeliveryPartnerCapability(credentials.userId);
        const profileComplete = Boolean(profile?.isProfileComplete);
        const ownerProfileComplete = profileComplete && hasValidOwnerBankDetails(profile);
        if (authenticatedUser.role === 'delivery_partner') {
            setPreLoginRoute(null);
            setRoute({ page: 'delivery-portal' });
            return;
        }
        if (preLoginRoute) {
            if (isOwnerProfileRoute(preLoginRoute.page) && !ownerProfileComplete) {
                setPostProfileRoute(preLoginRoute);
                setRoute({ page: 'profile-details' });
            } else if ((preLoginRoute.page === 'booking' || preLoginRoute.page === 'become-owner' || preLoginRoute.page === 'owner-availability' || preLoginRoute.page === 'owner-business') && !profileComplete) {
                setPostProfileRoute(preLoginRoute);
                setRoute({ page: 'profile-details' });
            } else if ((preLoginRoute.page === 'owner-availability' || preLoginRoute.page === 'owner-business') && !ownerCapability) {
                setRoute({ page: 'become-owner' });
            } else if (preLoginRoute.page === 'delivery-portal' && !deliveryPartnerCapability) {
                setRoute({ page: 'home' });
            } else {
                setRoute(preLoginRoute);
            }
            setPreLoginRoute(null);
        } else {
            setRoute({ page: 'home' });
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('user');
        setUser(null);
        setUserProfile(null);
        setIsProfileComplete(false);
        setHasOwnerCaravans(false);
        setHasDeliveryPartnerProfile(false);
        setPostProfileRoute(null);
        setRoute({ page: 'home' });
    };
    const handleSignUp = async (credentials) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Sign up failed');
            }

            const userData = await response.json();
            return {
                message: userData.message || 'Registration successful. You can log in now.',
                email: userData?.user?.email || credentials.email || ''
            };

        } catch (err) {
            console.error("Sign up error:", err);
            throw err;
        }
    };
    const handleDeliveryPartnerRegister = async (registrationData, existingUserId = null) => {
        try {
            const payload = new FormData();
            Object.entries(registrationData || {}).forEach(([key, value]) => {
                if (value === null || value === undefined || value === '') {
                    return;
                }

                if (key === 'drivingLicenseCopyFile') {
                    payload.append(key, value);
                    return;
                }

                if (typeof value === 'boolean') {
                    payload.append(key, value ? 'true' : 'false');
                    return;
                }

                payload.append(key, String(value));
            });

            const normalizedExistingUserId = Number(existingUserId);
            const isExistingUserRegistration = Number.isFinite(normalizedExistingUserId) && normalizedExistingUserId > 0;
            const endpoint = isExistingUserRegistration
                ? `${API_BASE_URL}/api/delivery-partners/register-existing/${normalizedExistingUserId}`
                : `${API_BASE_URL}/api/delivery-partners/register`;

            const response = await fetch(endpoint, {
                method: 'POST',
                body: payload
            });
            if (!response.ok) {
                throw new Error(await readApiErrorMessage(response, 'Delivery partner registration failed.'));
            }

            const data = await response.json();
            if (isExistingUserRegistration) {
                setHasDeliveryPartnerProfile(true);
            }
            return {
                message: data?.message || 'Delivery partner registration submitted. Admin approval is required before jobs are visible.',
                email: data?.user?.email || registrationData?.email || '',
                existingUser: isExistingUserRegistration
            };
        } catch (err) {
            console.error('Delivery partner sign up error:', err);
            throw err;
        }
    };

    const handleForgotPassword = async ({ email }) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to process forgot password request.');
            }

            return data?.message || 'If this email is registered, a temporary password has been sent.';
        } catch (err) {
            console.error("Forgot password error:", err);
            throw err;
        }
    };

    const handleChangePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
        if (!user?.userId) {
            throw new Error('User context is missing.');
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.userId,
                    currentPassword,
                    newPassword,
                    confirmPassword
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to change password.');
            }

            setUser(prev => {
                if (!prev) return prev;
                const updated = { ...prev, requiresPasswordChange: false };
                localStorage.setItem('user', JSON.stringify(updated));
                return updated;
            });

            return data?.message || 'Password changed successfully.';
        } catch (err) {
            console.error("Change password error:", err);
            throw err;
        }
    };

    // --- Main App Component Function ---
    // This function would be inside your main App.js file and passed down as a prop.
    const handleCreateBooking = async (bookingDto) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bookingDto),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to create booking: ${errorText}`);
            }
            return await response.json();
        } catch (err) {
            console.error("Booking creation error:", err);
            throw err;
        }
    };




    const renderPage = () => {
        switch (route.page) {
            case 'list':
                return <CaravanListPage onNavigate={handleNavigate} caravans={caravans} isLoading={isLoading} error={error} caravanTypes={masterCaravanTypes} />;
            case 'detail':
                return <CaravanDetailPage caravan_id={route.caravan_id} onNavigate={handleNavigate} user={user} initialStartDate={route.startDate || ''} initialEndDate={route.endDate || ''} />;
            case 'login':
                return <LoginPage onLogin={handleLogin} onNavigate={handleNavigate} initialEmail={route.email || ''} initialMessage={route.message || ''} />;
            case 'forgot-password':
                return <ForgotPasswordPage onForgotPassword={handleForgotPassword} onNavigate={handleNavigate} initialEmail={route.email || ''} />;
            case 'signup':
                return <SignUpPage onSignUp={handleSignUp} onNavigate={handleNavigate} />;
            case 'become-delivery-partner':
                return <DeliveryPartnerRegistrationPage onRegister={handleDeliveryPartnerRegister} onNavigate={handleNavigate} user={user} profile={userProfile} />;
            case 'change-password':
                if (!user) return <LoginPage onLogin={handleLogin} onNavigate={handleNavigate} />;
                return <ChangePasswordPage user={user} onChangePassword={handleChangePassword} onNavigate={handleNavigate} />;
            case 'profile-details':
                if (!user) return <LoginPage onLogin={handleLogin} onNavigate={handleNavigate} />;
                return <ProfileDetailsPage user={user} profile={userProfile} onSaveProfile={saveUserProfile} onNavigate={handleNavigate} redirectRoute={postProfileRoute} hasOwnerCaravans={hasOwnerCaravans} hasDeliveryPartnerProfile={hasDeliveryPartnerProfile} />;
            case 'delivery-portal':
                if (!user) return <LoginPage onLogin={handleLogin} onNavigate={handleNavigate} />;
                return <DeliveryPortalPage user={user} onNavigate={handleNavigate} onSyncProfile={syncDeliveryPartnerProfile} hasDeliveryPartnerProfile={hasDeliveryPartnerProfile} />;
            case 'booking':
                return <BookingPage caravanId={route.caravanId} onNavigate={handleNavigate} getCaravanById={getCaravanById} user={user} onCreateBooking={handleCreateBooking} initialStartDate={route.startDate || ''} initialEndDate={route.endDate || ''} />;
            case 'booking-success':
                return <BookingSuccessPage caravanName={route.caravanName} ownerContactNumber={route.ownerContactNumber} onNavigate={handleNavigate} />;
            case 'become-owner':
                return <BecomeOwnerPage onNavigate={handleNavigate} onAddCaravan={addCaravan} user={user} masterAmenities={masterAmenities} masterTypes={masterCaravanTypes} typeAmenityMappings={masterTypeAmenityMappings} onRefreshMasterData={fetchMasterData} />;
            case 'owner-availability':
                return <OwnerAvailabilityPage user={user} onNavigate={handleNavigate} />;
            case 'owner-business':
                return <OwnerBusinessPage user={user} onNavigate={handleNavigate} />;
            case 'admin':
                return <AdminPage user={user} caravans={caravans} onApproveCaravan={approveCaravan} initialTypeAmenityMappings={masterTypeAmenityMappings} onSaveTypeAmenityMapping={saveTypeAmenityMapping} />
            case 'my-bookings':
                return <MyBookingsPage user={user} onNavigate={handleNavigate} />;
            case 'terms':
                return <TermsPage onNavigate={handleNavigate} />;
            case 'about':
                return <AboutPage onNavigate={handleNavigate} fallbackCaravanCount={caravans.length} />;
            case 'home':
            default:
                return <HomePage onNavigate={handleNavigate} caravans={caravans} isLoading={isLoading} error={error} />;
        }
    };

    const t = useCallback((key) => {
        return translations[language]?.[key] || translations.en[key] || key;
    }, [language]);

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            <div className="bg-stone-50" style={{ fontFamily: "'Poppins', sans-serif" }}>
                <Header
                    user={user}
                    onNavigate={handleNavigate}
                    onLogout={handleLogout}
                    isProfileComplete={isProfileComplete}
                    showManageMenu={hasOwnerCaravans}
                    currentPage={route.page}
                    hasDeliveryPartnerProfile={hasDeliveryPartnerProfile}
                />
                <main className="min-h-screen">
                    {renderPage()}
                </main>
                <Footer onNavigate={handleNavigate} />
            </div>
        </LanguageContext.Provider>
    );
}

























